/**
 * Casualty Calculator v5.58.0 - PSP/CRE Tool v4.0
 *
 * v5.58.0 (April 10, 2026) — V4-013: Sub-phase Brigade/Division Tactical Differentiation.
 *   NEW IMPORT: getMissionCasualtyMultiplier from constants/missions.js.
 *   NEW CONSTANTS: _SUB_PHASE_TRANSITION_SURGE_TABLE (8 transition pairs + DEFAULT).
 *   NEW HELPERS: _getSubPhaseTransitionSurge(), _getSubPhaseForDay().
 *   In day loop (non-march path):
 *     - Pre-declare _subPhaseMissionMod, _subPhaseTransitionMod at outer scope (survive if/else).
 *     - Resolve _activeSub = _getSubPhaseForDay(phase.sub_phases, dayInPhase, phaseDuration).
 *     - If sub_phases valid: _subPhaseMissionMod = getMissionCasualtyMultiplier(activeSub.mission_type);
 *       terrain override = activeSub.terrain ?? scenario.environment.terrain;
 *       transition surge fires when _prevSubPhaseMission changes.
 *     - Terrain override passed to calculateDailyBattleCasualties() as brigade.environment.terrain.
 *     - After phase transition surge: if !isMarch and combined modifier != 1.0, scale kia/wia/total.
 *   Backward compat: sub_phases absent/empty/sum-mismatch → all modifiers = 1.0, no-op.
 *
 * v5.57.0 (April 8, 2026) — V3.1-BLOOD-STREAM2: Civilian T1 surgical blood demand.
 *   After the DCS blood supplement block (daily loop), civilian T1 patients from MRoE
 *   Stream 2 (_mroeLoad.civilian_stream.t1_surgical_daily) are added to blood totals at
 *   the same per-patient rate as military T1 (bloodByMechanism.average_per_casualty or
 *   fallback 6 PRBC / 5 FFP). Additive to WBB, MTP, DCS adjustments. Flows through the
 *   existing path: resourceResult.blood.requirements.totals.prbc -> daySummary.blood_prbc
 *   -> brigadeResult.totals.blood_consumed -> medicalSystemState.blood_dos.
 *   No-op when civilian_stream absent or t1_surgical_daily === 0 (backward compat).
 *
 * v5.56.0 (April 5, 2026) — V4-TAB6-004: Pre-combat build-up phase DNBI pre-computation.
 *   Before the brigade loop: IIFE runs calculateComprehensiveDNBI() for each build-up day
 *   using SUPPORT_ACTIVE state x 1.2 deployment stress modifier. Derives day0_medical_load
 *   (last 3 days x 15% evac fraction), effective_starting_strength, strength_reduction_pct.
 *   Result stored in results.mascal_analysis.buildup_phase (null when not configured).
 *   No effect on brigade strength in main loop — informational only. Backward compat: absent
 *   when buildup_config not in operation_parameters (Tab5 panel guards with ?.).
 *
 * v5.55.0 (April 5, 2026) — BUG-R2E-MARCH-001: R2E/R3 march backlog accumulator.
 *   buildAggregatedBedTimeline() was zeroing rawAdmissions when echelonCapacityFraction===0
 *   (MTF closed during march) without accumulating them in a backlog counter. Patients
 *   generated during march phases were permanently lost — post-march R2E census understated
 *   15-25%. Fix mirrors buildBrigadeR2BBedTimeline: added movementBacklogAgg counter,
 *   accumulate when fraction=0, drain up to 50% of headroom on first post-march days.
 *   Added movement_backlog and backlog_admitted to timeline entries; had_march_phases and
 *   peak_movement_backlog to return summary. Backward compat: new fields appended only.
 *
 * v5.54.0 (April 5, 2026) — V4-ECH-002: R2E destruction inject (per-division delta).
 *   Inside _divisions.map(): second buildAggregatedBedTimeline call with beds=0 and
 *   drainCapacityByDay=null (tactical evac disrupted by R2E loss) per division.
 *   Fallback path (no division structure) also gets same second pass.
 *   r2e_absent_delta added to each division object in _r2eDivisions.
 *   Fields: r2e_beds_removed, first_critical_day_without_r2e, baseline_critical_days,
 *   critical_days_without_r2e, total_unmet_patient_increase, excess_t1_deaths_estimate,
 *   avg_t1_fraction, r3_overflow_increase.
 *
 * v5.53.0 (April 5, 2026) — V4-ECH-001: R3-absent dual calculation.
 *   buildAggregatedBedTimeline() called a second time with echelonBeds=0 after
 *   baseline R3 timeline is computed. Second pass uses same brigade inputs, same
 *   ROLE_3 admission fraction, but 0 beds and no STRATEVAC drain (R3 loss disrupts APOD).
 *   Delta metrics: first_critical_day_without_r3, critical_days_without_r3,
 *   total_unmet_patient_increase, excess_t1_deaths_estimate (unmet × avg T1 fraction),
 *   r2e_overflow_increase, baseline_critical_days, avg_t1_fraction, r3_beds_removed.
 *   Output: results.mascal_analysis.r3_absent_delta (null when r3_configured=false).
 *   No re-run of full calculator — lightweight second pass using existing method only.
 *
 * v5.52.0 (April 5, 2026) — V4-CAL-001: EPW two-stream patient load.
 *   _mroeLoad now includes epw_stream and civilian_stream objects.
 *   epw_stream: reads pre-computed epw_stream from mroeModifs (V4 getMRoEModifiers),
 *     falls back to derived values for backward compat with V3.x saved scenarios.
 *   civilian_stream: reads pre-computed civilian_stream from mroeModifs (V4),
 *     includes triage profile (urban/rural), T1/T2/T3 breakdown, competes_with_military flag.
 *   EPW_BED_MULT 1.4 retained as local constant; canonical value moved to mroeConfig.EPW_BED_MULT.
 *   epw_level / civilian_level fields now read epw_volume / civilian_density first (V3.1 field names).
 *
 * v5.51.0 (March 28, 2026) — ENH-048b + ENH-048c: Monte Carlo wiring for surgical_capacity and golden_hour_success:
 *
 * v5.51.0 (March 28, 2026) — ENH-048b + ENH-048c: Monte Carlo wiring for surgical_capacity and golden_hour_success:
 *   ENH-048b: surgical_capacity modifier (±20%) stacked with ENH-044 Class VIII degradation (_class8CapMod).
 *     Reads scenario.operation_parameters.monte_carlo_modifiers.surgical_capacity.
 *     _effectiveSurgMod = _class8CapMod * _mcSurgCapMod. Applied to _dowMedConfig.surgical_capacity.
 *     surgical_capacity > 1.0 = more throughput = more T1 survive = lower DOW.
 *     surgical_capacity < 1.0 = degraded capacity = more T1 die = higher DOW.
 *     Activates _dowMedConfig even when Class VIII is not depleted (was _class8CapMod < 1.0 only).
 *   ENH-048c: golden_hour_success modifier (±30%) applied to DOW rate chain as inverse multiplier.
 *     Reads scenario.operation_parameters.monte_carlo_modifiers.golden_hour_success.
 *     _mcGoldenHourDOWMod = 1 / golden_hour_success (higher success = lower DOW rate).
 *     Applied to baseDOWRate in both TCCC-2025 path and non-TCCC path.
 *     Position: after FRT modifier, before TCCC (same as other Role 1 modifiers).
 *
 * v5.50.0 (March 28, 2026) — ENH-043: Per-brigade force quality resolution:
 *   ROOT CAUSE: Line 7154 passed force?.force_quality || {} (single corps-level object) to
 *     calculateBrigadeCasualties() for EVERY brigade, ignoring any division or brigade-level
 *     force_quality_override values set in Tab1 UnitEditDialog → ForceQualityOverrideSection.
 *     A coalition corps with brigades from different nations (e.g., NATO vs partner forces)
 *     would show identical training/morale/experience modifiers regardless of user configuration.
 *   FIX: Import resolvePerBrigadeQuality() from combatEffectivenessModifiers.js (ENH-043 utility).
 *     At line 7154: forceQuality now calls resolvePerBrigadeQuality(normalizedBrigadeConfig, force)
 *     which walks Corps → Division → Brigade override chain and returns the fully-resolved
 *     force quality object for each brigade. Falls back to corps baseline when no overrides exist
 *     (100% backward compatible — existing scenarios unaffected).
 *   RESOLVER LOGIC (in combatEffectivenessModifiers.js v1.2):
 *     1. Start: DEFAULT_FORCE_QUALITY merged with force.force_quality (corps baseline)
 *     2. Apply: division.force_quality_override (if brigade belongs to a division)
 *     3. Apply: brigade.force_quality_override (highest priority)
 *     Null/undefined override values are skipped — inherits from level above.
 *   NO SCHEMA CHANGE: force_quality_override already in data model; Tab1 UI already supports it.
 *
 * v5.49.0 (March 27, 2026) — ENH-044 + ENH-046 + ENH-047:
 *   ENH-044: Wire Class VIII days_of_supply → MTF surgical capability degradation.
 *     When operation day exceeds medicalConfig.blood_config.days_of_supply, DOW calculation
 *     receives a degraded surgical_capacity (_class8CapMod: <3 DOS → 0.75×, <1 DOS → 0.50×).
 *     Configured via Tab3 blood_config section. Default (0/absent): no change (backward compat).
 *   ENH-046: Phase transition demand surge.
 *     Detects posture change between consecutive phases. On day 1 of transition phase, applies
 *     surge multiplier to battle casualties (DEFENSE→OFFENSE: ×1.50, OFFENSE→DEFENSE: ×1.25,
 *     MARCH→COMBAT: ×1.40, other: ×1.15). Logged in daySummary.phase_transition for Tab5/6.
 *   ENH-047: Routine overflow routing in cross-division support.
 *     New crossDivConfig fields: routine_overflow_enabled (bool, default false) +
 *     routine_overflow_threshold (%, default 80). When enabled, cross-division activates
 *     at ≥threshold% utilization even without full MASCAL condition.
 *
 * v5.46.1 (March 26, 2026) — BUG-212: MTF ramp mismatch console warnings.
 *   Root cause: runtime validator used ceil(hours/24) + 1 to predict expected ramp length,
 *   but the correct formula is + 2 (day-0 halt entry + ceil(hours/24) intermediate days +
 *   final 100% entry). R2B(12h): expected 2 vs actual 3; R2E(24h): 2 vs 3; R3(48h): 3 vs 4.
 *   The ramp arrays in casualtyCalculatorConstants.js are correct — only the validator was wrong.
 *   Fix: changed + 1 to + 2 in _expected formula. No functional impact — dev-only guard.
 *
 * v5.45.0 (March 18, 2026) — BUG-159: Remove silent R3 200-bed fallback in MASCAL timeline.
 *   Root cause: `_r3Beds = _totalR3Beds > 0 ? _totalR3Beds : 200` applied 200 R3 beds when
 *   no Role 3 MTF was configured, violating APP_RULES no-silent-fallback rule. Planners saw
 *   "200 total R3 beds" in MASCAL despite Tab 3 showing Role 3: 0/0.
 *   Fix: _r3Beds = _totalR3Beds (no fallback). Added r3_configured: bool to mascal_analysis
 *   so Tab5 can surface a planning gap warning when r3_configured=false.
 *   Also fixed `role3?.beds || 200` default in mtf_structure fallback path → `?? 0`.
 * v5.42.0 (March 18, 2026) — BUG-146: getResupplyConfiguration units fallback.
 *   Root cause: calculateResupplyUnits() does PUSH_PACKAGES catalog lookup only.
 *   Custom Tab4 events store PRBC in event.supplies.blood_products.prbc_units,
 *   not in the catalog. Catalog miss returned {prbc:0,...} → Tab6 showed PRBC=0.
 *   supplyChainModule.js format-4 path already reads event.supplies.blood_products
 *   directly (actual simulation was correct). Now getResupplyConfiguration mirrors
 *   that fallback: catalog first, then event.supplies.blood_products, then zero.
 *   No change to stock simulation — display/reporting path only.
 *
 * v5.44.0 (March 15, 2026) — Phase 6: Configurable MASCAL Thresholds.
 *   assessMASCALStatus() fallback block now reads user-configured thresholds from
 *   medicalConfig.mascal_config.thresholds (saved by Tab 3 v4.25.0).
 *   Falls back to MASCAL_TRIGGERS constants when absent (backward compatible).
 *   No new imports — MASCAL_TRIGGERS already imported.
 *
 * v5.43.0 (March 15, 2026) — ENH-002 Phase 4: getEffectiveAssets per-day wiring.
 *   IMPORT: getEffectiveAssets added to medevacModule.js import block.
 *   In the tactical_evac_analysis IIFE (per-day brigade loop), after getTacticalEvacConfig():
 *   looks up brigade's R2B facility in medicalConfig.per_facility_evacuation_assets,
 *   calls getEffectiveAssets(r2bFacility, day) to compute delta_heli and delta_amb for
 *   that specific operation day. Applies deltas to the synthetic medevacFacility
 *   (helicopters.allocated ± delta_heli) and ambulanceFacility (wheeled buckets ± delta_amb)
 *   before passing to calculateAirCapacity / calculateGroundCapacity.
 *   Result: attachments increase effective capacity on active days; detachments decrease it.
 *   Backward compatible: facilities with no attached/detached arrays produce zero delta.
 *   Fallback: if no matching R2B found, falls back to any ROLE2_BASIC in perFacility.
 *   If perFacility is absent entirely, static tec is used unchanged (pre-ENH-002 behaviour).
 *
 * v5.42.0 (March 14, 2026) — Rule 3.2 + Rule 5.1 compliance:
 *   Rule 5.1: import STATUS_ICONS, TACTICAL_ICONS, GENERAL_ICONS, ARROW_ICONS
 *     from utils/icons.js. All bare emoji literals in code replaced:
 *     getPhaseIndicator() BRIGADE/DIVISION/CORPS/fallback => GENERAL_ICONS/ARROW_ICONS.
 *     Warning level icons => STATUS_ICONS.RED/YELLOW/INFO.
 *     movement_indicator.icon '[march]' => TACTICAL_ICONS.MARCH_TACTICAL.
 *   Rule 3.2: MTF_SETUP_TIMES_HOURS re-imported and wired into dev-mode runtime
 *     validation (ramp length cross-check vs setup hours).
 *     MTF_SETUP_RAMP_R3 re-imported as named anchor for ENH-006 Phase 0 R3 retrograde.
 * v5.41.0 (March 14, 2026) — IMP-MARCH-001: MTF availability during movement phases.
 *   Research finding: medical_facilities_operational flag was SET on daySummary (since v4.40)
 *   but NEVER READ by buildBrigadeR2BBedTimeline or buildAggregatedBedTimeline. March-phase
 *   DNBI was admitted to R2B at full rate despite the facility being documented as closed.
 *   Four sub-components implemented:
 *
 *   Sub A: MTF_SETUP_TIMES_HOURS + MTF_SETUP_RAMP_R2B/R2E/R3 constants added inline
 *     (TODO: migrate to casualtyCalculatorConstants.js next session).
 *     R2B ramp: 0% on halt day, 50% day 1 post-march, 100% from day 2 (AJMedP-1 / ENH-006 12h).
 *     R2E ramp: 0% halt day, 0% day 1, 100% from day 2 (24h estimated).
 *     R3: no ramp applied — corps-level facility does not displace during tactical movement.
 *
 *   Sub B: buildBrigadeR2BBedTimeline admission gate.
 *     When daySummary.is_march_operation === true: rawAdmissions = 0.
 *     Patients who cannot be admitted accumulate in movementBacklogTotal (running counter).
 *     When MTF re-opens: backlog admitted at up to 50% of available headroom per day.
 *     New timeline fields: is_march, capacity_fraction, movement_backlog, backlog_admitted,
 *     days_since_last_march. Return object gains: had_march_phases, peak_movement_backlog.
 *
 *   Sub C: Setup ramp wired to effectiveBeds.
 *     effectiveBeds = r2bBedsForBrigade * capacityFraction.
 *     effectiveSurgeCap = floor(effectiveBeds * 1.10).
 *     Occupancy denominator stays r2bBedsForBrigade (planning reference unchanged).
 *
 *   Sub D: buildAggregatedBedTimeline (R2E) extended.
 *     New marchRamp parameter (default null = no march reduction, used for R3).
 *     allMarching flag: true when ALL brigades in echelon have is_march_operation on day i.
 *     Capacity fraction computed from marchRamp[daysSinceMarchAgg].
 *     MTF_SETUP_RAMP_R2E passed to both R2E call sites; null passed to R3 call site.
 *     New timeline fields per day: is_march, capacity_fraction.
 *
 * v5.41.0 (March 17, 2026) — BUG-142: Apply casualty_rate_modifier from monte_carlo_modifiers
 *   to battle casualties. monteCarloSimulator sets modifier per iteration (±20%) but the
 *   battle path never read it — only dnbiModule received scenario. With 1000 iterations
 *   the spread was 7 casualties instead of ~300+ because the primary driver was ignored.
 *   Fix: read casualty_rate_modifier and scale battleResult.battle_casualties before
 *   sub-unit path. No-op when modifier absent (deterministic runs). (March 17, 2026)
 *
 * v5.46.0 (March 24, 2026) — BUG-194: BRIGADE echelon calculator crash.
 *   Root cause: validateScenario(), buildScenarioSummary(), and the main calculate()
 *   brigadeConfigs resolver all used a two-path lookup:
 *     force.brigades[]  OR  force.forceBuilderResult.brigades[]
 *   At BRIGADE echelon neither array exists — the top-level unit lives in
 *   force.forceBuilderResult.brigade (singular). Validation threw:
 *     "At least one brigade required (force.brigades or force.forceBuilderResult.brigades must be an array)"
 *   Fix: Added third path in all three resolvers — wrap singular brigade in array.
 *   Tab 3 calculator crash eliminated; Tab 4/5 downstream unblocked.
 *
 * v5.45.0 (March 18, 2026) — BUG-159: Remove silent R3 200-bed fallback in MASCAL timeline.
 *   Replaces passive sliding-window (LOS exit) with commander-driven evacuation pump.
 *   Bug 1 (already fixed v5.8.0): phase-varying admissions — by_day already read correctly.
 *   Bug 2 (this fix): passive drain replaced with active drain in all three census builders:
 *     buildBrigadeR2BBedTimeline() — R2B drain = tactical_evac_analysis.total_capacity_per_day
 *     buildAggregatedBedTimeline() — new optional drainCapacityByDay[] parameter (default null=0)
 *     R2E call sites — drainByDay computed from Σ brigade tactical_evac per day
 *     R3 call site  — drainByDay computed from STRATEVAC air (computeDepartureDays + STRATEVAC_AIRCRAFT)
 *                     + train (computeTrainDepartureDays + getTrainCapacityPerDeparture); all try/catch.
 *   Surge cap: physical_beds × 1.10. Overflow → unmet_demand field (DOW risk signal).
 *   New timeline fields: rtd_drain, evac_drain, unmet_demand. Existing fields unchanged.
 *   Result: MASCAL resolves in 1–3 days (matches Dr. Tatar exercise experience + SABRE output).
 *   455% sustained occupancy was a model artifact — no longer possible with active drain.
 *
 * v5.39.0 (March 13, 2026) — BUG-109: WBB days_activated deduplication.
 *   Root cause: calculateTotalMedicalRequirements() accumulated days_activated with .push()
 *   inside a per-brigade loop — producing one entry per brigade per activated day (e.g.
 *   16 brigades × Day 5 active = "Day 5" appears 16 times → 220+ entries for 20-day op).
 *   Fix: internal Set of day indices deduplicates cross-brigade; converted to sorted string
 *   array after loop. total_activations now = unique calendar days (any brigade active),
 *   not brigade×day count. units_contributed still summed additively (correct — whole corps).
 *   Output shape unchanged: { total_activations, total_units_contributed,
 *   total_universal_units, days_activated[] }. Tab5 render unchanged.
 *
 * v5.38.0 (March 13, 2026) — ENH-019: MROE/EPW load computed from scenario.mroe_config.modifiers; emitted to mascal_analysis.mroe_load
 *   v5.25.0 was recorded as CLOSED in prior session but code was never delivered.
 *   Three additions:
 *   (1) IMPORT: getHNSModifiers from hnsConfig.js.
 *   (2) COMPUTE: hnsModifiers = getHNSModifiers(scenario.operation_parameters.hns_config)
 *       once before the brigade loop — scenario-level, not per-brigade.
 *       Returns all-zero inert object when HNS disabled (backward compatible).
 *   (3a) APPLY DNBI absorption: after both march and normal DNBI branches,
 *       hnsAbsorbed = round(total_dnbi × dnbi_absorption). total_dnbi reduced,
 *       rtd.expected_rtd scaled proportionally. dnbiResult.hns_absorbed emitted
 *       for Tab5 HNS panel display. No-op when hnsModifiers.is_active === false.
 *   (3b) APPLY R3 bed augmentation: after _totalR3Beds (organic + PHA) computed
 *       in MASCAL analysis, adds round(_totalR3Beds × bed_augmentation_percent).
 *       mascal_analysis.hns_r3_beds_added emitted for Tab5 transparency.
 *       No-op when hnsModifiers.is_active === false (augmentation = 0).
 *   Tab5 renderHNSPanel() reads projections.by_brigade DNBI + mascal_analysis —
 *   no Tab5 changes required; hns_absorbed already consumed by existing HNS panel.
 *
 * v5.35.0 (March 12, 2026) — IMP-008 / BUG-085: Cohesion modifier implemented.
 *   Root cause of BUG-085 ("–" for all 14 days in Tab5 Force Strength table):
 *   IMP-008 was recorded as CLOSED in CURRENT_STATE v4.14 but the code was never
 *   delivered. getCohesionModifier/getCohesionBand were absent from all imports;
 *   daySummary.cohesion was never written; modifier was never applied.
 *   Three additions to the per-brigade per-day loop:
 *   (1) IMPORT: getCohesionModifier, getCohesionBand, STRENGTH_COHESION_MODIFIERS
 *       from operationalStates.js (v2.3).
 *   (2) COMPUTE: strengthPercent = currentStrength / initialStrength × 100,
 *       cohesionModifier, cohesionBand, cohesionDef — placed after <30% guard.
 *   (3) APPLY: battleResult.battle_casualties.kia/wia/total × cohesionModifier
 *       BEFORE force superiority (Dupuy TNDM: internal degradation precedes
 *       external force-ratio comparison). Branch skipped when modifier === 1.0
 *       (GREEN / ≥85% strength — majority of Day 1–3 in most scenarios).
 *   (4) EMIT: daySummary.cohesion { strength_percent, band, modifier, fm_color,
 *       description } — read by Tab5 dailyProjectionData worstCohesion aggregation.
 *   OUTPUT IMPACT: Cohesion modifier 1.00/1.08/1.20/1.40 applies once units drop
 *   below 85% strength. Day 1 at full strength: no change. Prolonged high-intensity
 *   operations (Iron Rampart Day 13 at 608% R3): cohesion amplifies late-day losses.
 *
 * v5.34.2 (March 12, 2026) — BUG-084: Fixed false NO_MEDICAL_BRIGADE warning:
 *   - validateMedicalBrigadeCoverage(): ForceService excludes Medical Brigade from
 *     force.brigades (routed to separate medicalBrigades array). brigadeConfigs therefore
 *     never contained unit_type === 'MEDICAL', so hasMedicalBrigade was always false.
 *   - Fix: Primary check now reads forceBuilderResult.has_medical_brigade /
 *     has_medical_brigades / medical_brigades[] flags set by ForceService.
 *     Secondary check retains brigadeConfigs scan for legacy structures, adds
 *     b.is_medical_brigade === true to the predicate.
 *
 * v5.36.0 (March 27, 2026) — ENH-041: MASCAL discrete event trigger.
 *   scenario.operation_parameters.discrete_events[]: { id, event_type, day, brigade_id,
 *   casualty_count, duration_minutes, kia_fraction (opt), description (opt) }
 *   In day loop, after Monte Carlo modifier: events matching day+brigade_id are overlaid
 *   on battleResult via applyDiscreteEvent() (imported from mascalThresholds.js).
 *   assessMASCALCondition() re-run with combined load if any events fired → correct MASCAL.
 *   daySummary.discrete_events: { count, total_casualties, events[], triggered_mascal }.
 *   If no events on a day: null (zero overhead, backward compatible).
 *   DISCRETE_EVENT_TYPES constant added to mascalThresholds.js:
 *   ARTILLERY_STRIKE / IED_MASS_CASUALTY / AMBUSH / ENEMY_COUNTERATTACK /
 *   MEDEVAC_ASSET_LOSS / FACILITY_STRIKE / LOC_INTERDICTION.
 *
 * v5.35.0 (March 27, 2026) — ENH-035: medevacPools allocation data path fix.
 *   calculateComprehensiveMEDEVAC() was always receiving medevacConfig: null because
 *   medical_configuration.medevac_config does not exist — Tab3 saves helicopter data to
 *   tactical_evac_config.air_pool (fixed in AMB-002 BUG 4 for tactical_evac_analysis path,
 *   but the comprehensive MEDEVAC path was never updated).
 *   Result: calculateHelicopterCapacity() always returned totalAircraft=0 → zero helicopter
 *   capacity → golden hour analysis always used ground-only numbers.
 *   Fix: build medevacConfigBuilt.pools from tactical_evac_config.air_pool before both
 *   calculateComprehensiveMEDEVAC() calls (phase25 + legacy). helicopterCount derived
 *   via nominal_daily_sorties / NOMINAL_SORTIES_PER_AIRCRAFT (=6) — same as getTacticalEvacConfig().
 *   Zero sorties → zero aircraft → medevacModule returns correct zero-capacity doctrinal result.
 *
 * v5.34.1 (March 12, 2026) — BUG-073: Removed 3x console.log (FRT-DOW, CSU-BED, PHA-BED)
 *
 * v5.34.0 (March 11, 2026) — ENH-018-C/D: Doctrinal constants + surgical/routing:
 *   - NEW IMPORT: BASE_CASUALTY_RATE, SUPPORT_ECHELON_CASUALTY_MULTIPLIER,
 *     SUPPORT_ECHELON_SURGICAL_FRACTION, SUPPORT_ECHELON_CASUALTY_DISTRIBUTION,
 *     SUPPORT_ECHELON_BYPASSES_ROLE_2B from casualtyCalculatorConstants.js.
 *   - calculateSupportUnitCasualties(): replaced hardcoded 0.003 with
 *     BASE_CASUALTY_RATE.daily × SUPPORT_ECHELON_CASUALTY_MULTIPLIER.
 *     Replaced inline 0.08/0.04/0.33/0.55 fractions with SUPPORT_ECHELON_CASUALTY_DISTRIBUTION.
 *     Added echelon_surgical_cases (WIA × SUPPORT_ECHELON_SURGICAL_FRACTION).
 *     Added echelon_bypasses_role2b flag to return object.
 *
 * v5.33.0 (March 11, 2026) — Corps/Division Organic Support Troops:
 *   - extractPersonnelBreakdown(): reads forceResult.support_troops.total_organic_strength
 *     (corps-level) and forceResult.division_support_strength (all division support troops)
 *     from ForceService v2.13.0. Exposes echelon_organic_support, corps_support_strength,
 *     division_support_strength. Backwards-compatible: returns 0 if fields absent.
 *   - calculateSupportUnitCasualties(): two-stream calculation:
 *     (a) HQ internal support staff: 0.7%/day (unchanged, BUG-065 rate)
 *     (b) Echelon organic support troops: 0.3%/day rear-area rate. Distribution
 *         8/4/33/55 KIA/DOW/WIA/DNBI (DNBI-dominant, Ukraine 2022-2024 + Dupuy).
 *     Returns hq_support_personnel, echelon_support_personnel, sub-totals.
 *     All existing return fields preserved — NonCombatPersonnelSection unaffected.
 *
 * v5.32.0 (March 10, 2026) — ENH-016: Friendly Air Situation modifier wired.
 *   - NEW IMPORT: AIR_SITUATION_LEVELS from airSituation.js
 *   - Reads scenario.operation_parameters.air_situation_config.level once per brigade.
 *   - Applies casualty_modifier after drone mod in battle casualty chain (PARITY=1.0 no-op).
 *   - daySummary.air_situation block emitted (level, label, modifier, medevac_ad_override).
 *   - Default PARITY (1.0×) = backward compatible with all existing saved scenarios.
 *   - MEDEVAC AD override handled in medevacModule.js v5.7.0 (see calculateComprehensiveMEDEVAC).
 *
 * v5.31.0 (March 10, 2026) — ENH-028 / BUG-ENG: Engagement level no longer hardcoded.
 *   scenario.operation_parameters.engagement_level is now passed to calculateDailyBattleCasualties.
 *   ENGAGEMENT_LEVELS keys: FULL (1.0×), SUPPORT (0.3×), RESERVE (0.0×), SECURITY (0.1×).
 *   'ACTIVE' was hardcoded previously — key does not exist in ENGAGEMENT_LEVELS → always
 *   fell back to multiplier 1.0. Tab2 saves valid keys (FULL/SUPPORT/RESERVE/SECURITY).
 *   Fix: engagement_level: scenario?.operation_parameters?.engagement_level || 'FULL'
 *   Backward-compat default: 'FULL' (1.0× — same effective behaviour as the broken 'ACTIVE').
 * v5.30.0 (March 10, 2026) — ENH-025: Drone Warfare Environment wired end-to-end (Cat-25).
 *   - NEW IMPORT: DRONE_THREAT from threatAssessment.js
 *   - Reads scenario.operation_parameters.threat_assessment.drone_threat once per brigade.
 *   - AXIS A — Casualty modifier: applied after force superiority + theater scalar.
 *     SATURATED = 1.8× KIA+WIA. NONE = 1.0 (branch skipped). No triage recalc needed —
 *     triageDistribution is computed from battleResult.battle_casualties.wia which is
 *     already post-modifier at that point (same pattern as force superiority).
 *   - AXIS B — DOW modifier: droneAdjustedDOW inserted after routeAdjustedDOW.
 *     SATURATED = 1.3× (Ukraine 2024 median evac 85-120 min, golden hour only 38%).
 *     Added to TCCC baseDOWRate chain (after routeDOWModifier, before frtDOWModifier).
 *     Non-TCCC path updated: routeAdjustedDOW → droneAdjustedDOW.
 *     Also wired into overwhelmed CASEVAC baseDOWRate for consistency.
 *   - AXIS C — MEDEVAC attrition: _droneAttritionFactor = (1-weeklyLoss)^(weeks elapsed).
 *     CONTESTED = 5%/week, SATURATED = 15%/week. Floor 0.30 (30% minimum residual fleet).
 *     Stateless: computed from day number — consistent across all brigades, no state bleed.
 *     Applied as multiplier directly on dailyMEDEVACCapacity (all three calculation paths).
 *   - Day summary: new 'drone' block (threat_level, all three modifiers, attrition factor,
 *     before/after medevac capacity, additional_dow_from_drone).
 *   - resources block: drone_attrition_factor field.
 *   See threatAssessment.js DRONE_THREAT for full level definitions and data sources.
 *
 * v5.29.0 (March 10, 2026) — ENH-024: Damage Control Surgery blood consumption wired end-to-end.
 *   - NEW IMPORT: DCS_PHASES + calculateDCSBloodRequirements from damageControlSurgery.js
 *   - DCS blood supplement: computed after WBB+MTP adjustments, added to live blood totals.
 *     Activates when cumulativeGHAnalysis.has_forward_surgery === true (Role 2E or Role 3).
 *     Supplement = (DCS Phase 1 per-patient pRBC/FFP) − mechanism-baseline average_per_casualty.
 *     Applied to resourceResult.blood.requirements.totals so blood_dos deduction and Tab4
 *     badge both see the correct increased surgical demand.
 *   - Day summary: new 'dcs' block (applicable, facility_type, surgical_patients, phase,
 *     blood_supplement_prbc, blood_supplement_ffp, survival rates, note).
 *   - resources block: new dcs_blood_supplement_prbc + dcs_blood_supplement_ffp fields.
 *   - NO new DOW modifier: FST/DCS survival benefit already captured by time-to-surgery
 *     pathway (cumulativeGHAnalysis.dow_modifier). Adding a second modifier would double-count.
 *   - Lethal triad (hypothermia/acidosis/coagulopathy): out of planner scope, not modelled.
 *   See damageControlSurgery.js for full DCS_PHASES constants and doctrinal basis.
 *
 * v5.28.0 (March 10, 2026) — ENH-023: Prolonged Field Care wired end-to-end.
 *   - NEW IMPORT: getPFCModifier from prolongedFieldCare.js (new constants file v1.0.0)
 *   - DOW chain: PFC modifier inserted after ghAdjustedDOW, before routeAdjustedDOW.
 *     pfcResult = getPFCModifier(golden_hour_compliant, forceQuality.medical_capability)
 *     pfcAdjustedDOW = Math.round(ghAdjustedDOW × pfcResult.modifier)
 *   - TCCC-2025 baseDOWRate updated to include pfcResult.modifier in the chain.
 *   - Day summary: new 'pfc' block (triggered, level, modifier, lives_saved_by_pfc, warning).
 *   - When golden hour IS achieved: pfcResult.modifier = 1.0, no effect on DOW.
 *   - When golden hour NOT achieved: modifier = 0.70–0.98 based on medical_capability.
 *     POOR → 0.70 (−30%); ADEQUATE → 0.88; GOOD → 0.95; EXCELLENT → 0.98.
 *   - Never a user input — PFC is a contingency per JTS PCC CPG-91 doctrine.
 *   See prolongedFieldCare.js v1.0.0 for full constants, doctrinal basis, and research.
 *
 * v5.27.0 (March 10, 2026) — ENH-022: Frontage density modifier wired end-to-end.
 *   No call-site change required: brigade is spread as { ...brigade, ... } which
 *   already carries brigade.position (including frontage_width_km) into
 *   battleCasualtyModule v5.6.0. getDensityModifier() reads it there.
 *   Tab2 sets position.frontage_width_km = 0 for support units (has_frontage = false),
 *   so density modifier auto-excludes them (returns 1.0).
 *   See frontageDensity.js v1.0.0 for constants and getDensityModifier() spec.
 *
 * v5.26.0 (March 10, 2026) — ENH-BAP: Body armor KIA reduction wired.
 *   - calculateDailyBattleCasualties call: armorLevel from forceQuality.body_armor_level
 *     (default 'LEVEL_III'). forceQuality = force.force_quality set in Tab1.
 *     (Originally operation_parameters.body_armor_level — relocated to Tab1 in COSMETIC-02/ENH-BAP-MOVE.)
 *   - Passed to battleCasualtyModule v5.5.0 → calculateCasualtyDistribution.
 *
 * v5.23.0 (March 9, 2026) — IMP-007+NIGHT-001: Unit-type evacuation factors + night battle fix.
 *   IMP-007: computeCompositeEvacFactor() computes strength-weighted composite of
 *   wheeled/tracked/helicopter/stretcher demand factors from brigade array using
 *   EVACUATION_ASSET_FACTORS via getEvacuationFactorForBrigade() + UNIT_TYPE_TO_EVAC_KEY.
 *   HEAVY brigade tracked=1.8x; INFANTRY stretcher=2.0x; AIR_ASSAULT helicopter=3.0x.
 *   compositeUnitFactors passed to calculateComprehensiveMEDEVAC() (both phase25 + legacy paths).
 *   NIGHT-001: isNight now passed to calculateDailyBattleCasualties() — 0.85x night modifier
 *   (NIGHT_BATTLE_MODIFIERS.casualty_rate_modifier) now applied to battle casualties.
 *   Previously isNight was only passed to DNBI, not battle. Both gaps now closed.
 *
 * v5.22.0 (March 7, 2026) — ENH-012: Geographic theater scalar applied after force superiority.
 *   Imports getTheaterScalar() from climateZones.js. Reads scenario.operation_parameters
 *   .climate_config.theater_region + .season. Applies scalar to kia/wia/total after
 *   forceSuperiorityModifier block. CENTRAL_EUROPE = 1.0 (baseline); null = 1.0 (backward compat).
 *   theater_scalar_applied emitted on daySummary for Tab5 transparency.
 *
 * v5.21.0 (March 7, 2026) — ENH-011: ICU vs ward bed split in both bed timeline functions.
 *   buildBrigadeR2BBedTimeline + buildAggregatedBedTimeline: extract T1 from daySummary.triage
 *   (already present in brigade by_day). icu_admissions = T1 × HOSPRATE × echelon_fraction.
 *   ward_admissions = total_admissions - icu_admissions. Two separate sliding-window accumulators
 *   produce icu_census and ward_census per timeline entry. Total invariant preserved:
 *   icu_census + ward_census = census. Backward-compat: triage absent (combat-ineffective day)
 *   → icu_census = 0, ward_census = census. Tab5 updated to render ICU|Ward column + tooltips.
 *
 * v5.20.0 (March 7, 2026) — TAB4-BADGE-001: Expose daily_consumption on resupply_summary.
 *   PROBLEM: Tab4 getConsumptionDataSource() checked medical_requirements.blood.daily_requirements
 *     — this sub-key was never written by the calculator. The field is
 *     medical_requirements.blood = { prbc, ffp, plt, cryo } (operation totals, not daily rates).
 *     Result: CALCULATED path never triggered → badge always "Data: ESTIMATE".
 *     Additionally, if CALCULATED path had triggered, field name mismatches would have zeroed
 *     dailyConsumption: data.prbcs (wrong) → prbc, data.platelets (wrong) → plt.
 *   FIX (calculator side): Add daily_consumption: { prbc, ffp, plt, cryo } to both
 *     resupply_summary branches (configured and not configured). Same avgDailyConsumption
 *     already computed internally — now surfaced for Tab4.
 *   FIX (Tab4 side): getConsumptionDataSource() now checks resupply_summary.daily_consumption.
 *     Field name references corrected (prbcs→prbc, platelets→plt).
 *
 * v5.19.0 (March 7, 2026) — ENH-STRAT-001: flights_per_day in computeStratevacScheduleAnalysis().
 *   PROBLEM: schedule.flights_per_day field existed in Tab3 UI from this session but was never
 *     read by the calculator. Every departure day was capped at 1 × litter_capacity regardless
 *     of how many sorties were configured. "Every 2nd day, 4 flights" was impossible to model.
 *   FIX: Read flightsPerDay = schedule.flights_per_day ?? 1. Multiply departure-day capacity:
 *     departed = Math.min(phaQueue, litterCap * flightsPerDay).
 *     Null/undefined field → 1 (backward compatible with all saved scenarios).
 *   OUTPUT: flights_per_day added to per-airfield result object and timeline day entry.
 *
 * v5.18.0 (March 7, 2026) — STRATEVAC-ERR-001 + STRATEVAC-COUNT-001 fixes in computeStratevacScheduleAnalysis().
 *   STRATEVAC-ERR-001: Alternate airfield saved with schedule:{} (empty object from React state).
 *     schedule !== null passed the null guard → computeDepartureDays({}) threw because
 *     aircraft_type and frequency_mode were absent from the empty object.
 *     Error return path used airfield.label which is empty on pre-ENH-003 saved scenarios
 *     → Tab5 rendered ': computeDepartureDays failed: ...' with a blank label prefix.
 *     FIX: Filter now requires af.schedule.aircraft_type && af.schedule.frequency_mode.
 *     Empty object fails this guard → silently excluded from activeAirfields.
 *     Label fallback: airfield.label || airfield.id || 'Airfield' on error return path.
 *   STRATEVAC-COUNT-001: active_airfield_count chip counted schedule:{} airfield as active.
 *     FIX: Same filter change — activeAirfields.length now only counts valid schedules.
 *     No separate fix needed; count auto-corrects with the filter.
 *
 * v5.17.0 (March 7, 2026) — TAC-EVAC-001 defensive NaN guard in computeTacticalEvacSufficiency().
 *   SYMPTOM: Status showed GREEN (0%) when fleet=0, demand=222.
 *   ROOT CAUSE: evacuationCapacityCalculator.js calculateGroundCapacity() returned NaN for
 *   total_patients_per_day when all vehicle counts were 0 (patients_per_day field never set).
 *   NaN propagated here: groundCap.total_patients_per_day ?? 0 = NaN (nullish coalescing
 *   does not catch NaN). NaN comparisons all false → status stayed 'GREEN'.
 *   FIX: ?? 0 → Number.isFinite() guard — catches NaN, Infinity, null, undefined.
 *   PRIMARY fix in evacuationCapacityCalculator.js v1.5 (initialization typo).
 *   This guard is the defensive second layer.
 *
 * v5.16.0 (March 6, 2026) — ENH-003 Phase 3: computeStratevacScheduleAnalysis().
 *   PHA queue model per active airfield. Per-day PHA occupancy, backflow to Role 3,
 *   wait-time DOW penalty. Results in mascal_analysis.stratevac_schedule_analysis.
 *
 *   BUG 1 (KEY PATH): ambConfig read from medicalConfig?.ambulance_config — key does not exist.
 *     Tab3 saves fleet to medical_configuration.tactical_evac_config.fleet.
 *     Fix: const ambConfig = medicalConfig?.tactical_evac_config?.fleet || {}
 *
 *   BUG 2 (FIELD NAME): capacity used val.count (TOE / On Strength).
 *     Capacity calculations must use operational vehicles, not authorized strength.
 *     Fix: val.available ?? val.count ?? 0  (available first; falls back to count if not set)
 *
 *   BUG 3 (CLASSIFICATION): substring matching on key name (light/armor/tracked).
 *     Real codes: TABC79, MLVM, SPRINTER, BOXER — all fell through to wheeled_light fallback.
 *     Fix: AMBULANCE_TYPES[key]?.category lookup → CATEGORY_TO_EVAC_BUCKET map.
 *     MLVM/BOXER (ARMORED) → wheeled_armored. TABC79/SPRINTER (LIGHT) → wheeled_light.
 *
 *   BUG 4 (AIR POOL KEY PATH): medevacConf read from medicalConfig?.medevac_config — does not exist.
 *     Tab3 saves air pool to medical_configuration.tactical_evac_config.air_pool.
 *     Fix: read air_pool.nominal_daily_sorties + air_pool.aircraft_type directly.
 *     helicopterCount derived: Math.round(nominal_daily_sorties / NOMINAL_SORTIES_PER_AIRCRAFT).
 *     helicopterType mapped via AIRCRAFT_TYPE_MAP to DEFAULT_HELICOPTER_SPECS keys.
 *
 *   NEW IMPORT: AMBULANCE_TYPES from ambulanceTypes.js (needed for category lookup).
 *   NOTE: AMB-001 (v5.14.0) fixed the synthetic facility shape — that fix stands unchanged.
 *   Combined effect: ground_capacity_per_day now correctly reflects operational ambulances
 *   by correct category; air_capacity_per_day reflects sorties from Tab3 air pool.
 *
 * v5.14.0 (March 6, 2026) — AMB-001: Fix synthetic facility key path in getTacticalEvacConfig().
 *   ROOT CAUSE: ambulanceFacility used evacuation_assets.ground.* key path.
 *   calculateEffectiveAssets() reads organic_assets.ground_ambulances.* — path mismatch → 0 ambulances always.
 *   FIX: ambulanceFacility.organic_assets.ground_ambulances = groundVehicles.
 *        medevacFacility.organic_assets.ground_ambulances = { zeros }.
 *        Both get attached_assets: {}, detached_assets: {} (calculateEffectiveAssets requirement).
 *   RESULT: ground_capacity_per_day now correctly reflects organic ambulance count from Tab3.
 *   NO OTHER CHANGES — Tab5 rendering, golden hour, DOW chain unaffected.
 *
 * v5.13.0 (March 6, 2026) — PHA-BED: PHA beds added to R3 MASCAL capacity.
 *
 * CHANGES IN v5.13.0:
 *
 * PHA-BED FIX: staging_facilities[] PHA entries now contribute to _totalR3Beds.
 *   Mirrors the CSU-BED pattern (v5.9.0) exactly.
 *   PHA placement (Role 3 vs Airfield) does not change the logic — both positions
 *   serve the R3/strategic echelon and their beds must be counted for R3 MASCAL.
 *   Activated when medical_configuration.use_advanced_staging === true (same guard as CSU).
 *   Added results.mascal_analysis.pha_beds_added for Tab5 display and audit trail.
 *   Source: AJP 4.10C — PHA is post-surgical STRATEVAC staging at corps echelon.
 *
 * v5.12.0 (March 6, 2026) — Phase 5.1: Per-day tactical_evac_analysis in day summaries.
 *
 * CHANGES IN v5.12.0:
 *
 * 1. IMPORT: calculateGroundCapacity, calculateAirCapacity, ENVIRONMENT_MODIFIERS,
 *    WEATHER_MODIFIERS from evacuationCapacityCalculator.js (already exported there).
 *
 * 2. NEW MODULE-LEVEL FUNCTION: getTacticalEvacConfig(medicalConfig, scenario)
 *    Extracts and normalises tactical evac inputs from medicalConfig + scenario environment.
 *    Returns { environment, weather, terrain, timeOfDay, ambulanceFacility, medevacFacility,
 *              distanceKm, helicopterType }.
 *    ambulanceFacility / medevacFacility are synthetic facility objects shaped for
 *    calculateGroundCapacity / calculateAirCapacity.
 *
 * 3. NEW MODULE-LEVEL FUNCTION: computeTacticalEvacSufficiency(groundCap, airCap, dailyCasualties)
 *    Computes demand, capacity, sufficiency ratio, and overflow.
 *    ARCH-010 COMPLIANT: dow_modifier is INFORMATIONAL ONLY — not applied to DOW chain.
 *    The existing calculateOverflowDOW() path is the sole DOW source (unchanged).
 *
 * 4. PER-DAY OUTPUT: daySummary.tactical_evac_analysis added at end of wound_distribution block.
 *    Fields: ground_capacity_per_day, air_capacity_per_day, total_capacity_per_day,
 *            hourly_demand, hourly_capacity, sufficiency_ratio, overflow_per_hour,
 *            status (GREEN/YELLOW/ORANGE/RED),
 *            arch010_dow_risk_informational (display only — not applied),
 *            environment, weather.
 *    Used by Tab5 Phase 5.2 Capacity vs Demand chart.
 *
 * v5.11.0 (March 5, 2026) — Phase 6.1: MTP, fatigue model wiring, shelf life, airfield class, FARP.
 *
 * CHANGES IN v5.11.0:
 *
 * 1. IMPORT: getSurgeonPerformance from surgicalCapacity.js (new dependency).
 *
 * 2. MTP PROTOCOL (6.1.1):
 *    Reads medical_configuration.blood_config.mtp_enabled / mtp_protocol.
 *    When mtp_enabled: adjusts FFP and PLT from resourceResult after WBB to enforce the
 *    configured ratio. Protocol '1:1:1' sets ffp = prbc, plt = prbc/6 (one apheresis PLT
 *    equivalent per 6 PRBC — CoTCCC April 2025). Protocol '2:1:1' sets ffp = prbc/2,
 *    plt = prbc/4. Only applies when mtp_patients > 0 for that day.
 *    Day summary resources: mtp_protocol_applied, mtp_ratio_prbc, mtp_ratio_ffp, mtp_ratio_plt.
 *
 * 3. FATIGUE MODEL WIRING (6.1.2):
 *    getSurgeonPerformance(hoursWorked) from surgicalCapacity.js is now applied as an
 *    ADDITIONAL multiplier on top of the day-based calculateFatigueEffect().
 *    Reads surgical_config.hours_worked (user-set in Tab 3 Expert mode).
 *    When hours_worked = 0, getSurgeonPerformance returns 1.0 — no change to existing behaviour.
 *    Existing day-based degradation is preserved and unchanged (tested, validated).
 *    Combined: fatigueEffect = dayFatigue × getSurgeonPerformance(hoursWorked).performance
 *    Day summary resources: surgeon_performance_pct (getSurgeonPerformance output separately tracked).
 *
 * 4. SHELF LIFE TRACKING (6.1.3):
 *    Reads medical_configuration.blood_config.shelf_life_tracking.
 *    When enabled: computes daily PLT wastage warning.
 *    PLT shelf life = 5 days. Model: plt_units_on_hand / 5 = daily attrition from expiry
 *    (steady-state assumption — units arriving continuously, oldest expire first).
 *    Day summary resources.shelf_life: { plt_daily_wastage_estimate, shelf_life_warning }.
 *    This is a DISPLAY field — Tab 5 Phase 6.2 renders it. Does not affect consumption totals.
 *
 * 5. AIRFIELD CLASS + FARP (6.1.4):
 *    Reads medical_configuration.strategic_evac_config.airfield_class / farp_enabled.
 *    Computes results.medical_requirements.stratevac_capacity:
 *      { class, litter_capacity, hourly_throughput, farp_enabled, farp_sortie_bonus,
 *        effective_hourly_throughput, aircraft_types[] }
 *    FARP adds 25% effective throughput bonus (rotary-wing sortie frequency increase).
 *    This is used by Tab 5 Phase 6.2 for STRATEVAC capacity display.
 *    Does NOT change DOW, MEDEVAC, or any existing calculation path.
 *    AIRFIELD_CLASS_DATA defined inline (same values as Tab3 AIRFIELD_CLASS_SPECS).
 *    TODO: Extract to trainEvacuation.js in next constants session.
 *
 * v5.10.0 (March 5, 2026) — MASCAL-R2E-R3: Per-division R2E and corps R3 MASCAL bed timelines.
 *   NEW METHOD: buildAggregatedBedTimeline(brigadeResultsArray, echelonBeds, avgLOS, admissionFraction)
 *   Aggregates daily casualties across multiple brigades, applies echelon fraction, sliding-window census.
 *   R2E: one timeline per division (brigades grouped by parent_division_id). Beds = totalR2EBeds / numDivisions.
 *   R3: one timeline for corps (all brigades). Beds = totalR3Beds.
 *   results.mascal_analysis extended with: r2e_divisions[], r3_corps, total_r2e_beds, total_r3_beds.
 * Main Orchestrator
 *
 * VERSION 5.9.0 - FRT-DOW + CSU-BED: Wire Tab3 Section 3D FRT and Section 3G CSU into calculator (March 4, 2026)
 * - IMPORT: FRT_DOW_REDUCTION_PERCENT from theatreHoldingPolicy.js v2.7
 * - FRT DOW modifier: read surgical_config.teams.FRT.count once before brigade loop.
 *   If count > 0: frtDOWModifier = 1 - FRT_DOW_REDUCTION_PERCENT/100 (= 0.93, binary — no stacking).
 *   Applied to baseDowRate BEFORE TCCC 2025 modifiers (FRT acts at Role 1, upstream).
 *   Was previously: WIA × baseDOWRate × GH_mod × route_mod → TCCC
 *   Now:            WIA × baseDOWRate × GH_mod × route_mod × frtMod → TCCC
 * - CSU beds in MASCAL timeline: read medical_configuration.staging_facilities[]
 *   when use_advanced_staging === true. Sum capacity_patients for CSU entries.
 *   Add to _totalR2BBeds before brigade division. Doctrinally: CSU at brigade level
 *   expands forward holding capacity, pushing MASCAL day later.
 *   Sourced from createFacilityFromStandards() in Tab3 — field: capacity_patients.
 *
 * VERSION 5.8.0 - MASCAL-WHEN-WHERE: Per-brigade R2B bed occupancy timelines (March 4, 2026)
 * - IMPORT: HOSPITALIZATION_RATE, ECHELON_ADMISSION_DISTRIBUTION, AVG_LOS_BY_THP
 *   from theatreHoldingPolicy.js (added to existing RTD_RATES import)
 * - NEW FUNCTION: buildBrigadeR2BBedTimeline(brigadeResult, r2bBedsForBrigade, avgLOS)
 *   Sliding window rolling census over last ceil(avgLOS) days. Per-day: admissions =
 *   (battle.total + dnbi.total) × HOSPITALIZATION_RATE × ECHELON_ADMISSION_DISTRIBUTION.ROLE_2_BASIC.
 *   Outputs timeline[], first_critical_day, peak_day, peak_pct per brigade.
 * - NEW OUTPUT: results.mascal_analysis — added after v5.4.2 by_day aggregation loop:
 *   { r2b_beds_per_brigade, total_r2b_beds, avg_los_days, thp_key, brigades[], system_summary }
 *   R2B beds per brigade = total R2B beds / non-medical brigade count (same counting logic
 *   as personnelIntelligence.js Priority 1 path — ensures consistency with Tab4 Section 4D).
 *   system_summary: first_critical_day, first_critical_brigade, brigades_ever_critical, total_brigades.
 * - Enables Tab5 renderMASCALTimeline() — day-by-day per-brigade R2B occupancy grid.
 *
 * VERSION 5.7.0 - WTD-01: Wound-type distribution integration (March 3, 2026)
 * - Import: getWoundDistribution, distributeCasualtiesByWound from woundTypeDistribution.js
 * - opType derived from mission_type → passed to calculateComprehensiveDOW() enabling
 *   wound-type DOW refinement in survivalModule.js v5.4.0
 * - daySummary.wound_distribution: { operation_type, distribution, t1_by_wound, t2_by_wound }
 * - brigadeResult.wound_distribution: aggregated counts across all days for Tab5 display
 * VERSION 5.6.0 - ARCH-010 FIX: CASEVAC removed from DOW calculation (March 1, 2026)
 *   ISSUE-03 FIX (March 2, 2026): constructor this.version corrected from '5.5.0' to '5.6.0'.
 *   The ARCH-010 and ARCH-013 changes were delivered as v5.6.0 but the constructor string
 *   was not incremented, causing Tab5 to display "Calculator v5.5.0" despite running 5.6.0 code.
 *
 * DOCTRINE SOURCE: AJMedP-2 (Medical Evacuation), AJP 4.10C (Medical Support Doctrine).
 * "CASEVAC is unplanned and opportunistic. This type of movement will inevitably occur
 *  but is NOT a factor in planning of the medical footprint and therefore cannot have
 *  influence on the medical estimate process."
 *
 * ROOT CAUSE: calculateCASEVACAdjustedDOW() split overflow into two tiers:
 *   - casevac_evacuated → baseDOWRate × 1.35 (reduced penalty — violation)
 *   - unevacuated       → baseDOWRate × 1.60 (correct unevacuated penalty)
 * CASEVAC availability was reducing the DOW rate relative to fully-unevacuated treatment.
 * This gave CASEVAC influence over the medical estimate, violating AJMedP-2.
 *
 * FIX — Three changes:
 *
 * G-02 — Rename calculateCASEVACAdjustedDOW() → calculateOverflowDOW() (line ~3317).
 *   New behaviour: all overflow (casevac_evacuated + unevacuated) receives the
 *   unevacuated_dow_penalty (1.60×). casevac_dow field → 0. casevac_dow_rate → 0.
 *   preventable_deaths now reflects true additional mortality vs baseline MEDEVAC DOW.
 *   Return structure field names preserved for call-site compatibility.
 *
 * G-02 — Call site updated (line ~6970): calculateCASEVACAdjustedDOW → calculateOverflowDOW.
 *   No parameter changes — casevacAnalysis object still passed through.
 *   casevac_evacuated inside casevacAnalysis is now consumed for informational UI only,
 *   not to determine a reduced-penalty DOW tier.
 *
 * G-04 — daySummary.casevac output block (line ~7092): arch010_informational_only: true added.
 *   All existing CASEVAC capacity tracking fields preserved (medevac_capacity,
 *   is_overwhelmed, casevac_evacuated, etc.) — these remain for Tab 3/5 UI display.
 *   analyzeCASEVACRequirements() and the entire brigadeResult.casevac tracking
 *   structure are UNCHANGED — they feed UI display only, not DOW.
 *
 * G-03 (Tab 3 UI note) is in Tab3_Medical.jsx — not this file.
 *
 * VERSION 5.5.0 - F-05 FIX: Post-loop surgical backlog re-evaluation (February 27, 2026)
 *
 * ROOT CAUSE: assessMASCALStatus() and evaluateBlueLightProtocol() were called BEFORE
 * the brigade loop. At call time surgical_backlog is always 0 — the brigade loop hasn't
 * run yet, and Tab3 buildUpdates() doesn't populate surgical_config.backlog (it saves
 * only teams/shift_mode/fatigue_level). The calculator DOES correctly compute backlog
 * per day per brigade (via resourceResult.surgical.end_of_day_backlog + fatigue adjustment),
 * accumulating the final value in medicalSystemState.surgical_backlog after the loop.
 * FIX: After the brigade loop, if surgical_backlog > 0, re-evaluate both mascalStatus
 * and (legacy path only) medevacAnalysis.blue_light using the actual computed value.
 * No new calculations — pure re-evaluation with already-available data.
 * Also adds evaluateBlueLightProtocol to medevacModule import for the re-evaluation call.
 *
 * VERSION 5.4.7 - APP_RULES 6.1 Compliance: Remove ?? fallbacks from P1-03 fix (February 22, 2026)
 *
 * P1-03 FALLBACK FIX: wiaRtdT1/T2/T3/T4 used `?? 0.05/0.20/0.70/0.00` after constants lookup.
 * - VIOLATION: APP_RULES 6.1 (no fallbacks that mask validation errors) and 6.3 (no default values).
 * - OLD: THP_RTD_RATES?.BY_TRIAGE_CATEGORY?.T1_IMMEDIATE?.rtd_rates?.[thpTimeframe] ?? 0.05
 * - NEW: Read rate directly; throw Error if undefined — surfaces constants issues immediately.
 * - PATTERN: Read all 4 rates first, then validate all 4 in one guard throw, then assign.
 * - IMPACT: If theatreHoldingPolicy.js RTD_RATES structure is wrong/missing, calculator
 *   throws a clear error instead of silently computing with wrong values.
 *
 * VERSION 5.4.6 - P1-02 + P1-03 Hardcoded Values Fix (February 22, 2026)
 *
 * P1-02 FIX: DNBI RTD was hardcoded at dnbiResult.total_dnbi * 0.85 (85% flat rate).
 * - OLD: Math.round(dnbiResult.total_dnbi * 0.85)  — two locations: dnbi.rtd and strength.end
 * - NEW: dnbiResult.rtd.expected_rtd  — already correctly computed by dnbiModule from
 *        DNBI_CATEGORY_BREAKDOWN evacuation_rate constants per category.
 * - IMPACT: Category-specific RTD rates (MUSCULOSKELETAL ~85%, BEHAVIORAL_HEALTH ~70%,
 *           VEHICLE_ACCIDENTS ~40%) now flow through correctly.
 *
 * P1-03 FIX: WIA RTD used hardcoded rates 0.05/0.15/0.60/0.02 regardless of THP config.
 * - OLD: triageDistribution.T1 * 0.05 + T2 * 0.15 + T3 * 0.60 + T4 * 0.02
 * - NEW: Reads RTD_RATES.BY_TRIAGE_CATEGORY.[T].rtd_rates[thpTimeframe] from
 *        theatreHoldingPolicy.js, where thpTimeframe is derived from scenario THP config.
 * - IMPORT ADDED: THP_RTD_RATES from ../constants/theatreHoldingPolicy.js
 * - IMPACT: RTD now reflects configured THP (3/5/7/15/30 day).
 *   Example THP_7_DAY: T1=0.05, T2=0.20, T3=0.70 (not 0.60 as hardcoded).
 *   Example THP_3_DAY: T1=0.00, T2=0.05, T3=0.40 (very different).
 *
 * VERSION 5.4.5 - Supply Key Mapping for Tab5 Compatibility (February 18, 2026)
 * - BUG FIX: days_remaining_enhanced keys didn't match Tab5 expectations
 * - PROBLEM: Tab5 looks for "prbc_units", calculator created "prbc" → "DATA MISSING" error
 * - ROOT CAUSE: Supply module uses lowercase no-suffix (prbc), Tab5 expects underscore-suffix (prbc_units)
 * - IMPACT: Despite correct calculations (0 days), Tab5 couldn't display blood DOS
 * - FIX: Added supplyKeyMap in enhancement code (line ~5535)
 *   - Maps prbc → prbc_units, ffp → ffp_units, platelets → platelets_units
 *   - Other items (antibiotics_doses, surgical_kits) pass through unchanged
 * - RESULT: Tab5 now finds blood product data at expected keys
 * - COMPLETE DATA FLOW NOW WORKING:
 *   1. Calculator computes triage (T1/T2/T3) ✅
 *   2. Aggregates to results.by_day ✅
 *   3. Supply module calculates consumption from triage ✅
 *   4. Calculator enhances with status (prbc → prbc_units) ✅
 *   5. Tab5 displays blood DOS correctly ✅
 * 
 * VERSION 5.4.4 - CRITICAL: Triage Data Location Fix (February 18, 2026)
 * - BUG FIX: Triage data aggregation reading from wrong location
 * - PROBLEM: Supply consumption still 0 despite by_day being populated
 * - ROOT CAUSE: Triage data in brigade.by_day[n].triage.T1/T2/T3, not brigade.by_day[n].totals.triage_1/2/3
 * - IMPACT: results.by_day had kia/dow/wia/dnbi but triage_1/2/3 were all 0 → zero blood consumption
 * - FIX: Changed aggregation lines 5471-5473
 *   - OLD: dayTotals.triage_1 += brigadeDay.totals.triage_1 || 0
 *   - NEW: dayTotals.triage_1 += brigadeDay.triage.T1 || 0
 *   - Same for T2, T3 (uppercase field names)
 * - RESULT: Supply module now receives correct triage data → realistic blood consumption
 * 
 * VERSION 5.4.3 - CRITICAL: by_day Aggregation Fix (February 18, 2026)
 * - BUG FIX: results.by_day was never populated, causing zero supply consumption
 * - PROBLEM: Supply module needs daily casualties from results.by_day but array was empty
 * - ROOT CAUSE: Calculator aggregated to brigade and corps level but skipped daily aggregation
 * - IMPACT: Average daily consumption returned 0 for all items (PRBC, FFP, etc.) → 999 DOS
 * - FIX: Added by_day aggregation loop after line 5413
 *   - Loops through each day (1 to duration)
 *   - Sums brigade.by_day data across all brigades per day
 *   - Distributes HQ/support casualties evenly across days
 *   - Populates results.by_day with complete daily totals
 * - RESULT: Supply module now receives proper daily casualty data → realistic consumption
 * - RELATED: Works with v5.4.2 defensive brigade lookup to complete data flow
 * 
 * VERSION 5.4.2 - DEFENSIVE BRIGADE LOOKUP: Multi-Location Support (February 18, 2026)
 * - BUG FIX: Calculator now checks BOTH brigade locations (flattened + nested)
 * - PROBLEM: Tab1/Tab2 v3.0 flatten brigades to force.brigades, calculator only checked force.forceBuilderResult.brigades
 * - ROOT CAUSE: Lines 5247, 7571, 7587 only read from nested location
 * - IMPACT: Calculator returned zero consumption (couldn't find brigades in flattened location)
 * - FIX: Defensive lookup checks force.brigades FIRST, falls back to force.forceBuilderResult.brigades
 * - LOCATIONS FIXED:
 *   - Line ~5247: calculateCasualties() main brigade reading
 *   - Line ~7571: validateScenario() brigade existence check
 *   - Line ~7587: buildScenarioSummary() brigade count
 * - RESULT: Calculator finds brigades regardless of data structure (forward + backward compatible)
 * - ARCHITECTURE: Supports Tab1/Tab2 flattening while maintaining Monte Carlo compatibility
 * 
 * VERSION 5.4.1 - CRITICAL BUG FIX: Export Function Options Parameter (February 16, 2026)
 * - BUG FIX: Export function calculateCasualties() didn't accept options parameter
 * - PROBLEM: Tab5 called calculateCasualties(scenario, { final: true }) but options were ignored
 * - ROOT CAUSE: Line 7732 function signature was calculateCasualties(scenario) - missing options param
 * - IMPACT: Monte Carlo never ran because { final: true } was discarded!
 * - FIX: Changed to calculateCasualties(scenario, options) and forwarded to method
 * - RESULT: Tab5's { final: true } now properly triggers Monte Carlo simulation
 * 
 * VERSION 5.4.0 - PHASE 1: Monte Carlo Casualty Integration (February 16, 2026)
 * Build Date: February 16, 2026
 *
 * v5.4.8 (February 24, 2026):
 * - DNBI-03 FIX: Removed hardcoded `* 0.30` DNBI evacuation literal from total_evacuated
 *   calculation. The 30% flat rate ignored operational state and was doctrinally incorrect.
 *   Correct formula: total_evacuated = total_wia − total_rtd + total_dnbi.
 *   total_rtd already encodes both WIA RTD and DNBI RTD (Feb 7 bugfix). The state-specific
 *   DNBI evacuation fraction is computed per-day in dnbiModule from DNBI_CATEGORY_BREAKDOWN
 *   and accumulates correctly through brigadeResult.totals.rtd → results.by_corps.total_rtd.
 *   No constants import required — algebraically correct with existing aggregation.
 * - Removed @institution line (APP_RULES compliance).
 *
 * v5.4.7
 * - **PHASE 1.1 COMPLETE:** Monte Carlo wrapper integrated into calculateCasualties()
 *   - NEW: calculateCasualties(scenario, options = {}) accepts `final` flag
 *   - NEW: Checks scenario.operation_parameters.monte_carlo_config for enablement
 *   - NEW: Runs calculateWithMonteCarlo() if enabled AND final=true
 *   - NEW: Always runs deterministic for preview/validation (final=false)
 *   - PATTERN: Tab 3 preview → deterministic, Tab 5 display → Monte Carlo if enabled
 *   - PERFORMANCE: Prevents double execution of expensive Monte Carlo simulations
 *   - IMPACT: Users can enable uncertainty analysis via Tab 2 toggle
 * - Constructor version updated: 5.2.0 → 5.4.0 (line ~5150)
 * - INTEGRATED: battleCasualtyModule.js v5.0, dnbiModule.js v3.0 (Phase 1.2 modifiers)
 *
 * v5.3.1 (February 15, 2026):
 * - **ESLINT CLEANUP:** Removed unused imports and variables (remnants from v5.3.0 debug cleanup)
 *   - REMOVED: OPERATIONAL_STATES import (Line 1372) - leftover from echelon_casualties removal
 *   - REMOVED: 'source' variable assignment (Line 6229) - leftover from debug logging removal
 *   - IMPACT: Zero ESLint warnings, cleaner imports
 *   - NO FUNCTIONAL CHANGES: Pure cleanup of v5.3.0 debug removal artifacts
 *
 * v5.3.0 (February 15, 2026):
 * - **BUG-065 COMPONENT 4 COMPLETE:** Removed orphaned echelon_casualties system
 *   - DELETED: calculateEchelonDNBI() method (98 lines) - unused since Feb 9
 *   - DELETED: results.echelon_casualties assignment (6 lines) - never displayed in Tab5
 *   - DELETED: echelon calculation and Task 3.1 log (3 lines)
 *   - TOTAL: 107 lines removed
 *   - IMPACT: Single HQ casualty system (hq_casualties only), eliminates dual system confusion
 *
 * - **DEBUG CLEANUP:** Removed completed task logging (12 console.log statements)
 *   - REMOVED: [TASK 3.1] Echelon DNBI calculated (1 log)
 *   - REMOVED: [TASK 3.2] Brigade phase resolution (1 log)
 *   - REMOVED: [TASK 3.3] Per-phase calculation logs (4 logs)
 *   - REMOVED: [TASK 3.4] mission_multiplier source (1 log)
 *   - REMOVED: [HQ CALC DEBUG] diagnostic logs (2 logs)
 *   - REMOVED: [ECHELON DNBI] logs (3 logs - in deleted method)
 *   - KEPT: All console.error('[SECTION 12] CRITICAL:...') for error detection
 *
 * - **FALLBACK VALIDATION:** Fixed duration_days fallback for v5.1.2 consistency
 *   - Line 7622: duration_days || 14 → duration_days (no fallback)
 *   - Rationale: Main calculation validates at Line 5191, summary should not re-fallback
 *   - Maintains v5.1.2 philosophy: No silent failures for Tab data
 *
 * - **ARCHITECTURE IMPROVEMENT:**
 *   - Single source of truth for HQ casualties (hq_casualties via v5.2.0)
 *   - Reduced code complexity (-107 lines)
 *   - Cleaner console output (no debug spam)
 *   - All functionality preserved via enhanced hq_casualties (v5.2.0)
 *   - Zero breaking changes (echelon_casualties was never used by Tab5)
 *
 * - **FALLBACK ANALYSIS:** 483 remaining || operators (all legitimate)
 *   - Constants from imports: 15 (modifiers, factors)
 *   - Optional chaining safe defaults: 224 (?.field || 0)
 *   - Field name variants: 15 (backward compatibility)
 *   - Phase cascades: 12 (intentional hierarchy)
 *   - Constants fallback chains: 210 (import fallbacks)
 *   - Miscellaneous safe: 7 (|| [], || {})
 *   - ELIMINATED: 3 problematic Tab data fallbacks
 *
 * VERSION 5.2.0 - BUG-065 FIX: HQ/Support Personnel Breakdown (February 12, 2026)
 *
 * v5.2.0 (February 12, 2026):
 * - BUG-065 FIX: Calculator now uses actual HQ/Support breakdown from Tab1 instead of fixed percentages
 *   - ROOT CAUSE: Calculator used fixed 4% for HQ, 37% for Support, ignoring user-configured breakdown
 *   - FIX: Added extractPersonnelBreakdown() to read breakdown.command and breakdown.support from
 *     all echelons (Corps, Divisions, Brigades) created in Tab1 v4.3.7
 *   - IMPACT: HQ and Support casualty calculations now reflect actual force composition
 * 
 * - NEW FUNCTION: extractPersonnelBreakdown(forceResult)
 *   - Extracts breakdown.command and breakdown.support from Corps, Divisions, Brigades
 *   - Calculates combat personnel as (brigade.strength - HQ breakdown)
 *   - Returns: { command, support, combat, total } with validation and fallbacks
 *   - Location: Lines ~7163-7255
 * 
 * - MODIFIED: calculateHQCasualtiesMethod()
 *   - OLD: hqPersonnel = totalStrength × 0.04 (fixed 4%)
 *   - NEW: hqPersonnel = personnelBreakdown.command (actual from Tab1)
 *   - Updated rate: 0.5% → 0.4% per day (research-validated)
 *   - Location: Lines ~7256-7338
 * 
 * - MODIFIED: calculateSupportUnitCasualties()
 *   - OLD: supportPersonnel = totalStrength × 0.37 (fixed 37%)
 *   - NEW: supportPersonnel = personnelBreakdown.support (actual from Tab1)
 *   - Updated rate: 0.3% → 0.7% per day (research-validated)
 *   - Location: Lines ~7340-7373
 * 
 * - DATA FLOW: Tab1 v4.3.7 → Tab2 v7.0 → Calculator v5.2.0 → Tab5
 *   - User sets breakdown in Tab1 edit dialog (e.g., Division: 150 HQ, 680 Support)
 *   - Tab2 preserves breakdown via spread operator (...brigade)
 *   - Calculator extracts and applies to casualty calculations
 *   - Tab5 displays accurate HQ/Support casualties
 * 
 * - CASUALTY RATES (Updated):
 *   - Command (HQ): 0.4% per day (was 0.5%)
 *   - Support: 0.7% per day (was 0.3%)
 *   - Combat: 1.5% per day (unchanged)
 * 
 * - COMPATIBILITY: Tab3 requires NO changes (same result structure, different values)
 *
 * VERSION 5.1.3 - SUB_UNITS BACKWARD COMPATIBILITY (February 10, 2026)
 *
 * v5.1.3 (February 10, 2026):
 * - BUG FIX: Calculator no longer throws when brigade.sub_units is missing
 *   - ROOT CAUSE: ForceService.buildBrigade() does not yet generate sub_units.
 *     Calculator v5.1.0 added a hard requirement, blocking all calculations.
 *   - FIX: Added ensureBrigadeSubUnits() - generates synthetic single-unit
 *     sub_units from brigade.unit_type + brigade.strength when sub_units absent.
 *   - MATH: Synthetic single-unit is mathematically identical to v5.0.0 behavior.
 *   - validateBrigadeSubUnits: softened to warn on missing array (defensive).
 *   - calculateSubUnitCasualties: calls ensureBrigadeSubUnits() before validate.
 *   - FLAG: brigade.sub_units_synthetic=true marks generated compositions.
 *
 * VERSION 5.1.2 - SECTION 12 COMPLETION: Remove Duration & HQ Fallbacks (February 8, 2026)
 * Build Date: February 10, 2026
 * 
 * v5.1.2 SECTION 12 COMPLETION - REMOVE ALL CRITICAL FALLBACKS (February 8, 2026):
 * ============================================================================
 * PHILOSOPHY: "Fallbacks mask validation errors and create silent failures"
 * 
 * THREE CRITICAL FALLBACKS REMOVED:
 * 
 * 1. OPERATION DURATION FALLBACK (Line 4988):
 *    OLD: const duration = operation_parameters?.duration_days || 14
 *    NEW: Validates duration, logs CRITICAL error, uses 1 as obviously wrong value
 *    IMPACT: Forces Tab2 to provide duration_days, exposes missing data
 * 
 * 2. FALLBACK PHASE DURATION (Line 5733):
 *    OLD: duration_days: 14 (hardcoded when brigade has no phases)
 *    NEW: duration_days: duration (uses parameter, validates, exposes if missing)
 *    IMPACT: Fallback phases respect operation duration, fixes ESLint error
 *    FIX: Removed underscore from duration parameter (Line 5746) to enable use
 * 
 * 3. HQ CALCULATION FORCE STRENGTH FALLBACKS (Lines 7061-7092):
 *    OLD: Complex fallback logic with console.log for Battalion/Brigade/Division/Corps
 *    NEW: Validates total_strength, logs CRITICAL error, uses 1 as obviously wrong value
 *    IMPACT: Forces Tab1 to provide forceBuilderResult.total_strength
 * 
 * CONSISTENT WITH v4.43 SECTION 12 PHASE 1:
 * - Brigade strength fallbacks removed
 * - Operational posture fallbacks removed
 * - Operational state fallbacks removed
 * - All critical data validated with console.error
 * - Missing data uses obviously wrong values (1, undefined, 'UNKNOWN')
 * 
 * REMAINING LEGITIMATE FALLBACKS (per v4.43 documentation):
 * ✓ Field name variants (brigade.mission_role || brigade.missionRole)
 * ✓ Phase overrides (phase.operational_state || brigade.operational_state)
 * ✓ Optional nested properties (organicAssets?.ground_ambulances?.count || 0)
 * ✓ Safe array/object handling (|| [] and || {})
 * ✓ Constants (not Tab data) with || 1.0
 * 
 * ESLint FIXES:
 * - Line 5746: duration: _duration → duration (no longer unused variable)
 * - ESLint error "duration assigned but never used" resolved
 * 
 * VERSION UPDATE:
 * - Header: v5.1.2
 * - Constructor: v5.1.2 (was v4.44 - now synced)
 * - Build date: 2026-02-08
 * 
 * ============================================================================
 * 
 * v5.1.1 CRITICAL BUG FIX - DNBI RTD CALCULATION (February 7, 2026):
 * ============================================================================
 * CRITICAL BUG: DNBI Return-to-Duty (RTD) was completely missing from calculations
 * 
 * SYMPTOM:
 * - Total casualties: 5,042
 * - RTD displayed: 652 (battle RTD only)
 * - Net Personnel Loss: 4,390
 * - Replacement Requirement: 1,982
 * - DISCREPANCY: 4,390 ≠ 1,982 (2,408 personnel error!)
 * 
 * ROOT CAUSE:
 * - Calculator only calculated and accumulated battle RTD
 * - DNBI RTD (~85% of DNBI) was never calculated
 * - Result: RTD under-reported by ~2,400 personnel
 * 
 * THE FIX (4 locations):
 * 1. Line ~6531: Add DNBI RTD calculation (dnbiResult.total_dnbi × 0.85)
 * 2. Line ~6720: Add DNBI RTD to strength calculation
 * 3. Line ~6735: Accumulate DNBI RTD in brigade totals
 * 4. Line ~6804: Accumulate DNBI RTD in phase breakdown totals
 * 
 * CORRECT RTD RATES (NATO Doctrine):
 * - Battle (WIA): Varies by triage (T1=5%, T2=15%, T3=60%, T4=2%)
 * - DNBI: ~85% average (disease ~90%, NBI ~80%, CSR ~50%)
 * 
 * AFTER FIX:
 * - RTD: 3,059 (battle 1,410 + DNBI 1,649)
 * - Net Personnel Loss: 1,983
 * - Replacement Requirement: 1,982
 * - Discrepancy: 1 personnel (rounding) ✓
 * 
 * IMPACT:
 * - All personnel calculations now accurate
 * - Net Personnel Loss = Replacement Requirement
 * - Brigade end strength correct
 * - Logistics planning based on correct numbers
 * 
 * ============================================================================
 * 
 * VERSION 5.1.0 - v3.0 ARCHITECTURAL INTEGRATION: Single Source of Truth (February 7, 2026)
 * Build Date: February 7, 2026
 * 
 * v5.1.0 ARCHITECTURAL INTEGRATION - SINGLE SOURCE OF TRUTH (February 7, 2026):
 * ============================================================================
 * CRITICAL DATA SOURCE CHANGE: Read Brigades from force.forceBuilderResult.brigades
 * 
 * WHAT CHANGED:
 * - OLD: Read from scenario.operation_parameters.brigade_configs
 * - NEW: Read from scenario.force.forceBuilderResult.brigades
 * - REASON: Tab 2 v7.0 refactor - eliminates dual data storage
 * 
 * LOCATIONS UPDATED:
 * 1. calculateCasualties() - Line ~4915: Main brigade data source
 * 2. validateScenario() - Line ~7393: Brigade existence validation
 * 3. buildScenarioSummary() - Line ~7409: Brigade counting
 * 
 * IMPACT:
 * - Calculator now receives ALL brigade data (Tab 1 + Tab 2 in one object)
 * - No more position inconsistencies (Tab 3 and Calculator see same data)
 * - Consistent with architectural refactor in Tab 2 v7.0
 * - All tabs now read from single location: force.forceBuilderResult.brigades
 * 
 * BACKWARDS COMPATIBILITY: None needed (no saved scenarios exist)
 * 
 * INTEGRATION:
 * - Requires Tab2 v7.0 (saves brigades to force.forceBuilderResult.brigades)
 * - Works with Tab 3 (already reads from this location)
 * - Maintains all v5.0.0 sub-unit calculation features
 * 
 * ============================================================================
 * 
 * VERSION 5.0.0 - v3.0 PHASE 3: Sub-Unit Composition Calculations (NO FALLBACKS)
 * Build Date: February 3, 2026
 * 
 * v5.0.0 PHASE 3 - SUB-UNIT COMPOSITION CASUALTY CALCULATIONS (February 3, 2026):
 * ============================================================================
 * CRITICAL ARCHITECTURE CHANGE: Casualties Calculated Per Sub-Unit
 * 
 * WHAT'S NEW:
 * - Brigades with sub_units[] calculate casualties per battalion/squadron/company
 * - Each sub-unit uses its own casualty_modifier from UNIT_TYPES
 * - NO FALLBACKS: Throws errors if brigade missing sub_units
 * - Example: MECHANIZED = 2×Mech(1.0) + 1×Armor(0.85) + 1×Artillery(0.6) + ...
 * 
 * NEW FUNCTIONS:
 * 1. validateBrigadeSubUnits(brigade)
 *    - Validates brigade has sub_units array
 *    - Validates each sub-unit has unit_type and strength
 *    - THROWS ERRORS if validation fails
 * 
 * 2. getCasualtyModifier(unitType)
 *    - Gets casualty_modifier from UNIT_TYPES[unitType]
 *    - THROWS ERROR if unit_type unknown
 *    - NO FALLBACKS (|| 1.0 removed)
 * 
 * 3. calculateSubUnitCasualties(config)
 *    - Iterates through brigade.sub_units
 *    - Applies each sub-unit's casualty_modifier
 *    - Returns total casualties + breakdown by sub-unit
 *    - THROWS ERRORS if data incomplete
 * 
 * MODIFIED SECTIONS:
 * - calculateBrigadeCasualties(): Lines ~6000
 *   - Replaced single unit_type_modifier with sub-unit iteration
 *   - Validates sub_units presence
 *   - THROWS ERROR if sub_units missing
 *   - No legacy fallback path
 * 
 * IMPORTS ADDED:
 * - UNIT_TYPES from '../constants/unitTypes.js'
 *   - For getCasualtyModifier() lookups
 * 
 * ACCURACY IMPROVEMENT:
 * Before: Brigade × single modifier (e.g., MECHANIZED × 1.0)
 * After:  Sum of (each sub-unit × its modifier)
 * Result: 15-21% more accurate for combat brigades
 * 
 * Example Calculation:
 * MECHANIZED Brigade (4700 personnel):
 * - 2× Mech Bn (800 ea) × 1.0  = 1600 × 1.0  = 1600 effective
 * - 1× Armor Bn (800)    × 0.85 = 800  × 0.85 = 680  effective
 * - 1× Artillery (500)   × 0.6  = 500  × 0.6  = 300  effective
 * - 1× Engineers (600)   × 0.5  = 600  × 0.5  = 300  effective
 * - 1× Support (700)     × 0.3  = 700  × 0.3  = 210  effective
 * - 1× Cavalry (400)     × 1.1  = 400  × 1.1  = 440  effective
 * - 1× HQ (100)          × 0.5  = 100  × 0.5  = 50   effective
 * TOTAL: 4700 personnel → 3580 effective (76% avg modifier vs 100% old)
 * 
 * BACKWARDS COMPATIBILITY: NONE
 * - Brigades WITHOUT sub_units will cause calculator to THROW ERROR
 * - This is INTENTIONAL - forces ForceService to generate proper data
 * - ForceService Phase 2 MUST be deployed first
 * 
 * INTEGRATION:
 * - Requires unitTypes.js v3.0 Phase 1 (doctrinal compositions)
 * - Requires ForceService.js v3.0 Phase 2 (sub-unit generation)
 * - All three phases must be deployed together
 * 
 * ============================================================================
 * 
 * VERIFICATION STATUS (January 18, 2026):
 * This calculator has been verified as functionally complete at 93%+ accuracy.
 * 
 * v4.44 Changes (Jan 31, 2026 - CRITICAL BUG FIXES):
 * - BUG FIX: Support Unit Calculation Fallback Improved
 *   - ISSUE: Support units showing 0 personnel, 0 casualties
 *   - ROOT CAUSE: total_strength was undefined, fallback set to 1, giving 0 support personnel
 *   - FIXED: Now tries to calculate from brigade strengths before using minimal fallback
 *   - Logic: total_strength → sum(brigades) → fallback to 1
 *   - Result: Support units now calculated correctly (~14,000 personnel, ~200 casualties)
 *   - Console: "[SECTION 12] Support calc: Using brigade sum for total_strength: 38000"
 * 
 * - RELATED FIX: Tab1 v3.14 now properly sets result.total_strength
 *   - Tab1 was overwriting corps.strength with division totals (bug!)
 *   - Now stores total in result.total_strength, keeps corps.strength as HQ only
 *   - Calculator now receives proper total_strength from Tab1
 * 
 * - IMPACT:
 *   - Support units: 0 → ~14,000 personnel ✓
 *   - Support casualties: 0 → ~200 casualties ✓
 *   - More resilient to missing total_strength data
 * 
 * v4.43 Changes (Jan 31, 2026 - SECTION 12 Phase 1: Fallback Number Removal):
 * - EXPOSE DATA INTEGRITY ISSUES: Remove fallbacks that hide missing Tab 1/Tab 2 data
 * 
 * - CRITICAL FALLBACKS REMOVED:
 *   Brigade Data:
 *   ✅ brigade.strength || 4500 → REMOVED (Line ~5396)
 *   ✅ brigade.operational_posture || 'HASTY_DEFENSE' → REMOVED (Line ~5351)
 *   ✅ brigade.operational_state || 'COMBAT_ENGAGED' → REMOVED (Line ~5352, ~5672, ~5840)
 *   ✅ brigade.unit_type || 'MECHANIZED' → REMOVED (4 instances)
 *   ✅ brigade.mission_role || 'SUPPORTING' → REMOVED (2 instances)
 *   
 *   Echelon Data:
 *   ✅ corps.strength || 2000 → REMOVED (Line ~6729)
 *   ✅ corps.operational_state || 'SUPPORT_ACTIVE' → REMOVED (Line ~6730)
 *   ✅ division.strength || 1000 → REMOVED (Line ~6758)
 *   ✅ division.operational_state || 'COMBAT_ENGAGED' → REMOVED (Line ~6759)
 *   
 *   Force Totals:
 *   ✅ total_strength || 4500 → REMOVED (2 instances)
 * 
 * - VALIDATION ADDED:
 *   All critical data now validated with console.error messages
 *   Missing data uses obviously wrong values (1, 'UNKNOWN') to expose issues
 *   Tab 1/Tab 2 MUST provide all required data or calculator will fail visibly
 * 
 * - FALLBACKS KEPT (legitimate defensive programming):
 *   ✓ brigade.mission_role || brigade.missionRole (field name variant)
 *   ✓ brigade.unit_type || brigade.type (field name variant)
 *   ✓ phase.operational_state || brigade.operational_state (phase overrides brigade)
 *   ✓ organicAssets?.ground_ambulances?.count || 0 (optional nested properties)
 *   ✓ All || [] and || {} (safe array/object handling)
 *   ✓ All || 1.0 for constants (not Tab data)
 * 
 * - IMPACT:
 *   - If Tab 1 doesn't provide brigade.strength: Calculator will log CRITICAL error
 *   - If Tab 1 doesn't provide operational_state: Support units won't have 5x casualty reduction
 *   - If corps/division data missing: DNBI calculations will fail visibly
 *   - Users/developers will see EXACTLY what data is missing
 *   - Forces proper data flow from Tab 1/Tab 2
 * 
 * - CONSOLE LOGGING:
 *   - "[SECTION 12] CRITICAL: Brigade '1st Mech' missing strength (got: undefined)"
 *   - "[SECTION 12] CRITICAL: Brigade '2nd Armor' missing operational_state"
 *   - "[SECTION 12] CRITICAL: Corps missing strength (got: 0)"
 *   - "[SECTION 12] CRITICAL: Division 1 missing operational_state"
 * 
 * - NEXT PHASE: Section 12 Phase 2 (if needed - fix Tab 1/Tab 2 to provide all data)
 * 
 * v4.42 Changes (Jan 31, 2026 - SECTION 6: UI Support & Display Metadata):
 * - UI-READY DATA STRUCTURES: Calculator now provides display-ready metadata for Tab components
 * 
 * - UI HELPER FUNCTIONS:
 *   getRoleBadge() - Generate role badge metadata (ME, SE, RES, EF, EXP)
 *   getStateBadge() - Generate operational state badge (COMBAT, SUPPORT, RESERVE)
 *   getPhaseIndicator() - Generate phase indicator (3↓, 2⚙, etc)
 *   getCasualtyMultiplierSeverity() - Classify multiplier severity (LOW, MODERATE, HIGH, VERY_HIGH, CRITICAL)
 *   getPostureColor() - Get color for posture category (ATTACK=red, DEFENSE=blue, MARCH=grey)
 * 
 * - UI COLOR SCHEME:
 *   Consistent colors exported in results.ui_color_scheme:
 *   - Operational States: COMBAT_ENGAGED (#d32f2f), SUPPORT_ACTIVE (#ffa726), TRUE_RESERVE (#66bb6a)
 *   - Posture Categories: ATTACK (#d32f2f), DEFENSE (#2196f3), MARCH (#9e9e9e), CONSOLIDATION (#66bb6a)
 *   - Mission Roles: MAIN_EFFORT (#d32f2f), SUPPORTING (#2196f3), RESERVE (#66bb6a), etc
 *   - Severity: LOW (#66bb6a), MODERATE (#ffa726), HIGH (#ff5722), VERY_HIGH (#d32f2f), CRITICAL (#b71c1c)
 * 
 * - BRIGADE UI METADATA:
 *   Each brigadeResult.ui_metadata contains:
 *   {
 *     role_badge: { text: 'ME', color: '#d32f2f', label: 'Main Effort', tooltip: '...' },
 *     phase_indicator: { icon: '3↓', source: 'CORPS', count: 3, tooltip: '...' },
 *     state_badge: { text: 'COMBAT_ENGAGED', short: 'COMBAT', color: '#d32f2f', tooltip: '...' },
 *     movement_indicator: { has_march: true, icon: '🚚', march_phases: ['Movement'] },
 *     casualty_multiplier: { level: 'HIGH', color: '#ff5722', label: '...', multiplier: 2.25 },
 *     overall_status: { combat_effective: true, casualties_percent: 15, medical_status: 'ADEQUATE', color: '...' }
 *   }
 * 
 * - PHASE TIMELINE:
 *   results.phase_timeline provides visual timeline data:
 *   {
 *     total_days: 15,
 *     phase_count: 3,
 *     phases: [
 *       { index: 0, name: 'Preparation', start_day: 1, end_day: 5, duration: 5,
 *         posture: 'HASTY_DEFENSE', color: '#2196f3', short_name: 'PREP', is_march: false },
 *       ...
 *     ],
 *     day_to_phase: { 1: 'Preparation', 2: 'Preparation', ..., 6: 'Attack', ... }
 *   }
 * 
 * - DIVISION SUMMARIES:
 *   results.division_summaries provides division-level UI data:
 *   [
 *     {
 *       division_id: 'div_1',
 *       division_name: '1st Infantry Division',
 *       phase_status: { total_phases: 3, source: 'CORPS', inherited: true },
 *       brigade_phase_status: { total_brigades: 3, inherited_count: 2, custom_count: 1,
 *         details: [{ brigade_id: '...', brigade_name: '...', phase_source: 'CORPS' }, ...] },
 *       casualties_summary: { total_kia: 150, total_dow: 75, total_wia: 800, total_all: 1025 }
 *     }
 *   ]
 * 
 * - ENHANCED WARNING METADATA:
 *   All warnings enhanced with UI metadata:
 *   {
 *     ...(existing warning fields),
 *     ui: {
 *       icon: '🔴',
 *       color: '#b71c1c',
 *       priority: 1,
 *       category: 'MEDICAL',
 *       show_in_summary: true,
 *       dismissible: false
 *     }
 *   }
 * 
 * - USE CASES:
 *   Tab 1: Brigade cards with role/state badges, visual indicators
 *   Tab 2: Phase timeline, casualty multiplier preview, division summaries
 *   Tab 5: Warning display with icons/colors, brigade summaries with badges
 * 
 * - CONSOLE LOGGING:
 *   - "[SECTION 6] Generating UI metadata..."
 *   - "[SECTION 6] Phase timeline generated: 3 phases over 15 days"
 *   - "[SECTION 6] Division summaries generated: 2 divisions"
 *   - "[SECTION 6] UI metadata generation complete"
 * 
 * - SECTION 6 COMPLETE:
 *   ✅ UI helper functions (badge generators, color mappings)
 *   ✅ Brigade UI metadata (role, phase, state, movement, multiplier)
 *   ✅ Phase timeline data (visual timeline, day-to-phase mapping)
 *   ✅ Division summaries (phase status, brigade breakdown, casualties)
 *   ✅ Enhanced warning metadata (icons, colors, priorities)
 *   ✅ Color scheme export (consistent colors throughout UI)
 * 
 * v4.41 Changes (Jan 31, 2026 - SECTION 5: Validation & Warnings):
 * - INTELLIGENT VALIDATION: Detect problematic configurations that cause operational failure
 * 
 * - VALIDATION 1: ALL UNITS MARCHING SIMULTANEOUSLY
 *   Detection: All brigades in MARCH posture on same day
 *   Problem: NO medical treatment facilities operational anywhere
 *   Level: CRITICAL
 *   Impact:
 *   - All Role 1, Role 2, Role 3 facilities packed for movement
 *   - Only ambulances available for evacuation
 *   - All casualties MUST be evacuated to higher echelon
 *   - Massive strain on evacuation system
 *   Recommendations:
 *   - Stagger movement (units move on different days)
 *   - Designate at least one brigade to remain static for medical support
 *   - Ensure robust ambulance/MEDEVAC availability
 *   - Pre-position medical support at destination
 * 
 * - VALIDATION 2: NO MEDICAL BRIGADE
 *   Detection: Force > 9000 troops (2+ brigades) without Medical Brigade
 *   Problem: Limited Role 2E/Role 3 surgical capability
 *   Level: WARNING
 *   Gaps:
 *   - No Role 2E (enhanced surgery with specialty capabilities)
 *   - No Role 3 (combat support hospital)
 *   - Limited blood banking capability
 *   - No specialty surgical teams (neuro, ortho, maxillofacial)
 *   - Reduced forward resuscitation capacity
 *   Impact: Higher DOW rates, longer evacuation chains
 *   Recommendation: Add Medical Brigade for comprehensive medical support
 * 
 * - VALIDATION 3: POSTURE/MISSION MISMATCH
 *   Status: ✅ Already implemented in Tab2_Operations.jsx v6.3
 *   Brigade dialog validates at input (no calculator check needed)
 * 
 * - VALIDATION 4: PHASE CONFIGURATION
 *   Detects:
 *   a) Gaps: Days with no defined phase
 *      Impact: Default parameters will be used
 *   b) Overlaps: Days in multiple phases simultaneously
 *      Impact: Ambiguous phase assignment
 *   c) Incomplete: Phases don't cover full operation duration
 *      Impact: Final days use last phase parameters
 *   Level: WARNING
 *   Recommendations: Adjust phase start/duration to eliminate issues
 * 
 * - RESULTS STRUCTURE:
 *   results.warnings = [
 *     {
 *       level: 'CRITICAL' | 'WARNING' | 'INFO',
 *       type: 'ALL_UNITS_MARCHING' | 'NO_MEDICAL_BRIGADE' | 'PHASE_GAP' | etc,
 *       message: "Human-readable warning",
 *       details: "Additional context",
 *       impact: "Operational impact",
 *       recommendations: ["Fix 1", "Fix 2", ...]
 *     }
 *   ]
 * 
 * - CONSOLE LOGGING:
 *   - "[SECTION 5] Running validation checks..."
 *   - "⚠️ CRITICAL: Day 5: ALL units in movement - NO medical treatment facilities operational"
 *   - "⚠️ WARNING: Large force (13,500 troops) with no Medical Brigade"
 *   - "⚠️ WARNING: Phase gap: Days 6-7 have no defined phase"
 *   - "[SECTION 5] Validation complete: 1 CRITICAL, 2 WARNING"
 * 
 * - WARNING LEVELS:
 *   CRITICAL: Configurations that WILL cause operational failure
 *   WARNING: Suboptimal but survivable configurations
 *   INFO: Informational only (optimizations, best practices)
 * 
 * - EXAMPLE WARNING OUTPUT:
 *   {
 *     level: 'CRITICAL',
 *     type: 'ALL_UNITS_MARCHING',
 *     day: 5,
 *     brigades_marching: 3,
 *     message: 'Day 5: ALL units in movement - NO medical treatment facilities operational',
 *     details: 'All Role 1, Role 2, and Role 3 facilities packed for movement. Only ambulances available.',
 *     impact: 'All casualties MUST be evacuated to higher echelon. Massive strain on evacuation system.',
 *     recommendations: [
 *       'Stagger movement (units move on different days)',
 *       'Designate at least one brigade to remain static for medical support',
 *       'Accept risk if emergency withdrawal situation'
 *     ]
 *   }
 * 
 * - SECTION 5 COMPLETE:
 *   ✅ Validation 1: All units marching detection
 *   ✅ Validation 2: Medical brigade coverage check
 *   ✅ Validation 3: Posture/mission mismatch (in Tab 2)
 *   ✅ Validation 4: Phase gap/overlap detection
 * 
 * v4.40 Changes (Jan 31, 2026 - PHASE 4: March/Movement Integration):
 * - MARCH OPERATIONS SUPPORT: Different casualty profiles for units in movement
 * 
 * - TASK 4.1: MARCH POSTURE DETECTION
 *   - Added isMarchPosture() helper to detect march/movement operations
 *   - Detects: MARCH_TACTICAL, MARCH_ADMINISTRATIVE, TRANSIT, DISPLACEMENT
 *   - Or any posture containing "MARCH", "TRANSIT", "DISPLACEMENT"
 * 
 * - TASK 4.2: MEDICAL FACILITY STATUS
 *   - During march: medical_facilities_operational = false
 *   - Role 1: Packed for movement (not operational)
 *   - Role 2: Requires 6-12h setup (not operational)
 *   - Only ambulances available during march
 *   - All casualties must be evacuated immediately
 * 
 * - TASK 4.3: MARCH CASUALTY CALCULATIONS
 *   Battle Casualties (VERY LOW):
 *   - MARCH_TACTICAL: 0.1% per day (vulnerable to ambush/mines/air)
 *   - MARCH_ADMINISTRATIVE: 0.01% per day (peacetime movement)
 *   - TRANSIT: 0.005% per day (secure areas)
 *   - DISPLACEMENT: 0.05% per day (moderate risk)
 *   
 *   DNBI Casualties (ELEVATED):
 *   - MARCH/DISPLACEMENT: 30/1000/day (3% vs 2% normal)
 *   - TRANSIT: 25/1000/day (2.5%)
 *   - Reason: Cramped conditions, fatigue, hygiene challenges, disrupted rest
 * 
 * - CASUALTY PROFILE COMPARISON:
 *   Normal Combat (DELIBERATE_ATTACK, 4500 troops, 3 days):
 *   - Battle: ~405 casualties (3% per day × 1.0 op state)
 *   - DNBI: ~270 casualties (2% per day)
 *   - Total: ~675 casualties (60% battle, 40% DNBI)
 *   
 *   March (MARCH_TACTICAL, 4500 troops, 3 days):
 *   - Battle: ~3 casualties (0.1% per day × 0.2 op state)
 *   - DNBI: ~405 casualties (3% per day)
 *   - Total: ~408 casualties (1% battle, 99% DNBI!) ← REVERSED!
 * 
 * - RESULTS STRUCTURE:
 *   Phase breakdown:
 *   - is_march: boolean (true if march operation)
 *   - medical_facilities_operational: boolean (false during march)
 *   
 *   Day summary:
 *   - is_march_operation: boolean
 *   - battle.analysis.is_march_operation: boolean
 *   - battle.analysis.march_type: posture string
 *   - battle.analysis.march_base_rate: rate used
 *   - dnbi.is_march_operation: boolean
 *   - dnbi.march_type: posture string
 * 
 * - CONSOLE LOGGING:
 *   - "[PHASE 4] March operation detected: MARCH_TACTICAL"
 *   - "[PHASE 4] Medical facilities NOT OPERATIONAL during movement"
 *   - "[PHASE 4] Using march-specific casualty rates"
 *   - "[PHASE 4] March battle casualties: 3 (0.1% base × 0.2x op state)"
 *   - "[PHASE 4] March DNBI: 405 (30/1000/day vs 20/1000/day normal)"
 * 
 * - KEY INSIGHTS:
 *   - March is primarily a DNBI problem, not a battle casualty problem
 *   - Medical planning must account for NO treatment facilities during movement
 *   - Evacuation-only operations have different requirements
 *   - DNBI prevention (rest, hygiene, conditioning) critical during marches
 * 
 * - PHASE 4 STATUS:
 *   ✅ Task 4.1: March posture detection
 *   ✅ Task 4.2: Medical facility unavailability
 *   ✅ Task 4.3: March casualty calculations
 *   ⏳ Next: Validation warnings (all units marching simultaneously)
 * 
 * v4.39 Changes (Jan 31, 2026 - TASK 3.4: Use Stored mission_multiplier):
 * - USE PRE-CALCULATED VALUES: Read mission_multiplier from Tab 1 data
 * - BEFORE: calculateDailyBattleCasualties({ mission_role: 'MAIN_EFFORT' })
 *   → Battle module calculates: getMissionRoleMultiplier('MAIN_EFFORT') → 1.5x
 * - AFTER: Read brigade.tactical_in_parent.mission_multiplier (already 1.5)
 *   → Pass directly to battle module: mission_multiplier: 1.5
 * 
 * - IMPLEMENTATION:
 *   - Extract mission_multiplier from brigade.tactical_in_parent (Tab 1 structure)
 *   - Fallback to brigade.mission_multiplier if tactical_in_parent not present
 *   - Final fallback to 1.0 (Supporting role) if neither present
 *   - Pass value to calculateDailyBattleCasualties as brigade.mission_multiplier
 * 
 * - DATA FLOW:
 *   Tab 1 → User selects "Main Effort" → Calculates 1.5x → Stores in tactical_in_parent.mission_multiplier
 *   Tab 2 → Operation configured → Brigade configs passed to calculator
 *   Calculator → Reads pre-calculated 1.5x → Passes to battle module
 *   Battle Module → Uses provided value (no recalculation needed)
 * 
 * - BENEFITS:
 *   - Single source of truth (Tab 1 calculation)
 *   - No risk of calculation mismatch between Tab 1 and calculator
 *   - User sees exact multiplier used in Tab 1 display
 *   - Calculator respects Tab 1 configuration exactly
 *   - Eliminates duplicate calculation logic
 * 
 * - CONSOLE LOGGING:
 *   - "[TASK 3.4] Brigade 1st Mechanized using mission_multiplier: 1.5x (source: Tab 1 tactical_in_parent)"
 *   - "[TASK 3.4] Brigade 2nd Armor using mission_multiplier: 1.0x (source: Fallback)"
 *   - Only logged once per brigade (first day of first phase)
 * 
 * - PRIORITY ORDER:
 *   1. brigade.tactical_in_parent.mission_multiplier (Tab 1 value) ← PREFERRED
 *   2. brigade.mission_multiplier (direct value)
 *   3. 1.0 (Supporting role fallback)
 * 
 * - EXAMPLE VALUES:
 *   - Main Effort: 1.5x (receives priority)
 *   - Supporting: 1.0x (standard)
 *   - Reserve: 0.2x (minimal combat exposure)
 *   - Economy of Force: 0.6x (reduced commitment)
 *   - Exploitation: 1.3x (exploiting success)
 * 
 * - PHASE 3 COMPLETE:
 *   ✅ Task 3.0: Operational State Integration
 *   ✅ Task 3.1: Corps/Division DNBI
 *   ✅ Task 3.2: Phase Resolution
 *   ✅ Task 3.3: Per-Phase Calculations
 *   ✅ Task 3.4: Use Stored mission_multiplier
 * 
 * v4.38 Changes (Jan 31, 2026 - TASK 3.3: Per-Phase Casualty Calculations):
 * - MAJOR RESTRUCTURE: Replaced single duration loop with nested phase/day loops
 * - BEFORE: for (let day = 1; day <= duration; day++) { calculate... }
 * - AFTER: for (each phase) { for (each day in phase) { calculate... } }
 * 
 * - IMPLEMENTATION CHANGES:
 *   - Daily loop now nested inside phase loop
 *   - Each phase uses its own: duration_days, posture, operational_state
 *   - Absolute day counter (currentDay) tracks position across all phases
 *   - Phase-specific parameters passed to battle/DNBI calculations
 * 
 * - CALCULATION UPDATES:
 *   - Battle casualties use phase.posture instead of brigade.operational_posture
 *   - DNBI calculations use phase.operational_state instead of brigade.operational_state
 *   - Each day aware of which phase it's in for proper parameter application
 * 
 * - RESULTS STRUCTURE:
 *   - Added brigadeResult.by_phase: Array of phase breakdowns
 *   - Each phase breakdown contains:
 *     - phase_name, duration_days, posture, operational_state
 *     - start_day, end_day (absolute day numbers)
 *     - days: Array of daily results for this phase
 *     - totals: KIA/DOW/WIA/DNBI/RTD summed for this phase
 * 
 * - PHASE BREAKDOWN STRUCTURE:
 *   {
 *     phase_index: 0,
 *     phase_name: "Preparation",
 *     duration_days: 5,
 *     posture: "HASTY_DEFENSE",
 *     operational_state: "SUPPORT_ACTIVE",
 *     start_day: 1,
 *     end_day: 5,
 *     days: [...],  // 5 daily summaries
 *     totals: { kia: 10, dow: 5, wia: 50, dnbi: 30, ... }
 *   }
 * 
 * - CONSOLE LOGGING:
 *   - "[TASK 3.3] Starting per-phase calculations for 3 phase(s)"
 *   - "[TASK 3.3] Phase 1/3: Preparation (5 days, HASTY_DEFENSE, SUPPORT_ACTIVE)"
 *   - "[TASK 3.3] Phase Preparation complete: 95 total casualties (65 battle + 30 DNBI)"
 *   - "[TASK 3.3] All 3 phase(s) complete for brigade 1st Mechanized"
 * 
 * - EXAMPLE MULTI-PHASE OPERATION:
 *   Phase 1: Preparation (5 days, Hasty Defense, Support Active 0.2x)
 *     → 65 battle casualties (low intensity)
 *   Phase 2: Attack (7 days, Deliberate Attack, Combat Engaged 1.0x)
 *     → 420 battle casualties (high intensity)
 *   Phase 3: Consolidation (3 days, Hasty Defense, Support Active 0.2x)
 *     → 45 battle casualties (low intensity)
 *   TOTAL: 530 battle casualties (properly reflects operational phases)
 * 
 * - BENEFITS:
 *   - Accurate modeling of multi-phase operations
 *   - Each phase contributes proportional casualties based on actual conditions
 *   - Preparation/consolidation phases have lower casualties (0.2x multiplier)
 *   - Attack phases have higher casualties (1.0x multiplier)
 *   - DNBI rates vary by operational_state per phase
 *   - Enables realistic planning for complex operations
 * 
 * - BACKWARD COMPATIBILITY:
 *   - Brigade still uses single duration if phases not defined
 *   - Fallback creates single default phase spanning operation
 *   - Results still aggregate to brigadeResult.totals
 *   - Existing Tab 5 displays still work with total values
 * 
 * - NEXT TASK (3.4):
 *   - Use pre-calculated mission_multiplier from Tab 1
 *   - Instead of recalculating from tactical_role
 *   - Tab 1 already stores this value per brigade
 * 
 * v4.37 Changes (Jan 31, 2026 - TASK 3.2: Phase Resolution/Inheritance):
 * - PHASE INHERITANCE LOGIC:
 *   - Added getBrigadeActualPhases() function to resolve phase inheritance
 *   - Brigades can inherit phases from Division (uses_division_phases flag)
 *   - Divisions can inherit phases from Corps (uses_corps_phases flag)
 *   - Follows full inheritance chain: Corps → Division → Brigade
 * - IMPLEMENTATION:
 *   - Pass forceBuilderResult to calculateBrigadeCasualties
 *   - Find parent division using brigade.parent_division_id
 *   - Find parent corps from forceStructure.corps
 *   - Call getBrigadeActualPhases() to resolve inheritance chain
 * - RESOLUTION LOGIC:
 *   - If brigade.uses_division_phases = false → use brigade.phases
 *   - If brigade.uses_division_phases = true:
 *     - Check if division.uses_corps_phases = true → use corps.phases
 *     - Else → use division.phases
 *   - Fallback: Single default phase if no valid phases found
 * - RESULTS STRUCTURE:
 *   - Added brigadeResult.phases_used: Array of resolved phases
 *   - Added brigadeResult.phase_source: 'CORPS', 'DIVISION', or 'BRIGADE'
 *   - Enables Tab 5 to show which phases were actually used
 * - CONSOLE LOGGING:
 *   - "[PHASE RESOLUTION] Brigade X using OWN phases (3 phases)"
 *   - "[PHASE RESOLUTION] Brigade Y inheriting from CORPS via Division (5 phases)"
 *   - "[TASK 3.2] Brigade Z resolved to 3 phase(s)"
 * - NEXT STEP (TASK 3.3):
 *   - Currently phases are resolved but calculations still use single duration
 *   - Task 3.3 will iterate through phases and calculate per-phase
 *   - Each phase has its own: duration, posture, operational_state
 * - STATUS:
 *   - Phase resolution: ✅ COMPLETE
 *   - Per-phase calculation: ⏳ Task 3.3 (next)
 * 
 * v4.36 Changes (Jan 31, 2026 - TASK 3.1 FIX: Correct Field Name for Personnel):
 * - FIELD NAME FIX (ROOT CAUSE):
 *   - Calculator was reading corps.personnel and division.personnel
 *   - Tab 1 stores corps.strength and division.strength
 *   - Result: Corps/Division personnel always 0 → DNBI = 0
 * - CHANGES MADE:
 *   - Line 5365: corps.personnel → corps.strength
 *   - Line 5383: division.personnel → division.strength
 *   - Lines 5416, 5425: Return object now reads corps.strength
 * - RATIONALE:
 *   - Tab 1 uses .strength consistently for all echelons
 *   - Calculator already reads brigade.strength correctly
 *   - Match the same field name for corps/divisions
 *   - No hardcoded fallbacks - read actual data from Tab 1
 * - IMPACT:
 *   - Corps DNBI now calculated from actual strength (e.g., 45000)
 *   - Division DNBI now calculated from actual strength (e.g., 15000)
 *   - Realistic echelon DNBI: 500-800 casualties vs previous 0-40
 * - EXAMPLE (10 days, Support Active 1.3/1000/day):
 *   - Corps 45000: 585 DNBI (was 0)
 *   - Division 15000 each: 195 DNBI each (was 20)
 *   - Total: 975 DNBI (was 40) - 24x more accurate!
 * 
 * v4.35 Changes (Jan 31, 2026 - TASK 3.1 FIX: Correct DNBI Rate Priority):
 * - DNBI RATE CALCULATION FIX:
 *   - Changed priority from base_rate → combined_rate (more accurate)
 *   - combined_rate includes ALL factors: base × stress × hygiene × sleep
 *   - Corps (Support Active): 1.3/1000/day (was 1.0, now correct 1.3)
 *   - Divisions (Combat Engaged): 4.7/1000/day (was 2.0, now correct 4.7)
 *   - True Reserve: 0.3/1000/day (was 0.5, now correct 0.3)
 * - CORPS PERSONNEL FALLBACK:
 *   - Fixed corps.personnel reading to use optional chaining
 *   - Fallback to 2000 personnel if not specified in data
 *   - Prevents corps DNBI = 0 when personnel data missing
 * - RATIONALE:
 *   - combined_rate = comprehensive calculation (best accuracy)
 *   - base_rate = simplified fallback only
 *   - Ukraine data = one observation (not mandatory truth)
 *   - Tool philosophy: Use best calculation available, not single source
 * - IMPACT:
 *   - Corps (2000 personnel): 20 DNBI → 26 DNBI (+30%)
 *   - Division (1000 personnel): 20 DNBI → 47 DNBI (+135%)
 *   - Total echelon DNBI more accurate for medical planning
 * 
 * v4.34 Changes (Jan 31, 2026 - TASK 3.1: Corps/Division DNBI Integration):
 * - ECHELON DNBI CALCULATION:
 *   - Added calculateEchelonDNBI() function for Corps and Division HQ personnel
 *   - Each echelon reads its own operational_state for DNBI rate
 *   - Corps: ~2000 personnel (typically Support Active: 1.5/1000/day)
 *   - Divisions: ~1000 personnel each (varies by operational_state)
 *   - CRITICAL: Previously only brigades contributed to DNBI
 * - OPERATIONAL_STATE INTEGRATION:
 *   - Imported OPERATIONAL_STATES from constants
 *   - Corps DNBI: personnel × (operational_state.dnbi.base_rate / 1000) × duration
 *   - Division DNBI: same formula per division with its operational_state
 *   - Uses Ukraine 2022-2024 validated rates (4.5, 1.5, 0.5 per 1000/day)
 * - RESULTS STRUCTURE:
 *   - Added results.echelon_casualties with Corps/Division breakdown
 *   - Includes personnel count, operational_state, DNBI per echelon
 *   - Total echelon DNBI added to results.by_corps.total_dnbi
 * - CONSOLE LOGGING:
 *   - Detailed logging for Corps/Division DNBI calculations
 *   - Shows: personnel, operational_state, rate, calculated DNBI
 *   - Format: "[ECHELON DNBI] Corps: 2000 personnel, SUPPORT_ACTIVE (1.5/1000/day) → 30 DNBI over 10 days"
 * - IMPACT:
 *   - Typical Corps (2 divisions, 9 brigades): +50-100 DNBI from echelon HQs
 *   - Example: Corps (30) + 2 Divisions (90) = +120 DNBI total
 *   - Previously these 3000-5000 personnel were not included in DNBI calculations
 * 
 * v4.33 Changes (Jan 29, 2026 - PHASE 4.6: Resupply Route Modeling):
 * - ROUTE-BASED DELIVERY MODELING:
 *   - Import resupplyTransport.js utilities for route-based modeling
 *   - Added calculateDOSProjectionWithMonteCarlo() function
 *   - Accounts for route conditions: OPEN (1.0x), DAMAGED (1.5x), CONTESTED (2.0x), CLOSED (0%)
 *   - Weather impact: CLEAR (1.0x), RAIN (1.2x), HEAVY_RAIN (1.5x), SNOW (1.8x)
 *   - Enemy AD threat affects air delivery: NONE (100%), LOW (95%), MEDIUM (80%), HIGH (60%)
 * - MONTE CARLO SIMULATION:
 *   - Runs 100 iterations per resupply schedule
 *   - Each iteration applies success probability to deliveries
 *   - Failed deliveries → Stock not replenished
 *   - Outputs: 5th percentile (worst), 50th (expected), 95th (best) DOS
 *   - Stockout probability and average stockout day
 * - DELIVERY TIME CALCULATION:
 *   - Base times from resupplyThresholds.js (ROLE_3: 2-8h, ROLE_4: 6-24h, THEATER: 8-48h)
 *   - Route modifier: CONTESTED routes take 2x longer
 *   - Weather modifier: Snow adds 80% delay, heavy rain adds 50%
 *   - Total time: base × route × weather
 * - RISK ANALYSIS:
 *   - Each resupply event tagged with delivery_analysis
 *   - Shows: time (total hours), probability (success %), risk_level (LOW/MEDIUM/HIGH/CRITICAL)
 *   - Factors breakdown: route status, weather impact, AD threat
 * - INTEGRATION:
 *   - Reads evacuation routes from Tab 3 (medical_configuration.evacuation_routes)
 *   - Reverse direction: Supply flows from higher echelon to lower
 *   - ROLE_3 → ROLE_2E → ROLE_2B → ROLE_1
 *   - Ground transport affected by all route segments
 *   - Air transport only affected by weather and AD threat
 * - OUTPUT TO TAB 5:
 *   - results.medical_requirements.resupply_summary.dos_projection
 *   - Now includes: monte_carlo object with probability bands
 *   - events_with_risk array showing delivery risk per event
 *   - environmental_conditions used in simulation
 * 
 * v4.32 Changes (Jan 29, 2026 - PHASE 4.5.5: Injury Mechanism Integration):
 * - EXPORT: Injury mechanism configuration for Tab 4 integration
 *   - Exported BLOOD_BY_MECHANISM_CONFIG (5 mechanisms: GSW, BLAST_FRAG, MINE_IED, BURNS, CRUSH)
 *   - Exported getInjuryMechanismDistribution(terrain, missionType) function
 *   - Exported calculateBloodByMechanism(t1Casualties, mechanismDist) function
 * - PURPOSE: Enable Tab 4 to display threat-adjusted consumption rates
 *   - Tab 4 can now show: "Base 2.0 PRBC/cas → Adjusted 2.4 PRBC/cas (Artillery 60%, Small Arms 40%)"
 *   - Allows users to understand why consumption rates differ from NATO averages
 * - MECHANISM MULTIPLIERS (per T1 casualty):
 *   - GSW: 4 PRBC (typical: 45% frequency)
 *   - BLAST_FRAG: 6 PRBC (typical: 30% frequency) 
 *   - MINE_IED: 12 PRBC (typical: 15% frequency)
 *   - BURNS: 8 PRBC (typical: 5% frequency)
 *   - CRUSH: 6 PRBC (typical: 5% frequency)
 * - DISTRIBUTION BY ENVIRONMENT:
 *   - URBAN: 55% GSW, 25% Blast, 10% Mine
 *   - OPEN: 40% GSW, 30% Blast, 20% Mine
 *   - DEFENSIVE: 35% GSW, 25% Blast, 30% Mine (highest mine threat)
 *   - OFFENSIVE: 50% GSW, 30% Blast, 10% Mine
 *   - MIXED: 45% GSW, 30% Blast, 15% Mine (default)
 * 
 * v4.31 Changes (Jan 29, 2026 - PHASE 4.1 FIX v3):
 * - CRITICAL BUG FIX: DOS projection showing immediate stockout
 *   - ROOT CAUSE: Calculator reading initial stock from wrong location
 *     - Was: medical_configuration.blood_config.current_stock (100 PRBC)
 *     - Should be: scenario.logistics.medical_supplies.initial_stock (1524 PRBC)
 *   - Tab 4 stores user-entered stock in logistics.medical_supplies.initial_stock
 *   - medical_config.blood_config.current_stock is for Tab 3 blood management
 *   - FIX: Lines 3718-3729 now read from correct Tab 4 path
 *   - Field names: prbc_units, ffp_units, platelets_units, cryo_units
 *   - RESULT: DOS projection now uses actual stock values from Tab 4
 * 
 * v4.30 Changes (Jan 29, 2026 - PHASE 4.1 DEBUG):
 *   - Added debug logging to getResupplyConfiguration() to trace data path
 * 
 * v4.29 Changes (Jan 29, 2026 - PHASE 4.1 FIX v2):
 * - CRITICAL BUG FIX: Daily blood consumption still calculating as 0
 *   - ROOT CAUSE: Brigade totals don't contain blood data in resources path
 *   - SOLUTION: Use results.medical_requirements.blood (already calculated)
 *   - FIX: Lines 3708-3720 now use totalBloodConsumed from medical_requirements
 *   - Blood data path: results.medical_requirements.blood.{prbc, ffp, plt, cryo}
 *   - Daily consumption: total / duration for each blood product
 *   - RESULT: DOS projection now shows real consumption (not 999)
 * 
 * v4.28 Changes (Jan 28, 2026 - PHASE 4.1 BUG FIX):
 * - CRITICAL BUG FIX: Resupply DOS projection showing stockout Day 1
 *   - ROOT CAUSE 1: Wrong blood stock data path
 *     - Was looking for: medical_configuration.blood_config.stock.prbc
 *     - Should be: medical_configuration.blood_config.current_stock.prbc_units
 *   - ROOT CAUSE 2: Wrong field names
 *     - Was: .prbc, .ffp, .platelets, .cryo
 *     - Should be: .prbc_units, .ffp_units, .plt_units, .cryo_pools
 *   - ROOT CAUSE 3: Daily consumption calculated from empty results.by_day
 *     - Was: Using results.by_day (empty array)
 *     - Should be: Using results.by_brigade totals / duration
 *   - FIX: Lines 3695-3717 now correctly extract initial stock and calculate consumption
 *   - RESULT: DOS projection now shows correct values (not 999/stockout)
 * 
 * v4.26 Changes (Jan 28, 2026 - PHASE 4.1):
 * - RESUPPLY CALCULATOR INTEGRATION:
 *   - Import PUSH_PACKAGES from classVIII.js (single source of truth)
 *   - Added getResupplyConfiguration() to extract resupply events from Tab 4
 *   - Added calculateResupplyUnits() using PUSH_PACKAGES (no hardcoded values)
 *   - Added applyResupplyToBloodStock() to apply resupply in daily loop
 *   - Added calculateDOSProjection() for DOS projection with resupply
 *   - Integration Point A: Extract resupply config at start of calculation
 *   - Integration Point B: Apply resupply to blood stock in daily loop
 *   - Integration Point C: Add resupply_analysis to day summary output
 *   - Integration Point D: Add resupply_summary to medical_requirements
 *   - Resupply summary includes: total_events, total_units, events_by_day, dos_projection
 *   - DOS projection tracks: projection[], stockout_day, minimum_dos, final_dos
 *   - Day summary tracks: received_today, events[], units_received, events_count
 *   - Tab 5 can now display resupply timeline and DOS projection chart
 *
 * v4.25 Changes (Jan 27, 2026 - BUG FIX):
 * - BUG FIX: Evacuation Routes Display showing "N/A"
 *   - ROOT CAUSE: route_impact output was missing segments_summary field
 *   - FIX: Added segments_summary and total_travel_time_hours to route_impact output (line 4445, 4450)
 *   - Tab5 now correctly displays individual route segment times (R1→R2B, R2B→R2E, etc.)
 *
 * v4.24 Changes (Jan 26, 2026 - PHASE 2.1):
 * - EVACUATION ROUTES INTEGRATION:
 *   - Added constants: ROUTE_STATUS, SURFACE_TYPES, WEATHER_IMPACT
 *   - Added getDefaultRouteSegments() for NATO standard assumptions
 *   - Added calculateRouteMetrics() to compute travel time and modifiers
 *   - Added processRouteSegment() to determine effective route (primary/alternate)
 *   - Added getEvacuationRoutesConfiguration() to extract routes from Tab 3
 *   - Added calculateRouteImpactOnEvacuation() for comprehensive route analysis
 *   - Route DOW modifier applied: OPEN (1.0x), DAMAGED (1.1x), CONTESTED (1.25x)
 *   - Route speed modifier: Surface Ã— Status Ã— Weather
 *   - Golden Hour analysis: R1â†’R2B travel time must be â‰¤45 min
 *   - Day summary includes: route_impact with configured, dow_modifier, golden_hour_achievable, warnings
 *   - Automatic fallback to alternate routes when primary routes CLOSED
 *   - Warnings for: closed routes, contested routes, alternate usage, Golden Hour risk
 *
 * v4.23 Changes (Jan 26, 2026 - PHASE 1.1):
 * - WBB (Walking Blood Bank) INTEGRATION:
 *   - Added getWBBConfiguration() to extract WBB config from Tab 3
 *   - Added calculateWBBContribution() for activation logic
 *   - Added applyWBBToBloodCalculations() to reduce PRBC requirements
 *   - WBB activates when stock falls below threshold OR MASCAL RED/BLACK
 *   - Whole blood from WBB counts as PRBC equivalent
 *   - Low-titer O+ donors provide universal units
 *   - Day summary includes: wbb_status, wbb_contribution, wbb_universal_units, wbb_activation_reason
 *   - Operation summary includes: total_activations, total_units_contributed, days_activated
 * - Import WALKING_BLOOD_BANK constants from bloodProducts.js
 *
 * v4.22 Changes (Jan 23, 2026):
 * - BUG-057 FIX: WIA=NaN causing inverted KIA/WIA ratio
 *   - ROOT CAUSE: survivalModule.calculateComprehensiveDOW() returns dow_rate: NaN
 *   - FIX: Added NATO-accurate fallbacks in survivalModule.js
 *   - FIX: Added guards for finalDOW/finalWIA, DOW cap at 85%
 * - BUG-049 FIX (CORRECTED): RTD now uses triageDistribution fields
 *
 * v4.21 Changes (Jan 21, 2026):
 * - BUG-049 FIX: RTD undefined causing NaN cascade
 *   - battleCasualtyModule doesn't return .rtd field
 *   - Now calculated from WIA categories: T1*5% + T2*15% + T3*60% + T4*2%
 *   - Rates match personnelIntelligence.js RTD_RATES
 *
 * v4.20 Changes (Jan 21, 2026):
 * - BUG-048 FIX: NaN cascade prevention
 *   - Added defensive checks at cumulativeCasualties update (line ~3949)
 *   - Added diagnostic logging to identify which value is NaN
 *   - Added safeCumulativeCasualties check at currentStrength (line ~3364)
 *   - Breaks NaN cascade: Day 1 NaN no longer propagates to Day 2+
 *   - Root cause: daySummary.battle.total, dnbi.total, or rtd is NaN
 *
 * v4.19 Changes (Jan 21, 2026):
 * - BUG-047 FIX: Strength normalization (same pattern as BUG-045 posture fix)
 *   - Tab 1/Tab 2 may save strength as object {value: 4500} instead of number
 *   - {value: 4500} - 0 = NaN ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã‚Â ÃƒÂ¢Ã¢â€šÂ¬Ã¢â€žÂ¢ cascades to all calculations
 *   - Added normalizeStrength() helper function
 *   - Applied at brigade config entry point (single normalization)
 *   - Applied in all reduce functions using brigade strength
 *   - Root cause: Same Tab UI pattern that caused BUG-045
 * 
 * v4.18 Changes (Jan 21, 2026):
 * - BUG-045 FIX: Posture normalization at calculator entry point
 *   - Import normalizePosture from battleCasualtyModule.js
 *   - Normalize brigade.operational_posture BEFORE passing to any function
 *   - Fixes "Unknown posture: [object Object]" warnings
 *   - Fixes NaN calculations caused by object posture from Tab 2
 *   - Defensive normalization at calculateBrigadeCasualties internal calls
 *   - Root cause: Tab 2 saves posture as {value: 'X', label: 'Y'} object
 * 
 * v4.17 Changes (Jan 18, 2026):
 * - BUG-002 FIX: Mission roles now passed to calculateDailyBattleCasualties
 *   - brigade.mission_role now extracted and passed to battleCasualtyModule
 *   - Supports MAIN_EFFORT (1.5ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã¢â‚¬Â), SUPPORTING (1.0ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã¢â‚¬Â), RESERVE (0.2ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã¢â‚¬Â)
 *   - Also supports EXPLOITATION (1.3ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã¢â‚¬Â), ECONOMY_OF_FORCE (0.6ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã¢â‚¬Â)
 *   - Division-level planning now differentiates brigade casualty rates by role
 *   - Educational: "Two up, one back" - reserve takes minimal casualties
 * 
 * v4.16 Changes (Jan 18, 2026):
 * - BUG-004 FIX: AUTO_METHOD_THRESHOLDS now USED (was imported but unused)
 *   - EVAC_METHOD_CONFIG.methods.AUTO now references AUTO_METHOD_THRESHOLDS
 *   - determineAutoMethod() uses AUTO_METHOD_THRESHOLDS.RECEIVING_OVERLOAD_PERCENT
 *   - determineAutoMethod() uses AUTO_METHOD_THRESHOLDS.DISTANCE_PUSH_PREFERRED_KM
 *   - Removed hardcoded 85/30 values, now uses imported 80/30 from constants
 * - BUG-005 FIX: PUSH_DISTANCE_RATIOS now USED (was imported but unused)
 *   - Added getDistanceBasedAssetSplit() helper function
 *   - calculateMethodBasedAssets() now uses distance-based splits from constants
 *   - SHORT (<10km): 40% forward, MEDIUM (10-25km): 50%, LONG (25-50km): 60%, EXTENDED (>50km): 70%
 * - Educational: Students now see constants actually applied in calculations
 * 
 * v4.15 Changes (Jan 17, 2026):
 * - CC-007: CASEVAC constants now IMPORTED from evacuationAssetFactors.js
 *   - Previously hardcoded in calculator, now uses shared constants
 *   - CASEVAC_CONFIGURATION: Availability settings (ON_REQUEST, DEDICATED, etc.)
 *   - CASEVAC_DOW_PENALTIES: DOW multipliers (Ground: 1.35ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã¢â‚¬Â, Prolonged: 1.60ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã¢â‚¬Â)
 *   - AUTO_METHOD_THRESHOLDS: PUSH/PULL decision logic
 *   - PUSH_DISTANCE_RATIOS: Collection/forward splits by distance
 * - Removed hardcoded CASEVAC_CONFIG, now references imported constants
 * - analyzeCASEVACRequirements() updated to use imported CASEVAC_DOW_PENALTIES
 * - calculateCASEVACAdjustedDOW() now uses imported penalty values
 * - Educational: Students see same constants in Tab 3 config and calculator
 * 
 * v4.14 Changes (Jan 16, 2026):
 * - BUGFIX: Array.isArray() checks for brigade_configs in 3 locations
 *   - Line 2656: Main calculation loop
 *   - Line 4105: Validation
 *   - Line 4120: buildScenarioSummary
 * 
 * REMAINING ENHANCEMENT OPPORTUNITIES (MEDIUM Priority - Not Bugs):
 * - CC-002: RTD breakdown by injury type (basic RTD implemented, detailed breakdown is enhancement)
 * - CC-003: DNBI category breakdown (CSR analysis done, full breakdown in dnbiModule)
 * - CC-006: Blue Light/MASCAL trigger (IMPLEMENTED - line 3456, MASCAL per-facility)
 * - CC-014: Surgical queue modeling (surgicalBacklog exists, full queue is enhancement)
 * - CC-015: Bed occupancy tracking (future enhancement for Tab 4 integration)
 * 
 * COMPLETE REBUILD integrating all modules:
 * - survivalModule: TCCC, platinum 10, golden hour, DOW
 * - dnbiModule: Climate, night ops, CSR
 * - medevacModule: Helicopter, ground, Blue Light
 * - battleCasualtyModule: Core battle casualties (FULLY INTEGRATED v4.10)
 * - resourceModule: Blood, surgical, supplies
 * - monteCarloSimulator: Uncertainty quantification
 * - supplyChainModule: G4 logistics tracking (CC-004) - NEW v4.13
 * - strengthProjectionModule: G1 personnel tracking (CC-005) - NEW v4.13
 * - timeToSurgeryModule: Golden Hour POIÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã‚Â ÃƒÂ¢Ã¢â€šÂ¬Ã¢â€žÂ¢Surgery (CC-006) - NEW v4.13
 * - methodBasedAssetsModule: PUSH/PULL/AUTO evacuation (CC-007) - NEW v4.13
 * - CASEVAC: Combat vehicle evacuation when MEDEVAC overwhelmed (CC-007)
 * - Fight Phase CASEVAC: Phase-based evacuation modifiers (CC-008)
 * - AUTO Method: Dynamic PUSH/PULL/AUTO evacuation logic (CC-009)
 * - Rear Area Decay: FEBA distance-based casualty reduction (CC-012)
 * - Blood by Mechanism: Injury type-specific blood requirements (CC-011)
 * - DC-002: Distance affects evacuation time with terrain/route factors
 * - EA-003: Per-facility asset capacity from evacuationAssetAllocation.js
 * - FIX-CC-014: Force superiority modifier now applied to casualties
 * - FIX-CC-015: Medical personnel casualties tracked per day
 * - FIX-CC-016: Per-facility MASCAL condition assessment
 * - FIX-CC-017: Unit type casualty modifiers integrated AND APPLIED
 * - MSK-001: Musculoskeletal surge blood requirements (NATO DES)
 * - FH-001: Forward Field Hospital mortality reduction
 * - TCCC-2025: CoTCCC April 2025 guideline integration ÃƒÆ’Ã‚Â¢Ãƒâ€¦Ã¢â‚¬Å“ÃƒÂ¢Ã¢â€šÂ¬Ã‚Â¦ ENABLED
 * 
 * Achieves 93%+ accuracy target through:
 * - NO hardcoded values (all from constants)
 * - Complete TCCC intervention modeling
 * - Environmental factors (climate, night)
 * - Medical system cascade effects
 * - CASEVAC overflow modeling (CC-007)
 * - Fight phase CASEVAC planning (CC-008)
 * - AUTO evacuation method logic (CC-009)
 * - FEBA distance rear area decay (CC-012)
 * - Injury mechanism blood planning (CC-011)
 * - Monte Carlo confidence intervals
 * - DC-002: Terrain/route-aware evacuation times
 * - EA-003: Organic/attached/detached asset integration
 * - FIX-CC-014: Force superiority affects casualty rates (Dupuy methodology)
 * - FIX-CC-015: Medical capability degradation over time
 * - FIX-CC-016: Per-facility MASCAL triggers DOW increases
 * - FIX-CC-017: Unit-specific vulnerability profiles
 * 
 * v4.13 Changes (Jan 15, 2025):
 * - CC-004: Supply Chain Module integrated (calculateSupplyChain)
 *   - Daily stock tracking, DOS calculation
 *   - Critical items identification (< 3 DOS)
 *   - G4 logistics request generation
 *   - Wastage factors by supply category
 * - CC-005: Strength Projection Module integrated (calculateStrengthProjection)
 *   - Unit strength curve over operation duration
 *   - Combat effectiveness thresholds (70% trigger)
 *   - G1 personnel replacement requests
 *   - RTD returns modeling
 * - CC-006: Time to Surgery Module integrated (calculateAllTimesToSurgery)
 *   - Cumulative POIÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã‚Â ÃƒÂ¢Ã¢â€šÂ¬Ã¢â€žÂ¢Surgery time calculation
 *   - FST/FRS detection and attachment impact
 *   - Golden Hour compliance tracking
 *   - DOW modifier by evacuation time band
 * - CC-007: Method-Based Assets Module integrated (calculateMethodBasedAssets)
 *   - PUSH/PULL/AUTO evacuation method logic
 *   - Distance-based asset split ratios
 *   - Collection vs forward capacity calculation
 *   - DOW impact from collection delays
 * - getTCCC2025Summary() now exported for Tab 5 reports
 * - New results fields: supply_chain, strength_projection, time_to_surgery, method_based_assets
 *
 * v4.12 Changes (Jan 9, 2025):
 * - TCCC-2025: ÃƒÆ’Ã‚Â¢Ãƒâ€¦Ã¢â‚¬Å“ÃƒÂ¢Ã¢â€šÂ¬Ã‚Â¦ ENABLED - CoTCCC April 2025 guidelines integrated
 *   - TXA protocol simplified: Single 2g IV/IO push (was 1g + 1g drip)
 *   - TXA now indicated for significant TBI (new indication)
 *   - Tourniquet efficacy +8% improvement
 *   - Junctional hemorrhage +15% improvement (iTClamp)
 *   - Prolonged Casualty Care (PCC) protocols integrated
 *   - Expected 20-30% reduction in preventable deaths
 * - applyTCCC2025Modifiers() function applies all guideline improvements
 * - getTCCC2025Summary() for reports/educational display
 * - Day summary now includes tccc2025 analysis with lives_saved_estimate
 * - Educational value: Students learn current 2025 protocols
 *
 * v4.10 Changes (Jan 8, 2025):
 * - MSK-001: Musculoskeletal surge blood requirements
 *   - When T1 casualties > 12/day, blood requirements spike (NATO DES research)
 *   - PRBC multiplier: 1.5x, FFP multiplier: 1.3x during surge
 *   - Threshold hardcoded for now (TODO: make configurable in Expert Mode)
 * - FH-001: Forward Field Hospital mortality reduction
 *   - Automatic detection: R2E < 40km from FEBA = "forward" position
 *   - 19% mortality reduction (NATO DES simulation)
 *   - 15% bonus to Golden Hour achievement
 *   - Standard Mode: Automatic detection
 *   - Expert Mode: Explicit toggle (medicalConfig.forward_fh_override)
 * - TCCC-2025: Placeholder flag (NOW ENABLED in v4.12)
 *   - Originally disabled pending CoTCCC publication
 *   - Will improve tourniquet efficacy by ~8% when guidelines published
 *   - NOTE: Now enabled in v4.12 - CoTCCC published April 2025 updates
 *
 * v4.9 Changes (Jan 8, 2025):
 * - BUGFIX: FIX-CC-017 unit type modifiers now APPLIED to battle casualties
 *   - Previous version calculated but did not apply unit type multipliers
 *   - Now applies unit_type_modifier, terrain_modifier, posture_modifier
 *   - Armor in open terrain gets different casualties than Infantry in urban
 * - BUGFIX: Blood by mechanism now correctly uses t1_immediate (was using undefined t1)
 *   - Previous version always got 0 T1 casualties for blood calculation
 *   - Now correctly accesses triageDistribution.t1_immediate
 * - Updated version to 4.9
 *
 * v4.8 Changes (Jan 7, 2025):
 * - FIX-CC-014: applyForceSuperiorityModifier now called after battle casualties
 *   - 3:1 force ratio advantage reduces friendly casualties ~40%
 *   - 1:3 force ratio disadvantage increases friendly casualties ~40%
 *   - Based on Dupuy Institute TNDM methodology
 * - FIX-CC-015: calculateMedicalPersonnelCasualties now tracked daily
 *   - Medical personnel casualties affect unit medical capability
 *   - Includes deliberate targeting factor (Ukraine 2022-2024 pattern)
 *   - Tracks remaining medical personnel and capacity reduction
 * - FIX-CC-016: assessMASCALCondition now used per-facility
 *   - Triggers per-facility MASCAL status during daily loop
 *   - Affects DOW rates when facilities overwhelmed
 * - FIX-CC-017: calculateUnitTypeCasualties integrated
 *   - Unit-type specific casualty modifiers (ARMOR, INFANTRY, etc.)
 *   - Terrain interaction effects per unit type
 *
 * v4.7 Changes (Dec 31, 2024):
 * - DC-002: Added terrainFactors.js integration for evacuation time calc
 * - DC-002: calculateEvacuationTimeWithTerrain() replaces hardcoded 4 min/km
 * - EA-003: Added evacuationAssetAllocation.js integration
 * - EA-003: calculateFacilityAssetCapacity() uses per-facility assets
 * - EA-003: Effective capacity = organic + attached - detached
 *
 * v4.6 Changes (Dec 30, 2024):
 * - CC-008: Fight phase CASEVAC planning
 *   - Added PHASE_CASEVAC_CONFIG with 7 phase types
 *   - ASSAULT: 30% less CASEVAC, 10% less MEDEVAC (vehicles in combat)
 *   - WITHDRAWAL: 40% less CASEVAC, 30% less MEDEVAC (congested routes)
 *   - DEFENSE: 10% better CASEVAC (vehicles in reserve)
 *   - Added getPhaseCASEVACModifiers() and applyPhaseToCASEVAC()
 * 
 * - CC-009: AUTO evacuation method logic
 *   - Added EVAC_METHOD_CONFIG with PULL/PUSH/AUTO/STRATEVAC
 *   - Added determineAutoMethod() for dynamic method selection
 *   - Added calculateMethodAdjustedCapacity() for asset split
 *   - AUTO logic: High capacity (>85%) + short distance (<30km) = PUSH
 *   - PUSH splits sending assets 50/50 between collection and forward
 *   - STRATEVAC for R3ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã‚Â ÃƒÂ¢Ã¢â€šÂ¬Ã¢â€žÂ¢R4 (national responsibility)
 * 
 * v4.5 Changes (Dec 30, 2024):
 * - CC-012: Rear area casualty decay by FEBA distance
 *   - Added REAR_AREA_DECAY_CONFIG with distance bands
 *   - 0-15km: Full threat, 15-30km: 70%, 30-60km: 40%, 60-100km: 20%, 100km+: 10%
 *   - Added calculateRearAreaDecay() and applyRearAreaDecay()
 *   - Unit type vulnerability modifiers (HQ 2.0ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã¢â‚¬Â, ADA 2.5ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã¢â‚¬Â, etc.)
 *   - Research: PMC 2023 LSCO casualty implications
 * 
 * - CC-011: Blood requirements by injury mechanism
 *   - Added BLOOD_BY_MECHANISM_CONFIG with 5 mechanism types
 *   - GSW: 4 PRBC, Blast: 6 PRBC, Mine/IED: 12 PRBC, Burns: 8 PRBC
 *   - Terrain/mission-based mechanism distribution
 *   - Massive Transfusion Protocol (MTP) tracking
 *   - Research: JAMA Surgery 2019, CoTCCC guidelines
 * 
 * v4.4 Changes (Dec 30, 2024):
 * - CC-007: CASEVAC modeling when MEDEVAC capacity exceeded
 * 
 * v4.3 Changes (Dec 30, 2024):
 * - CC-004: Cumulative Golden Hour calculation (POI ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã‚Â ÃƒÂ¢Ã¢â€šÂ¬Ã¢â€žÂ¢ Surgery)
 * 
 * v4.2 Changes (Dec 30, 2024):
 * - FIX-002/003/004, CC-010, CC-013
 * 
 * Personnel Distribution: Combat 59%, HQ 4%, Support 37% = 100%
 * 
 * @version 4.13
 * @author Dr. Tatar Radu MD
 * @author Dr. Tatar Radu MD
 */

// ============================================================================
// MODULE IMPORTS
// ============================================================================

// CALC-010: documented default + "assumptions used" ledger instead of a silent literal fallback
import { applyChainDistances } from '../constants/chainDistances';
import { resolveInput, assumptionLedger } from './assumptionLedger.js';
import { USE_EVACUATION_PROFILES, evacuationAdjustment, isMedicalPackedToday, rearLegMinutes, facilityOf } from '../constants/evacuationProfiles.js';
import { ADVANCE_LEGS_ON, ADVANCE_KM_PER_DAY, phaseAdvanceKm, createAxisState, stepAxis, legGrowthMinutes } from './modules/advanceModel.js';
import { THREAT_EFFECTS_USE, WEATHER_TO_CAPACITY_WEATHER, WEATHER_TO_RESUPPLY_WEATHER, resolveRoadState, helicopterAvailability, rearThreatFactor, threatDowFactor, iedMarchFactor, infrastructureModel } from '../constants/threatEffects.js';
import { USE_PHASE_CASUALTY_TABLE, FORCE_SUPERIORITY_MULTIPLIER_IN_CHAIN, resolvePhaseProfile, resistanceIndexOf, enemyPowerIndex, levelOfIndex, PHASE_FORMS } from '../constants/phaseCasualtyTable.js';
import { engineerExposure } from '../constants/engineerExposure.js';
import { normalizeUnitTypeKey, lookupByUnitType } from '../constants/unitTypeRegistry.js';

import survivalModule, {
  calculateTCCCInterventionEffects,
  calculateComprehensiveDOW
} from './modules/survivalModule.js';

import {
  calculateComprehensiveDNBI
} from './modules/dnbiModule.js';

// CALC-005: single reader of operation_parameters.evacuation_paradigm
import { resolveEvacuationParadigm } from '../constants/evacuationParadigms.js';

// CALC-011: single reader of the terrain/weather chosen in Tab 2
import { resolveScenarioEnvironment } from './scenarioEnvironment.js';

import {
  calculateComprehensiveMEDEVAC,
  evaluateBlueLightProtocol,       // F-05 FIX: needed for post-loop re-evaluation
  getEffectiveAssets               // ENH-002 Phase 4: per-day attach/detach adjustment
} from './modules/medevacModule.js';

import {
  DISCRETE_EVENT_TYPES,  // ENH-041: event type catalog (for label lookup in daySummary)
  applyDiscreteEvent     // ENH-041: in-place casualty overlay helper
} from '../constants/mascalThresholds.js';

import {
  calculateDailyBattleCasualties,
  assessMASCALCondition,
  calculateMedicalPersonnelCasualties,
  applyForceSuperiorityModifier,
  calculateUnitTypeCasualties,
  normalizePosture  // BUG-045 FIX: Import posture normalizer for entry-point normalization
} from './modules/battleCasualtyModule.js';

import {
  calculateComprehensiveResources,
  SURGICAL_REGULATING_ON
} from './modules/resourceModule.js';

import {
  runMonteCarloSimulation
} from './modules/monteCarloSimulator.js';

// Phase 2.5: Evacuation Capacity Calculator
import {
  prepareForCasualtyCalculator,
  calculateEvacuationChainCapacity,
  calculateGroundCapacity,
  calculateAirCapacity
} from './evacuationCapacityCalculator.js';

// CC-004: Supply Chain Module - G4 logistics tracking
import {
  calculateSupplyChain,
  calculateDailyConsumption,
  getSupplyStatusColor,
  getSupplyStatusText,
  formatG4Request,
  DOS_THRESHOLDS
} from './modules/supplyChainModule.js';

// Ground evacuation capacity model (docs/documentation/SPEC_EVAC_CAPACITY.md): speed by vehicle class and route surface, handling
// time, two 12 h crews per vehicle per day; replaces the flat 4 runs x 2 litter per ambulance.
import { groundGroupCapacity, vehicleClassFor } from './modules/evacuationCapacityModel.js';
import { resolvePsychiatricSupport } from '../constants/psychiatricSupport.js';
import { DNBI_HOLDING, DNBI_CATEGORIES,
  DNBI_RETURN_TO_DUTY, MARCH_DNBI_FACTOR
} from '../constants/dnbiRates.js';
import { getAmbulanceCalcSpec } from '../constants/ambulanceTypes.js';
import { MEDEVAC_OPERATIONAL_FACTORS, getPrimaryHelicopter } from '../constants/helicopterSpecs.js';
import { FACILITY_LEG, LEGACY_VEHICLE_PLACES } from '../constants/evacuationCapacityParameters.js';

// Wound-type hazard model (docs/planning/PLAN_WOUND_HAZARD_IMPL.md step 5): ON by default since 2026-10-10.
import { arrivalHoursFromSegments } from './modules/woundHazardModule.js';
import { createSupplyTracker } from './modules/hazardCareModule.js';
import { WOUND_HAZARD_MODES, resolveWoundHazardMode, FRT_BENEFIT_WITHOUT_BLOOD } from '../constants/woundHazardParameters.js';

// WTD-01: Wound-type distribution — anatomical breakdown + DOW refinement
import {
  getWoundDistribution,
  distributeCasualtiesByWound,
  blendWoundMixByUnitTypes,
  woundDistributionKeyFor
} from '../constants/woundTypeDistribution.js';

// 2026-10-11: body armor changes the wound mix (fewer thorax wounds)
import { applyArmorToWoundMix, DEFAULT_BODY_ARMOR_LEVEL } from '../constants/bodyArmorProtection.js';
import { REAR_AREA_DECAY_CONFIG, rearDistanceFactor } from '../constants/rearAreaDecay.js';
import { roundWithCarry } from './modules/fractionCarry.js';
import { brigadeEnemyRatio } from './modules/enemyForces.js';
import { DATE_SEASON_ON, seasonOfDay } from '../constants/exerciseDates.js';
import { BRIGADE_SUPPORT_UNITS_ON, splitBrigadeRemainder, anchoredPositionKm } from '../constants/brigadeSupportStructure.js';

// ENH-037: Aviation assets pool constraints — use pool_code → primary_aircraft lookup
// when aircraft_type is not explicitly set in tactical_evac_config.air_pool.
import {
  MEDEVAC_POOLS
} from '../constants/aviationAssets.js';

// CC-005: Strength Projection Module - G1 personnel tracking
import {
  calculateStrengthProjection,
  getEffectivenessRating,
  formatG1Request,
  STRENGTH_EFFECTIVENESS,
  REPLACEMENT_THRESHOLDS
} from './modules/strengthProjectionModule.js';

// CC-006: Time to Surgery Module - Golden Hour POIÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã‚Â ÃƒÂ¢Ã¢â€šÂ¬Ã¢â€žÂ¢Surgery
import {
  calculateTimeToSurgery,
  calculateAllTimesToSurgery,
  getGoldenHourSummary,
  recommendFSTAttachments,
  // Note: hasSurgicalCapability NOT imported - local version exists with different signature
  GOLDEN_HOUR_THRESHOLDS,
  SURGICAL_TEAMS
} from './modules/timeToSurgeryModule.js';

// CC-007: Method-Based Assets Module - PUSH/PULL/AUTO evacuation
import {
  calculateMethodBasedAssets,
  calculateAllFacilityAssets,
  getEchelonCapacitySummary,
  calculatePushDOWImpact,
  shouldAutoPush,
  EVACUATION_METHODS,
  AUTO_THRESHOLDS
} from './modules/methodBasedAssetsModule.js';

// ============================================================================
// CONSTANTS IMPORTS
// ============================================================================

// Import HQ casualties calculation from nationalStandards (proper version with constants)
import {
  calculateHQCasualties,
  NATIONAL_STANDARDS  // BUG-SUPPORT-001: brigade support_ratio lookup
} from '../constants/nationalStandards.js';

import {
  calculateTriageDistribution
} from '../constants/triageCategories.js';

import {
  evaluateMASCALStatus,
  MASCAL_TRIGGERS
} from '../constants/mascalThresholds.js';

// DC-002: Import terrain factors for distance-based evacuation time calculations
// Note: Using getTerrainFactor() function + fallback values instead of direct constants
import {
  getTerrainFactor,
  calculateGroundEvacuationTime,
  getTerrainOptions,
  getRouteOptions,
  TERRAIN_TYPES,
  ROUTE_TYPES
} from '../constants/terrainFactors.js';

// EA-003: Import asset allocation for per-facility capacity calculations
import {
  calculateEffectiveAssets,
  ROLE1_ORGANIC_ASSETS,
  ROLE2_BASIC_ORGANIC_ASSETS,
  ROLE2_ENHANCED_ORGANIC_ASSETS,
  ROLE3_ORGANIC_ASSETS,
  UNIT_TYPE_TO_ROLE1_PROFILE
} from '../constants/evacuationAssetAllocation.js';

// CC-007: Import CASEVAC constants from evacuationAssetFactors.js
// These replace the previously hardcoded CASEVAC_CONFIG values
// IMP-007: Also import getEvacuationFactorForBrigade for computeCompositeEvacFactor()
// Note: UNIT_TYPE_TO_EVAC_KEY is used internally inside getEvacuationFactorForBrigade — not needed here
import {
  CASEVAC_CONFIGURATION,
  CASEVAC_DOW_PENALTIES,
  AUTO_METHOD_THRESHOLDS,
  PUSH_DISTANCE_RATIOS,
  getEvacuationFactorForBrigade
} from '../constants/evacuationAssetFactors.js';

// PHASE 1.1: Import WBB constants from bloodProducts.js
import {
  WALKING_BLOOD_BANK,
  getBloodAlertLevel
} from '../constants/bloodProducts.js';

// Phase 6.1: getSurgeonPerformance — shift-hour fatigue modifier (SURGICAL_FATIGUE_MODEL).
// Used as additional multiplier on top of day-based calculateFatigueEffect().
import {
  getSurgeonPerformance,
  SURGICAL_TEAM_COMPOSITION
} from '../constants/surgicalCapacity.js';

// PHASE 4.1: Import PUSH_PACKAGES + DRONE_DELIVERY_SPECS from classVIII.js for resupply calculations
import {
  PUSH_PACKAGES,
  DRONE_DELIVERY_SPECS
} from '../constants/classVIII.js';

// v3.0 PHASE 3: Import UNIT_TYPES for sub-unit casualty calculations
import {
  UNIT_TYPES,
  BATTALION_TYPES,
  SUBUNIT_ONLY_TYPES,
  getTypicalPosition
} from '../constants/unitTypes.js';

// AMB-002: Import AMBULANCE_TYPES for category-based vehicle classification in getTacticalEvacConfig().
// Replaces substring key matching (TABC79/MLVM/SPRINTER/BOXER all fell through to wheeled_light fallback).
import {
  AMBULANCE_TYPES,
  getDefaultGroundFleet
} from '../constants/ambulanceTypes.js';

// PHASE 4.6: Import resupply transport modeling for route-based delivery simulation
import {
  calculateDeliveryTime,
  calculateDeliveryProbability,
  runMonteCarloResupplySimulation
} from '../services/ResupplyTransportService.js';

// P1-03 FIX: Import RTD_RATES from theatreHoldingPolicy for THP-aware WIA RTD calculation.
// Previous code used hardcoded 0.05/0.15/0.60/0.02 ignoring the configured THP.
// v5.8.0: Add HOSPITALIZATION_RATE, ECHELON_ADMISSION_DISTRIBUTION, AVG_LOS_BY_THP for
// MASCAL-WHEN-WHERE per-brigade R2B bed occupancy timeline calculation.
// v5.9.0: Add FRT_DOW_REDUCTION_PERCENT for Forward Resuscitation Team DOW modifier.
import {
  RTD_RATES as THP_RTD_RATES,
  HOSPITALIZATION_RATE,
  ECHELON_ADMISSION_DISTRIBUTION,
  AVG_LOS_BY_THP,
  FRT_DOW_REDUCTION_PERCENT
} from '../constants/theatreHoldingPolicy.js';

// ENH-003: STRATEVAC scheduling — departure days, PHA thresholds, backflow rules
import {
  STRATEVAC_AIRCRAFT,
  PHA_THRESHOLDS,
  PHA_CRITICAL_WAIT_HOURS,
  PHA_WARNING_WAIT_HOURS,
  BACKFLOW_RULES,
  computeDepartureDays,
  getPHAStatus
} from '../constants/stratevacScheduling.js';

// ENH-004: Train schedule analysis — constants and helpers
import {
  MEDICAL_TRAIN_SPECS,
  TRAIN_STATION_STANDARDS,
  TRAIN_QUEUE_THRESHOLDS,
  TRAIN_WAIT_PENALTY,
  computeTrainDepartureDays,
  getTrainCapacityPerDeparture
} from '../constants/trainEvacuation.js';

// ENH-012: Theater region scalar — geographic battle casualty rate adjustment.
// getTheaterScalar(region, season) returns 1.0 for null/unknown (backward compat).
import { getTheaterScalar } from '../constants/climateZones';

// ENH-023: Prolonged Field Care / PCC (Cat-23 Research Inventory Audit)
import { getPFCModifier } from '../constants/prolongedFieldCare.js';
import { derivePfcLevelCached, MEDIC_LOSS_REDUCES_PFC_ON, medicShareOf } from '../constants/pfcFromFacilities.js';
import { resolveClsLevel } from '../constants/pointOfInjuryCare.js';

// ENH-024: Damage Control Surgery (Cat-24 Research Inventory Audit)
// DCS_PHASES used for Phase 1 blood consumption and survival rates.
// calculateDCSBloodRequirements used to compute the blood supplement for T1
// surgical patients beyond what the mechanism-based baseline already captures.
// NOTE: FST DOW benefit is already captured via the time-to-surgery pathway
// (cumulativeGHAnalysis.dow_modifier). No new DOW modifier added — that would
// double-count. DCS contribution here is purely the blood consumption side.
import {
  DCS_PHASES,
  calculateDCSBloodRequirements
} from '../constants/damageControlSurgery.js';

// ENH-025: Drone Warfare Environment (Cat-25 Research Inventory Audit)
// Three axes wired: casualty modifier (raw casualties × drone level), DOW
// modifier (delayed evacuation under drone saturation), MEDEVAC attrition
// (weekly fleet losses compounding across the operation duration).
// DRONE_THREAT carries all three values per threat level from threatAssessment.js.
import { DRONE_THREAT, resolveDroneForDay } from '../constants/threatAssessment.js';
import {
  AIR_SITUATION_LEVELS
} from '../constants/airSituation.js';

// IMP-008: Cohesion modifier — FM 101-5-1 Gumball Chart (Dupuy TNDM supplement).
// Applied per-brigade per-day: as unit strength falls, per-capita casualty rate rises.
// 1.00× (GREEN, ≥85%) / 1.08× (AMBER, ≥70%) / 1.20× (RED, ≥50%) / 1.40× (BLACK, ≥30%).
// Units <30% exit via the combat-ineffective guard; modifier is never applied there.
import {
  getCohesionModifier,
  getCohesionBand,
  getDNBIRate,
  STRENGTH_COHESION_MODIFIERS
} from '../constants/operationalStates.js';

// HNS-001: Host Nation Support — DNBI absorption + R3 bed augmentation.
// getHNSModifiers() returns reliability-adjusted modifiers from scenario HNS config.
// Applied once per calculation: DNBI per-day per-brigade; R3 beds in MASCAL analysis.
import {
  getHNSModifiers
} from '../constants/hnsConfig.js';

// Rule 5.1: Icon constants from utils/icons.js — no bare emoji in source
import {
  STATUS_ICONS, TACTICAL_ICONS, GENERAL_ICONS, ARROW_ICONS
} from '../utils/icons.js';

// ENH-043: Per-brigade force quality resolver — walks Corps → Division → Brigade override chain.
// Replaces single-global force?.force_quality || {} at calculateBrigadeCasualties call site.
import {
  resolvePerBrigadeQuality,
  resolveClsQuality,
  TECHNOLOGY_ENVIRONMENT,
  EQUIPMENT_LEVELS
} from '../constants/combatEffectivenessModifiers';
import { EQUIPMENT_KILL_EFFECT_ON, killedShareFactor } from '../constants/equipmentKillEffect.js';

// GROUP-1-D: Gielerak bo factor constants for medical structure protection level.
// _boCapMod = MEDICAL_PROTECTION_BO_FACTOR_MIN + (bo/100) * (1 - MEDICAL_PROTECTION_BO_FACTOR_MIN)
// At bo=100%: _boCapMod=1.0 (no degradation). At bo=0%: _boCapMod=0.5 (50% degradation).
import {
  MEDICAL_PROTECTION_DEFAULT,
  MEDICAL_PROTECTION_BO_FACTOR_MIN,
  // CALC-001/002/011: Tab 2's key sets (OPEN/URBAN/FOREST/MOUNTAIN/DESERT/MIXED; CLEAR/RAIN/SNOW/...);
  // aliased because terrainFactors.js exports same-named constants with different keys.
  TERRAIN_TYPES as TAB2_TERRAIN_TYPES,           // .medical.evacuation_speed
  WEATHER_CONDITIONS as TAB2_WEATHER_CONDITIONS, // .evacuation_speed_modifier
  ENGAGEMENT_LEVELS as TAB2_ENGAGEMENT_LEVELS,   // FULL / SUPPORT / RESERVE / SECURITY
  OPERATION_TYPES as TAB2_OPERATION_TYPES        // GROUND_COMBAT, URBAN_COMBAT, ...
} from '../constants/operationsConfig';

// CALC-010: required-constant reader (throws with the path instead of a literal fallback)
import { requireNumber } from './requireValue.js';
import { t1HeldForBlood, t1HeldForSurgeon, combineHolds } from '../constants/dynamicTriage.js';
import { evacuationWindowFromParts, waitSpreadPoints, pfcEffectiveWait, pfcLevelOf, suggestParadigm } from './modules/evacuationWindowModel.js';
import { evacuationQueueDay } from './modules/evacuationQueueModel.js';
import { protectionCapacityFactor } from './modules/facilityProtection.js';
import { eligibilityLoad } from './modules/civilianLoad.js';
import { coldInjuryOfDay, coldIndexOfWeather, COLD_INJURY_RTD_SHARE } from './modules/coldInjuryModule.js';
import { heatInjuryOfDay, heatIndexOfWeather, HEAT_INJURY_RTD_SHARE } from './modules/heatInjuryModule.js';

// ENH-018-C: Echelon support troop casualty planning factors
// IMP-MARCH-001: MTF setup ramp planning factors (AJMedP-1 / ENH-006)
import {
  BASE_CASUALTY_RATE,
  SUPPORT_ECHELON_CASUALTY_MULTIPLIER,
  SUPPORT_ECHELON_SURGICAL_FRACTION,
  SUPPORT_ECHELON_CASUALTY_DISTRIBUTION,
  SUPPORT_ECHELON_BYPASSES_ROLE_2B,
  MTF_SETUP_RAMP_R2B,
  MTF_SETUP_RAMP_R2E,
  MTF_SETUP_TIMES_HOURS,  // Rule 3.2: imported and wired — validates ramp derivation
  MTF_SETUP_RAMP_R3,      // Rule 3.2: imported — reserved for ENH-006 Phase 0 R3 displacement
  calculateDOWRate        // NATO planning-norm DOW rate, reported as a reference next to the model's DOW
} from '../constants/casualtyCalculatorConstants.js';

// V4-013: Sub-phase tactical differentiation
import { getMissionCasualtyMultiplier, getSubPhaseCombinedMultiplier } from '../constants/missions.js';


// ============================================================================
// HELPER FUNCTIONS (Added for fixes FIX-004, CC-010, CC-013)
// ============================================================================

// ============================================================================
// V4-013: SUB-PHASE TACTICAL DIFFERENTIATION CONSTANTS + HELPERS
// ============================================================================

/**
 * V4-013: Per-day tactical mission casualty multipliers for sub-phases.
 * Applied STACKED on top of brigade role multiplier (MAIN_EFFORT/RESERVE/etc. from Tab 1).
 * The brigade role multiplier = brigade's operational role within the division.
 * The sub-phase multiplier = brigade's tactical mission on a specific day.
 * These are orthogonal — stacking is correct per Dupuy TNDM methodology.
 *
 * Values from missions.js MISSIONS.casualty_multiplier — referenced here as constants
 * for calculator-internal use. getMissionCasualtyMultiplier() is the authoritative source.
 * Source: AJP-4.10 Table 4-3; CREstT urban/river historical ranges.
 */
const _SUB_PHASE_TRANSITION_SURGE_TABLE = {
  // Source: doctrinal inference — rapid tactical reconfiguration creates friction/surprise.
  // Lighter than full phase posture transition (DEFENSE->OFFENSE = 1.50x).
  'MAIN_EFFORT->URBAN':          1.25,  // Transition to city CQB: high confusion risk
  'SUPPORTING->URBAN':           1.25,
  'MAIN_EFFORT->RIVER_CROSSING': 1.20,  // Transition to crossing: concentrated, exposed
  'SUPPORTING->RIVER_CROSSING':  1.20,
  'RIVER_CROSSING->MAIN_EFFORT': 1.10,  // Post-crossing consolidation friction
  'RIVER_CROSSING->URBAN':       1.15,  // Double obstacle
  'URBAN->MAIN_EFFORT':          1.10,  // Exiting urban: reorganization cost
  'URBAN->RIVER_CROSSING':       1.15,
  'DEFAULT':                     1.08,  // Any other intra-phase tactical transition
};

/**
 * Returns the transition surge multiplier for a day 1 sub-phase change.
 * @param {string} fromMission - Previous sub-phase mission_type (MISSIONS value)
 * @param {string} toMission   - Current sub-phase mission_type (MISSIONS value)
 * @returns {number} Surge multiplier (>= 1.0)
 */
function _getSubPhaseTransitionSurge(fromMission, toMission) {
  if (!fromMission || fromMission === toMission) return 1.0;
  const key = `${fromMission}->${toMission}`;
  return _SUB_PHASE_TRANSITION_SURGE_TABLE[key] ?? _SUB_PHASE_TRANSITION_SURGE_TABLE.DEFAULT;
}

/**
 * V4-013: Returns the active sub-phase object for the given 1-indexed dayInPhase.
 * Returns null if sub_phases absent, empty, or if their sum does not match phaseDuration
 * (sum mismatch = data integrity error — calculator ignores sub_phases, uses 1.0 baseline).
 * @param {Array}  subPhases     - phase.sub_phases array
 * @param {number} dayInPhase    - 1-indexed day within the phase
 * @param {number} phaseDuration - parent phase duration_days
 * @returns {Object|null} sub-phase object or null
 */
function _getSubPhaseForDay(subPhases, dayInPhase, phaseDuration) {
  if (!subPhases || subPhases.length === 0) return null;
  const totalSP = subPhases.reduce((s, sp) => s + (sp.duration_days || 0), 0);
  if (totalSP === 0) return null;
  // BUG-S102-025 FIX: Replace strict equality guard with proportional scaling.
  // The previous guard `totalSP !== phaseDuration → return null` silently discarded
  // all sub-phases whenever a planner added a sub-phase row without adjusting durations
  // to match (addSubPhase seeds 1 day, pushing the total 1 over phaseDuration).
  // Fix: map dayInPhase (1..phaseDuration) proportionally into the sub-phase coordinate
  // space (1..totalSP) so every day resolves to a sub-phase regardless of whether the
  // durations sum exactly. When totalSP === phaseDuration the scaled day equals dayInPhase
  // and behaviour is identical to the original implementation.
  const scaledDay = (dayInPhase / phaseDuration) * totalSP;
  let cursor = 0;
  for (const sp of subPhases) {
    cursor += (sp.duration_days || 0);
    if (scaledDay <= cursor) return sp;
  }
  return subPhases[subPhases.length - 1];  // floating-point guard at final day
}

// ============================================================================
// v3.0 PHASE 3: SUB-UNIT CASUALTY CALCULATION (NO FALLBACKS)
// ============================================================================

/**
 * v5.1.3: Ensure brigade has sub_units array.
 *
 * Scenario: Brigade was saved before ForceService Phase 2 was deployed.
 * The brigade object in force.forceBuilderResult.brigades lacks sub_units.
 *
 * Strategy (priority order):
 *  1. Brigade already has sub_units => use as-is (normal v3.0 path)
 *  2. UNIT_TYPES[unitType].doctrinal_composition.brigade exists => generate
 *     proper doctrinal sub_units (mirrors ForceService.generateSubUnitsFromDoctrine)
 *  3. No doctrinal data => synthetic single-unit (mathematically identical to v5.0.0)
 *
 * @param {Object} brigade - Brigade object (may or may not have sub_units)
 * @returns {Object} Brigade with guaranteed sub_units array
 */
function ensureBrigadeSubUnits(brigade) {
  // Path 1: Already has sub_units - no change
  if (brigade.sub_units && Array.isArray(brigade.sub_units) && brigade.sub_units.length > 0) {
    return brigade;
  }

  const brigadeStrength = resolveInput(brigade.strength || brigade.calculated_strength, 'brigade_strength', { zeroIsMissing: true });
  const brigadeType    = normalizeUnitTypeKey(brigade.unit_type) || 'MECHANIZED';
  const brigadeName    = brigade.name || brigade.brigade_name || 'Brigade';
  const brigadeId      = brigade.id || brigade.brigade_id || 'unknown';

  // Path 2: Generate doctrinal sub_units from UNIT_TYPES (mirrors ForceService.generateSubUnitsFromDoctrine)
  try {
    const unitTypeDef = UNIT_TYPES[brigadeType];
    const docComp     = unitTypeDef?.doctrinal_composition?.brigade;

    if (docComp && Array.isArray(docComp.sub_units) && docComp.sub_units.length > 0) {
      const generated = [];
      let letterIndex = 0;
      const letters   = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

      for (const template of docComp.sub_units) {
        if (!template.unit_type || !template.count || !template.strength_each || !template.name_pattern) {
          continue; // Skip malformed template - don't throw
        }
        for (let i = 1; i <= template.count; i++) {
          let subName = template.name_pattern
            .replace('{parent}', brigadeName)
            .replace('{index}', i.toString());
          if (subName.includes('{letter}')) {
            subName = subName.replace('{letter}', letters[letterIndex % 26]);
            letterIndex++;
          }
          generated.push({
            id: `${template.type || 'sub'}_gen_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
            type: template.type || template.unit_type,
            unit_type: template.unit_type,
            strength: template.strength_each,
            name: subName,
            parent_id: brigadeId,
            notes: template.notes || '',
            composition_source: 'doctrinal_runtime'  // Distinguish from ForceService-generated
          });
        }
      }

      if (generated.length > 0) {
        return { ...brigade, sub_units: generated, sub_units_synthetic: false, composition_source: 'doctrinal_runtime' };
      }
    }
  } catch (docError) {
  }

  // Path 3: Synthetic single-unit fallback (identical math to v5.0.0 calculator)
  return {
    ...brigade,
    sub_units: [{
      id: `synthetic_${brigadeId}`,
      type: 'BATTALION',
      unit_type: brigadeType,
      strength: brigadeStrength,
      name: brigadeName,
      parent_id: brigadeId,
      notes: 'Synthetic - no doctrinal composition available',
      composition_source: 'synthetic'
    }],
    sub_units_synthetic: true
  };
}

/**
 * OBS-S96-006: Compute effective R1 ground ambulance + helicopter fleet from ORBAT.
 * = ORBAT organic (sub_units × ROLE1_ORGANIC_ASSETS) + attached − detached at R1 facilities.
 * Replaces the hardcoded || 4 fallback in calculateMethodAdjustedCapacity.
 *
 * @param {Array} brigadeConfigs - Brigade array from force structure
 * @param {Array} perFacilityAssets - medicalConfig.per_facility_evacuation_assets
 * @returns {{ total: { wheeled, armored, helo, all }, by_nation: {} }}
 */
function computeEffectiveR1Fleet(brigadeConfigs, perFacilityAssets) {
  const byNation = {};
  let totalWheeled = 0, totalArmored = 0, totalHelo = 0;

  const r1Facilities = (perFacilityAssets || [])
    .filter(f => f.facility_type === 'ROLE1' || f.facility_type === 'ROLE_1');

  for (const brigade of (brigadeConfigs || [])) {
    const nation = brigade.national_standard || brigade.nationalStandard || 'UNKNOWN';
    if (!byNation[nation]) byNation[nation] = { wheeled: 0, armored: 0, helo: 0 };

    // ORBAT organic — each sub_unit maps to a ROLE1_ORGANIC_ASSETS profile
    const b = ensureBrigadeSubUnits(brigade);
    let orgW = 0, orgA = 0;
    for (const su of (b.sub_units || [])) {
      const profile = lookupByUnitType(UNIT_TYPE_TO_ROLE1_PROFILE, su.unit_type, 'UNIT_TYPE_TO_ROLE1_PROFILE');
      const assets  = ROLE1_ORGANIC_ASSETS[profile];
      if (!assets) continue;
      orgW += assets.ground_ambulances?.wheeled?.count || 0;
      orgA += assets.ground_ambulances?.armored?.count || 0;
    }

    // Attached / detached at any R1 facility allocated to this brigade
    const brigadeId = brigade.id || brigade.brigade_id;
    let attW = 0, attA = 0, attH = 0, detW = 0, detA = 0, detH = 0;
    r1Facilities
      .filter(f => f.allocated_to?.unit_id === brigadeId)
      .forEach(f => {
        (f.attached_assets || []).forEach(a => {
          attW += a.ground_ambulances?.wheeled?.count || 0;
          attA += a.ground_ambulances?.armored?.count || 0;
          attH += a.helicopters?.count || 0;
        });
        (f.detached_assets || []).forEach(d => {
          detW += d.ground_ambulances?.wheeled?.count || 0;
          detA += d.ground_ambulances?.armored?.count || 0;
          detH += d.helicopters?.count || 0;
        });
      });

    const w = orgW + attW - detW;
    const a = orgA + attA - detA;
    const h = Math.max(0, attH - detH);

    byNation[nation].wheeled += w;
    byNation[nation].armored += a;
    byNation[nation].helo    += h;
    totalWheeled += w;
    totalArmored += a;
    totalHelo    += h;
  }

  return {
    total: { wheeled: totalWheeled, armored: totalArmored, helo: totalHelo,
             all: totalWheeled + totalArmored },
    by_nation: byNation,
  };
}

/**
 * v3.0 Phase 3: Validate brigade has required sub_units data
 * NOTE (v5.1.3): validateBrigadeSubUnits is now defensive only.
 * ensureBrigadeSubUnits() is called FIRST to guarantee sub_units exist.
 * 
 * @param {Object} brigade - Brigade object to validate
 */
function validateBrigadeSubUnits(brigade) {
  const brigadeName = brigade.name || brigade.brigade_name || 'Unknown';
  
  // v5.1.3: Missing sub_units is handled by ensureBrigadeSubUnits() called BEFORE this.
  // This block is a defensive fallback only - should not normally be reached.
  if (!brigade.sub_units || !Array.isArray(brigade.sub_units)) {
    return; // Let ensureBrigadeSubUnits handle this upstream
  }
  
  if (brigade.sub_units.length === 0) {
    return;
  }
  
  // Validate each sub-unit has required fields
  for (let i = 0; i < brigade.sub_units.length; i++) {
    const subUnit = brigade.sub_units[i];
    
    if (!subUnit.unit_type) {
      throw new Error(
        `[v3.0 CALCULATOR] CRITICAL: Brigade "${brigadeName}" sub-unit ${i} ` +
        `(${subUnit.name || 'unnamed'}) missing unit_type field`
      );
    }
    
    if (!subUnit.strength || subUnit.strength < 1) {
      throw new Error(
        `[v3.0 CALCULATOR] CRITICAL: Brigade "${brigadeName}" sub-unit ${i} ` +
        `(${subUnit.name || 'unnamed'}) missing or invalid strength field (got: ${subUnit.strength})`
      );
    }
  }
  
}

// ============================================================================
// 2026-10-11: engagement level and operation type reach the battle casualty rate
// ============================================================================
// Tab 2 creates a per-brigade `operation` object holding the placeholder engagement 'PREPARED_ENGAGEMENT' (not a key of
// ENGAGEMENT_LEVELS) and the scenario-level selectors (operation_parameters.engagement_level / operation_type) were
// ignored once that object existed. The scenario-level choice now applies; a valid brigade-level key still wins.
const LEGACY_BRIGADE_ENGAGEMENT_PLACEHOLDERS = ['PREPARED_ENGAGEMENT'];

function resolveEngagementLevel(brigade, scenario) {
  const own = brigade?.operation?.engagement_level;
  if (own !== undefined && own !== null) {
    if (TAB2_ENGAGEMENT_LEVELS[own]) return own;
    if (!LEGACY_BRIGADE_ENGAGEMENT_PLACEHOLDERS.includes(own)) {
      throw new Error(`calculateBrigadeCasualties: unknown engagement level '${own}' on brigade '${brigade?.name || brigade?.id}' (valid: ${Object.keys(TAB2_ENGAGEMENT_LEVELS).join(', ')})`);
    }
  }
  const level = resolveInput(scenario?.operation_parameters?.engagement_level, 'engagement_level');
  if (!TAB2_ENGAGEMENT_LEVELS[level]) {
    throw new Error(`calculateBrigadeCasualties: unknown engagement level '${level}' in operation_parameters (valid: ${Object.keys(TAB2_ENGAGEMENT_LEVELS).join(', ')})`);
  }
  return level;
}

function resolveOperationType(scenario) {
  const type = resolveInput(scenario?.operation_parameters?.operation_type, 'operation_type');
  if (!TAB2_OPERATION_TYPES[type]) {
    throw new Error(`calculateBrigadeCasualties: unknown operation type '${type}' in operation_parameters (valid: ${Object.keys(TAB2_OPERATION_TYPES).join(', ')})`);
  }
  return type;
}

// 2026-10-13 (Tab 2 plan, step 6): ONE technology environment. 2026-10-25 (Dr. Tatar): TAB 2 OWNS IT (it is the drone and electronic-warfare environment
// of the operation): the operation default (default_technology_env_user_set) and the brigade choice (environment.technology_env_override). The
// force quality value of Tab 1 (DEFAULT_FORCE_QUALITY, DRONE_CONTESTED; the Tab 1 field is gone) is only the failsafe and the value of older saves. The Tab 2 choices were saved and never read. An explicit Tab 2 choice wins; the brigade
// choice wins over the operation default.
function applyTab2TechnologyEnvironment(quality, brigadeConfig, operationParameters) {
  const corpsConfig = operationParameters?.corps_config;
  let env = quality?.technology_env;
  if (corpsConfig?.default_technology_env_user_set === true && corpsConfig.default_technology_env) env = corpsConfig.default_technology_env;
  if (brigadeConfig?.environment?.technology_env_override === true && brigadeConfig.environment.technology_env) env = brigadeConfig.environment.technology_env;
  return env === quality?.technology_env ? quality : { ...quality, technology_env: env };
}

// 2026-10-13 (Tab 2 plan, steps 2 and 3): enemy resistance of a phase, as the enemy combat power index. Order:
//   1. the phase's own choice (LIGHT / MODERATE / HEAVY or a number);
//   2. the enemy assessment of the scenario (Tab 2: enemy to friendly strength ratio, training, equipment, morale), with the
//      strength ratio of the phase when it has its own (the enemy weakens or is reinforced);
//   3. the scenario-level choice `resistance_level`;
//   4. the planning default MODERATE (reported to the assumptions ledger).
function resolveResistanceIndex(phase, scenario, ctx = null) {
  const own = phase?.resistance;
  if (own !== undefined && own !== null && own !== '' && own !== 'AUTO') return resistanceIndexOf(own);
  const params = scenario?.operation_parameters;
  const quality = params?.enemy_force_quality;
  let ratio = phase?.enemy_strength_ratio ?? params?.enemy_strength_ratio;
  // 2026-10-22: enemy forces by sector and reinforcements by day (modules/enemyForces.js). Only the RATIO changes (the quality stays in the enemy
  // card): base = phase ratio, else the sector forces, else the card ratio; reinforcements are added on their day. A resistance typed in the
  // phase returned above and is never changed by them.
  if (ctx && params?.enemy_forces) {
    const fromForces = brigadeEnemyRatio({
      phaseRatio: phase?.enemy_strength_ratio,
      cardRatio: params.enemy_strength_ratio,
      forces: params.enemy_forces,
      brigadeId: ctx.brigadeId,
      day: ctx.day,
      friendlyStrength: ctx.friendlyStrength
    });
    if (fromForces.ratio !== undefined) ratio = fromForces.ratio;
  }
  const hasAssessment = ratio !== undefined || (quality && Object.keys(quality).length > 0);
  if (hasAssessment) return enemyPowerIndex({ ...(quality || {}), strength_ratio: ratio }).index;
  return resistanceIndexOf(resolveInput(params?.resistance_level, 'resistance_level'));
}

/**
 * v3.0 Phase 3: Get casualty modifier for a unit type
 * NO FALLBACKS: Throws error if unit type unknown
 * 
 * @param {string} unitType - Unit type (e.g., 'MECHANIZED', 'ARMOR')
 * @returns {number} Casualty modifier (e.g., 1.0, 0.85, 0.6)
 * @throws {Error} If unit type not found in UNIT_TYPES
 */
function getCasualtyModifier(unitType) {
  if (!unitType) {
    throw new Error(`[v3.0 CALCULATOR] getCasualtyModifier: unitType is required (got: ${unitType})`);
  }
  
  // 2026-10-11: sub-units are battalions, and Tab 1's battalion list shows the BATTALION_TYPES modifiers. The
  // brigade-level UNIT_TYPES table was read instead (7 types differed, 4 support battalion types crashed). The
  // battalion table is the source; UNIT_TYPES stays as the second lookup for keys that only exist there (HEAVY, STRYKER, HQ ...).
  const unitTypeDefinition = BATTALION_TYPES[unitType] || UNIT_TYPES[unitType] || SUBUNIT_ONLY_TYPES[unitType];
  if (!unitTypeDefinition) {
    throw new Error(
      `[v3.0 CALCULATOR] getCasualtyModifier: Unknown unit type "${unitType}". ` +
      `Valid battalion types: ${Object.keys(BATTALION_TYPES).join(', ')}.`
    );
  }
  
  const modifier = unitTypeDefinition.casualty_modifier;
  if (modifier === undefined || modifier === null) {
    throw new Error(
      `[v3.0 CALCULATOR] getCasualtyModifier: Unit type "${unitType}" ` +
      `missing casualty_modifier field in its unit type definition`
    );
  }
  
  return modifier;
}

// ============================================================================
// 2026-10-11: THE BATTALIONS OF TAB 1 DRIVE THE CASUALTY CALCULATION; SUPPORT BATTALIONS ARE REAR-AREA CASUALTIES
// ============================================================================
// Before: the calculator took its sub-units from the doctrinal template (brigade.sub_units, 8 units for a mechanized
// brigade) and ignored the battalions built in Tab 1 (type, strength, position). Now the battalions are the sub-units and
// the personnel not in any battalion (brigade HQ and support) form one rear unit.
// Frontline battalions (has_frontage): strength x rate x unit modifier, as before.
// Rear battalions (support, CSS, HQ): strength x rate x unit modifier x distance curve of their own position x technology
// rear factor (rear_area_multiplier replaces the general drone casualty multiplier, so drones count once) x national
// rear-area security modifier. Rear casualties stay in the casualty total (they need care) and are reported as rear area.
// The strengths follow the depletion of the force: Dr. Tatar, day 2 starts from the soldiers left, not from the initial
// strength (before, the sub-unit step used the initial strengths, so a depleted brigade kept losing as if at full strength).
const SUBUNIT_STRENGTH_FOLLOWS_DEPLETION = true;

// 2026-10-21 (Dr. Tatar): carry the fraction of a day's casualties to the next day. Daily results are small whole numbers (about
// 17 a day for a brigade of 5000): rounding 17.4 to 17 every day loses 2 percent, rounding a 0.4 of a small unit to 0 loses it
// all, and over a division or a corps the losses add up. The rate behind the rounded total is used, the front and rear sums
// and the killed share carry what they lose to the next day. false restores the earlier daily rounding.
export const CASUALTY_FRACTION_CARRY_ON = true;

// 2026-10-23 (research-track check): the weather of the day never reached the base casualty rate. The battle module reads a top-level
// `weather` (default 'FAIR', not a key of WEATHER_CONDITIONS, so the factor was always 1.0) while the calculator passed it only inside
// brigade.environment. true passes the weather of the day (clear 1.0, rain 1.05, snow 1.15, extreme cold 1.25, extreme heat 1.2, fog 1.1,
// adverse 1.3, rasputitsa 1.0). The threat level is NOT passed (Dr. Tatar, option 2: enemy resistance in the phase table already
// represents it; threat and the modern-warfare factor stay at 1.0). false restores the earlier numbers.
export const BASE_RATE_WEATHER_ON = true;

// 2026-10-24 (R1-10, Dr. Tatar): engineer battalions take a share of the infantry rate by task (constants/engineerExposure.js: 0.10
// ordinary and defense, 0.20 attack phases, 0.50 first day of an assault condition) instead of the flat modifier 0.8. EDUCATED_GUESS.
// true since 2026-10-24 (approved by Dr. Tatar); false restores the flat battalion modifier.
export const ENGINEER_TASK_EXPOSURE_ON = true;

/**
 * R2-09 (2026-10-22): the season of an operation day. With a start date (operation_parameters.operation_start_date, Zulu start time) the
 * season is that of the calendar date of the day, so an operation that starts on 28 November is fall on day 1 and winter on day 5;
 * without a date the planner's selector (climate_config.season) applies for the whole run, as before.
 */
export function seasonForDay(scenario, day, selectorSeason) {
  const op = scenario?.operation_parameters;
  if (DATE_SEASON_ON && op?.operation_start_date) {
    const fromDate = seasonOfDay(op.operation_start_date, op.operation_start_time_z, day, op.climate_config?.hemisphere || 'NORTH');
    if (fromDate) return fromDate;
  }
  return selectorSeason;
}

/**
 * Multiply the killed and wounded of the day by a modifier of the chain (cohesion, force ratio, theater, drones, air, Monte
 * Carlo, sub-phase, division role). With the carry on, the unrounded numbers (battleResult._unrounded) are scaled and the
 * integers are their rounding, so rounding does not compound step by step; off, the integers are scaled and rounded as before.
 */
function scaleBattleCasualties(battleResult, modifier, killedModifier = modifier) {
  const bc = battleResult.battle_casualties;
  const u = battleResult._unrounded;
  if (CASUALTY_FRACTION_CARRY_ON && u) {
    u.kia *= killedModifier;
    u.wia *= modifier;
    bc.kia = Math.max(0, Math.round(u.kia));
    bc.wia = Math.max(0, Math.round(u.wia));
  } else {
    bc.kia = Math.max(0, Math.round(bc.kia * killedModifier));
    bc.wia = Math.max(0, Math.round(bc.wia * modifier));
  }
  bc.total = bc.kia + bc.wia;
}

/** Move the killed share of the day (same total) to a new share; works on the unrounded copy when there is one. */
function setKilledShare(battleResult, share) {
  const bc = battleResult.battle_casualties;
  const u = battleResult._unrounded;
  if (CASUALTY_FRACTION_CARRY_ON && u) {
    const total = u.kia + u.wia;
    u.kia = total * share;
    u.wia = total - u.kia;
    bc.kia = Math.round(u.kia);
    bc.wia = Math.max(0, Math.round(u.wia));
  } else {
    const total = bc.kia + bc.wia;
    bc.kia = Math.round(total * share);
    bc.wia = Math.max(0, total - bc.kia);
  }
  bc.total = bc.kia + bc.wia;
}

// 2026-10-11 (Dr. Tatar): soldiers in treatment are out of the fight and come back after their treatment time. Before, the
// wounded who return to duty were subtracted the SAME day (instant return) and the non-battle cases never returned. The
// return pool schedules each group's return: wounded after the median days of their triage category (limited to the theatre
// holding policy), non-battle cases after DNBI_RETURN_TO_DUTY.delay_days. false restores the earlier behaviour.
const OUT_OF_ACTION_POOL = true;

// Unit type of the personnel who are not in any battalion (brigade HQ, sustainment, signal ...): mostly sustainment, so
// the sustainment modifier (0.3) is used, not the HQ one (0.5). EDUCATED GUESS (docs/MASTER_TODO.md 2026-10-11).
const BRIGADE_REMAINDER_UNIT_TYPE = 'LOGISTICS';   // 2026-10-25: SUSTAINMENT is an alias of LOGISTICS

function subUnitTypeDefinition(unitType) {
  return BATTALION_TYPES[unitType] || UNIT_TYPES[unitType] || SUBUNIT_ONLY_TYPES[unitType];
}

/** True when the sub-unit is a rear unit (no frontage). */
function isRearSubUnit(subUnit) {
  const def = subUnitTypeDefinition(subUnit.unit_type);
  if (!def) {
    throw new Error(`[v3.0 CALCULATOR] isRearSubUnit: Unknown unit type "${subUnit.unit_type}"`);
  }
  return def.has_frontage === false || (def.has_frontage === undefined && def.category !== 'COMBAT');
}

/**
 * The sub-units used by the casualty calculation: the Tab 1 battalions plus one rear unit for the personnel not in a
 * battalion; the doctrinal sub_units when the brigade carries no battalions.
 */
function resolveCasualtySubUnits(brigade) {
  const battalions = brigade.battalions;
  if (!Array.isArray(battalions) || battalions.length === 0) {
    return { subUnits: brigade.sub_units, source: 'SUB_UNITS' };
  }
  const subUnits = battalions.map((bn) => {
    const unitType = normalizeUnitTypeKey(bn.unit_type || bn.type);
    if (typeof bn.strength !== 'number' || Number.isNaN(bn.strength) || bn.strength < 0) {
      throw new Error(`[v3.0 CALCULATOR] battalion '${bn.name || bn.id}' has no valid strength (got: ${bn.strength})`);
    }
    // a card anchored to its brigade (HQ, signal, support) stands where the brigade stands today (2026-10-25: the brigade position moved no number)
    const anchored = anchoredPositionKm(bn.position_anchor, brigade.position_feba_km);
    return { id: bn.id, name: bn.name, type: 'BATTALION', unit_type: unitType, strength: bn.strength, position_feba_km: anchored === null ? bn.position_feba_km : anchored };
  });
  const battalionStrength = subUnits.reduce((sum, su) => sum + su.strength, 0);
  const brigadeStrength = brigade.strength || brigade.calculated_strength || battalionStrength;
  if (brigadeStrength > battalionStrength) {
    const _supportUnits = BRIGADE_SUPPORT_UNITS_ON
      ? splitBrigadeRemainder({ brigadeType: normalizeUnitTypeKey(brigade.unit_type || brigade.brigade_type), brigadeId: brigade.id, remainder: brigadeStrength - battalionStrength, brigadePositionKm: brigade.position_feba_km, nationalStandard: brigade.nationalStandard || brigade.national_standard, hasReconBattalion: battalions.some((bn) => (bn.unit_type || bn.type) === 'RECONNAISSANCE') })
      : null;
    if (_supportUnits) {
      subUnits.push(..._supportUnits);
      return { subUnits, source: 'BATTALIONS_AND_SUPPORT_UNITS' };
    }
    subUnits.push({
      id: `${brigade.id || 'brigade'}_hq_support`,
      name: 'Brigade HQ and support',
      type: 'BATTALION',
      unit_type: BRIGADE_REMAINDER_UNIT_TYPE,
      strength: brigadeStrength - battalionStrength,
      position_feba_km: brigade.position_feba_km
    });
  }
  return { subUnits, source: 'BATTALIONS' };
}

/**
 * v3.0 Phase 3: Calculate casualties using sub-unit composition
 * Iterates through each sub-unit and applies its specific casualty modifier (see the 2026-10-11 note above).
 *
 * @param {Object} config - Calculation configuration
 * @param {Object} config.brigade - Brigade with battalions (Tab 1) or sub_units
 * @param {number} config.baseCasualtyRate - Casualty rate of the day (per person)
 * @param {number} config.additionalModifiers - Product of all other modifiers
 * @param {number} [config.currentStrength] - Strength of the brigade today (sub-unit strengths are scaled to it)
 * @param {number} [config.technologyRearFactor=1] - rear_area_multiplier / casualty_multiplier of the technology environment
 * @param {number} [config.nationalRearModifier=1] - rear_area_security_modifier of the national standard
 * @param {number} [config.threatRearFactor=1] - artillery threat x insurgent activity (threatEffects.rearThreatFactor)
 * @returns {Object} Casualty breakdown with sub-unit details
 */
function calculateSubUnitCasualties(config) {
  // v5.1.3: Ensure sub_units exist before validation (handles old scenario data)
  const brigade = ensureBrigadeSubUnits(config.brigade);
  const {
    baseCasualtyRate, additionalModifiers, currentStrength,
    technologyRearFactor = 1.0, nationalRearModifier = 1.0, threatRearFactor = 1.0, carry = null, engineerModifier = null
  } = config;

  // Validate brigade has sub_units (defensive - ensureBrigadeSubUnits handles the real work)
  validateBrigadeSubUnits(brigade);

  const { subUnits, source } = resolveCasualtySubUnits(brigade);
  const initialStrength = subUnits.reduce((sum, su) => sum + su.strength, 0);
  const strengthScale = (SUBUNIT_STRENGTH_FOLLOWS_DEPLETION && typeof currentStrength === 'number' && currentStrength > 0 && initialStrength > 0)
    ? currentStrength / initialStrength
    : 1;

  const subUnitResults = [];
  let frontCasualties = 0;
  let rearCasualties = 0;

  for (const subUnit of subUnits) {
    const casualtyModifier = (engineerModifier !== null && subUnit.unit_type === 'ENGINEERS')
      ? engineerModifier
      : getCasualtyModifier(subUnit.unit_type);
    const strength = subUnit.strength * strengthScale;
    const rear = isRearSubUnit(subUnit);

    let rearFactor = 1;
    let distanceKm = null;
    let distanceFactor = 1;
    if (rear) {
      distanceKm = typeof subUnit.position_feba_km === 'number' ? subUnit.position_feba_km : getTypicalPosition(subUnit.unit_type);
      if (typeof distanceKm !== 'number' || Number.isNaN(distanceKm)) {
        throw new Error(`[v3.0 CALCULATOR] rear unit '${subUnit.name || subUnit.id}' (${subUnit.unit_type}) has no position from the front line`);
      }
      distanceFactor = rearDistanceFactor(distanceKm);
      // 2026-10-13 (Tab 2 plan 7.5): artillery threat x insurgent activity act on rear units, not on the front (resistance carries the front)
      rearFactor = distanceFactor * technologyRearFactor * nationalRearModifier * threatRearFactor;
    }

    // Formula: strength x base_rate x unit_modifier x other_modifiers (x rear factor for rear units)
    // 2026-10-21: the casualties of a sub-unit are kept unrounded; the front and rear sums are rounded once (rounding each small
    // rear unit separately lost the fractions and made the total depend on how many units a brigade is split into)
    const subUnitCasualties = strength * baseCasualtyRate * casualtyModifier * additionalModifiers * rearFactor;

    subUnitResults.push({
      name: subUnit.name,
      unit_type: subUnit.unit_type,
      strength: Math.round(strength),
      casualty_modifier: casualtyModifier,
      is_rear: rear,
      distance_km: distanceKm,
      distance_factor: Math.round(distanceFactor * 1000) / 1000,
      rear_factor: Math.round(rearFactor * 1000) / 1000,
      casualties: Math.round(subUnitCasualties * 100) / 100
    });

    if (rear) rearCasualties += subUnitCasualties; else frontCasualties += subUnitCasualties;
  }
  if (carry) {
    const frontToday = frontCasualties + carry.front;
    const rearToday = rearCasualties + carry.rear;
    frontCasualties = Math.round(frontToday);
    rearCasualties = Math.round(rearToday);
    carry.front = frontToday - frontCasualties;
    carry.rear = rearToday - rearCasualties;
  } else {
    frontCasualties = Math.round(frontCasualties);
    rearCasualties = Math.round(rearCasualties);
  }

  return {
    total_casualties: frontCasualties + rearCasualties,
    front_casualties: frontCasualties,
    rear_casualties: rearCasualties,
    sub_unit_breakdown: subUnitResults,
    sub_unit_source: source,
    calculation_method: 'sub_unit_composition',
    sub_units_count: subUnits.length
  };
}

// ============================================================================
// DC-002: TERRAIN-AWARE EVACUATION TIME CALCULATION
// ============================================================================

/**
 * DC-002: Validate terrain type against available options
 * Uses imported getTerrainOptions() from terrainFactors.js
 * Falls back to TERRAIN_TYPES constant or getTerrainFactor() if function not available
 * 
 * @param {string} terrainType - Terrain type to validate
 * @returns {string} Valid terrain type (falls back to ROLLING_HILLS if invalid)
 */
function validateTerrainType(terrainType) {
  // Try using helper function first
  if (typeof getTerrainOptions === 'function') {
    const validOptions = getTerrainOptions();
    const validKeys = validOptions.map(opt => opt.value);
    
    if (validKeys.includes(terrainType)) {
      return terrainType;
    }
  }
  
  // Fallback: Check directly against TERRAIN_TYPES constant
  if (TERRAIN_TYPES && TERRAIN_TYPES[terrainType]) {
    return terrainType;
  }
  
  // Fallback: Use getTerrainFactor which returns default if invalid
  if (typeof getTerrainFactor === 'function') {
    const terrainConfig = getTerrainFactor(terrainType);
    // getTerrainFactor returns PLAINS if invalid, so check if we got what we asked for
    if (terrainConfig && terrainConfig.value === terrainType) {
      return terrainType;
    }
  }
  
  return 'ROLLING_HILLS';
}

/**
 * DC-002: Validate route type against available options
 * Uses imported getRouteOptions() from terrainFactors.js
 * Falls back to ROUTE_TYPES constant if function not available
 * 
 * @param {string} routeType - Route type to validate
 * @returns {string} Valid route type (falls back to PAVED_SECONDARY if invalid)
 */
function validateRouteType(routeType) {
  // Try using helper function first
  if (typeof getRouteOptions === 'function') {
    const validOptions = getRouteOptions();
    const validKeys = validOptions.map(opt => opt.value);
    
    if (validKeys.includes(routeType)) {
      return routeType;
    }
  }
  
  // Fallback: Check directly against ROUTE_TYPES constant
  if (ROUTE_TYPES && ROUTE_TYPES[routeType]) {
    return routeType;
  }
  
  return 'PAVED_SECONDARY';
}

/**
 * BUG-047 FIX: Normalize strength value
 * Tab 1/Tab 2 may save strength as object {value: 4500} instead of number 4500
 * This causes NaN in calculations: {value: 4500} - 0 = NaN
 * 
 * @param {number|Object|string} strength - Strength value (may be object or number)
 * @param {number} defaultStrength - Default value if invalid (default: 4500)
 * @returns {number} Normalized strength as number
 */
function normalizeStrength(strength, defaultStrength = 4500) {
  // Already a valid number
  if (typeof strength === 'number' && !isNaN(strength) && strength > 0) {
    return strength;
  }
  
  // Object with .value property (Tab UI pattern)
  if (strength && typeof strength === 'object') {
    if (typeof strength.value === 'number' && !isNaN(strength.value) && strength.value > 0) {
      return strength.value;
    }
    // Object might have string value
    if (typeof strength.value === 'string') {
      const parsed = parseInt(strength.value, 10);
      if (!isNaN(parsed) && parsed > 0) {
        return parsed;
      }
    }
  }
  
  // String that can be parsed
  if (typeof strength === 'string') {
    const parsed = parseInt(strength, 10);
    if (!isNaN(parsed) && parsed > 0) {
      return parsed;
    }
  }
  
  // Invalid - log warning and return default
  if (strength !== undefined && strength !== null) {
  }
  return defaultStrength;
}

/**
 * DC-002: Calculate evacuation time using terrain and route factors
 * 
 * USES IMPORTED FUNCTION from terrainFactors.js for consistency.
 * Adds additional military factors (threat, loading time) on top.
 * 
 * @param {number} distanceKm - Distance in kilometers
 * @param {string} terrainType - Terrain type (PLAINS, ROLLING_HILLS, MOUNTAINS, etc.)
 * @param {string} routeType - Route type (PAVED_HIGHWAY, PAVED_SECONDARY, UNPAVED, etc.)
 * @param {Object} options - Additional options (weather, night, threat)
 * @returns {Object} Evacuation time analysis
 */
function calculateEvacuationTimeWithTerrain(distanceKm, terrainType = 'ROLLING_HILLS', routeType = 'PAVED_SECONDARY', options = {}) {
  const LOADING_TIME_MIN = 10; // Loading/unloading time
  const PROCESSING_TIME_MIN = 10; // Medical processing at receiving facility
  
  // DC-002: Validate terrain and route types using imported helpers
  const validTerrain = validateTerrainType(terrainType);
  const validRoute = validateRouteType(routeType);
  
  // DC-002: Use imported calculateGroundEvacuationTime from terrainFactors.js
  const baseEvacTime = calculateGroundEvacuationTime(
    distanceKm,
    validTerrain,
    validRoute,
    options.weather || 'CLEAR',
    options.night || false
  );
  
  // Apply threat modifier (military-specific, not in base function)
  const threatModifier = options.threat === 'HIGH' ? 0.60 : 
                         options.threat === 'MEDIUM' ? 0.80 : 1.0;
  
  // Adjust time for threat (slower movement in high threat)
  const threatAdjustedTravelTime = Math.round(baseEvacTime.time_minutes / threatModifier);
  
  // Total evacuation time
  const totalTimeMin = threatAdjustedTravelTime + LOADING_TIME_MIN + PROCESSING_TIME_MIN;
  
  return {
    distance_km: distanceKm,
    terrain_type: validTerrain,
    route_type: validRoute,
    terrain_factor: baseEvacTime.factors?.terrain_modifier || 1.0,
    route_factor: baseEvacTime.factors?.route_speed_factor || 1.0,
    weather_modifier: baseEvacTime.factors?.weather_modifier || 1.0,
    night_modifier: baseEvacTime.factors?.night_operations ? baseEvacTime.factors.night_penalty : 1.0,
    threat_modifier: threatModifier,
    effective_speed_kmh: baseEvacTime.effective_speed_kmh,
    travel_time_min: threatAdjustedTravelTime,
    loading_time_min: LOADING_TIME_MIN,
    processing_time_min: PROCESSING_TIME_MIN,
    total_time_min: totalTimeMin,
    golden_hour_compliant: totalTimeMin <= 60,
    // Include base analysis for debugging
    base_calculation: baseEvacTime,
    // DC-002: Include available options for UI reference
    available_terrain_options: getTerrainOptions(),
    available_route_options: getRouteOptions()
  };
}

// ============================================================================
// EA-003: PER-FACILITY ASSET CAPACITY CALCULATION
// ============================================================================

/**
 * Nationality (national standard) of the force, for the default helicopter type (helicopterSpecs getPrimaryHelicopter).
 * Undefined when the scenario does not carry one; getPrimaryHelicopter then returns its documented default (UH-60).
 */
function resolveForceNation(scenario) {
  const fbr = scenario?.force?.forceBuilderResult;
  return scenario?.force?.nationalStandard || fbr?.nationalStandard || fbr?.national_standard ||
    fbr?.brigade?.national_standard || fbr?.brigades?.[0]?.national_standard || fbr?.brigades?.[0]?.nationalStandard;
}

/**
 * Daily patients a group of helicopters can move, from the constants: sorties per aircraft per day for the
 * evacuation environment and patients per sortie for the helicopter type (MEDEVAC_OPERATIONAL_FACTORS), times the
 * maintenance and crew availability. Type = the force's primary MEDEVAC helicopter.
 */
function helicopterDailyCapacity(count, environment, nationalStandard) {
  if (!count) return 0;
  const heli = getPrimaryHelicopter(nationalStandard);
  const f = MEDEVAC_OPERATIONAL_FACTORS;
  const sorties = f.sorties_per_aircraft_per_day?.[environment];
  const perSortie = f.patients_per_sortie?.[heli.code] ?? f.patients_per_sortie?.DEFAULT;
  if (typeof sorties !== 'number' || typeof perSortie !== 'number') {
    throw new Error(`casualtyCalculator.helicopterDailyCapacity: no sorties/patients constants for environment '${environment}' / helicopter '${heli.code}'`);
  }
  return count * sorties * perSortie * f.maintenance_availability * f.crew_availability;
}

/**
 * EA-003: Calculate effective evacuation asset capacity for a facility
 * 
 * USES IMPORTED FUNCTIONS from evacuationAssetAllocation.js:
 * - calculateEffectiveAssets(): Computes organic + attached - detached
 * - ROLE*_ORGANIC_ASSETS: Default assets by facility type
 * 
 * @param {Object} facility - Facility with asset structure
 * @param {Object} options - Terrain/threat modifiers
 * @returns {Object} Effective asset capacity analysis
 */
function calculateFacilityAssetCapacity(facility, options = {}) {
  // EA-003: Get default organic assets based on facility type if none specified
  const getDefaultOrganicAssets = (facilityType) => {
    switch (facilityType) {
      case 'ROLE1':
      case 'ROLE_1':
        return ROLE1_ORGANIC_ASSETS?.STANDARD || null;
      case 'ROLE2_BASIC':
      case 'ROLE_2_BASIC':
        return ROLE2_BASIC_ORGANIC_ASSETS?.STANDARD || null;
      case 'ROLE2_ENHANCED':
      case 'ROLE_2_ENHANCED':
        return ROLE2_ENHANCED_ORGANIC_ASSETS?.STANDARD || null;
      case 'ROLE3':
      case 'ROLE_3':
        return ROLE3_ORGANIC_ASSETS?.STANDARD || null;
      default:
        return null;
    }
  };
  
  // Get organic assets - use facility's own or fall back to type defaults
  // ForceBuilder facilities carry facility_type (ROLE1, ROLE2_BASIC, ...); older saves use type or role_type.
  const facilityType = facility?.facility_type || facility?.type || facility?.role_type;
  const organicAssets = facility?.organic_assets || getDefaultOrganicAssets(facilityType);
  
  // Build asset structure for calculateEffectiveAssets
  const assetStructure = {
    organic_assets: organicAssets,
    attached_assets: facility?.attached_assets || [],
    detached_assets: facility?.detached_assets || []
  };
  
  // EA-003: Use imported calculateEffectiveAssets for the calculation
  const effectiveAssets = calculateEffectiveAssets(assetStructure);
  
  // Extract values from effective assets
  const organicGround = (organicAssets?.ground_ambulances?.wheeled?.count || 0) + 
                        (organicAssets?.ground_ambulances?.armored?.count || 0);
  const organicLitter = organicAssets?.total_litter_capacity || 
                        ((organicAssets?.ground_ambulances?.wheeled?.litter_capacity || 0) * (organicAssets?.ground_ambulances?.wheeled?.count || 0)) +
                        ((organicAssets?.ground_ambulances?.armored?.litter_capacity || 0) * (organicAssets?.ground_ambulances?.armored?.count || 0));
  const organicAmbulatory = organicAssets?.total_ambulatory_capacity || 
                            ((organicAssets?.ground_ambulances?.wheeled?.ambulatory_capacity || 0) * (organicAssets?.ground_ambulances?.wheeled?.count || 0)) +
                            ((organicAssets?.ground_ambulances?.armored?.ambulatory_capacity || 0) * (organicAssets?.ground_ambulances?.armored?.count || 0));
  const organicHelo = organicAssets?.helicopters?.count || 0;
  
  // Get effective totals from calculateEffectiveAssets result
  const effectiveGround = (effectiveAssets?.ground_ambulances?.wheeled?.count || 0) + 
                          (effectiveAssets?.ground_ambulances?.armored?.count || 0);
  const effectiveLitter = effectiveAssets?.total_litter_capacity || organicLitter;
  const effectiveAmbulatory = effectiveAssets?.total_ambulatory_capacity || organicAmbulatory;
  const effectiveHelo = effectiveAssets?.helicopters?.count || 0;
  
  // Calculate attached/detached for reporting
  const attachedAssets = facility?.attached_assets || [];
  const detachedAssets = facility?.detached_assets || [];
  
  let attachedGround = 0, attachedLitter = 0, attachedAmbulatory = 0, attachedHelo = 0;
  let detachedGround = 0, detachedLitter = 0, detachedAmbulatory = 0, detachedHelo = 0;
  
  if (Array.isArray(attachedAssets)) {
    attachedAssets.forEach(attached => {
      attachedGround += (attached.ground_ambulances?.wheeled?.count || 0) + 
                        (attached.ground_ambulances?.armored?.count || 0);
      attachedLitter += attached.total_litter_capacity || 0;
      attachedAmbulatory += attached.total_ambulatory_capacity || 0;
      attachedHelo += attached.helicopters?.count || 0;
    });
  }
  
  if (Array.isArray(detachedAssets)) {
    detachedAssets.forEach(detached => {
      detachedGround += (detached.ground_ambulances?.wheeled?.count || 0) + 
                        (detached.ground_ambulances?.armored?.count || 0);
      detachedLitter += detached.total_litter_capacity || 0;
      detachedAmbulatory += detached.total_ambulatory_capacity || 0;
      detachedHelo += detached.helicopters?.count || 0;
    });
  }
  
  // Apply terrain/threat modifiers
  // CALC-011: key was 'MOUNTAINS'; Tab 2's terrain key is 'MOUNTAIN', so mountains never reduced capacity.
  // These literals still violate APP_RULES 13.2 (hardcoded rates) - tracked in backlog CALC-016.
  const terrainModifier = options.terrain === 'MOUNTAIN' ? 0.60 :
                          options.terrain === 'SWAMP' ? 0.50 :
                          options.terrain === 'URBAN' ? 0.75 :
                          options.terrain === 'FOREST' ? 0.70 : 1.0;
  
  // Tab 2 threat keys (LOW_THREAT ... EXTREME_THREAT); the old comparison used 'HIGH' / 'MEDIUM' and never matched,
  // so threat never reduced capacity. Both spellings are accepted.
  const threatModifier = (options.threat === 'HIGH' || options.threat === 'HIGH_THREAT' || options.threat === 'EXTREME_THREAT') ? 0.50 :
                         (options.threat === 'MEDIUM' || options.threat === 'MODERATE_THREAT') ? 0.75 : 1.0;
  
  // R2-18 (2026-10-20): helicopters in the weather of the day = the helicopter availability of the operation weather relative to clear
  // (operationsConfig WEATHER_CONDITIONS). Was two literals (ADVERSE 0.6, MARGINAL 0.8) that never matched, since the weather was always clear.
  const _wxOption = TAB2_WEATHER_CONDITIONS?.[options.weather];
  const weatherModifier = _wxOption ? _wxOption.helicopter_availability / TAB2_WEATHER_CONDITIONS.CLEAR.helicopter_availability : 1.0;
  
  // Ground assets affected by terrain/threat
  const effectiveGroundModified = Math.round(effectiveGround * terrainModifier * threatModifier);
  
  // Air assets affected by weather/threat
  const effectiveHeloModified = Math.round(effectiveHelo * weatherModifier * threatModifier * 10) / 10;
  
  // Calculate daily patient capacity
  // Ground (2026-10-10): runs per day from the leg's distance, the route surface, handling time and two 12 h crews
  // (evacuationCapacityModel); patients per run from each vehicle's litter and sitting places. Was a flat
  // 4 trips x 2 litter per ambulance regardless of distance.
  const _leg = resolveEvacLeg(options.routesConfig, facilityType);
  const _groundClasses = [{ key: 'wheeled', vehicleClass: 'LIGHT' }, { key: 'armored', vehicleClass: 'HEAVY' }];
  const groundByClass = {};
  let groundDailyCapacity = 0;
  _groundClasses.forEach(({ key, vehicleClass: defaultClass }) => {
    const count = effectiveAssets?.ground_ambulances?.[key]?.count || 0;
    if (count === 0) return;
    const asset = organicAssets?.ground_ambulances?.[key];
    // Vehicle data from the constants (ambulanceTypes.js): planning load per run, cruise speed, range, availability.
    const vehicle = getAmbulanceCalcSpec(asset?.type);
    const vehicleClass = vehicle ? vehicleClassFor(vehicle.category) : defaultClass;
    const litter = asset?.litter_capacity ?? vehicle?.litter_capacity ?? LEGACY_VEHICLE_PLACES[vehicleClass].litter;
    const ambulatory = asset?.ambulatory_capacity ?? vehicle?.ambulatory_capacity ?? LEGACY_VEHICLE_PLACES[vehicleClass].ambulatory;
    const cap = groundGroupCapacity({
      vehicles: count * terrainModifier * threatModifier,
      availability: vehicle?.typical_availability ?? 1,
      distanceKm: _leg.distanceKm,
      vehicleClass,
      surface: _leg.surface,
      speedModifier: _leg.speedModifier,
      speedCapKph: vehicle?.speed_km_h ?? Infinity,
      rangeKm: vehicle?.range_km ?? Infinity,
      litterPlaces: litter,
      ambulatoryPlaces: ambulatory,
      mixedLitter: vehicle ? vehicle.mixed_litter : null,
      mixedAmbulatory: vehicle ? vehicle.mixed_ambulatory : null
    });
    groundByClass[key] = { vehicles: count, vehicle_code: vehicle?.code || asset?.type || null, ...cap };
    groundDailyCapacity += cap.patientsPerDay;
  });
  groundDailyCapacity = Math.round(groundDailyCapacity);
  // Air: sorties per aircraft for the evacuation environment and patients per sortie for the helicopter type, from
  // MEDEVAC_OPERATIONAL_FACTORS (was 6 sorties x 2 litter patients). Weather and threat reductions as before.
  const heloDailyCapacity = Math.round(helicopterDailyCapacity(effectiveHeloModified, options.environment, options.nationalStandard));
  const totalDailyCapacity = groundDailyCapacity + heloDailyCapacity;
  
  return {
    facility_id: facility?.id,
    facility_type: facilityType,
    using_defaults: !facility?.organic_assets,
    
    organic: {
      ground_ambulances: organicGround,
      litter_capacity: organicLitter,
      ambulatory_capacity: organicAmbulatory,
      helicopters: organicHelo
    },
    
    attached: {
      ground_ambulances: attachedGround,
      litter_capacity: attachedLitter,
      ambulatory_capacity: attachedAmbulatory,
      helicopters: attachedHelo
    },
    
    detached: {
      ground_ambulances: detachedGround,
      litter_capacity: detachedLitter,
      ambulatory_capacity: detachedAmbulatory,
      helicopters: detachedHelo
    },
    
    effective: {
      ground_ambulances: effectiveGround,
      ground_ambulances_modified: effectiveGroundModified,
      litter_capacity: effectiveLitter,
      ambulatory_capacity: effectiveAmbulatory,
      helicopters: effectiveHelo,
      helicopters_modified: effectiveHeloModified
    },
    
    modifiers: {
      terrain: terrainModifier,
      threat: threatModifier,
      weather: weatherModifier
    },
    
    ground_leg: { leg: _leg.legKey, distance_km: _leg.distanceKm, surface: _leg.surface, speed_modifier: _leg.speedModifier, by_class: groundByClass },
    daily_capacity: {
      ground: groundDailyCapacity,
      helicopter: heloDailyCapacity,
      total: totalDailyCapacity
    },
    
    // EA-003: Include raw effective assets from imported function
    effective_assets_raw: effectiveAssets
  };
}

/**
 * EA-003: Calculate total evacuation capacity across all facilities
 * 
 * @param {Array} facilities - Array of facilities with asset structures
 * @param {Object} options - Terrain/threat/weather modifiers
 * @returns {Object} Aggregated capacity analysis
 */
function calculateTotalEvacuationCapacity(facilities, options = {}) {
  if (!Array.isArray(facilities) || facilities.length === 0) {
    return {
      total_ground_ambulances: 0,
      total_helicopters: 0,
      total_daily_capacity: 0,
      by_facility: []
    };
  }
  
  let totalGround = 0;
  let totalHelo = 0;
  const byFacility = [];
  const byLeg = {};
  
  facilities.forEach(facility => {
    const capacity = calculateFacilityAssetCapacity(facility, options);
    totalGround += capacity.effective.ground_ambulances_modified;
    totalHelo += capacity.effective.helicopters_modified;
    byFacility.push(capacity);
    const leg = capacity.ground_leg.leg;
    byLeg[leg] = (byLeg[leg] || 0) + capacity.daily_capacity.total;
  });
  
  // The chain moves patients only as fast as its narrowest leg (Dr. Tatar, 2026-10-10): the capacity that decides
  // overflow is the smallest total among the legs that have evacuation assets. Summing the legs overstated it.
  const legTotals = Object.values(byLeg);
  const totalDailyCapacity = legTotals.length > 0 ? Math.min(...legTotals) : 0;
  
  return {
    total_ground_ambulances: totalGround,
    total_helicopters: Math.round(totalHelo * 10) / 10,
    total_daily_capacity: totalDailyCapacity,
    by_leg: byLeg,
    narrowest_leg: Object.keys(byLeg).find((k) => byLeg[k] === totalDailyCapacity) || null,
    by_facility: byFacility,
    options_applied: options
  };
}

// ============================================================================

/**
 * FIX-004 + DC-002: Get evacuation time using per-facility distances with terrain factors
 * Falls back to generic distances if per-facility not configured
 * 
 * DC-002 Enhancement: Now uses calculateEvacuationTimeWithTerrain() instead of
 * hardcoded 4 min/km. Properly accounts for terrain type and route type.
 * 
 * @param {Object} medicalConfig - Medical configuration from scenario
 * @param {string} facilityId - Optional facility ID to look up specific distance
 * @param {string} facilityType - Type of facility (ROLE1, ROLE2_BASIC, etc.)
 * @param {Object} options - Additional options (weather, night, threat)
 * @returns {number} Estimated evacuation time in minutes
 */
function getEvacuationTimeToRole2(medicalConfig, facilityId = null, facilityType = 'ROLE1', options = {}) {
  // Try per-facility distance first (Phase 2.5+)
  if (medicalConfig?.per_facility_evacuation_assets && facilityId) {
    const facilityData = medicalConfig.per_facility_evacuation_assets.find(
      f => f.facility_id === facilityId
    );
    
    // If pre-calculated time exists, use it
    if (facilityData?.evacuation?.estimated_time_min) {
      return facilityData.evacuation.estimated_time_min;
    }
    
    // DC-002: Use terrain-aware calculation if distance available
    if (facilityData?.evacuation?.distance_km) {
      const terrainType = facilityData.evacuation.terrain_type || 'ROLLING_HILLS';
      const routeType = facilityData.evacuation.route_type || 'PAVED_SECONDARY';
      
      const evacAnalysis = calculateEvacuationTimeWithTerrain(
        facilityData.evacuation.distance_km,
        terrainType,
        routeType,
        {
          weather: options.weather,
          night: options.night,
          threat: options.threat
        }
      );
      
      return evacAnalysis.total_time_min;
    }
  }
  
  // Fallback to generic distances with DC-002 calculation
  const genericDistance = resolveInput(medicalConfig?.mtf_distances?.role2_distance_km || 
                          medicalConfig?.mtf_distances?.role2, 'role2_distance_km', { zeroIsMissing: true });
  
  // Use scenario terrain if available
  const scenarioTerrain = options.scenarioTerrain || 'ROLLING_HILLS';
  
  const evacAnalysis = calculateEvacuationTimeWithTerrain(
    genericDistance,
    scenarioTerrain,
    'PAVED_SECONDARY', // Default route type
    options
  );
  
  return evacAnalysis.total_time_min;
}

/**
 * CC-010: Calculate triage distribution adjusted by operation day
 * 
 * Early operation (days 1-3): Higher T1 immediate due to intense initial combat
 * Mid operation (days 4-7): Standard distribution
 * Late operation (days 8+): Lower T1, more T2/T3 as wounds accumulate
 * 
 * @param {number} wia - Wounded in action count
 * @param {string} missionType - Type of mission
 * @param {number} day - Current day of operation
 * @returns {Object} Triage distribution by category
 */
function calculateTimeAdjustedTriageDistribution(wia, missionType, day) {
  // Base distribution varies by day
  let t1Percent, t2Percent, t3Percent, t4Percent;
  
  if (day <= 3) {
    // Early operation: intense fighting, more immediate casualties
    t1Percent = 0.30;  // Higher T1 (was 0.25)
    t2Percent = 0.38;
    t3Percent = 0.27;  // Lower T3 (was 0.30)
    t4Percent = 0.05;
  } else if (day <= 7) {
    // Mid operation: standard distribution
    t1Percent = 0.25;
    t2Percent = 0.40;
    t3Percent = 0.30;
    t4Percent = 0.05;
  } else {
    // Late operation: attrition phase, fewer immediate life threats
    t1Percent = 0.20;  // Lower T1
    t2Percent = 0.42;  // Higher T2 (delayed surgeries accumulating)
    t3Percent = 0.33;  // Higher T3 (minor wounds)
    t4Percent = 0.05;
  }
  
  // Adjust for mission type
  if (missionType === 'HASTY_ATTACK' || missionType === 'DELIBERATE_ATTACK') {
    // Offensive operations: more severe injuries
    t1Percent += 0.05;
    t3Percent -= 0.05;
  } else if (missionType === 'WITHDRAWAL' || missionType === 'DELAY') {
    // Defensive retreat: more minor injuries from harassing fire
    t1Percent -= 0.03;
    t3Percent += 0.03;
  }
  
  return {
    t1_immediate: Math.round(wia * t1Percent),
    t2_delayed: Math.round(wia * t2Percent),
    t3_minimal: Math.round(wia * t3Percent),
    t4_expectant: Math.round(wia * t4Percent),
    day_adjusted: true,
    day: day
  };
}

/**
 * CC-013: Calculate fatigue effect on surgical capacity
 * 
 * Medical teams degrade over extended operations:
 * - Days 1-3: 100% capacity
 * - Days 4-7: 90% capacity (early fatigue)
 * - Days 8-14: 80% capacity (significant fatigue)
 * - Days 15+: 70% capacity (exhaustion)
 * 
 * @param {number} cumulativeFatigue - Accumulated fatigue value (0.02 per day per brigade)
 * @param {number} day - Current day of operation
 * @returns {number} Fatigue modifier (0.7 to 1.0)
 */
function calculateFatigueEffect(cumulativeFatigue, day) {
  // Base fatigue from operation duration
  let baseFatigueEffect;
  if (day <= 3) {
    baseFatigueEffect = 1.0;
  } else if (day <= 7) {
    baseFatigueEffect = 0.90;
  } else if (day <= 14) {
    baseFatigueEffect = 0.80;
  } else {
    baseFatigueEffect = 0.70;
  }
  
  // Additional penalty from cumulative fatigue (max additional 10% reduction)
  const cumulativePenalty = Math.min(0.10, cumulativeFatigue * 0.5);
  
  // Final effect (minimum 60% capacity)
  return Math.max(0.60, baseFatigueEffect - cumulativePenalty);
}

// ============================================================================
// CC-004: CUMULATIVE GOLDEN HOUR CALCULATION
// ============================================================================

/**
 * CC-004: Treatment time constants per facility type
 * Based on NATO AJMedP-2 and observed data from Ukraine 2022-2024
 */
const TREATMENT_TIMES = {
  CCP: 5,             // Casualty Collection Point - minimal processing
  ROLE1: 20,          // Stabilization, no surgery
  ROLE2_BASIC: 30,    // Advanced stabilization, damage control resuscitation
  ROLE2_ENHANCED: 0,  // Surgery destination - time is part of surgery
  ROLE3: 0,           // Surgery destination - time is part of surgery
  ROLE4: 0            // Surgery destination - time is part of surgery
};

/**
 * CC-004: Default evacuation time segments
 * Used when per-facility times not configured
 */
const DEFAULT_EVACUATION_SEGMENTS = {
  poi_to_ccp_min: 15,         // Self/buddy aid, carry to collection point
  ccp_to_role1_min: 20,       // Ambulance collection
  role1_to_role2b_min: 35,    // Forward evacuation
  role2b_to_role2e_min: 45,   // Rearward evacuation (ground)
  role2b_to_role2e_helo_min: 20,  // Rearward evacuation (helicopter)
  role2e_to_role3_min: 60     // Theater evacuation
};

/**
 * CC-004: Check if a facility type has surgical capability
 * 
 * @param {string} facilityType - Type of facility
 * @param {Object} facility - Optional facility object to check for attached FST
 * @returns {boolean} True if facility can perform surgery
 */
function hasSurgicalCapability(facilityType, facility = null) {
  // Role 2 Enhanced, Role 3, and Role 4 have organic surgery
  const surgicalFacilityTypes = ['ROLE2_ENHANCED', 'ROLE3', 'ROLE4'];
  if (surgicalFacilityTypes.includes(facilityType)) {
    return true;
  }
  
  // Role 2 Basic with attached FST/FRS has surgery (CC-005 future)
  if (facilityType === 'ROLE2_BASIC' && facility) {
    if (facility.attached_surgical_teams?.length > 0) {
      return facility.attached_surgical_teams.some(team => 
        team.status === 'OPERATIONAL' || team.operational !== false
      );
    }
    if (facility.has_attached_fst || facility.has_surgical) {
      return true;
    }
  }
  
  return false;
}

/**
 * CC-004: Calculate cumulative time from Point of Injury to Surgery
 * 
 * This is the CORRECT Golden Hour calculation per NATO doctrine.
 * Golden Hour = POI to SURGICAL CAPABILITY, not just to next MTF.
 * 
 * Time segments:
 * POI ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã‚Â ÃƒÂ¢Ã¢â€šÂ¬Ã¢â€žÂ¢ CCP ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã‚Â ÃƒÂ¢Ã¢â€šÂ¬Ã¢â€žÂ¢ Role 1 ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã‚Â ÃƒÂ¢Ã¢â€šÂ¬Ã¢â€žÂ¢ Role 2B ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã‚Â ÃƒÂ¢Ã¢â€šÂ¬Ã¢â€žÂ¢ Role 2E (surgery) or Role 3 (surgery)
 * 
 * @param {Object} params - Calculation parameters
 * @param {Object} params.medicalConfig - Medical configuration from scenario
 * @param {string} params.primaryRole1Id - ID of the primary Role 1 facility
 * @param {Object} params.evacuationChain - Evacuation chain configuration
 * @param {boolean} params.hasHelicopterEvac - Whether helicopter evacuation available
 * @returns {Object} Cumulative time analysis
 */
function calculateCumulativeTimeToSurgery({
  medicalConfig,
  primaryRole1Id = null,
  evacuationChain = null,
  hasHelicopterEvac = false,
  groundSpeedFactor   // CALC-001/002/011: terrain x weather ground evacuation speed (1.0 = unhindered), required
}) {
  requireNumber(groundSpeedFactor, 'casualtyCalculator.calculateCumulativeTimeToSurgery', 'groundSpeedFactor');
  if (groundSpeedFactor <= 0) {
    throw new Error(`casualtyCalculator.calculateCumulativeTimeToSurgery: groundSpeedFactor must be > 0 (got: ${groundSpeedFactor})`);
  }
  const segments = [];
  let cumulativeTime = 0;
  let surgicalFacilityType = null;
  let surgicalFacilityId = null;
  
  // Segment 1: POI ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã‚Â ÃƒÂ¢Ã¢â€šÂ¬Ã¢â€žÂ¢ CCP (self/buddy aid, carry to collection point)
  const poiToCcp = medicalConfig?.poi_to_ccp_min || DEFAULT_EVACUATION_SEGMENTS.poi_to_ccp_min;
  cumulativeTime += poiToCcp;
  segments.push({
    from: 'POI',
    to: 'CCP',
    time_min: poiToCcp,
    method: 'SELF_AID',
    description: 'Self/buddy aid and carry to CCP'
  });
  
  // Segment 2: CCP ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã‚Â ÃƒÂ¢Ã¢â€šÂ¬Ã¢â€žÂ¢ Role 1 (ambulance collection)
  const ccpToRole1 = Math.round(
    (medicalConfig?.ccp_to_role1_min || DEFAULT_EVACUATION_SEGMENTS.ccp_to_role1_min) / groundSpeedFactor
  );
  cumulativeTime += ccpToRole1;
  segments.push({
    from: 'CCP',
    to: 'ROLE1',
    time_min: ccpToRole1,
    method: 'PULL',
    description: 'Ambulance collection to BAS'
  });
  
  // Segment 3: Role 1 treatment time
  const role1Treatment = TREATMENT_TIMES.ROLE1;
  cumulativeTime += role1Treatment;
  segments.push({
    from: 'ROLE1',
    to: 'ROLE1_TREATMENT',
    time_min: role1Treatment,
    method: 'TREATMENT',
    description: 'Stabilization at Role 1'
  });
  
  // Segment 4: Role 1 ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã‚Â ÃƒÂ¢Ã¢â€šÂ¬Ã¢â€žÂ¢ Role 2B evacuation
  let role1ToRole2Time;
  if (medicalConfig?.per_facility_evacuation_assets && primaryRole1Id) {
    role1ToRole2Time = getEvacuationTimeToRole2(medicalConfig, primaryRole1Id, 'ROLE1');
  } else {
    role1ToRole2Time = medicalConfig?.mtf_distances?.role2_distance_km 
      ? Math.round((medicalConfig.mtf_distances.role2_distance_km * 4) + 20)
      : DEFAULT_EVACUATION_SEGMENTS.role1_to_role2b_min;
  }
  role1ToRole2Time = Math.round(role1ToRole2Time / groundSpeedFactor);
  cumulativeTime += role1ToRole2Time;
  segments.push({
    from: 'ROLE1',
    to: 'ROLE2_BASIC',
    time_min: role1ToRole2Time,
    method: evacuationChain?.method || 'PULL',
    description: 'Forward evacuation to Medical Company'
  });
  
  // Check if Role 2 Basic has surgery (FST attached)
  const role2bFacility = medicalConfig?.per_facility_evacuation_assets?.find(
    f => f.facility_type === 'ROLE2_BASIC'
  );
  
  if (hasSurgicalCapability('ROLE2_BASIC', role2bFacility)) {
    // Surgery at Role 2 Basic (FST attached) - Golden Hour can be met!
    surgicalFacilityType = 'ROLE2_BASIC_FST';
    surgicalFacilityId = role2bFacility?.facility_id || 'role2b';
    
    return {
      cumulative_time_min: cumulativeTime,
      golden_hour_compliant: cumulativeTime <= 60,
      platinum_ten_compliant: cumulativeTime <= 10,
      surgical_facility_type: surgicalFacilityType,
      surgical_facility_id: surgicalFacilityId,
      segments,
      has_fst_forward: true,
      time_to_surgery_category: getTimeToCareCategory(cumulativeTime)
    };
  }
  
  // Segment 5: Role 2 Basic treatment (no surgery, just stabilization)
  const role2bTreatment = TREATMENT_TIMES.ROLE2_BASIC;
  cumulativeTime += role2bTreatment;
  segments.push({
    from: 'ROLE2_BASIC',
    to: 'ROLE2_BASIC_TREATMENT',
    time_min: role2bTreatment,
    method: 'TREATMENT',
    description: 'Advanced stabilization at Medical Company'
  });
  
  // Segment 6: Role 2B ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã‚Â ÃƒÂ¢Ã¢â€šÂ¬Ã¢â€žÂ¢ Role 2E/Role 3 (surgery destination)
  let role2bToSurgeryTime;
  if (hasHelicopterEvac) {
    role2bToSurgeryTime = DEFAULT_EVACUATION_SEGMENTS.role2b_to_role2e_helo_min;
  } else if (medicalConfig?.r2e_distance_km) {
    // R2E-DIST: Brigade-supplied R2E distance (doctrinal defaults path).
    // Brigade planner entered R2B→R2E distance from division G4/surgeon.
    // Formula: distance_km × 3 min/km + 30 min loading/admin.
    role2bToSurgeryTime = Math.round((medicalConfig.r2e_distance_km * 3) + 30);
  } else {
    role2bToSurgeryTime = medicalConfig?.mtf_distances?.role3_distance_km
      ? Math.round(((medicalConfig.mtf_distances.role3_distance_km -
                     (resolveInput(medicalConfig.mtf_distances.role2_distance_km, 'role2_distance_km', { zeroIsMissing: true }))) * 3) + 30)
      : DEFAULT_EVACUATION_SEGMENTS.role2b_to_role2e_min;
  }
  // Ground transport only: the helicopter leg is not slowed by terrain
  if (!hasHelicopterEvac) {
    role2bToSurgeryTime = Math.round(role2bToSurgeryTime / groundSpeedFactor);
  }
  cumulativeTime += role2bToSurgeryTime;
  
  // Determine surgery destination
  const hasRole2e = medicalConfig?.per_facility_evacuation_assets?.some(
    f => f.facility_type === 'ROLE2_ENHANCED'
  );
  
  if (hasRole2e) {
    surgicalFacilityType = 'ROLE2_ENHANCED';
    segments.push({
      from: 'ROLE2_BASIC',
      to: 'ROLE2_ENHANCED',
      time_min: role2bToSurgeryTime,
      method: hasHelicopterEvac ? 'MEDEVAC' : 'GROUND',
      description: 'Evacuation to Division Field Hospital'
    });
  } else {
    surgicalFacilityType = 'ROLE3';
    segments.push({
      from: 'ROLE2_BASIC',
      to: 'ROLE3',
      time_min: role2bToSurgeryTime,
      method: hasHelicopterEvac ? 'MEDEVAC' : 'GROUND',
      description: 'Evacuation to Combat Support Hospital'
    });
  }
  
  return {
    cumulative_time_min: cumulativeTime,
    golden_hour_compliant: cumulativeTime <= 60,
    platinum_ten_compliant: cumulativeTime <= 10, // Almost never achievable in combat
    surgical_facility_type: surgicalFacilityType,
    surgical_facility_id: surgicalFacilityId,
    segments,
    has_fst_forward: false,
    time_to_surgery_category: getTimeToCareCategory(cumulativeTime),
    warning: cumulativeTime > 60 ? 
      `Golden Hour exceeded: ${Math.round(cumulativeTime)} minutes to surgery` : null
  };
}

/**
 * CC-004: Categorize time to care based on Ukraine 2022-2024 observed data
 * 
 * @param {number} minutes - Time to surgical care in minutes
 * @returns {string} Category label
 */
function getTimeToCareCategory(minutes) {
  if (minutes <= 60) return 'GOLDEN_HOUR';
  if (minutes <= 90) return 'ACCEPTABLE';
  if (minutes <= 120) return 'DELAYED';
  if (minutes <= 180) return 'CRITICAL_DELAY';
  return 'SEVERE_DELAY';
}

/**
 * CC-004: Calculate DOW modifier based on cumulative time to surgery
 * 
 * Based on Ukraine 2022-2024 data showing DOW rates by evacuation time.
 * DOW increases significantly when Golden Hour is exceeded.
 * 
 * Restored 2026-10-09 after an incorrect removal. Time to surgery now also acts through the T1 survival
 * curve, so this empirical multiplier is not applied on top (that would count time twice); it is
 * reported as ukraine_time_band_modifier for comparison until the choice between the two is made.
 * 
 * @param {number} cumulativeTimeMin - Cumulative time to surgery in minutes
 * @returns {number} DOW modifier (1.0 = baseline)
 */
function calculateDOWModifierFromCumulativeTime(cumulativeTimeMin) {
  if (cumulativeTimeMin <= 60) {
    return 1.0;  // Golden Hour met - baseline DOW
  } else if (cumulativeTimeMin <= 90) {
    return 1.15; // 15% increase in DOW
  } else if (cumulativeTimeMin <= 120) {
    return 1.35; // 35% increase in DOW
  } else if (cumulativeTimeMin <= 180) {
    return 1.60; // 60% increase in DOW
  } else {
    return 2.0;  // Double DOW rate for severe delays
  }
}

// ============================================================================
// FH-001: FORWARD FIELD HOSPITAL CONFIGURATION (NATO DES Research)
// ============================================================================

/**
 * FH-001: Forward Field Hospital Positioning
 * 
 * When Role 2 Enhanced (Field Hospital) is positioned forward (closer to FEBA),
 * mortality decreases due to faster surgical access.
 * 
 * Research: NATO Framework for M&S of Medical Support (2024)
 * - Base mortality: 61% at 12 casualties/day with standard positioning
 * - Forward FH mortality: 49.5% at 12 casualties/day
 * - Improvement: 19% mortality reduction (0.495/0.61 = 0.81)
 * 
 * Standard Mode: Automatic detection based on R2E position
 * Expert Mode: Explicit toggle (medicalConfig.forward_fh_override)
 */
const FORWARD_FH_CONFIG = {
  // Position threshold: R2E closer than this = "forward"
  position_threshold_km: 40,        // Normal R2E is ~50-60km from FEBA
  
  // Mortality reduction when forward-positioned
  mortality_reduction_factor: 0.81, // 19% better survival
  
  // Golden Hour bonus (more patients make it in time)
  golden_hour_bonus_percent: 15,
  
  // Description for logs/reports
  description: 'Forward FH positioning per NATO DES simulation (2024)'
};

// ============================================================================
// ============================================================================
// TCCC-2025: TACTICAL COMBAT CASUALTY CARE 2025 GUIDELINES
// ============================================================================

/**
 * TCCC-2025: Tactical Combat Casualty Care 2025 Guidelines
 * 
 * STATUS: ÃƒÆ’Ã‚Â¢Ãƒâ€¦Ã¢â‚¬Å“ÃƒÂ¢Ã¢â€šÂ¬Ã‚Â¦ ENABLED - CoTCCC published updates (April 2025)
 * 
 * Key Changes from 2024:
 * 1. TXA Protocol Simplified:
 *    - Single 2g IV/IO push (was 1g + 1g over 8 hours)
 *    - Now indicated for significant TBI (new indication)
 *    - Earlier administration emphasized
 * 
 * 2. Prolonged Casualty Care (PCC) Integration:
 *    - TCCC ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã‚Â ÃƒÂ¢Ã¢â€šÂ¬Ã¢â€žÂ¢ PCC transition formalized
 *    - Extended wound care and splinting protocols
 *    - Relevant for scenarios where evacuation > 4 hours
 * 
 * 3. Hemorrhage Control Improvements:
 *    - iTClamp for craniomaxillofacial/neck injuries
 *    - Updated junctional hemorrhage guidance
 *    - Tourniquet conversion protocols refined
 * 
 * 4. Airway Management Updates:
 *    - i-gel EGA recommended as primary extraglottic airway
 *    - Updated TBI monitoring guidance
 * 
 * Clinical Impact (per CoTCCC April 2025):
 * - 20-30% reduction in preventable deaths when fully applied
 * - Simplified protocols improve compliance under stress
 * 
 * Sources:
 * - jts.health.mil/committees/cotccc (April 2025 statements)
 * - C-TECC Guidelines 2025-1-2
 * - MED-TAC International analysis (July 2025)
 * 
 * @version 2025.04 (CoTCCC April 2025 update)
 */
const TCCC_2025_CONFIG = {
  enabled: true,  // ÃƒÆ’Ã‚Â¢Ãƒâ€¦Ã¢â‚¬Å“ÃƒÂ¢Ã¢â€šÂ¬Ã‚Â¦ ENABLED - January 9, 2025
  version: '2025.04',
  effective_date: '2025-04-01',
  
  // =========================================================================
  // TXA Protocol Changes (most significant update)
  // =========================================================================
  txa: {
    // New simplified dosing
    dose_grams: 2,                    // Single 2g dose (was 1g + 1g)
    route: 'IV/IO push',              // Can now be given as push
    max_time_from_injury_hours: 3,    // Still within 3 hours of injury
    
    // New indication: TBI
    indications: [
      'hemorrhagic_shock',
      'major_amputation',
      'penetrating_torso_trauma',
      'significant_tbi',              // NEW in 2025
      'anticipated_transfusion'
    ],
    
    // Efficacy improvement from simplified protocol
    // Faster administration = better outcomes
    efficacy_multiplier: 1.12,        // 12% better outcomes vs 2024
    tbi_mortality_reduction: 0.08,    // 8% mortality reduction for TBI cases
    
    // Educational note
    note: 'Single 2g IV/IO push simplifies battlefield administration'
  },
  
  // =========================================================================
  // Hemorrhage Control Improvements
  // =========================================================================
  hemorrhage: {
    // Tourniquet protocols refined
    tourniquet_efficacy_boost: 1.08,  // 8% better hemorrhage control
    tourniquet_conversion_guidance: true,
    
    // Junctional hemorrhage (groin, axilla, neck)
    junctional: {
      improvement_factor: 1.15,       // 15% better junctional management
      itclamp_available: true,        // iTClamp now CoTCCC recommended
      itclamp_efficacy: 0.85          // 85% hemorrhage control rate
    },
    
    // Combat gauze/hemostatic dressings unchanged but emphasized
    hemostatic_dressing_emphasis: true
  },
  
  // =========================================================================
  // Prolonged Casualty Care (PCC) Integration
  // =========================================================================
  prolonged_care: {
    enabled: true,
    threshold_hours: 4,               // PCC protocols activate after 4 hours
    
    // Survival improvement when PCC properly applied
    survival_improvement: {
      hours_4_to_8: 1.10,             // 10% better survival
      hours_8_to_24: 1.15,            // 15% better survival
      hours_24_plus: 1.20             // 20% better survival (vs no PCC)
    },
    
    // PCC components
    components: [
      'extended_wound_care',
      'splinting_protocols',
      'pain_management',
      'fluid_resuscitation',
      'antibiotic_prophylaxis',
      'hypothermia_prevention'
    ],
    
    // Relevance to Ukraine scenarios
    note: 'Critical for Ukraine-type scenarios with evacuation delays'
  },
  
  // =========================================================================
  // Airway Management Updates
  // =========================================================================
  airway: {
    // i-gel now recommended EGA
    preferred_ega: 'i-gel',
    ega_success_rate: 0.92,           // 92% first-pass success
    
    // TBI-specific airway guidance
    tbi_monitoring: {
      improved_guidance: true,
      secondary_insult_prevention: 1.10  // 10% better TBI outcomes
    }
  },
  
  // =========================================================================
  // Overall Impact Modifiers
  // =========================================================================
  impact: {
    // When all TCCC-2025 protocols applied
    preventable_death_reduction: 0.25,  // 25% reduction (middle of 20-30% range)
    
    // Component breakdown
    breakdown: {
      txa_simplified: 0.08,           // 8% from TXA improvements
      hemorrhage_control: 0.08,       // 8% from hemorrhage improvements
      pcc_integration: 0.05,          // 5% from PCC (when applicable)
      airway_tbi: 0.04                // 4% from airway/TBI updates
    },
    
    // Compliance factor - protocols only work if followed
    // Higher compliance expected due to simplified protocols
    expected_compliance: 0.85          // 85% compliance (up from 75% in 2024)
  },
  
  // Educational metadata
  education: {
    training_update_required: true,
    key_message: 'Simplified protocols = better compliance under stress',
    student_focus: [
      'TXA single 2g push - faster, simpler',
      'TBI is now TXA indication',
      'PCC planning for delayed evacuation',
      'iTClamp for junctional hemorrhage'
    ]
  }
};

/**
 * TCCC-2025: Apply TCCC 2025 guideline improvements to DOW/survival calculations
 * 
 * @param {Object} params - Calculation parameters
 * @param {number} params.baseDOWRate - Base died-of-wounds rate
 * @param {number} params.evacuationTimeHours - Time from POI to surgery
 * @param {boolean} params.txaAdministered - Whether TXA was given
 * @param {boolean} params.hasTBI - Whether casualty has TBI
 * @param {string} params.hemorrhageType - 'extremity', 'junctional', 'torso', 'craniofacial'
 * @returns {Object} Modified survival parameters
 */
function applyTCCC2025Modifiers(params) {
  if (!TCCC_2025_CONFIG.enabled) {
    return {
      modified_dow_rate: params.baseDOWRate,
      tccc2025_applied: false,
      modifiers: {}
    };
  }
  
  const {
    baseDOWRate,
    evacuationTimeHours = 1,
    txaAdministered = true,  // Assume TXA given per protocol
    hasTBI = false,
    hemorrhageType = 'extremity'
  } = params;
  
  let modifiedDOWRate = baseDOWRate;
  const modifiers = {};
  
  // 1. TXA improvements (simplified 2g protocol = better compliance = better outcomes)
  if (txaAdministered) {
    const txaModifier = 1 / TCCC_2025_CONFIG.txa.efficacy_multiplier; // Lower DOW
    modifiedDOWRate *= txaModifier;
    modifiers.txa = {
      applied: true,
      factor: txaModifier,
      note: 'Single 2g IV/IO push protocol'
    };
    
    // Additional TBI benefit if applicable
    if (hasTBI) {
      const tbiBonus = 1 - TCCC_2025_CONFIG.txa.tbi_mortality_reduction;
      modifiedDOWRate *= tbiBonus;
      modifiers.txa_tbi = {
        applied: true,
        factor: tbiBonus,
        note: 'TXA now indicated for TBI (2025)'
      };
    }
  }
  
  // 2. Hemorrhage control improvements
  if (hemorrhageType === 'junctional') {
    const junctionalModifier = 1 / TCCC_2025_CONFIG.hemorrhage.junctional.improvement_factor;
    modifiedDOWRate *= junctionalModifier;
    modifiers.junctional = {
      applied: true,
      factor: junctionalModifier,
      note: 'iTClamp + improved junctional protocols'
    };
  } else {
    // Standard tourniquet improvement
    const tourniquetModifier = 1 / TCCC_2025_CONFIG.hemorrhage.tourniquet_efficacy_boost;
    modifiedDOWRate *= tourniquetModifier;
    modifiers.tourniquet = {
      applied: true,
      factor: tourniquetModifier,
      note: 'Refined tourniquet protocols'
    };
  }
  
  // 3. Prolonged Casualty Care (if evacuation > 4 hours)
  if (evacuationTimeHours >= TCCC_2025_CONFIG.prolonged_care.threshold_hours) {
    let pccModifier;
    if (evacuationTimeHours < 8) {
      pccModifier = 1 / TCCC_2025_CONFIG.prolonged_care.survival_improvement.hours_4_to_8;
    } else if (evacuationTimeHours < 24) {
      pccModifier = 1 / TCCC_2025_CONFIG.prolonged_care.survival_improvement.hours_8_to_24;
    } else {
      pccModifier = 1 / TCCC_2025_CONFIG.prolonged_care.survival_improvement.hours_24_plus;
    }
    modifiedDOWRate *= pccModifier;
    modifiers.pcc = {
      applied: true,
      factor: pccModifier,
      hours: evacuationTimeHours,
      note: 'PCC protocols applied for delayed evacuation'
    };
  }
  
  // 4. Overall compliance bonus (simplified protocols = better field compliance)
  // This is already factored into the above, but we track it
  modifiers.compliance = {
    expected_rate: TCCC_2025_CONFIG.impact.expected_compliance,
    note: 'Simplified protocols improve compliance under stress'
  };
  
  return {
    modified_dow_rate: modifiedDOWRate,
    original_dow_rate: baseDOWRate,
    improvement_percent: Math.round((1 - modifiedDOWRate / baseDOWRate) * 100),
    tccc2025_applied: true,
    tccc2025_version: TCCC_2025_CONFIG.version,
    modifiers
  };
}

/**
 * TCCC-2025: Get summary of guideline changes for reports
 */
function getTCCC2025Summary() {
  if (!TCCC_2025_CONFIG.enabled) {
    return { enabled: false };
  }
  
  return {
    enabled: true,
    version: TCCC_2025_CONFIG.version,
    effective_date: TCCC_2025_CONFIG.effective_date,
    key_changes: [
      `TXA: Single ${TCCC_2025_CONFIG.txa.dose_grams}g ${TCCC_2025_CONFIG.txa.route}`,
      'TXA now indicated for significant TBI',
      'Prolonged Casualty Care (PCC) protocols formalized',
      'iTClamp recommended for junctional hemorrhage',
      'i-gel preferred extraglottic airway'
    ],
    expected_impact: `${Math.round(TCCC_2025_CONFIG.impact.preventable_death_reduction * 100)}% reduction in preventable deaths`,
    student_focus: TCCC_2025_CONFIG.education.student_focus
  };
}

/**
 * CC-004: Enhanced Golden Hour analysis for a brigade
 * FH-001: Now includes Forward Field Hospital detection
 * 
 * @param {Object} medicalConfig - Medical configuration
 * @param {Object} brigade - Brigade data
 * @param {Object} medevacAnalysis - MEDEVAC analysis results
 * @returns {Object} Enhanced golden hour analysis
 */
function analyzeGoldenHourCompliance(medicalConfig, brigade, medevacAnalysis, environment) {
  const hasHelo = medevacAnalysis?.available_helicopters > 0 ||
                  medicalConfig?.evacuation_assets?.air?.total > 0;

  // CALC-001/002/011: ground evacuation is slower in difficult terrain and weather. Both factors are
  // designed constants (operationsConfig) keyed like Tab 2's selectors.
  const where = 'casualtyCalculator.analyzeGoldenHourCompliance';
  const terrainSpeed = requireNumber(TAB2_TERRAIN_TYPES?.[environment?.terrain]?.medical?.evacuation_speed,
    where, `operationsConfig.TERRAIN_TYPES.${environment?.terrain}.medical.evacuation_speed`);
  const weatherSpeed = requireNumber(TAB2_WEATHER_CONDITIONS?.[environment?.weather]?.evacuation_speed_modifier,
    where, `operationsConfig.WEATHER_CONDITIONS.${environment?.weather}.evacuation_speed_modifier`);

  const cumulativeAnalysis = calculateCumulativeTimeToSurgery({
    medicalConfig,
    primaryRole1Id: brigade?.primary_role1_id || null,
    evacuationChain: medicalConfig?.evacuation_chain,
    hasHelicopterEvac: hasHelo,
    groundSpeedFactor: terrainSpeed * weatherSpeed * (environment?.night_ground_speed ?? 1)   // R2-05: night operations
  });
  
  // Calculate estimated achievement percentage
  // Based on distribution of casualties and evacuation capacity
  let achievementPercent;
  if (cumulativeAnalysis.golden_hour_compliant) {
    achievementPercent = 85; // Most casualties can make golden hour
  } else if (cumulativeAnalysis.cumulative_time_min <= 90) {
    achievementPercent = 60; // Some casualties with fast evac
  } else if (cumulativeAnalysis.cumulative_time_min <= 120) {
    achievementPercent = 35; // Only helicopter cases
  } else {
    achievementPercent = 15; // Very few achieve golden hour
  }
  
  // Adjust for helicopter availability
  if (hasHelo && !cumulativeAnalysis.golden_hour_compliant) {
    achievementPercent += 15; // Helicopters help more casualties make it
  }
  
  // ========================================================================
  // FH-001: Forward Field Hospital Detection & Mortality Reduction
  // ========================================================================
  // Standard Mode: Automatic detection based on R2E position
  // Expert Mode: Explicit toggle overrides automatic detection
  
  let forwardFHApplied = false;
  let forwardFHPosition = null;
  let forwardFHDOWModifier = 1.0;
  
  // INC-010: per-facility forward_fh_override takes priority; scalar medicalConfig fallback
  // for backward compatibility with any code that sets medicalConfig.forward_fh_override directly.
  const role2eFacilityOverride = medicalConfig?.per_facility_evacuation_assets?.find(
    f => f.facility_type === 'ROLE2_ENHANCED' || f.facility_type === 'ROLE_2_ENHANCED'
  )?.forward_fh_override;
  const expertModeOverride = role2eFacilityOverride ?? medicalConfig?.forward_fh_override;

  if (expertModeOverride !== undefined) {
    // Expert Mode: Use explicit toggle (undefined=AUTO, true=FORCE FORWARD, false=FORCE STANDARD)
    forwardFHApplied = expertModeOverride === true;
    if (forwardFHApplied) {
      forwardFHDOWModifier = FORWARD_FH_CONFIG.mortality_reduction_factor;
      achievementPercent += FORWARD_FH_CONFIG.golden_hour_bonus_percent;
    }
  } else {
    // Standard Mode: Automatic detection based on R2E position
    const role2eFacility = medicalConfig?.per_facility_evacuation_assets?.find(
      f => f.facility_type === 'ROLE2_ENHANCED' || f.facility_type === 'ROLE_2_ENHANCED'
    );
    
    forwardFHPosition = role2eFacility?.position_feba_km ||
                        role2eFacility?.feba_distance_km ||
                        medicalConfig?.mtf_distances?.role2_enhanced_km ||
                        // R2E-DIST: Brigade-supplied R2E distance when using doctrinal defaults
                        medicalConfig?.r2e_distance_km;
    
    if (forwardFHPosition && forwardFHPosition < FORWARD_FH_CONFIG.position_threshold_km) {
      forwardFHApplied = true;
      forwardFHDOWModifier = FORWARD_FH_CONFIG.mortality_reduction_factor;
      achievementPercent += FORWARD_FH_CONFIG.golden_hour_bonus_percent;
    }
  }
  
  // CALC-001/002: time to surgery now enters DOW once, through the T1 survival curve in
  // calculateComprehensiveDOW (it receives cumulative_time_min). The former 1.0-2.0 multiplier by
  // time band was a second application of the same effect and has been removed. What remains
  // here is the forward field hospital effect only.
  const finalDOWModifier = forwardFHDOWModifier;

  return {
    cumulative_time_to_surgery_min: cumulativeAnalysis.cumulative_time_min,
    golden_hour_compliant: cumulativeAnalysis.golden_hour_compliant,
    estimated_achievement_percent: Math.min(95, achievementPercent),
    surgical_destination: cumulativeAnalysis.surgical_facility_type,
    has_forward_surgery: cumulativeAnalysis.has_fst_forward,
    time_category: cumulativeAnalysis.time_to_surgery_category,
    dow_modifier: finalDOWModifier,
    ukraine_time_band_modifier: calculateDOWModifierFromCumulativeTime(cumulativeAnalysis.cumulative_time_min),
    segments: cumulativeAnalysis.segments,
    warning: cumulativeAnalysis.warning,
    has_helicopter_evac: hasHelo,   // 2026-10-13: read by the evacuation profile (transport availability)
    
    // FH-001: Forward Field Hospital tracking
    forward_fh: {
      applied: forwardFHApplied,
      position_km: forwardFHPosition,
      threshold_km: FORWARD_FH_CONFIG.position_threshold_km,
      mortality_reduction_factor: forwardFHApplied ? FORWARD_FH_CONFIG.mortality_reduction_factor : null,
      golden_hour_bonus_applied: forwardFHApplied ? FORWARD_FH_CONFIG.golden_hour_bonus_percent : 0,
      detection_mode: expertModeOverride !== undefined ? 'EXPERT_OVERRIDE' : 'AUTOMATIC',
      final_dow_modifier: finalDOWModifier
    }
  };
}

// ============================================================================
// CC-007: CASEVAC (CASUALTY EVACUATION) MODELING
// ============================================================================

/**
 * CC-007: CASEVAC Configuration Constants
 * 
 * v4.15: Now uses imported constants from evacuationAssetFactors.js
 * 
 * CASEVAC = Casualty Evacuation using non-medical vehicles
 * Used when MEDEVAC capacity is exceeded or unavailable.
 * 
 * Research sources:
 * - JAMA Surgery 2019 (Afghanistan/Iraq 2001-2017)
 * - PMC Ukraine LSCO Studies 2023-2024
 * - Military Medicine Oxford 2024 (Ukraine prehospital lessons)
 * 
 * Key finding: Ukraine 2022-2024 mortality rate 5ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã¢â‚¬Â higher than GWOT,
 * largely due to reliance on CASEVAC and delayed evacuation.
 */

// Map imported CASEVAC_CONFIGURATION to internal format for backward compatibility
const CASEVAC_CONFIG = {
  // Availability settings from imported constants
  availability: {
    NEVER: { 
      factor: 0, 
      label: 'Never available (worst-case)',
      description: 'No CASEVAC - plan for MEDEVAC only'
    },
    LIMITED: {
      factor: (requireNumber(CASEVAC_CONFIGURATION?.AVAILABILITY?.LIMITED?.availability_percent, 'casualtyCalculator.js:3995', 'CASEVAC_CONFIGURATION.AVAILABILITY.LIMITED.availability_percent')) / 100,
      label: CASEVAC_CONFIGURATION?.AVAILABILITY?.LIMITED?.label || 'Limited Availability - 50%',
      description: CASEVAC_CONFIGURATION?.AVAILABILITY?.LIMITED?.description || 'CASEVAC assets committed to combat'
    },
    ON_REQUEST: { 
      factor: (requireNumber(CASEVAC_CONFIGURATION?.AVAILABILITY?.ON_REQUEST?.availability_percent, 'casualtyCalculator.js:4000', 'CASEVAC_CONFIGURATION.AVAILABILITY.ON_REQUEST.availability_percent')) / 100,
      label: CASEVAC_CONFIGURATION?.AVAILABILITY?.ON_REQUEST?.label || 'On request - 80% (realistic)',
      description: CASEVAC_CONFIGURATION?.AVAILABILITY?.ON_REQUEST?.description || 'CASEVAC available when requested'
    },
    ASSUME_AVAILABLE: { 
      factor: 0.90,  // Keep 90% as default for backward compatibility
      label: 'Assume available - 90% (default)',
      description: 'CASEVAC likely available when requested'
    },
    DEDICATED: {
      factor: (requireNumber(CASEVAC_CONFIGURATION?.AVAILABILITY?.DEDICATED?.availability_percent, 'casualtyCalculator.js:4010', 'CASEVAC_CONFIGURATION.AVAILABILITY.DEDICATED.availability_percent')) / 100,
      label: CASEVAC_CONFIGURATION?.AVAILABILITY?.DEDICATED?.label || 'Dedicated - 95%',
      description: CASEVAC_CONFIGURATION?.AVAILABILITY?.DEDICATED?.description || 'Pre-arranged dedicated CASEVAC'
    },
    GUARANTEED: { 
      factor: 1.0, 
      label: 'Guaranteed - 100%',
      description: 'Pre-arranged dedicated CASEVAC support'
    }
  },
  
  // DOW penalties from imported CASEVAC_DOW_PENALTIES constants
  // Ground CASEVAC: 35% higher DOW (no en-route medical care)
  casevac_dow_penalty: requireNumber(CASEVAC_DOW_PENALTIES?.GROUND_CASEVAC?.dow_multiplier, 'casualtyCalculator.js:4023', 'CASEVAC_DOW_PENALTIES.GROUND_CASEVAC.dow_multiplier'),
  
  // Air CASEVAC (utility helicopter): 15% higher DOW
  air_casevac_dow_penalty: requireNumber(CASEVAC_DOW_PENALTIES?.AIR_CASEVAC?.dow_multiplier, 'casualtyCalculator.js:4026', 'CASEVAC_DOW_PENALTIES.AIR_CASEVAC.dow_multiplier'),
  
  // Un-evacuated casualty penalty (prolonged CASEVAC or no evacuation)
  // 60% higher DOW for >60 min delay without care
  unevacuated_dow_penalty: requireNumber(CASEVAC_DOW_PENALTIES?.PROLONGED_CASEVAC?.dow_multiplier, 'casualtyCalculator.js:4030', 'CASEVAC_DOW_PENALTIES.PROLONGED_CASEVAC.dow_multiplier'),
  
  // Legacy 2.5x penalty for truly unevacuated (2-4 hour delay)
  // Research: 5ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã¢â‚¬Â mortality in Ukraine LSCO vs GWOT
  no_evacuation_dow_penalty: 2.5,
  
  // Additional coordination delay for CASEVAC (minutes)
  coordination_delay_min: requireNumber(CASEVAC_CONFIGURATION?.AVAILABILITY?.ON_REQUEST?.response_time_min, 'casualtyCalculator.js:4037', 'CASEVAC_CONFIGURATION.AVAILABILITY.ON_REQUEST.response_time_min'),
  
  // Default availability setting
  default_availability: 'ASSUME_AVAILABLE',
  
  // Mitigation notes (for educational display)
  mitigation: {
    ground_casevac: CASEVAC_DOW_PENALTIES?.GROUND_CASEVAC?.mitigation || 'CLS-trained crew can reduce to 1.20ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã¢â‚¬Â',
    air_casevac: CASEVAC_DOW_PENALTIES?.AIR_CASEVAC?.mitigation || 'Flight medic can reduce to 1.05ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã¢â‚¬Â'
  }
};

/**
 * CC-007 + EA-003: Calculate daily MEDEVAC capacity
 * 
 * EA-003 Enhancement: Now uses calculateTotalEvacuationCapacity() to compute
 * effective capacity from per-facility organic/attached/detached assets.
 * 
 * @param {Object} medevacAnalysis - MEDEVAC analysis from Phase 2.5
 * @param {Object} medicalConfig - Medical configuration
 * @param {Object} options - Terrain/threat/weather modifiers for EA-003
 * @returns {number} Daily evacuation capacity (patients per day)
 */
function calculateDailyMEDEVACCapacity(medevacAnalysis, medicalConfig, options = {}) {
  // EA-003: per-facility assets (the structure ForceBuilder and Tab 3 save). Each facility's ambulances serve their
  // own leg of the evacuation chain (distance and route surface from Tab 3 section B.3).
  if (medicalConfig?.per_facility_evacuation_assets) {
    const facilities = medicalConfig.per_facility_evacuation_assets;
    
    // Check if facilities have the new asset structure
    const hasAssetStructure = facilities.some(f => 
      f.organic_assets || f.attached_assets || f.detached_assets
    );
    
    if (hasAssetStructure) {
      const capacityAnalysis = calculateTotalEvacuationCapacity(facilities, {
        terrain: options.terrain || medicalConfig?.terrain_type || 'ROLLING_HILLS',
        threat: options.threat || 'LOW',
        weather: options.weather || 'CLEAR',
        routesConfig: options.routesConfig,
        environment: options.environment,
        nationalStandard: options.nationalStandard
      });
      // A configured fleet that adds up to zero is a zero capacity (2026-10-10): it no longer falls through to a default.
      return capacityAnalysis.total_daily_capacity;
    }
  }
  
  // Legacy evacuation_assets structure: counts only. Ground runs from the model on the Role 1 leg.
  const legacy = medicalConfig?.evacuation_assets;
  if (legacy) {
    const leg = resolveEvacLeg(options.routesConfig, 'ROLE1');
    const ground = legacy.ground || {};
    const classes = [
      { count: ground.wheeled_light?.count || 0, vehicleClass: 'LIGHT' },
      { count: ground.wheeled_armored?.count || 0, vehicleClass: 'HEAVY' },
      { count: ground.tracked?.count || 0, vehicleClass: 'HEAVY' }
    ];
    let groundCapacity = 0;
    classes.forEach(({ count, vehicleClass }) => {
      if (count === 0) return;
      groundCapacity += groundGroupCapacity({
        vehicles: count,
        distanceKm: leg.distanceKm,
        vehicleClass,
        surface: leg.surface,
        speedModifier: leg.speedModifier,
        litterPlaces: LEGACY_VEHICLE_PLACES[vehicleClass].litter,
        ambulatoryPlaces: LEGACY_VEHICLE_PLACES[vehicleClass].ambulatory
      }).patientsPerDay;
    });
    
    // Air assets from the constants (sorties per aircraft and patients per sortie of the force's primary helicopter).
    const airAssets = legacy.air || {};
    const totalHelicopters = (airAssets.iar330_puma?.count || 0) +
                             (airAssets.uh60_blackhawk?.count || 0) +
                             (airAssets.ch47_chinook?.count || 0);
    return Math.round(groundCapacity) + Math.round(helicopterDailyCapacity(totalHelicopters, options.environment, options.nationalStandard));
  }
  
  // Nothing configured: use the capacity the MEDEVAC module derived from the force (ORBAT-based fleet), and say so.
  // The old silent 100 per day is gone: with no fleet and no analysis there is no honest number, so it throws.
  const derived = medevacAnalysis?.total_daily_capacity;
  if (typeof derived === 'number' && Number.isFinite(derived)) {
    assumptionLedger.note('evac_capacity_from_force');
    return derived;
  }
  throw new Error('casualtyCalculator.calculateDailyMEDEVACCapacity: no evacuation fleet is configured (Tab 3) and the MEDEVAC analysis has no total_daily_capacity');
}

/**
 * CC-007: Analyze CASEVAC requirements for a given day
 * 
 * Determines how many casualties require CASEVAC (overflow) and
 * how many cannot be evacuated at all (unevacuated).
 * 
 * CC-017: Cross-Division Support (Phase 1)
 * When enabled, routes overflow to adjacent divisions before using CASEVAC.
 * 
 * @param {Object} params - Analysis parameters
 * @param {number} params.dailyCasualties - Total daily WIA requiring evacuation
 * @param {number} params.medevacCapacity - Available MEDEVAC capacity
 * @param {string} params.casevacAvailability - CASEVAC availability setting
 * @param {Object} params.medicalConfig - Medical configuration (may have custom settings)
 * @param {Object} params.crossDivisionConfig - Cross-division support configuration
 * @returns {Object} CASEVAC analysis
 */
function analyzeCASEVACRequirements({
  dailyCasualties,
  medevacCapacity,
  casevacAvailability = 'ASSUME_AVAILABLE',
  medicalConfig = {},
  crossDivisionConfig = {},
  exact = false   // 2026-10-21: true = the counts are not rounded to whole casualties (fraction carry)
}) {
  // Get CASEVAC availability factor
  const availabilitySetting = CASEVAC_CONFIG.availability[casevacAvailability] ||
                               CASEVAC_CONFIG.availability.ASSUME_AVAILABLE;
  const casevacFactor = medicalConfig?.casevac_config?.custom_availability || 
                        availabilitySetting.factor;
  
  // Get custom penalties if configured
  const casevacPenalty = medicalConfig?.casevac_config?.custom_dow_penalty ||
                         CASEVAC_CONFIG.casevac_dow_penalty;
  const unevacPenalty = medicalConfig?.casevac_config?.unevacuated_penalty ||
                        CASEVAC_CONFIG.unevacuated_dow_penalty;
  
  // Calculate overflow (casualties exceeding MEDEVAC capacity)
  const medevacEvacuated = Math.min(dailyCasualties, medevacCapacity);
  let overflow = Math.max(0, dailyCasualties - medevacCapacity);
  
  // CC-017: Cross-Division Support - route overflow to adjacent divisions first
  // Phase 2: Enhanced with MASCAL-only, national caveats, configurable capacity, and division pairs
  let crossDivisionEvacuated = 0;
  let crossDivisionBlocked = false;
  let crossDivisionBlockReason = null;
  let allowedPairsCount = 0;
  
  // Get cross-division configuration
  const crossDivConfig = medicalConfig?.cross_division_config || crossDivisionConfig || {};
  const crossDivisionEnabled = medicalConfig?.enable_cross_division_sharing || 
                                crossDivConfig?.enabled || false;
  
  if (crossDivisionEnabled && overflow > 0) {
    // Phase 2: Check MASCAL-only condition
    const mascalOnly = crossDivConfig?.mascal_only !== false; // Default true
    const capacityUtilTemp = medevacCapacity > 0 ? (dailyCasualties / medevacCapacity) * 100 : 100;
    const isMascalCondition = capacityUtilTemp > 100; // Over capacity = MASCAL

    // ENH-047: Routine overflow routing — activates cross-division at a configurable utilization
    // threshold below 100%, without requiring full MASCAL condition. Default: disabled (false).
    // routine_overflow_threshold default 80%: at ≥80% utilization, overflow routed to adjacent
    // division even when not in MASCAL. HNS/coalition sharing for stressed-but-not-saturated MTFs.
    const _routineOverflowEnabled = crossDivConfig?.routine_overflow_enabled === true;
    const _routineOverflowThreshold = resolveInput(crossDivConfig?.routine_overflow_threshold, 'cross_division_overflow_threshold_percent');
    const _isRoutineOverflowCondition = _routineOverflowEnabled && capacityUtilTemp >= _routineOverflowThreshold;

    if (mascalOnly && !isMascalCondition && !_isRoutineOverflowCondition) {
      crossDivisionBlocked = true;
      crossDivisionBlockReason = _routineOverflowEnabled
        ? `ROUTINE_OVERFLOW: Utilization ${Math.round(capacityUtilTemp)}% below ${_routineOverflowThreshold}% threshold`
        : 'MASCAL_ONLY: Capacity not exceeded, cross-division support not activated';
    }
    
    // Phase 2: Check national caveats
    const respectCaveats = crossDivConfig?.respect_national_caveats !== false; // Default true
    const hasCaveatRestriction = respectCaveats && (
      medicalConfig?.national_caveats?.includes('NATIONAL_MEDEVAC') ||
      medicalConfig?.national_caveats?.includes('NATIONAL_TREATMENT') ||
      crossDivisionConfig?.has_caveat_restriction
    );
    
    if (hasCaveatRestriction && !crossDivisionBlocked) {
      crossDivisionBlocked = true;
      crossDivisionBlockReason = 'NATIONAL_CAVEAT: National MEDEVAC/treatment restrictions prevent cross-division support';
    }
    
    // Phase 2: Check division pairs (if configured)
    const divisionPairs = crossDivConfig?.division_pairs || [];
    const currentDivisionId = crossDivisionConfig?.current_division_id || medicalConfig?.current_division_id;
    
    if (divisionPairs.length > 0 && !crossDivisionBlocked) {
      // Filter pairs where current division can SEND to another division
      const enabledPairs = divisionPairs.filter(p => 
        p.enabled && (!currentDivisionId || p.from_id === currentDivisionId)
      );
      allowedPairsCount = enabledPairs.length;
      
      if (enabledPairs.length === 0) {
        crossDivisionBlocked = true;
        crossDivisionBlockReason = 'NO_PAIRS: No enabled cross-division support pairs for this division';
      }
    }
    
    // If not blocked, proceed with cross-division routing
    if (!crossDivisionBlocked) {
      // Phase 2: Use configurable max capacity percentage
      const maxCapacityPercent = resolveInput(crossDivConfig?.max_capacity_percent, 'cross_division_max_capacity_percent', { zeroIsMissing: true });
      
      // Phase 2: Scale capacity by number of allowed pairs (if pairs configured)
      // If 3 divisions available (2 pairs), full capacity; if only 1 pair, half capacity
      let pairScaleFactor = 1.0;
      if (divisionPairs.length > 0 && allowedPairsCount > 0) {
        const maxPossiblePairs = divisionPairs.filter(p => 
          !currentDivisionId || p.from_id === currentDivisionId
        ).length;
        pairScaleFactor = maxPossiblePairs > 0 ? allowedPairsCount / maxPossiblePairs : 0;
      }
      
      const adjacentDivisionCapacity = crossDivisionConfig?.adjacent_capacity || 
                                        (exact ? medevacCapacity * (maxCapacityPercent / 100) * pairScaleFactor : Math.round(medevacCapacity * (maxCapacityPercent / 100) * pairScaleFactor));
      
      // Route up to adjacent capacity or overflow, whichever is less
      crossDivisionEvacuated = Math.min(overflow, adjacentDivisionCapacity);
      overflow = overflow - crossDivisionEvacuated;
      
    } else {
    }
  }
  
  // Calculate CASEVAC evacuations (based on availability) - now from reduced overflow
  const casevacEvacuated = exact ? overflow * casevacFactor : Math.round(overflow * casevacFactor);
  
  // Calculate unevacuated (those who couldn't get CASEVAC either)
  const unevacuated = overflow - casevacEvacuated;
  
  // Calculate capacity utilization
  const capacityUtilization = medevacCapacity > 0 ? 
    Math.min(100, Math.round((dailyCasualties / medevacCapacity) * 100)) : 100;
  
  return {
    // Counts
    total_casualties: dailyCasualties,
    medevac_evacuated: medevacEvacuated,
    cross_division_evacuated: crossDivisionEvacuated, // NEW
    casevac_evacuated: casevacEvacuated,
    unevacuated: unevacuated,
    
    // Capacity analysis
    medevac_capacity: medevacCapacity,
    capacity_utilization_percent: capacityUtilization,
    overflow_count: overflow + crossDivisionEvacuated, // Original overflow before cross-division
    overflow_after_cross_division: overflow, // NEW: Remaining after cross-division help
    
    // Cross-division support info (Phase 2 enhanced)
    cross_division_support: {
      enabled: crossDivisionEnabled,
      casualties_routed: crossDivisionEvacuated,
      reduced_casevac_need: crossDivisionEvacuated > 0,
      blocked: crossDivisionBlocked,
      block_reason: crossDivisionBlockReason,
      allowed_pairs_count: allowedPairsCount,
      config: {
        mascal_only: crossDivConfig?.mascal_only !== false,
        respect_caveats: crossDivConfig?.respect_national_caveats !== false,
        max_capacity_percent: resolveInput(crossDivConfig?.max_capacity_percent, 'cross_division_max_capacity_percent', { zeroIsMissing: true }),
        division_pairs_configured: (crossDivConfig?.division_pairs?.length || 0) > 0,
        // ENH-047: Routine overflow routing fields
        routine_overflow_enabled: crossDivConfig?.routine_overflow_enabled === true,
        routine_overflow_threshold: resolveInput(crossDivConfig?.routine_overflow_threshold, 'cross_division_overflow_threshold_percent')
      }
    },
    
    // Settings used
    casevac_availability: casevacAvailability,
    casevac_availability_factor: casevacFactor,
    casevac_dow_penalty: casevacPenalty,
    unevacuated_dow_penalty: unevacPenalty,
    
    // Warnings
    is_overwhelmed: overflow > 0,
    has_unevacuated: unevacuated > 0,
    warning: unevacuated > 0 ? 
      `${Math.round(unevacuated)} casualties could not be evacuated - critical shortage` :
      overflow > 0 ? 
        `${Math.round(overflow)} casualties required CASEVAC (no en-route care)` : 
        crossDivisionEvacuated > 0 ?
          `${Math.round(crossDivisionEvacuated)} casualties routed to adjacent divisions` : null
  };
}

/**
 * ARCH-010 FIX (v5.6.0): calculateOverflowDOW — replaces calculateCASEVACAdjustedDOW.
 *
 * DOCTRINE: AJMedP-2 — "CASEVAC is unplanned and opportunistic... cannot have
 * influence on the medical estimate process."
 *
 * Previous behaviour (VIOLATION): split overflow into casevac_evacuated (baseDOWRate x 1.35)
 * and unevacuated (baseDOWRate x 1.60). CASEVAC availability reduced DOW vs full unevacuated.
 *
 * Correct behaviour: all overflow (casevac_evacuated + unevacuated) receives the
 * unevacuated_dow_penalty. CASEVAC does not reduce DOW in the medical estimate.
 * casevacAnalysis is still passed through so casevac_evacuated/unevacuated remain
 * available for UI display in daySummary.casevac (arch010_informational_only: true).
 *
 * @param {Object} params
 * @param {number} params.baseWIA - Total WIA before DOW
 * @param {number} params.baseDOWRate - Base DOW rate (from survival/GH/TCCC factors)
 * @param {Object} params.casevacAnalysis - CASEVAC analysis; casevac_evacuated no longer
 *   receives a reduced penalty — it is now informational only per ARCH-010.
 * @returns {Object} Overflow-adjusted DOW breakdown — ARCH-010 compliant
 */
function calculateOverflowDOW({
  baseWIA,
  baseDOWRate,
  casevacAnalysis,
  exact = false   // 2026-10-21: true = counts are not rounded here (the caller rounds the day's total once, with the carry)
}) {
  const rnd = exact ? (v) => v : Math.round;
  const {
    medevac_evacuated,
    casevac_evacuated,
    unevacuated,
    unevacuated_dow_penalty  // ARCH-010: this penalty now applies to ALL overflow
  } = casevacAnalysis;

  // MEDEVAC-evacuated casualties: base DOW rate (TCCC + en-route care intact)
  const medevacDOW = rnd(medevac_evacuated * baseDOWRate);

  // ARCH-010 FIX: ALL overflow treated as unevacuated for the medical estimate.
  // casevac_evacuated is informational (visible in daySummary.casevac) but does NOT
  // receive a reduced DOW penalty — CASEVAC is unplanned and not a planning factor.
  const totalOverflow = casevac_evacuated + unevacuated;
  const overflowDOWRate = baseDOWRate * unevacuated_dow_penalty;
  const overflowDOW = rnd(totalOverflow * overflowDOWRate);

  // Total DOW
  const totalDOW = medevacDOW + overflowDOW;

  // Effective DOW rate across all WIA
  const effectiveDOWRate = baseWIA > 0 ? totalDOW / baseWIA : baseDOWRate;

  return {
    // DOW counts — field names preserved for call-site compatibility
    total_dow: totalDOW,
    medevac_dow: medevacDOW,
    casevac_dow: 0,            // ARCH-010: CASEVAC not a planning factor — zero contribution
    unevacuated_dow: overflowDOW,  // ARCH-010: all overflow treated as unevacuated

    // Rates
    base_dow_rate: baseDOWRate,
    effective_dow_rate: effectiveDOWRate,
    casevac_dow_rate: 0,       // ARCH-010: zero
    unevacuated_dow_rate: overflowDOWRate,

    // Impact analysis
    additional_dow_from_casevac: 0,  // ARCH-010: zero
    additional_dow_from_unevac: overflowDOW - rnd(totalOverflow * baseDOWRate),
    preventable_deaths: overflowDOW - rnd(totalOverflow * baseDOWRate),

    // ARCH-010 compliance marker
    arch010_compliant: true,
    arch010_note: 'AJMedP-2: CASEVAC is unplanned/opportunistic — all overflow treated as unevacuated for DOW. CASEVAC tracking in daySummary.casevac is informational only.'
  };
}
// ============================================================================
// CC-008: FIGHT PHASE CASEVAC PLANNING
// ============================================================================

/**
 * CC-008: Fight Phase CASEVAC Configuration
 * 
 * Different operational phases have different CASEVAC availability.
 * High-intensity phases (assault) may have better CASEVAC (more vehicles available)
 * but also higher demand. Consolidation phases may have reduced availability.
 * 
 * This allows planners to model realistic phase-by-phase medical planning.
 */
const PHASE_CASEVAC_CONFIG = {
  // Phase types with default CASEVAC availability modifiers
  phase_modifiers: {
    PREPARATION: {
      casevac_availability_modifier: 1.2,  // 20% better - vehicles staged
      medevac_capacity_modifier: 1.0,      // Normal
      description: 'Pre-battle staging, CASEVAC assets pre-positioned'
    },
    ASSAULT: {
      casevac_availability_modifier: 0.7,  // 30% worse - vehicles in combat
      medevac_capacity_modifier: 0.9,      // 10% reduction - some assets forward
      description: 'Active assault, vehicles committed to combat tasks'
    },
    EXPLOITATION: {
      casevac_availability_modifier: 0.8,  // 20% worse - rapid advance
      medevac_capacity_modifier: 0.85,     // 15% reduction - stretched logistics
      description: 'Rapid advance, extended evacuation distances'
    },
    CONSOLIDATION: {
      casevac_availability_modifier: 1.0,  // Normal
      medevac_capacity_modifier: 1.1,      // 10% better - assets catching up
      description: 'Consolidating gains, medical assets repositioning'
    },
    DEFENSE: {
      casevac_availability_modifier: 1.1,  // 10% better - vehicles in reserve
      medevac_capacity_modifier: 1.0,      // Normal
      description: 'Defensive posture, combat vehicles available for CASEVAC'
    },
    WITHDRAWAL: {
      casevac_availability_modifier: 0.6,  // 40% worse - vehicles retrograding
      medevac_capacity_modifier: 0.7,      // 30% reduction - congested routes
      description: 'Withdrawal under pressure, limited CASEVAC availability'
    },
    TRANSITION: {
      casevac_availability_modifier: 0.9,  // 10% worse - reorganizing
      medevac_capacity_modifier: 0.95,     // 5% reduction
      description: 'Transitioning between phases, some disruption'
    }
  },
  
  // Default phase if not specified
  default_phase: 'DEFENSE'
};

/**
 * CC-008: Get CASEVAC modifiers for current operational phase
 * 
 * @param {string} phase - Current operational phase
 * @param {Object} customConfig - Optional custom phase configuration
 * @returns {Object} Phase-adjusted CASEVAC parameters
 */
function getPhaseCASEVACModifiers(phase, customConfig = null) {
  const phaseKey = (phase || PHASE_CASEVAC_CONFIG.default_phase).toUpperCase();
  
  // Check for custom configuration first
  if (customConfig?.phase_modifiers?.[phaseKey]) {
    return customConfig.phase_modifiers[phaseKey];
  }
  
  // Use default configuration
  const modifiers = PHASE_CASEVAC_CONFIG.phase_modifiers[phaseKey] ||
                    PHASE_CASEVAC_CONFIG.phase_modifiers.DEFENSE;
  
  return {
    phase: phaseKey,
    casevac_availability_modifier: modifiers.casevac_availability_modifier,
    medevac_capacity_modifier: modifiers.medevac_capacity_modifier,
    description: modifiers.description
  };
}

/**
 * CC-008: Apply phase modifiers to CASEVAC analysis
 * 
 * @param {Object} baseCasevacAnalysis - Base CASEVAC analysis from analyzeCASEVACRequirements
 * @param {Object} phaseModifiers - From getPhaseCASEVACModifiers
 * @returns {Object} Phase-adjusted CASEVAC analysis
 */
function applyPhaseToCASEVAC(baseCasevacAnalysis, phaseModifiers, exact = false) {
  const rnd = exact ? (v) => v : Math.round;   // 2026-10-21: exact = capacity and cases are not rounded to whole numbers (fraction carry)
  // Adjust CASEVAC availability factor
  const adjustedFactor = baseCasevacAnalysis.casevac_availability_factor * 
                         phaseModifiers.casevac_availability_modifier;
  
  // Recalculate overflow with adjusted MEDEVAC capacity
  const adjustedMedevacCapacity = rnd(
    baseCasevacAnalysis.medevac_capacity * phaseModifiers.medevac_capacity_modifier
  );
  
  // Recalculate evacuations with phase adjustments
  const medevacEvacuated = Math.min(baseCasevacAnalysis.total_casualties, adjustedMedevacCapacity);
  const overflow = Math.max(0, baseCasevacAnalysis.total_casualties - adjustedMedevacCapacity);
  const casevacEvacuated = rnd(overflow * Math.min(1, adjustedFactor));
  const unevacuated = overflow - casevacEvacuated;
  
  return {
    ...baseCasevacAnalysis,
    phase: phaseModifiers.phase,
    phase_description: phaseModifiers.description,
    original_medevac_capacity: baseCasevacAnalysis.medevac_capacity,
    adjusted_medevac_capacity: adjustedMedevacCapacity,
    medevac_capacity: adjustedMedevacCapacity,
    medevac_evacuated: medevacEvacuated,
    casevac_evacuated: casevacEvacuated,
    unevacuated: unevacuated,
    original_casevac_factor: baseCasevacAnalysis.casevac_availability_factor,
    adjusted_casevac_factor: adjustedFactor,
    casevac_availability_factor: adjustedFactor,
    is_overwhelmed: overflow > 0,
    has_unevacuated: unevacuated > 0,
    capacity_utilization_percent: adjustedMedevacCapacity > 0 ?
      Math.min(100, Math.round((baseCasevacAnalysis.total_casualties / adjustedMedevacCapacity) * 100)) : 100
  };
}

// ============================================================================
// CC-009: AUTO EVACUATION METHOD (PUSH/PULL/AUTO)
// ============================================================================

/**
 * CC-009: Evacuation Method Configuration
 * 
 * PULL: Receiving facility sends assets to collect casualties (NATO default)
 * PUSH: Sending facility pushes casualties forward using own assets
 * AUTO: Dynamically decides based on capacity and distance
 * STRATEVAC: Strategic evacuation (R3ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã‚Â ÃƒÂ¢Ã¢â€šÂ¬Ã¢â€žÂ¢R4, national responsibility)
 * 
 * AUTO Logic: When receiving facility is at high capacity, PUSH may be preferred
 * to prevent asset bottleneck. However, PUSH over long distances is risky.
 */
const EVAC_METHOD_CONFIG = {
  methods: {
    PULL: {
      code: 'PULL',
      name: 'Pull (Collection)',
      description: 'Receiving facility sends assets to collect',
      collection_efficiency: 1.0,  // 100% of receiving facility assets available
      push_asset_split: 0,         // No split - all assets for collection
      time_modifier: 1.0           // Standard time
    },
    PUSH: {
      code: 'PUSH',
      name: 'Push (Forward)',
      description: 'Sending facility pushes casualties forward',
      collection_efficiency: 0.5,  // 50% of sending assets used for push
      push_asset_split: 0.5,       // Half assets now doing forward transport
      time_modifier: 0.85          // 15% faster (direct transport, no wait)
    },
    AUTO: {
      code: 'AUTO',
      name: 'Automatic',
      description: 'System decides based on capacity and distance',
      default_method: 'PULL',
      // BUG-004 FIX: Reference imported constants instead of hardcoding
      capacity_threshold: AUTO_METHOD_THRESHOLDS.RECEIVING_OVERLOAD_PERCENT,  // 80% (was 85)
      distance_threshold_km: AUTO_METHOD_THRESHOLDS.DISTANCE_PUSH_PREFERRED_KM // 30km
    },
    STRATEVAC: {
      code: 'STRATEVAC',
      name: 'Strategic Evacuation',
      description: 'National-level strategic medical evacuation',
      applies_to: ['ROLE3_TO_ROLE4', 'ROLE3_TO_AIRFIELD'],
      requires_coordination: true,
      typical_assets: ['C-130', 'C-17', 'Civilian Air Ambulance']
    }
  },
  
  // Default methods by facility link
  default_methods: {
    'POI_TO_CCP': 'SELF',          // Self/buddy aid, not configurable
    'CCP_TO_ROLE1': 'PULL',        // R1 collects from CCP
    'ROLE1_TO_ROLE2B': 'AUTO',     // Can push or pull based on capacity
    'ROLE2B_TO_ROLE2E': 'AUTO',    // Can push or pull
    'ROLE2E_TO_ROLE3': 'PULL',     // R3 typically collects
    'ROLE3_TO_ROLE4': 'STRATEVAC', // Strategic evacuation
    'ROLE3_TO_AIRFIELD': 'STRATEVAC'
  }
};

/**
 * CC-009: Determine optimal evacuation method using AUTO logic
 * 
 * Uses getDefaultEvacMethod() to get the configured default for the facility link,
 * then applies AUTO logic to potentially override based on current conditions.
 * 
 * @param {Object} params - Method determination parameters
 * @param {number} params.receivingCapacityPercent - Current capacity of receiving facility (%)
 * @param {number} params.distanceKm - Distance between facilities
 * @param {number} params.casualtyLoad - Current casualties waiting
 * @param {string} params.fromType - Sending facility type (e.g., 'ROLE1')
 * @param {string} params.toType - Receiving facility type (e.g., 'ROLE2B')
 * @param {Object} params.config - Optional custom thresholds
 * @returns {Object} Recommended method with reasoning
 */
function determineAutoMethod({
  receivingCapacityPercent,
  distanceKm,
  casualtyLoad = 0,
  fromType = 'ROLE1',
  toType = 'ROLE2B',
  config = {}
}) {
  // BUG-004 FIX: Use imported AUTO_METHOD_THRESHOLDS instead of hardcoded values
  const capacityThreshold = config.capacity_threshold || 
                            AUTO_METHOD_THRESHOLDS.RECEIVING_OVERLOAD_PERCENT;
  const distanceThreshold = config.distance_threshold_km || 
                            AUTO_METHOD_THRESHOLDS.DISTANCE_PUSH_PREFERRED_KM;
  
  // CC-009: Get configured default method for this facility link
  const configuredDefault = getDefaultEvacMethod(fromType, toType);
  
  // High capacity + short distance = consider PUSH
  if (receivingCapacityPercent > capacityThreshold && distanceKm < distanceThreshold) {
    return {
      method: 'PUSH',
      reason: `Receiving facility at ${receivingCapacityPercent.toFixed(0)}% capacity, ` +
              `distance ${distanceKm}km allows efficient push`,
      confidence: 'HIGH',
      alternative: 'PULL',
      warning: null,
      configured_default: configuredDefault
    };
  }
  
  // High capacity + long distance = PULL but with warning
  if (receivingCapacityPercent > capacityThreshold && distanceKm >= distanceThreshold) {
    return {
      method: 'PULL',
      reason: `Receiving at ${receivingCapacityPercent.toFixed(0)}% capacity but ` +
              `${distanceKm}km distance makes PUSH risky`,
      confidence: 'MEDIUM',
      alternative: 'PUSH',
      warning: `Long PUSH distance (${distanceKm}km) - risk to assets and delayed collection`,
      configured_default: configuredDefault
    };
  }
  
  // Very high casualty load = urgent PUSH regardless of distance
  if (casualtyLoad > 20 && receivingCapacityPercent > 90) {
    return {
      method: 'PUSH',
      reason: `High casualty load (${casualtyLoad}) with receiving at ${receivingCapacityPercent.toFixed(0)}%`,
      confidence: 'HIGH',
      alternative: 'PULL',
      warning: 'MASCAL-level casualty flow - consider PUSH to prevent bottleneck',
      configured_default: configuredDefault
    };
  }
  
  // Normal = use configured default (usually PULL per NATO standard)
  return {
    method: configuredDefault,
    reason: `Using configured default method for ${fromType}ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã‚Â ÃƒÂ¢Ã¢â€šÂ¬Ã¢â€žÂ¢${toType}`,
    confidence: 'HIGH',
    alternative: configuredDefault === 'PULL' ? 'PUSH' : 'PULL',
    warning: null,
    configured_default: configuredDefault
  };
}

/**
 * BUG-005 FIX: Get distance-based asset split ratio using PUSH_DISTANCE_RATIOS
 * 
 * Uses imported PUSH_DISTANCE_RATIOS constant to determine how to split
 * sending facility's assets between collection and forward transport.
 * 
 * @param {number} distanceKm - Distance between facilities
 * @returns {Object} Split ratios for collection and forward transport
 */
function getDistanceBasedAssetSplit(distanceKm) {
  // BUG-005 FIX: Use imported PUSH_DISTANCE_RATIOS instead of hardcoded 0.5 split
  if (distanceKm <= PUSH_DISTANCE_RATIOS.SHORT.max_km) {
    return {
      category: 'SHORT',
      forward_ratio: PUSH_DISTANCE_RATIOS.SHORT.forward_ratio,      // 0.40
      collection_ratio: PUSH_DISTANCE_RATIOS.SHORT.collection_ratio, // 0.60
      description: PUSH_DISTANCE_RATIOS.SHORT.description
    };
  }
  
  if (distanceKm <= PUSH_DISTANCE_RATIOS.MEDIUM.max_km) {
    return {
      category: 'MEDIUM',
      forward_ratio: PUSH_DISTANCE_RATIOS.MEDIUM.forward_ratio,      // 0.50
      collection_ratio: PUSH_DISTANCE_RATIOS.MEDIUM.collection_ratio, // 0.50
      description: PUSH_DISTANCE_RATIOS.MEDIUM.description
    };
  }
  
  if (distanceKm <= PUSH_DISTANCE_RATIOS.LONG.max_km) {
    return {
      category: 'LONG',
      forward_ratio: PUSH_DISTANCE_RATIOS.LONG.forward_ratio,      // 0.60
      collection_ratio: PUSH_DISTANCE_RATIOS.LONG.collection_ratio, // 0.40
      description: PUSH_DISTANCE_RATIOS.LONG.description
    };
  }
  
  // EXTENDED (>50km)
  return {
    category: 'EXTENDED',
    forward_ratio: PUSH_DISTANCE_RATIOS.EXTENDED.forward_ratio,      // 0.70
    collection_ratio: PUSH_DISTANCE_RATIOS.EXTENDED.collection_ratio, // 0.30
    description: PUSH_DISTANCE_RATIOS.EXTENDED.description
  };
}

/**
 * CC-009: Calculate effective evacuation capacity based on method
 * 
 * PUSH method splits sending facility's assets between collection and forward transport.
 * This reduces collection capacity from POI/CCP but speeds up forward movement.
 * 
 * @param {Object} params - Capacity calculation parameters
 * @param {string} params.method - PULL, PUSH, or AUTO
 * @param {number} params.sendingAssets - Ambulances at sending facility
 * @param {number} params.receivingAssets - Ambulances at receiving facility
 * @param {number} params.patientsPerTripPerAsset - Patients per trip (default 2)
 * @param {number} params.tripsPerDay - Trips per asset per day (default 4)
 * @param {number} params.distanceKm - Distance between facilities (for dynamic split)
 * @returns {Object} Capacity breakdown by method
 * @returns {Object} Effective capacity breakdown
 */
function calculateMethodAdjustedCapacity({
  method,
  sendingAssets = 0,
  receivingAssets = 0,
  patientsPerTripPerAsset = 2,
  tripsPerDay = 4,
  distanceKm = 20  // BUG-005 FIX: Add distance parameter for dynamic split
}) {
  const methodConfig = EVAC_METHOD_CONFIG.methods[method] || 
                       EVAC_METHOD_CONFIG.methods.PULL;
  
  const baseCapacityPerAsset = patientsPerTripPerAsset * tripsPerDay;
  
  if (method === 'PULL') {
    // All receiving assets available for collection
    return {
      method: 'PULL',
      collection_capacity: receivingAssets * baseCapacityPerAsset,
      forward_transport_capacity: 0,
      total_daily_capacity: receivingAssets * baseCapacityPerAsset,
      assets_collecting: receivingAssets,
      assets_pushing: 0,
      time_modifier: methodConfig.time_modifier || 1.0,  // Use methodConfig
      efficiency_note: 'Full collection capacity from receiving facility'
    };
  }
  
  if (method === 'PUSH') {
    // BUG-005 FIX: Use distance-based asset split from PUSH_DISTANCE_RATIOS
    // Instead of hardcoded 0.5 split, use dynamic split based on distance
    const distanceSplit = getDistanceBasedAssetSplit(distanceKm);
    const pushSplit = distanceSplit.forward_ratio;
    const assetsForPush = Math.floor(sendingAssets * pushSplit);
    const assetsForCollection = sendingAssets - assetsForPush;
    
    return {
      method: 'PUSH',
      collection_capacity: assetsForCollection * baseCapacityPerAsset,
      forward_transport_capacity: assetsForPush * baseCapacityPerAsset,
      total_daily_capacity: (assetsForCollection + assetsForPush) * baseCapacityPerAsset,
      assets_collecting: assetsForCollection,
      assets_pushing: assetsForPush,
      distance_category: distanceSplit.category,  // BUG-005 FIX: Include distance category
      time_modifier: methodConfig.time_modifier || 0.85,  // PUSH is 15% faster (use methodConfig)
      efficiency_note: `${(pushSplit * 100).toFixed(0)}% forward transport (${distanceSplit.category} distance: ${distanceKm}km)`
    };
  }
  
  // AUTO or unknown - return PULL as default
  return {
    method: 'AUTO_DEFAULT_PULL',
    collection_capacity: receivingAssets * baseCapacityPerAsset,
    forward_transport_capacity: 0,
    total_daily_capacity: receivingAssets * baseCapacityPerAsset,
    assets_collecting: receivingAssets,
    assets_pushing: 0,
    time_modifier: 1.0,
    efficiency_note: 'AUTO defaulting to PULL method'
  };
}

/**
 * CC-009: Get default evacuation method for a facility link
 * 
 * @param {string} fromType - Sending facility type
 * @param {string} toType - Receiving facility type
 * @returns {string} Default method code
 */
function getDefaultEvacMethod(fromType, toType) {
  const linkKey = `${fromType}_TO_${toType}`;
  return EVAC_METHOD_CONFIG.default_methods[linkKey] || 'PULL';
}

// ============================================================================
// CC-012: REAR AREA CASUALTY DECAY BY FEBA DISTANCE
// ============================================================================

// CC-012: rear area decay configuration lives in constants/rearAreaDecay.js (moved 2026-10-11, status EDUCATED_GUESS).


/**
 * CC-012: Calculate rear area decay modifier based on FEBA distance
 * 
 * @param {number} febaDistanceKm - Distance from FEBA in kilometers
 * @param {string} threatLevel - Threat environment (PEER, NEAR_PEER, etc.)
 * @param {string} unitType - Type of unit (for vulnerability modifier)
 * @returns {Object} Decay analysis
 */
function calculateRearAreaDecay(febaDistanceKm, threatLevel = 'PEER', unitType = 'DEFAULT') {
  // Find applicable distance band
  const band = REAR_AREA_DECAY_CONFIG.distance_bands.find(b => febaDistanceKm <= b.max_km) ||
               REAR_AREA_DECAY_CONFIG.distance_bands[REAR_AREA_DECAY_CONFIG.distance_bands.length - 1];
  
  // Get threat environment modifier
  const threatMod = REAR_AREA_DECAY_CONFIG.threat_environment[threatLevel] || 1.0;
  
  // Get unit vulnerability modifier
  const vulnMod = REAR_AREA_DECAY_CONFIG.unit_vulnerability[unitType] ||
                  REAR_AREA_DECAY_CONFIG.unit_vulnerability.DEFAULT;
  
  // Calculate final modifier (distance decay ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã¢â‚¬Â threat ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã¢â‚¬Â vulnerability)
  // Clamp between 0.05 and 2.5 to avoid extreme values
  const rawModifier = band.multiplier * threatMod * vulnMod;
  const finalModifier = Math.max(0.05, Math.min(2.5, rawModifier));
  
  return {
    feba_distance_km: febaDistanceKm,
    distance_band: band.threat_level,
    distance_decay: band.multiplier,
    threat_modifier: threatMod,
    vulnerability_modifier: vulnMod,
    final_modifier: finalModifier,
    description: band.description
  };
}

/**
 * CC-012: Apply rear area decay to casualties
 * 
 * @param {number} baseCasualties - Base rear area casualties
 * @param {Object} decayAnalysis - From calculateRearAreaDecay
 * @returns {Object} Adjusted casualties with breakdown
 */
function applyRearAreaDecay(baseCasualties, decayAnalysis) {
  const adjustedCasualties = Math.round(baseCasualties * decayAnalysis.final_modifier);
  
  return {
    original: baseCasualties,
    adjusted: adjustedCasualties,
    reduction: baseCasualties - adjustedCasualties,
    modifier_applied: decayAnalysis.final_modifier,
    threat_band: decayAnalysis.distance_band
  };
}

// ============================================================================
// CC-011: BLOOD REQUIREMENTS BY INJURY MECHANISM
// ============================================================================

/**
 * CC-011: Blood Requirements by Injury Mechanism
 * 
 * Different injury types require vastly different blood products.
 * Based on:
 * - JAMA Surgery 2019 (Afghanistan/Iraq data)
 * - Committee on Tactical Combat Casualty Care (CoTCCC) guidelines
 * - Ukraine 2022-2024 massive transfusion protocols
 * 
 * Key finding: Mine/IED injuries require 2-3ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã¢â‚¬Â more blood than GSW
 * due to traumatic amputations and massive soft tissue damage.
 */
const BLOOD_BY_MECHANISM_CONFIG = {
  // Blood requirements by injury mechanism (units per T1 casualty)
  mechanisms: {
    GSW: {
      name: 'Gunshot Wound',
      prbc_units: 4,
      ffp_units: 4,
      platelets_units: 1,
      cryo_units: 0,
      massive_transfusion_percent: 15, // % requiring MTP
      typical_frequency: 0.45 // 45% of combat injuries
    },
    BLAST_FRAG: {
      name: 'Blast/Fragmentation',
      prbc_units: 6,
      ffp_units: 6,
      platelets_units: 2,
      cryo_units: 1,
      massive_transfusion_percent: 25,
      typical_frequency: 0.30 // 30% in LSCO
    },
    MINE_IED: {
      name: 'Mine/IED (with amputation)',
      prbc_units: 12,
      ffp_units: 12,
      platelets_units: 4,
      cryo_units: 2,
      massive_transfusion_percent: 60, // Very high MTP rate
      typical_frequency: 0.15 // 15% but rising in Ukraine
    },
    BURNS: {
      name: 'Burn Injury',
      prbc_units: 8,
      ffp_units: 10,
      platelets_units: 2,
      cryo_units: 2,
      massive_transfusion_percent: 40,
      typical_frequency: 0.05 // 5%
    },
    CRUSH: {
      name: 'Crush Injury',
      prbc_units: 6,
      ffp_units: 8,
      platelets_units: 2,
      cryo_units: 1,
      massive_transfusion_percent: 30,
      typical_frequency: 0.05 // 5%
    }
  },
  
  // Distribution by operational environment
  mechanism_distribution: {
    // Urban combat: more GSW, less mines
    URBAN: {
      GSW: 0.55, BLAST_FRAG: 0.25, MINE_IED: 0.10, BURNS: 0.05, CRUSH: 0.05
    },
    // Open terrain: more mines in defensive positions
    OPEN: {
      GSW: 0.40, BLAST_FRAG: 0.30, MINE_IED: 0.20, BURNS: 0.05, CRUSH: 0.05
    },
    // Defensive operations: highest mine threat
    DEFENSIVE: {
      GSW: 0.35, BLAST_FRAG: 0.25, MINE_IED: 0.30, BURNS: 0.05, CRUSH: 0.05
    },
    // Offensive operations: more GSW, assault casualties
    OFFENSIVE: {
      GSW: 0.50, BLAST_FRAG: 0.30, MINE_IED: 0.10, BURNS: 0.05, CRUSH: 0.05
    },
    // Default/mixed
    MIXED: {
      GSW: 0.45, BLAST_FRAG: 0.30, MINE_IED: 0.15, BURNS: 0.05, CRUSH: 0.05
    }
  },
  
  // Massive Transfusion Protocol (MTP) requirements
  mtp: {
    prbc_per_pack: 6,
    ffp_per_pack: 6,
    platelets_per_pack: 1,
    cryo_per_pack: 10,
    typical_packs_needed: 2.5 // Average packs per MTP patient
  },
  
  // MSK-001: Musculoskeletal Surge Configuration (NATO DES Research)
  // When T1 casualties exceed threshold, blood requirements spike due to:
  // - Multi-trauma patterns from overwhelmed system
  // - Delayed treatment ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã‚Â ÃƒÂ¢Ã¢â€šÂ¬Ã¢â€žÂ¢ worse outcomes ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã‚Â ÃƒÂ¢Ã¢â€šÂ¬Ã¢â€žÂ¢ more blood needed
  // - "Wait state" deaths from surgical queue backlog
  // Research: NATO Framework for M&S of Medical Support (2024)
  surge: {
    threshold_t1_per_day: 12,       // NATO DES finding: mortality spikes at 12/day (INC-010: configurable via Tab 3)
    prbc_multiplier: 1.5,           // 50% more PRBC during surge
    ffp_multiplier: 1.3,            // 30% more FFP during surge
    platelets_multiplier: 1.2,      // 20% more platelets during surge
    description: 'MSK surge: Multi-trauma overwhelm threshold per NATO DES'
  }
};

/**
 * CC-011: Get injury mechanism distribution for scenario
 * 
 * @param {string} terrain - Terrain type (URBAN, OPEN, MIXED)
 * @param {string} missionType - Mission type (OFFENSE, DEFENSE, etc.)
 * @returns {Object} Mechanism distribution
 */
function getInjuryMechanismDistribution(terrain = 'MIXED', missionType = 'DEFENSE') {
  // Map mission type to distribution category
  let category = 'MIXED';
  
  if (terrain === 'URBAN' || terrain === 'CITY') {
    category = 'URBAN';
  } else if (missionType?.includes('OFFENSE') || missionType?.includes('ATTACK')) {
    category = 'OFFENSIVE';
  } else if (missionType?.includes('DEFENSE')) {
    category = 'DEFENSIVE';
  } else if (terrain === 'OPEN' || terrain === 'DESERT' || terrain === 'PLAINS') {
    category = 'OPEN';
  }
  
  return {
    category,
    distribution: BLOOD_BY_MECHANISM_CONFIG.mechanism_distribution[category] ||
                  BLOOD_BY_MECHANISM_CONFIG.mechanism_distribution.MIXED
  };
}

/**
 * CC-011: Calculate blood requirements by injury mechanism
 * 
 * @param {number} t1Casualties - Number of T1 (immediate) casualties
 * @param {Object} mechanismDist - Injury mechanism distribution
 * @param {number} [mskSurgeThreshold] - INC-010: optional override for surge threshold (default 12)
 * @returns {Object} Blood requirements breakdown
 */
function calculateBloodByMechanism(t1Casualties, mechanismDist, mskSurgeThreshold) {
  const distribution = mechanismDist.distribution;
  const mechanisms = BLOOD_BY_MECHANISM_CONFIG.mechanisms;
  
  let totalPRBC = 0;
  let totalFFP = 0;
  let totalPlatelets = 0;
  let totalCryo = 0;
  let mtpPatients = 0;
  
  const breakdown = {};
  
  // Calculate for each mechanism
  for (const [mechKey, freq] of Object.entries(distribution)) {
    const mechConfig = mechanisms[mechKey];
    if (!mechConfig) continue;
    
    const casualties = Math.round(t1Casualties * freq);
    const mtpCount = Math.round(casualties * (mechConfig.massive_transfusion_percent / 100));
    
    // Regular transfusion requirements
    const prbc = casualties * mechConfig.prbc_units;
    const ffp = casualties * mechConfig.ffp_units;
    const platelets = casualties * mechConfig.platelets_units;
    const cryo = casualties * mechConfig.cryo_units;
    
    breakdown[mechKey] = {
      name: mechConfig.name,
      casualties,
      mtp_patients: mtpCount,
      prbc_units: Math.round(prbc),
      ffp_units: Math.round(ffp),
      platelets_units: Math.round(platelets),
      cryo_units: Math.round(cryo)
    };
    
    totalPRBC += prbc;
    totalFFP += ffp;
    totalPlatelets += platelets;
    totalCryo += cryo;
    mtpPatients += mtpCount;
  }
  
  // MSK-001: Check for musculoskeletal surge condition
  // INC-010: threshold now configurable via mskSurgeThreshold param (Tab 3 Expert Mode)
  const surgeConfig = BLOOD_BY_MECHANISM_CONFIG.surge;
  const effectiveSurgeThreshold = mskSurgeThreshold ?? surgeConfig.threshold_t1_per_day;
  const surgeActive = t1Casualties > effectiveSurgeThreshold;
  
  // Apply surge multipliers if threshold exceeded
  let finalPRBC = Math.round(totalPRBC);
  let finalFFP = Math.round(totalFFP);
  let finalPlatelets = Math.round(totalPlatelets);
  let finalCryo = Math.round(totalCryo);
  
  if (surgeActive) {
    finalPRBC = Math.round(totalPRBC * surgeConfig.prbc_multiplier);
    finalFFP = Math.round(totalFFP * surgeConfig.ffp_multiplier);
    finalPlatelets = Math.round(totalPlatelets * surgeConfig.platelets_multiplier);
    // Cryo not multiplied (already high in MTP cases)
    
  }
  
  return {
    category: mechanismDist.category,
    t1_casualties: t1Casualties,
    totals: {
      prbc_units: finalPRBC,
      ffp_units: finalFFP,
      platelets_units: finalPlatelets,
      cryo_units: finalCryo,
      mtp_patients: mtpPatients
    },
    // MSK-001: Surge tracking
    surge: {
      active: surgeActive,
      threshold: surgeConfig.threshold_t1_per_day,
      multipliers_applied: surgeActive ? {
        prbc: surgeConfig.prbc_multiplier,
        ffp: surgeConfig.ffp_multiplier,
        platelets: surgeConfig.platelets_multiplier
      } : null,
      pre_surge_totals: surgeActive ? {
        prbc_units: Math.round(totalPRBC),
        ffp_units: Math.round(totalFFP),
        platelets_units: Math.round(totalPlatelets)
      } : null,
      warning: surgeActive ? 
        `MSK surge: ${t1Casualties} T1 casualties exceeds ${surgeConfig.threshold_t1_per_day}/day threshold - blood requirements increased` : null
    },
    by_mechanism: breakdown,
    // Average units per T1 casualty (for comparison with flat-rate)
    average_per_casualty: {
      prbc: t1Casualties > 0 ? Math.round((finalPRBC / t1Casualties) * 10) / 10 : 0,
      ffp: t1Casualties > 0 ? Math.round((finalFFP / t1Casualties) * 10) / 10 : 0
    }
  };
}

// ============================================================================
// PHASE 5.1: TACTICAL EVAC CAPACITY HELPERS
// ============================================================================

/**
 * Extract and normalise tactical evac inputs for one brigade.
 * Builds synthetic facility objects compatible with calculateGroundCapacity /
 * calculateAirCapacity from evacuationCapacityCalculator.js.
 *
 * distanceKm: R1→R2B ground leg. Read from medicalConfig evacuation distances if
 * available; falls back to 15 km (doctrinal default R1→R2B).
 *
 * @param {Object} medicalConfig - scenario.medical_configuration
 * @param {Object} scenario      - full scenario (environment + op_params)
 * @returns {Object} tacEvacConfig
 */
/**
 * R2-29b: the share of each ground ambulance type of the Tab 3 fleet that belongs to this brigade, in proportion to the standard brigade fleet of its
 * national standard (GROUND_FLEET_BY_NATION). A multinational force has different fleets per brigade; a type that no brigade standard lists is split equally.
 * @returns {Object|null} {vehicleCode: share 0..1}; null when the force has one brigade
 */
export function groundFleetShares(brigades, brigade, fallbackStandard = 'NATO') {
  if (!Array.isArray(brigades) || brigades.length < 2) return null;
  const stdOf = (b) => (b?.nationalStandard && b.nationalStandard !== 'MULTINATIONAL') ? b.nationalStandard : fallbackStandard;
  const id = brigade.id || brigade.brigade_id;
  const types = new Set();
  const toe = brigades.map((b) => {
    const v = getDefaultGroundFleet(stdOf(b)).vehicles;
    Object.keys(v).forEach((k) => types.add(k));
    return { id: b.id || b.brigade_id, vehicles: v };
  });
  const shares = {};
  types.forEach((t) => {
    const total = toe.reduce((a, x) => a + (x.vehicles[t]?.count || 0), 0);
    const mine = toe.find((x) => x.id === id);
    shares[t] = total > 0 ? ((mine?.vehicles[t]?.count || 0) / total) : 1 / brigades.length;
  });
  shares.__n = brigades.length;
  return shares;
}

function getTacticalEvacConfig(medicalConfig, scenario, environmentOverride = null, weatherKey = null, groundShares = null) {
  // CALC-005: environment IS the paradigm chosen in Tab 2. Was derived from
  // scenario.environment.threat_level === 'HIGH'/'EXTREME' (not threat-level keys, field never written),
  // so it was always PERMISSIVE.
  const environment = environmentOverride || resolveEvacuationParadigm(scenario).paradigm;   // R2-28: the day's derived environment when given
  // R2-18 (2026-10-20): the weather of the day (operation weather or the phase's own) reaches the evacuation capacity; it used to read
  // scenario.environment.weather, which no tab writes, so the capacity was always computed in clear weather.
  const weather   = weatherKey ? (WEATHER_TO_CAPACITY_WEATHER[weatherKey] || (() => { throw new Error(`getTacticalEvacConfig: no capacity weather for '${weatherKey}'`); })()) : (scenario?.environment?.weather || 'CLEAR');
  const terrain   = resolveScenarioEnvironment(scenario).terrain;   // CALC-011: planner's terrain (Tab 2)
  const timeOfDay = scenario?.operation_parameters?.time_of_day || 'DAY';

  // AMB-002 BUG 1 FIX: Tab3 saves fleet to tactical_evac_config.fleet, NOT ambulance_config.
  // Old: medicalConfig?.ambulance_config || {} → always empty object → 0 vehicles always.
  const ambConfig = medicalConfig?.tactical_evac_config?.fleet || {};

  // AMB-002 BUG 3 FIX: Category-based classification via AMBULANCE_TYPES lookup.
  // Old: substring matching on key name → TABC79/MLVM/SPRINTER/BOXER all → wheeled_light.
  // Map: LIGHT/PROTECTED/MRAP → wheeled_light (wheeled, road-capable)
  //      ARMORED/HEAVY_ARMORED → wheeled_armored (armored platform, lower speed)
  // Tracked is a separate bucket retained for future tracked-only platforms (M113, AMPV).
  const CATEGORY_TO_EVAC_BUCKET = {
    LIGHT:         'wheeled_light',
    PROTECTED:     'wheeled_light',
    MRAP:          'wheeled_light',
    ARMORED:       'wheeled_armored',
    HEAVY_ARMORED: 'wheeled_armored'
  };

  const groundVehicles = { wheeled_light: 0, wheeled_armored: 0, tracked: 0 };

  for (const [key, val] of Object.entries(ambConfig)) {
    // AMB-002 BUG 2 FIX: Use available (operational vehicles), not count (TOE/authorized strength).
    // available = what the commander actually has ready to move; count = authorized establishment.
    const cnt = typeof val === 'object'
      ? (val.available ?? val.count ?? 0)
      : (val ?? 0);
    if (cnt <= 0) continue;

    const cat    = AMBULANCE_TYPES[key]?.category;
    const bucket = CATEGORY_TO_EVAC_BUCKET[cat] || 'wheeled_light';
    // R2-29b: only this brigade's share of the type (a type that is in the planner's fleet but in no standard fleet keeps an equal share)
    const _share = groundShares ? (groundShares[key] ?? 1 / groundShares.__n) : 1;
    groundVehicles[bucket] += cnt * _share;
  }

  // AMB-002 BUG 4 FIX: Tab3 saves air pool to tactical_evac_config.air_pool, NOT medevac_config.
  // IMP-S107-001: Now supports air_pools array (multi-fleet). Backward compat: if legacy air_pool
  // single-object exists, wrap it in array. Sum nominal sorties across all pools.
  const _tec = medicalConfig?.tactical_evac_config || {};
  const airPools = _tec.air_pools
    ? _tec.air_pools
    : _tec.air_pool
      ? [_tec.air_pool]
      : [];
  const nominalSorties = airPools.reduce(
    (s, p) => s + (typeof p.nominal_daily_sorties === 'number' ? p.nominal_daily_sorties : 0), 0
  );
  const NOMINAL_SORTIES_PER_AIRCRAFT = 6; // doctrine: 6 sorties/aircraft/day in PERMISSIVE/CLEAR
  const helicopterCount = nominalSorties > 0
    ? Math.max(1, Math.round(nominalSorties / NOMINAL_SORTIES_PER_AIRCRAFT))
    : 0;

  // Map Tab3 aircraft_type codes to DEFAULT_HELICOPTER_SPECS keys in evacuationCapacityCalculator.
  // Use primary pool's aircraft_type for evacuation capacity model (litter capacity per sortie).
  const AIRCRAFT_TYPE_MAP = {
    IAR330:         'IAR330_PUMA',
    IAR_330:        'IAR330_PUMA',
    IAR330_PUMA:    'IAR330_PUMA',
    UH60:           'UH60_BLACKHAWK',
    UH_60:          'UH60_BLACKHAWK',
    UH60_BLACKHAWK: 'UH60_BLACKHAWK',
    CH47:           'CH47_CHINOOK',
    CH_47:          'CH47_CHINOOK',
    CH47_CHINOOK:   'CH47_CHINOOK'
  };
  const _primaryPool = airPools[0] || {};
  const rawType      = (_primaryPool.aircraft_type || '').toUpperCase().replace(/-/g, '_');
  const helicopterType = AIRCRAFT_TYPE_MAP[rawType] || 'IAR330_PUMA'; // Romanian default

  // Distance: R1→R2B ground leg
  const distanceKm = resolveInput(medicalConfig?.chain_distances?.r1_to_r2b?.km
                  || medicalConfig?.evacuation_routes?.r1_to_r2b_km
                  || medicalConfig?.evacuation_routes?.role1_to_role2b_km, 'role1_to_role2b_km', { zeroIsMissing: true }); // doctrinal default km

  return {
    environment,
    weather,
    terrain,
    timeOfDay,
    helicopterType,
    distanceKm,
    // AMB-001 FIX (v5.14.0 — unchanged): calculateEffectiveAssets() reads
    // facility.organic_assets.ground_ambulances.{wheeled_light, wheeled_armored, tracked}
    ambulanceFacility: {
      organic_assets: {
        ground_ambulances: groundVehicles,
        helicopters: { allocated: 0, on_call: 0 }
      },
      attached_assets: {},
      detached_assets: {}
    },
    medevacFacility: {
      organic_assets: {
        ground_ambulances: { wheeled_light: 0, wheeled_armored: 0, tracked: 0 },
        helicopters: { allocated: helicopterCount, on_call: 0 }
      },
      attached_assets: {},
      detached_assets: {}
    }
  };
}

/**
 * Tactical evacuation capacity of one day (ground and air) from the Tab 3 fleet, with the day's attachments and detachments of the brigade's
 * Role 2 Basic. Used by the daily tactical_evac_analysis and, since R2-29, by the evacuation queue before the wound model runs.
 */
function tacticalCapacityOfDay({ medicalConfig, scenario, brigade, day, environmentOverride = null, weatherKey = null, groundShares = null }) {
  const tec = getTacticalEvacConfig(medicalConfig, scenario, environmentOverride, weatherKey, groundShares);

  // ENH-002 Phase 4: Apply per-day attach/detach deltas to synthetic facilities.
  // Find this brigade's R2B facility in per_facility_evacuation_assets, then call
  // getEffectiveAssets(facility, day) to get net counts after attachments/detachments.
  // The delta is applied to the synthetic medevacFacility and ambulanceFacility counts
  // so calculateAirCapacity / calculateGroundCapacity reflect the actual day's fleet.
  const perFacility = medicalConfig?.per_facility_evacuation_assets;
  if (Array.isArray(perFacility) && perFacility.length > 0) {
    // Find the most forward R2B for this brigade (primary planning facility)
    const r2bFacility = perFacility.find(
      f => (f.facility_type === 'ROLE2_BASIC' || f.facility_type === 'ROLE2B') &&
           (f.brigade_id === brigade.id || f.parent_brigade_id === brigade.id)
    ) || perFacility.find(
      f => f.facility_type === 'ROLE2_BASIC' || f.facility_type === 'ROLE2B'
    );

    if (r2bFacility) {
      const eff = getEffectiveAssets(r2bFacility, day);
      // Adjust helicopter count in medevacFacility
      const baseHeli = tec.medevacFacility.organic_assets.helicopters.allocated;
      const adjHeli  = Math.max(0, baseHeli + eff.delta_heli);
      tec.medevacFacility = {
        ...tec.medevacFacility,
        organic_assets: {
          ...tec.medevacFacility.organic_assets,
          helicopters: { allocated: adjHeli, on_call: 0 }
        }
      };
      // Adjust ground ambulance count in ambulanceFacility
      if (eff.delta_amb !== 0) {
        const gv = { ...tec.ambulanceFacility.organic_assets.ground_ambulances };
        if (eff.delta_amb > 0) {
          gv.wheeled_light = (gv.wheeled_light || 0) + eff.delta_amb;
        } else {
          // Subtract proportionally from wheeled buckets
          const totalGround = (gv.wheeled_light || 0) + (gv.wheeled_armored || 0);
          const reduction   = Math.min(Math.abs(eff.delta_amb), totalGround);
          const wlRatio = totalGround > 0 ? (gv.wheeled_light || 0) / totalGround : 1;
          gv.wheeled_light  = Math.max(0, (gv.wheeled_light  || 0) - Math.round(reduction * wlRatio));
          gv.wheeled_armored = Math.max(0, (gv.wheeled_armored || 0) - Math.round(reduction * (1 - wlRatio)));
        }
        tec.ambulanceFacility = {
          ...tec.ambulanceFacility,
          organic_assets: {
            ...tec.ambulanceFacility.organic_assets,
            ground_ambulances: gv
          }
        };
      }
    }
  }

  const gCap = calculateGroundCapacity({
    facility:    tec.ambulanceFacility,
    distanceKm:  tec.distanceKm,
    environment: tec.environment,
    timeOfDay:   tec.timeOfDay,
    weather:     tec.weather,
    terrain:     tec.terrain
  });
  const aCap = calculateAirCapacity({
    facility:       tec.medevacFacility,
    distanceKm:     tec.distanceKm,
    environment:    tec.environment,
    timeOfDay:      tec.timeOfDay,
    weather:        tec.weather,
    terrain:        tec.terrain,
    helicopterType: tec.helicopterType
  });
  return { tec, gCap, aCap };
}

/**
 * R2-28b: the evacuation environment for the SET-UP calculations (MEDEVAC method choice, survival set-up, legacy chain path), derived from the
 * operation in the same way as the day-by-day one: open share of the evacuation window with the road, air, drone, artillery and enemy
 * assessment of the scenario. Returns a copy of the scenario whose operation_parameters.evacuation_paradigm is the derived label (the typed
 * value is kept as evacuation_paradigm_typed and no longer read). Only when the wound hazard model and the threat effects are on, the same
 * conditions under which the daily window is built; otherwise the scenario is returned unchanged.
 */
function withDerivedEvacuationEnvironment(scenario) {
  const op = scenario?.operation_parameters;
  if (!EVAC_ENV_DERIVED_ON || !EVAC_WAIT_DERIVED_ON || !THREAT_EFFECTS_USE || !op) return scenario;
  // 2026-10-21: ON only. In SHADOW the typed paradigm stays (it was replaced by the derived label, which dropped the flat contested x1.4 while the
  // derived wait never entered the result: SHADOW gave 8.6 percent against 12.6 percent of OFF)
  if (resolveWoundHazardMode(op.wound_hazard_mode) !== WOUND_HAZARD_MODES.ON) return scenario;
  const ta = op.threat_assessment || {};
  const weatherKey = resolveScenarioEnvironment(scenario).weather;
  const infra = infrastructureModel(op.infrastructure_config);
  const road = (op.loc_config || ta.ied_threat || ta.insurgent_activity) ? resolveRoadState({ locConfig: op.loc_config || {}, threatAssessment: ta, weather: weatherKey }) : null;
  const heli = helicopterAvailability({ airSituationLevel: op.air_situation_config?.level || 'PARITY', enemyAd: ta.enemy_ad || 'NONE', weather: weatherKey, coordination: infra ? infra.medevac_coordination : 1 });
  const win = evacuationWindowFromParts({
    roadAvailability: road ? road.availability : null,
    helicopterValue: heli.value,
    droneLevel: resolveDroneForDay(ta, 1).level,
    artilleryLevel: ta.artillery_threat,
    resistanceLevel: levelOfIndex(resolveResistanceIndex(null, scenario)),
    weather: weatherKey
  });
  return {
    ...scenario,
    operation_parameters: {
      ...op,
      evacuation_paradigm: suggestParadigm(win.p_open),
      evacuation_paradigm_typed: op.evacuation_paradigm,
      evacuation_paradigm_source: 'DERIVED_FROM_WINDOW',
      evacuation_paradigm_p_open: win.p_open
    }
  };
}

/**
 * Compute capacity vs demand for one day.
 * ARCH-010 COMPLIANT: dow_risk is a display field only — never applied to DOW chain.
 *
 * @param {Object} groundCap      - output of calculateGroundCapacity()
 * @param {Object} airCap         - output of calculateAirCapacity()
 * @param {number} dailyCasualties - total casualties this day (battle + DNBI)
 * @returns {Object} sufficiency block for daySummary.tactical_evac_analysis
 */
function computeTacticalEvacSufficiency(groundCap, airCap, dailyCasualties) {
  // TAC-EVAC-001 DEFENSIVE GUARD: Number.isFinite() catches NaN, Infinity, null, undefined.
  // ?? 0 was insufficient — nullish coalescing passes NaN through, causing all status comparisons
  // to return false and status to stay 'GREEN' regardless of actual capacity deficit.
  const groundPerDay = Number.isFinite(groundCap?.total_patients_per_day) ? groundCap.total_patients_per_day : 0;
  const airPerDay    = Number.isFinite(airCap?.total_patients_per_day)    ? airCap.total_patients_per_day    : 0;
  const totalPerDay  = groundPerDay + airPerDay;

  const hourlyCapacity = totalPerDay / 24;
  const hourlyDemand   = (dailyCasualties || 0) / 24;

  const sufficiencyRatio = hourlyDemand > 0 ? hourlyCapacity / hourlyDemand : 1.0;
  const overflowPerHour  = Math.max(0, hourlyDemand - hourlyCapacity);

  let status = 'GREEN';
  if      (sufficiencyRatio < 0.50) status = 'RED';
  else if (sufficiencyRatio < 0.75) status = 'ORANGE';
  else if (sufficiencyRatio < 1.00) status = 'YELLOW';

  // ARCH-010: dow_risk is informational only — not applied to DOW calculation
  const dowRiskModifier = status === 'RED'    ? 1.25
                        : status === 'ORANGE' ? 1.12
                        : status === 'YELLOW' ? 1.05
                        : 1.0;

  return {
    ground_capacity_per_day: Math.round(groundPerDay),
    air_capacity_per_day:    Math.round(airPerDay),
    total_capacity_per_day:  Math.round(totalPerDay),
    hourly_capacity:         Math.round(hourlyCapacity * 10) / 10,
    hourly_demand:           Math.round(hourlyDemand * 10) / 10,
    sufficiency_ratio:       Math.round(sufficiencyRatio * 100) / 100,
    overflow_per_hour:       Math.round(overflowPerHour * 10) / 10,
    status,
    // ARCH-010: Display only — shows DOW risk if capacity were applied.
    // This value is NOT used in calculateOverflowDOW() or any DOW chain.
    arch010_dow_risk_informational: dowRiskModifier
  };
}

// ============================================================================
// PHASE 6.1: AIRFIELD CLASS DATA
// Inline copy — same values as Tab3 AIRFIELD_CLASS_SPECS.
// TODO: Extract to trainEvacuation.js in next constants session (APP_RULES 2.1).
// ============================================================================
const AIRFIELD_CLASS_DATA = {
  CLASS_A: { aircraft: ['C-17', 'C-130', 'A400M'], litter_capacity: 200, hourly_throughput: 50 },
  CLASS_B: { aircraft: ['C-130', 'A400M'],          litter_capacity: 120, hourly_throughput: 30 },
  CLASS_C: { aircraft: ['C-27J', 'CN-235'],          litter_capacity: 40,  hourly_throughput: 10 },
  CLASS_D: { aircraft: ['Helicopter only'],           litter_capacity: 16,  hourly_throughput: 4  }
};

// ============================================================================
// PHASE 6.1: MTP — MASSIVE TRANSFUSION PROTOCOL ADJUSTMENT
// ============================================================================

/**
 * Apply MTP ratio to blood requirements for the day.
 *
 * MTP activates for haemorrhagic shock patients (T1 severe).
 * Source: CoTCCC April 2025 — 1:1:1 (PRBC:FFP:PLT) is current standard.
 * 2:1:1 used when platelet supply is constrained.
 *
 * Approach: We do NOT add new blood units on top of existing requirements.
 * Instead we enforce the ratio: if FFP or PLT as computed are below the
 * MTP target ratio, we raise them to the ratio. This reflects how MTP is
 * activated in practice — it re-orders the mix, not the volume.
 *
 * PLT unit conversion: 1 apheresis PLT ≈ equivalent of 6 whole blood PLT units.
 * We track PLT in apheresis units throughout (same as bloodProducts.js).
 *
 * @param {Object} bloodTotals - { prbc_units, ffp_units, plt_units, ... }
 * @param {number} mtpPatients - Count of MTP-requiring patients this day
 * @param {string} mtpProtocol - '1:1:1' | '2:1:1'
 * @returns {Object} Adjusted blood totals + mtp tracking fields
 */
function applyMTPAdjustment(bloodTotals, mtpPatients, mtpProtocol) {
  if (mtpPatients <= 0) {
    return { ...bloodTotals, mtp_protocol_applied: false };
  }

  const prbc = bloodTotals.prbc_units || bloodTotals.prbc || 0;

  let targetFFP, targetPLT;
  if (mtpProtocol === '1:1:1') {
    targetFFP = prbc;               // 1:1 ratio
    targetPLT = Math.ceil(prbc / 6); // 1 apheresis PLT per 6 PRBC
  } else {
    // 2:1:1 — PRBC-heavy
    targetFFP = Math.ceil(prbc / 2);
    targetPLT = Math.ceil(prbc / 4);
  }

  const adjustedFFP = Math.max(bloodTotals.ffp_units || bloodTotals.ffp || 0, targetFFP);
  const adjustedPLT = Math.max(bloodTotals.plt_units || bloodTotals.plt || 0, targetPLT);

  return {
    ...bloodTotals,
    ffp_units: adjustedFFP,
    ffp: adjustedFFP,
    plt_units: adjustedPLT,
    plt: adjustedPLT,
    mtp_protocol_applied: true,
    mtp_protocol: mtpProtocol,
    mtp_ratio_prbc: 1,
    mtp_ratio_ffp: mtpProtocol === '1:1:1' ? 1 : 0.5,
    mtp_ratio_plt: mtpProtocol === '1:1:1' ? '1/6' : '1/4'
  };
}

// ============================================================================
// PHASE 6.1: SHELF LIFE TRACKING
// ============================================================================

/**
 * Compute PLT shelf life wastage estimate for the day.
 * PLT = 5-day shelf life (AABB / CoTCCC).
 * Steady-state model: 1/5 of on-hand PLT expires each day.
 * This is a DISPLAY field — does not reduce consumption totals.
 *
 * @param {number} pltOnHand - Current PLT units on hand
 * @param {boolean} shelfLifeEnabled - From blood_config.shelf_life_tracking
 * @returns {Object} { plt_daily_wastage_estimate, shelf_life_warning }
 */
function computeShelfLifeTracking(pltOnHand, shelfLifeEnabled) {
  if (!shelfLifeEnabled) {
    return { plt_daily_wastage_estimate: 0, shelf_life_warning: false };
  }
  const pltShelfLifeDays = 5;
  const dailyWastage = Math.ceil((pltOnHand || 0) / pltShelfLifeDays);
  return {
    plt_daily_wastage_estimate: dailyWastage,
    shelf_life_warning: dailyWastage > 0
  };
}

// ============================================================================
// PHASE 6.1: STRATEVAC CAPACITY BY AIRFIELD CLASS + FARP
// ============================================================================

/**
 * Compute STRATEVAC throughput capacity from airfield class and FARP status.
 * Called once per scenario (not per-day) — stored on results.medical_requirements.
 *
 * @param {string} airfieldClass - 'CLASS_A' | 'CLASS_B' | 'CLASS_C' | 'CLASS_D'
 * @param {boolean} farpEnabled - Forward Arming/Refuelling Point available
 * @returns {Object} stratevac_capacity block for results.medical_requirements
 */
function calculateStratevacCapacityByClass(airfieldClass, farpEnabled) {
  const spec = AIRFIELD_CLASS_DATA[airfieldClass] || AIRFIELD_CLASS_DATA.CLASS_B;
  const farpSortieBonus = farpEnabled ? 1.25 : 1.0; // +25% rotary sorties with FARP
  return {
    class: airfieldClass || 'CLASS_B',
    litter_capacity: spec.litter_capacity,
    hourly_throughput: spec.hourly_throughput,
    farp_enabled: farpEnabled || false,
    farp_sortie_bonus: farpSortieBonus,
    effective_hourly_throughput: Math.round(spec.hourly_throughput * farpSortieBonus),
    aircraft_types: spec.aircraft
  };
}

// ============================================================================
// ENH-003: STRATEVAC SCHEDULE ANALYSIS — PHA QUEUE MODEL
// ============================================================================

/**
 * computeStratevacScheduleAnalysis
 *
 * Models the daily PHA patient queue for each active airfield that has a
 * configured flight schedule. Returns per-airfield per-day PHA occupancy,
 * MASCAL status, backflow to Role 3, and wait-time DOW penalties.
 *
 * PATIENT FLOW MODEL:
 *   Each day: new patients arrive at PHA = corps T1 × ROLE_3 admission fraction
 *             × HOSPITALIZATION_RATE (i.e. patients who reach Role 3 and need
 *             strategic evacuation — they are NOT RTD, NOT KIA).
 *   On departure days: aircraft dequeues up to litter_capacity patients (FIFO).
 *   PHA fill = cumulative arrivals − cumulative departed.
 *   PHA fraction = pha_fill / pha_beds → getPHAStatus().
 *   Overflow (fraction ≥ 1.0): excess patients backflow to Role 3 beds.
 *   Wait time: tracked per cohort. Patients waiting > PHA_CRITICAL_WAIT_HOURS
 *              receive DOW_WAIT_PENALTY_PER_24H per 24h excess.
 *
 * MULTI-AIRFIELD:
 *   When alternate airfield is_active, patient load is split proportionally
 *   by PHA beds (larger PHA = larger share). Simplest doctrinal model —
 *   planners assign specific patient categories in real ops, but for planning
 *   the bed-proportional split is a valid conservative estimate.
 *
 * @param {Object} scenario  - Full scenario object
 * @param {number} duration  - Operation duration in days
 * @param {Array}  byDay     - results.by_day (already populated)
 * @returns {Object}         - stratevac_schedule_analysis block
 */
function computeStratevacScheduleAnalysis(scenario, duration, byDay, modalLoadFraction = 1.0) {
  const secCfg = scenario?.medical_configuration?.strategic_evac_config;

  // If no strategic evac config or air not enabled — return empty
  if (!secCfg || secCfg.air_enabled === false) {
    return { enabled: false, airfields: [] };
  }

  // Resolve active airfields with valid configured schedules.
  // STRATEVAC-ERR-001 FIX: schedule !== null is insufficient — React saves {} for an
  // unconfigured alternate airfield. {} passes the null guard, then computeDepartureDays({})
  // throws because aircraft_type and frequency_mode are missing.
  // STRATEVAC-COUNT-001 FIX: active_airfield_count must not count schedule:{} airfields.
  // Both bugs share this root: require aircraft_type AND frequency_mode to be set.
  const allAirfields = secCfg.airfields ?? [];
  const activeAirfields = allAirfields.filter(af =>
    af != null &&
    af.is_active === true &&
    af.schedule != null &&
    af.schedule.aircraft_type &&
    af.schedule.frequency_mode
  );

  if (activeAirfields.length === 0) {
    return {
      enabled: true,
      airfields: [],
      warning: 'No active airfield with a configured schedule. STRATEVAC capacity unknown.'
    };
  }

  // --- Per-day corps T1 → PHA arrivals ---
  // T1 patients reaching PHA = corps daily T1 × ROLE_3 admission fraction × HOSPITALIZATION_RATE
  // These are the patients who need strategic evacuation (not RTD, not KIA/DOW).
  const r3AdmissionFraction = requireNumber(ECHELON_ADMISSION_DISTRIBUTION.ROLE_3, 'casualtyCalculator.js:r3AdmissionFraction', 'ECHELON_ADMISSION_DISTRIBUTION.ROLE_3');
  const hospRate = requireNumber(HOSPITALIZATION_RATE, 'casualtyCalculator.js:hospRate', 'HOSPITALIZATION_RATE');

  // INC-011: modalLoadFraction < 1.0 when both air and rail STRATEVAC are enabled —
  // the patient pool is split proportionally between modes at the call site.
  const dailyPHAArrivals = byDay.map(d => {
    const t1 = d.totals?.triage_1 ?? 0;
    return Math.round(t1 * r3AdmissionFraction * hospRate * modalLoadFraction);
  });

  // --- Split load across airfields by PHA bed capacity ---
  const totalPhaBeds = activeAirfields.reduce((s, af) => s + (af.pha_beds ?? 50), 0);
  const airfieldLoadFractions = activeAirfields.map(af =>
    totalPhaBeds > 0 ? (af.pha_beds ?? 50) / totalPhaBeds : 1 / activeAirfields.length
  );

  // --- Model each airfield independently ---
  const airfieldResults = activeAirfields.map((airfield, afIdx) => {
    const schedule = airfield.schedule;
    const phaBeds  = airfield.pha_beds ?? 50;
    const loadFrac = airfieldLoadFractions[afIdx];

    // Resolve primary departure days
    let departureDays;
    try {
      departureDays = new Set(computeDepartureDays(schedule, duration));
    } catch (err) {
      return {
        airfield_id:    airfield.id,
        // STRATEVAC-ERR-001 FIX: airfield.label may be undefined on pre-ENH-003 saved scenarios.
        // Fallback chain prevents empty-label error prefix in Tab5 display.
        airfield_label: airfield.label || airfield.id || 'Airfield',
        error: `computeDepartureDays failed: ${err.message}`,
        timeline: [],
        summary: { total_departed: 0, total_backflow: 0, total_wait_dow_penalty: 0, mascal_days: 0, warning_days: 0 }
      };
    }

    const aircraft     = STRATEVAC_AIRCRAFT[schedule.aircraft_type];
    const litterCap    = aircraft?.litter_capacity ?? 40;
    const turnaroundH  = aircraft?.turnaround_hours ?? 2;
    // ENH-STRAT-001: number of sorties on each departure day (default 1 — backward compat).
    const flightsPerDay = schedule.flights_per_day ?? 1;
    // Effective capacity per departure day = single-aircraft litter capacity × sorties
    const effectiveDayCap = litterCap * flightsPerDay;

    // BUG-079 FIX (March 13, 2026): Secondary aircraft schedule per airfield.
    // schedule_b is optional — null/missing = current behaviour (backward compatible).
    // Validity guard mirrors activeAirfields filter: requires aircraft_type + frequency_mode.
    const schedB = (airfield.schedule_b?.aircraft_type && airfield.schedule_b?.frequency_mode)
      ? airfield.schedule_b : null;
    let departureDaysB = new Set();
    let effectiveDayCapB = 0;
    let aircraftB = null;
    if (schedB) {
      try {
        departureDaysB   = new Set(computeDepartureDays(schedB, duration));
        aircraftB        = STRATEVAC_AIRCRAFT[schedB.aircraft_type];
        const litterCapB = aircraftB?.litter_capacity ?? 40;
        const fpd_b      = schedB.flights_per_day ?? 1;
        effectiveDayCapB = litterCapB * fpd_b;
      } catch (_err) {
        // schedule_b parse failure — silently ignore, primary schedule still used
        departureDaysB   = new Set();
        effectiveDayCapB = 0;
      }
    }

    let phaQueue    = 0;   // current patients in PHA
    let waitHours   = 0;   // rolling wait accumulator (worst-case patient in queue)
    let totalDeparted  = 0;
    let totalBackflow  = 0;
    let totalWaitDOWPenalty = 0;
    let mascal_days  = 0;
    let warning_days = 0;

    const timeline = byDay.map((d, idx) => {
      const dayNum  = d.day ?? (idx + 1);

      // Patients arriving at PHA today
      const arrivals = Math.round(dailyPHAArrivals[idx] * loadFrac);
      phaQueue += arrivals;

      // Aircraft departs today? (primary schedule)
      let departed = 0;
      if (departureDays.has(dayNum)) {
        const cap = Math.min(phaQueue, effectiveDayCap);
        departed   += cap;
        phaQueue   -= cap;
        totalDeparted += cap;
        waitHours = 0;
      }
      // Secondary aircraft departs today? (BUG-079: schedule_b)
      if (schedB && departureDaysB.has(dayNum) && phaQueue > 0) {
        const capB = Math.min(phaQueue, effectiveDayCapB);
        departed      += capB;
        phaQueue      -= capB;
        totalDeparted += capB;
        waitHours = 0;
      }

      // Patients not departed — accumulate wait (24h per day)
      if (phaQueue > 0 && departed === 0) {
        waitHours += 24;
      }

      // DOW wait penalty: per 24h beyond PHA_CRITICAL_WAIT_HOURS
      let waitDOWPenaltyToday = 0;
      if (waitHours > PHA_CRITICAL_WAIT_HOURS) {
        const excessHours = waitHours - PHA_CRITICAL_WAIT_HOURS;
        const excess24hBlocks = Math.floor(excessHours / 24);
        // Penalty applies to patients in queue
        waitDOWPenaltyToday = phaQueue * excess24hBlocks * BACKFLOW_RULES.DOW_WAIT_PENALTY_PER_24H;
        totalWaitDOWPenalty += waitDOWPenaltyToday;
      }

      // PHA overflow → backflow to Role 3
      let backflowToday = 0;
      if (phaQueue > phaBeds) {
        backflowToday = phaQueue - phaBeds;
        phaQueue = phaBeds;             // clamp PHA to capacity
        totalBackflow += backflowToday;
      }

      // PHA occupancy status
      const phaFraction = phaBeds > 0 ? phaQueue / phaBeds : 0;
      const phaStatus   = getPHAStatus(phaFraction);

      if (phaStatus.label === 'MASCAL')   mascal_days++;
      if (phaStatus.label === 'WARNING')  warning_days++;

      // Warning if no flight scheduled and queue is filling
      const nextFlightDay = [...departureDays].filter(fd => fd > dayNum).sort((a, b) => a - b)[0] ?? null;

      return {
        day:              dayNum,
        arrivals,
        departed,
        backflow:         backflowToday,
        pha_queue:        phaQueue,
        pha_beds:         phaBeds,
        pha_fraction:     Math.round(phaFraction * 1000) / 1000,
        pha_status:       phaStatus.label,
        pha_color:        phaStatus.color,
        wait_hours:       waitHours,
        wait_critical:    waitHours > PHA_CRITICAL_WAIT_HOURS,
        wait_warning:     waitHours > PHA_WARNING_WAIT_HOURS,
        wait_dow_penalty: Math.round(waitDOWPenaltyToday * 100) / 100,
        is_departure_day: departureDays.has(dayNum),
        next_flight_day:  nextFlightDay,
        aircraft_type:    schedule.aircraft_type,
        flights_per_day:  flightsPerDay,
        turnaround_hours: turnaroundH
      };
    });

    return {
      airfield_id:    airfield.id,
      airfield_label: airfield.label,
      airfield_class: airfield.class,
      is_primary:     airfield.is_primary,
      pha_beds:       phaBeds,
      aircraft_type:  schedule.aircraft_type,
      aircraft_label: aircraft?.label ?? schedule.aircraft_type,
      litter_capacity: litterCap,
      flights_per_day: flightsPerDay,
      effective_day_capacity: effectiveDayCap,
      // BUG-079: secondary schedule summary (null when not configured)
      schedule_b: schedB ? {
        aircraft_type:         schedB.aircraft_type,
        aircraft_label:        aircraftB?.label ?? schedB.aircraft_type,
        litter_capacity:       aircraftB?.litter_capacity ?? 40,
        flights_per_day:       schedB.flights_per_day ?? 1,
        effective_day_capacity: effectiveDayCapB,
        departure_days:        [...departureDaysB].sort((a, b) => a - b)
      } : null,
      departure_days: [...departureDays].sort((a, b) => a - b),
      load_fraction:  Math.round(loadFrac * 1000) / 1000,
      timeline,
      summary: {
        total_arrived:       dailyPHAArrivals.reduce((s, v) => s + Math.round(v * loadFrac), 0),
        total_departed:      totalDeparted,
        total_backflow:      totalBackflow,
        total_wait_dow_penalty: Math.round(totalWaitDOWPenalty * 100) / 100,
        mascal_days,
        warning_days,
        first_mascal_day:   timeline.find(t => t.pha_status === 'MASCAL')?.day ?? null,
        first_warning_day:  timeline.find(t => t.pha_status === 'WARNING')?.day ?? null,
        phaThresholds:      PHA_THRESHOLDS
      }
    };
  });

  // --- Cross-airfield summary ---
  const anyMASCAL = airfieldResults.some(af => (af.summary?.mascal_days ?? 0) > 0);
  const totalBackflowAllAirfields = airfieldResults.reduce((s, af) => s + (af.summary?.total_backflow ?? 0), 0);
  const totalDOWPenalty = airfieldResults.reduce((s, af) => s + (af.summary?.total_wait_dow_penalty ?? 0), 0);

  return {
    enabled: true,
    airfields: airfieldResults,
    summary: {
      active_airfield_count: activeAirfields.length,
      any_mascal:            anyMASCAL,
      total_backflow_to_r3:  totalBackflowAllAirfields,
      total_wait_dow_penalty: Math.round(totalDOWPenalty * 100) / 100,
      backflow_triggers_corps_mascal: BACKFLOW_RULES.ROLE3_OVERFLOW_TRIGGERS_CORPS_MASCAL
    }
  };
}

// ENH-004: TRAIN SCHEDULE ANALYSIS — STATION QUEUE MODEL
// ============================================================================

/**
 * computeTrainScheduleAnalysis
 *
 * Models the daily station holding-area queue for medical train evacuation.
 * Mirrors computeStratevacScheduleAnalysis() exactly — same FIFO queue model,
 * same wait-time DOW penalty logic, same backflow semantics — but uses the
 * train departure schedule instead of the airfield flight schedule.
 *
 * PATIENT FLOW MODEL (identical to STRATEVAC air):
 *   Each day: new patients arrive at station = corps T1 x ROLE_3 fraction
 *             x HOSPITALIZATION_RATE (stable patients needing strategic evac).
 *   On departure days: train dequeues up to (capacity x trains_per_departure).
 *   Station queue fill = cumulative arrivals - cumulative departed.
 *   Overflow (queue > station holding_area_beds): excess backflows to Role 3.
 *   Wait penalty: patients waiting > TRAIN_QUEUE_THRESHOLDS.MAX_WAIT_CRITICAL_HOURS
 *                 receive TRAIN_WAIT_PENALTY.DOW_PENALTY_PER_24H_BEYOND_THRESHOLD
 *                 per 24h excess (capped at MAX_PENALTY_MULTIPLIER).
 *
 * CROSS-MODAL NOTE:
 *   When both air (ENH-003) and train (ENH-004) are enabled, both models draw
 *   from the same patient pool independently. This is a known first-pass
 *   simplification -- full cross-modal load-balancing is deferred (TODO).
 *
 * ON_DEMAND MODE:
 *   computeTrainDepartureDays() returns [] for ON_DEMAND. Departure is
 *   triggered whenever the station queue reaches min_patients_for_departure.
 *
 * @param {Object} scenario  - Full scenario object
 * @param {number} duration  - Operation duration in days
 * @param {Array}  byDay     - results.by_day (already populated)
 * @returns {Object}         - train_schedule_analysis block
 */
function computeTrainScheduleAnalysis(scenario, duration, byDay, modalLoadFraction = 1.0) {
  const secCfg = scenario?.medical_configuration?.strategic_evac_config;

  if (!secCfg || !secCfg.train_enabled) {
    return { enabled: false };
  }

  const trainSchedule = secCfg.train_schedule;

  if (!trainSchedule || !trainSchedule.frequency_mode) {
    return {
      enabled: true,
      warning: 'Train enabled but no schedule configured. Rail evacuation capacity unknown.',
      timeline: [],
      summary: { total_departed: 0, total_backflow: 0, total_wait_dow_penalty: 0, mascal_days: 0, warning_days: 0 }
    };
  }

  const trainType   = secCfg.train_type ?? 'STANDARD';
  const stationType = secCfg.station_type ?? 'MEDIUM';
  const trainSpec   = MEDICAL_TRAIN_SPECS[trainType];
  const stationSpec = TRAIN_STATION_STANDARDS[stationType];
  const stationBeds = stationSpec?.capacity?.holding_area_beds ?? 50;
  const capPerDep   = getTrainCapacityPerDeparture({
    train_type: trainType,
    trains_per_departure: trainSchedule.trains_per_departure ?? 1
  });

  const isOnDemand = trainSchedule.frequency_mode === 'ON_DEMAND';
  let departureDaySet = new Set();

  if (!isOnDemand) {
    try {
      const days = computeTrainDepartureDays(trainSchedule, duration);
      departureDaySet = new Set(days);
    } catch (err) {
      return {
        enabled: true,
        error: `computeTrainDepartureDays failed: ${err.message}`,
        timeline: [],
        summary: { total_departed: 0, total_backflow: 0, total_wait_dow_penalty: 0, mascal_days: 0, warning_days: 0 }
      };
    }
  }

  const minPatientsForDeparture = trainSchedule.min_patients_for_departure ?? 50;

  const r3AdmissionFraction = requireNumber(ECHELON_ADMISSION_DISTRIBUTION.ROLE_3, 'casualtyCalculator.js:r3AdmissionFraction', 'ECHELON_ADMISSION_DISTRIBUTION.ROLE_3');
  const hospRate            = requireNumber(HOSPITALIZATION_RATE, 'casualtyCalculator.js:hospRate', 'HOSPITALIZATION_RATE');

  // INC-011: modalLoadFraction < 1.0 when both air and rail STRATEVAC are enabled —
  // the patient pool is split proportionally between modes at the call site.
  const dailyArrivals = byDay.map(d => {
    const t1 = d.totals?.triage_1 ?? 0;
    return Math.round(t1 * r3AdmissionFraction * hospRate * modalLoadFraction);
  });

  const criticalWaitHours = TRAIN_QUEUE_THRESHOLDS.MAX_WAIT_CRITICAL_HOURS;
  const warningWaitHours  = TRAIN_QUEUE_THRESHOLDS.MAX_WAIT_WARNING_HOURS;
  const dowPenaltyPer24h  = TRAIN_WAIT_PENALTY.DOW_PENALTY_PER_24H_BEYOND_THRESHOLD;
  const maxPenaltyCap     = TRAIN_WAIT_PENALTY.MAX_PENALTY_MULTIPLIER;

  const getStationStatus = (fraction) => {
    if (fraction >= TRAIN_QUEUE_THRESHOLDS.MASCAL_PERCENT / 100)   return { label: 'MASCAL',   color: 'error' };
    if (fraction >= TRAIN_QUEUE_THRESHOLDS.CRITICAL_PERCENT / 100)  return { label: 'CRITICAL', color: 'warning' };
    if (fraction >= TRAIN_QUEUE_THRESHOLDS.WARNING_PERCENT / 100)   return { label: 'WARNING',  color: 'warning' };
    return { label: 'NORMAL', color: 'success' };
  };

  let stationQueue        = 0;
  let waitHours           = 0;
  let totalDeparted       = 0;
  let totalBackflow       = 0;
  let totalWaitDOWPenalty = 0;
  let mascal_days         = 0;
  let warning_days        = 0;

  const timeline = byDay.map((d, idx) => {
    const dayNum   = d.day ?? (idx + 1);
    const arrivals = dailyArrivals[idx];
    stationQueue  += arrivals;

    const onDemandTrigger = isOnDemand && stationQueue >= minPatientsForDeparture;
    const isDepartureDay  = isOnDemand ? onDemandTrigger : departureDaySet.has(dayNum);

    let departed = 0;
    if (isDepartureDay && capPerDep > 0) {
      departed      = Math.min(stationQueue, capPerDep);
      stationQueue -= departed;
      totalDeparted += departed;
      waitHours     = 0;
    }

    if (stationQueue > 0 && departed === 0) {
      waitHours += 24;
    }

    let waitDOWPenaltyToday = 0;
    if (waitHours > criticalWaitHours) {
      const excessHours     = waitHours - criticalWaitHours;
      const excess24hBlocks = Math.floor(excessHours / 24);
      const rawPenalty      = stationQueue * excess24hBlocks * dowPenaltyPer24h;
      waitDOWPenaltyToday   = Math.min(rawPenalty, stationQueue * maxPenaltyCap);
      totalWaitDOWPenalty  += waitDOWPenaltyToday;
    }

    let backflowToday = 0;
    if (stationQueue > stationBeds) {
      backflowToday  = stationQueue - stationBeds;
      stationQueue   = stationBeds;
      totalBackflow += backflowToday;
    }

    const stationFraction = stationBeds > 0 ? stationQueue / stationBeds : 0;
    const stationStatus   = getStationStatus(stationFraction);

    if (stationStatus.label === 'MASCAL')  mascal_days++;
    if (stationStatus.label === 'WARNING') warning_days++;

    const nextDepartureDay = isOnDemand
      ? null
      : ([...departureDaySet].filter(fd => fd > dayNum).sort((a, b) => a - b)[0] ?? null);

    return {
      day:                dayNum,
      arrivals,
      departed,
      backflow:           backflowToday,
      station_queue:      stationQueue,
      station_beds:       stationBeds,
      station_fraction:   Math.round(stationFraction * 1000) / 1000,
      station_status:     stationStatus.label,
      station_color:      stationStatus.color,
      wait_hours:         waitHours,
      wait_critical:      waitHours > criticalWaitHours,
      wait_warning:       waitHours > warningWaitHours,
      wait_dow_penalty:   Math.round(waitDOWPenaltyToday * 100) / 100,
      is_departure_day:   isDepartureDay,
      on_demand_trigger:  onDemandTrigger,
      next_departure_day: nextDepartureDay,
      train_type:         trainType,
      trains_per_dep:     trainSchedule.trains_per_departure ?? 1,
      capacity_per_dep:   capPerDep
    };
  });

  const departureDaysSorted = isOnDemand
    ? timeline.filter(t => t.is_departure_day).map(t => t.day)
    : [...departureDaySet].sort((a, b) => a - b);

  return {
    enabled:                true,
    frequency_mode:         trainSchedule.frequency_mode,
    is_on_demand:           isOnDemand,
    train_type:             trainType,
    train_label:            trainSpec?.name ?? trainType,
    station_type:           stationType,
    station_label:          stationSpec?.label ?? stationType,
    station_beds:           stationBeds,
    capacity_per_departure: capPerDep,
    trains_per_departure:   trainSchedule.trains_per_departure ?? 1,
    departure_days:         departureDaysSorted,
    timeline,
    summary: {
      total_arrived:          dailyArrivals.reduce((s, v) => s + v, 0),
      total_departed:         totalDeparted,
      total_backflow:         totalBackflow,
      total_wait_dow_penalty: Math.round(totalWaitDOWPenalty * 100) / 100,
      mascal_days,
      warning_days,
      first_mascal_day:       timeline.find(t => t.station_status === 'MASCAL')?.day ?? null,
      first_warning_day:      timeline.find(t => t.station_status === 'WARNING')?.day ?? null,
      thresholds:             TRAIN_QUEUE_THRESHOLDS
    },
    doctrinal_note: 'Ukraine-derived capability. No NATO STANAG. National/theater authorization required.'
  };
}


/**
 * Get WBB configuration from scenario
 * PHASE 1.1: Extract Walking Blood Bank configuration
 * 
 * @param {Object} scenario - Full scenario object
 * @returns {Object} WBB configuration with defaults
 */
function getWBBConfiguration(scenario) {
  const bloodConfig = scenario?.medical_configuration?.blood_config;
  
  return {
    enabled: bloodConfig?.wbb_enabled ?? false,
    donors: bloodConfig?.wbb_donors ?? 0,
    lowTiterDonors: bloodConfig?.wbb_low_titer_donors ?? 0,
    activationThreshold: resolveInput(bloodConfig?.wbb_activation_threshold, 'wbb_activation_threshold_percent'), // % of stock
    eligibilityRate: requireNumber(WALKING_BLOOD_BANK?.planning?.eligibility_rate, 'casualtyCalculator.getWBBConfiguration', 'WALKING_BLOOD_BANK.planning.eligibility_rate'), // 65% of donors typically eligible
    unitsPerDonor: WALKING_BLOOD_BANK?.units_per_donor || 1, // Standard whole blood unit
    emergencyYield: requireNumber(WALKING_BLOOD_BANK?.planning?.emergency_yield, 'casualtyCalculator.getWBBConfiguration', 'WALKING_BLOOD_BANK.planning.emergency_yield') // 80% of eligible actually donate in emergency
  };
}

/**
 * Calculate WBB contribution to blood supply
 * PHASE 1.1: Determine if WBB should activate and how many units available
 * 
 * @param {Object} wbbConfig - WBB configuration from getWBBConfiguration
 * @param {number} currentStockPercent - Current stock as % of objective
 * @param {string} mascalLevel - Current MASCAL level (NORMAL/GREEN/YELLOW/ORANGE/RED/BLACK)
 * @returns {Object} WBB analysis with activation status and available units
 */
function calculateWBBContribution(wbbConfig, currentStockPercent, mascalLevel) {
  if (!wbbConfig.enabled) {
    return {
      activated: false,
      availableUnits: 0,
      universalUnits: 0,
      reason: 'WBB not enabled'
    };
  }
  
  // Check activation triggers
  const shouldActivate = 
    currentStockPercent <= wbbConfig.activationThreshold ||
    mascalLevel === 'RED' ||
    mascalLevel === 'BLACK';
  
  if (!shouldActivate) {
    const potentialUnits = Math.floor(wbbConfig.donors * wbbConfig.eligibilityRate * wbbConfig.unitsPerDonor);
    return {
      activated: false,
      availableUnits: 0,
      universalUnits: 0,
      potentialUnits,
      reason: `Stock at ${currentStockPercent}%, threshold is ${wbbConfig.activationThreshold}%`
    };
  }
  
  // Calculate available units
  const eligibleDonors = Math.floor(wbbConfig.donors * wbbConfig.eligibilityRate);
  const actualDonors = Math.floor(eligibleDonors * wbbConfig.emergencyYield);
  const availableUnits = actualDonors * wbbConfig.unitsPerDonor;
  
  // Low-titer O donors can donate to anyone
  const universalUnits = Math.floor(wbbConfig.lowTiterDonors * wbbConfig.eligibilityRate * wbbConfig.emergencyYield);
  
  return {
    activated: true,
    availableUnits,
    universalUnits,
    eligibleDonors,
    actualDonors,
    reason: currentStockPercent <= wbbConfig.activationThreshold 
      ? `Stock at ${Math.round(currentStockPercent)}% (threshold: ${wbbConfig.activationThreshold}%)`
      : `MASCAL ${mascalLevel} activation`
  };
}

/**
 * Apply WBB to blood calculations
 * PHASE 1.1: Reduce PRBC requirements by WBB contribution (whole blood = PRBC equivalent)
 * 
 * @param {Object} bloodRequirements - Calculated blood requirements from resourceModule
 * @param {Object} wbbAnalysis - WBB analysis from calculateWBBContribution
 * @returns {Object} Modified blood requirements with WBB applied
 */
function applyWBBToBloodCalculations(bloodRequirements, wbbAnalysis) {
  if (!wbbAnalysis.activated) {
    return {
      ...bloodRequirements,
      wbb_contribution: 0,
      wbb_universal_units: 0,
      wbb_status: 'STANDBY'
    };
  }
  
  // WBB provides whole blood (counts as PRBC equivalent)
  const wbbContribution = wbbAnalysis.availableUnits;
  const effectivePRBC = bloodRequirements.prbc_units - wbbContribution;
  
  return {
    ...bloodRequirements,
    prbc_units_original: bloodRequirements.prbc_units,
    prbc_units: Math.max(0, effectivePRBC),
    wbb_contribution: wbbContribution,
    wbb_universal_units: wbbAnalysis.universalUnits,
    wbb_status: 'ACTIVATED',
    wbb_activation_reason: wbbAnalysis.reason,
    wbb_eligible_donors: wbbAnalysis.eligibleDonors,
    wbb_actual_donors: wbbAnalysis.actualDonors
  };
}

// ============================================================================
// PHASE 2.1: EVACUATION ROUTES FUNCTIONS
// ============================================================================

// Route status constants
const ROUTE_STATUS = {
  OPEN: { label: 'Open', color: 'success', speed_modifier: 1.0, dow_modifier: 1.0 },
  DAMAGED: { label: 'Damaged', color: 'warning', speed_modifier: 0.6, dow_modifier: 1.1 },
  CONTESTED: { label: 'Contested', color: 'error', speed_modifier: 0.4, dow_modifier: 1.25 },
  CLOSED: { label: 'Closed', color: 'default', speed_modifier: 0, dow_modifier: null }
};

// Surface type constants
const SURFACE_TYPES = {
  PAVED: { label: 'Paved Road', base_speed_kph: 60 },
  GRAVEL: { label: 'Gravel Road', base_speed_kph: 40 },
  UNPAVED: { label: 'Unpaved/Dirt', base_speed_kph: 25 },
  TRAIL: { label: 'Trail/Track', base_speed_kph: 15 }
};

// Weather impact constants
const WEATHER_IMPACT = {
  LOW: { label: 'Minimal', speed_modifier: 1.0 },
  MEDIUM: { label: 'Moderate', speed_modifier: 0.8 },
  HIGH: { label: 'Severe', speed_modifier: 0.5 }
};

/**
 * Get default route segments when not configured
 * PHASE 2.1: Provide NATO standard route assumptions
 * 
 * @returns {Object} Default route segments
 */
function getDefaultRouteSegments() {
  return {
    r1_to_r2b: {
      name: 'R1â†’R2B',
      primary: {
        name: 'MSR Alpha',
        distance_km: 12,
        surface: 'PAVED',
        status: 'OPEN',
        weather_impact: 'LOW'
      },
      alternate: null,
      effective: null
    },
    r2b_to_r2e: {
      name: 'R2Bâ†’R2E',
      primary: {
        name: '',
        distance_km: 30,
        surface: 'PAVED',
        status: 'OPEN',
        weather_impact: 'LOW'
      },
      alternate: null,
      effective: null
    },
    r2e_to_r3: {
      name: 'R2Eâ†’R3',
      primary: {
        name: '',
        distance_km: 70,
        surface: 'PAVED',
        status: 'OPEN',
        weather_impact: 'LOW'
      },
      alternate: null,
      effective: null
    },
    r3_to_r4: {
      name: 'R3â†’R4',
      primary: {
        name: '',
        distance_km: 500,
        surface: 'PAVED',
        status: 'OPEN',
        weather_impact: 'LOW'
      },
      alternate: null,
      effective: null
    }
  };
}

/**
 * Calculate metrics for a single route
 * PHASE 2.1: Compute travel time and DOW modifier based on route conditions
 * 
 * @param {Object} route - Route configuration
 * @returns {Object} Route with calculated metrics
 */
function calculateRouteMetrics(route) {
  const surface = SURFACE_TYPES[route.surface] || SURFACE_TYPES.PAVED;
  const status = ROUTE_STATUS[route.status] || ROUTE_STATUS.OPEN;
  const weather = WEATHER_IMPACT[route.weather_impact] || WEATHER_IMPACT.LOW;
  
  // Effective speed = base speed Ã— status modifier Ã— weather modifier
  const effectiveSpeedKph = surface.base_speed_kph * status.speed_modifier * weather.speed_modifier;
  
  // Travel time in minutes
  const travelTimeMin = effectiveSpeedKph > 0 
    ? Math.round((route.distance_km / effectiveSpeedKph) * 60)
    : Infinity;
  
  return {
    ...route,
    base_speed_kph: surface.base_speed_kph,
    effective_speed_kph: effectiveSpeedKph,
    travel_time_min: travelTimeMin,
    dow_modifier: status.dow_modifier,
    status_label: status.label,
    using_alternate: false
  };
}

/**
 * Leg (distance, surface, speed modifier) that a facility's ambulances serve, from the Tab 3 evacuation routes.
 * The effective route (primary, else alternate) is used; with routes not configured the default segment's primary
 * route applies. A closed leg has speedModifier 0, so its ground capacity is 0.
 *
 * @param {Object} routesConfig - from getEvacuationRoutesConfiguration()
 * @param {string} facilityType - ROLE1 | ROLE2_BASIC | ROLE2_ENHANCED | ROLE3
 */
function resolveEvacLeg(routesConfig, facilityType) {
  if (!routesConfig || !routesConfig.segments) {
    throw new Error('casualtyCalculator.resolveEvacLeg: routesConfig (evacuation routes) is required to compute ground capacity');
  }
  const legKey = FACILITY_LEG[facilityType];
  if (!legKey) {
    throw new Error(`casualtyCalculator.resolveEvacLeg: no evacuation leg for facility type '${facilityType}' (valid: ${Object.keys(FACILITY_LEG).filter((k) => k !== 'status' && k !== 'basis').join(', ')})`);
  }
  const segment = routesConfig.segments[legKey];
  if (!segment) {
    throw new Error(`casualtyCalculator.resolveEvacLeg: evacuation routes have no segment '${legKey}'`);
  }
  if (segment.both_closed) {
    return { legKey, distanceKm: segment.primary?.distance_km ?? 0, surface: segment.primary?.surface ?? 'PAVED', speedModifier: 0 };
  }
  const route = segment.effective || (segment.primary ? calculateRouteMetrics(segment.primary) : null);
  if (!route) {
    throw new Error(`casualtyCalculator.resolveEvacLeg: segment '${legKey}' has no route`);
  }
  const base = SURFACE_TYPES[route.surface] || SURFACE_TYPES.PAVED;
  const speedModifier = base.base_speed_kph > 0 ? (route.effective_speed_kph ?? base.base_speed_kph) / base.base_speed_kph : 0;
  return { legKey, distanceKm: route.distance_km, surface: SURFACE_TYPES[route.surface] ? route.surface : 'PAVED', speedModifier };
}

/**
 * Process a single route segment
 * PHASE 2.1: Determine effective route (primary or alternate)
 * 
 * @param {Object} segment - Route segment with primary/alternate
 * @param {string} name - Segment display name
 * @returns {Object} Processed segment with effective route
 */
function processRouteSegment(segment, name) {
  if (!segment) {
    return { name, primary: null, alternate: null, effective: null };
  }
  
  const primary = segment.primary ? calculateRouteMetrics(segment.primary) : null;
  const alternate = segment.alternate ? calculateRouteMetrics(segment.alternate) : null;
  
  // Determine effective route (primary if not CLOSED, else alternate, else null)
  let effective = null;
  if (primary && primary.status !== 'CLOSED') {
    effective = primary;
  } else if (alternate && alternate.status !== 'CLOSED') {
    effective = { ...alternate, using_alternate: true };
  }
  
  return {
    name,
    primary,
    alternate,
    effective,
    both_closed: !effective && (primary || alternate),
    no_alternate_warning: primary?.status === 'CLOSED' && !alternate
  };
}

/**
 * Extract evacuation routes configuration from scenario
 * PHASE 2.1: Get configured routes or use defaults
 * 
 * @param {Object} scenario - Full scenario object
 * @returns {Object} Routes configuration with calculated travel times
 */
function getEvacuationRoutesConfiguration(scenario) {
  const routes = scenario?.medical_configuration?.evacuation_routes;
  
  if (!routes) {
    // Return default routes if not configured
    return {
      configured: false,
      segments: getDefaultRouteSegments()
    };
  }
  
  return {
    configured: true,
    segments: {
      r1_to_r2b: processRouteSegment(routes.r1_to_r2b, 'R1â†’R2B'),
      r2b_to_r2e: processRouteSegment(routes.r2b_to_r2e, 'R2Bâ†’R2E'),
      r2e_to_r3: processRouteSegment(routes.r2e_to_r3, 'R2Eâ†’R3'),
      r3_to_r4: processRouteSegment(routes.r3_to_r4, 'R3â†’R4')
    }
  };
}

/**
 * Calculate how route conditions affect evacuation and DOW
 * PHASE 2.1: Analyze route impact on Golden Hour and casualty outcomes
 * 
 * @param {Object} routesConfig - Routes configuration from getEvacuationRoutesConfiguration()
 * @param {Object} options - Additional options (weather, threat level)
 * @returns {Object} Route impact analysis
 */
function calculateRouteImpactOnEvacuation(routesConfig, options = {}) {
  if (!routesConfig.configured) {
    return {
      configured: false,
      total_travel_time_min: 120, // Default assumption
      golden_hour_achievable: true,
      dow_modifier: 1.0,
      warnings: []
    };
  }
  
  const segments = routesConfig.segments;
  const warnings = [];
  let totalTravelTime = 0;
  let combinedDOWModifier = 1.0;
  let routesClosed = 0;
  let usingAlternate = 0;
  
  // Process each segment
  for (const segment of Object.values(segments)) {
    if (segment.both_closed) {
      warnings.push(`CRITICAL: ${segment.name} - All routes CLOSED`);
      routesClosed++;
      totalTravelTime = Infinity;
    } else if (segment.effective) {
      totalTravelTime += segment.effective.travel_time_min;
      combinedDOWModifier *= segment.effective.dow_modifier || 1.0;
      
      if (segment.effective.using_alternate) {
        const timeDiff = segment.effective.travel_time_min - (segment.primary?.travel_time_min || 0);
        warnings.push(`${segment.name}: Using alternate route (+${timeDiff} min)`);
        usingAlternate++;
      }
      
      if (segment.effective.status === 'CONTESTED') {
        warnings.push(`${segment.name}: Route CONTESTED - increased travel time and DOW risk`);
      }
      
      if (segment.no_alternate_warning) {
        warnings.push(`${segment.name}: No alternate route configured - single point of failure`);
      }
    }
  }
  
  // Golden Hour analysis (POI to Surgery should be â‰¤60 min)
  const r1ToR2bTime = segments.r1_to_r2b?.effective?.travel_time_min || 30;
  const goldenHourAchievable = r1ToR2bTime <= 45; // Allow 15 min for other activities
  
  if (!goldenHourAchievable) {
    warnings.push(`Golden Hour at risk: R1â†’R2B travel time ${r1ToR2bTime} min exceeds 45 min threshold`);
  }
  
  return {
    configured: true,
    segments_summary: {
      r1_to_r2b: segments.r1_to_r2b?.effective?.travel_time_min || null,
      r2b_to_r2e: segments.r2b_to_r2e?.effective?.travel_time_min || null,
      r2e_to_r3: segments.r2e_to_r3?.effective?.travel_time_min || null,
      r3_to_r4: segments.r3_to_r4?.effective?.travel_time_min || null
    },
    total_travel_time_min: totalTravelTime,
    total_travel_time_hours: Math.round(totalTravelTime / 60 * 10) / 10,
    golden_hour_achievable: goldenHourAchievable,
    golden_hour_margin_min: 45 - r1ToR2bTime,
    dow_modifier: combinedDOWModifier,
    routes_closed: routesClosed,
    using_alternate_count: usingAlternate,
    warnings,
    evacuation_possible: routesClosed === 0
  };
}

// ============================================================================
// PHASE 4.1: RESUPPLY CALCULATOR INTEGRATION
// ============================================================================

/**
 * Extract resupply events from scenario and calculate units
 * CRITICAL: Uses PUSH_PACKAGES from classVIII.js - NO HARDCODED VALUES
 * 
 * @param {Object} scenario - Full scenario object
 * @returns {Object} Resupply configuration with calculated units
 */
function getResupplyConfiguration(scenario) {
  // Extract resupply events from Tab 4
  const resupplyEvents = scenario?.logistics?.medical_supplies?.resupply_events || [];
  
  if (resupplyEvents.length === 0) {
    return {
      configured: false,
      events: [],
      by_day: {},
      total_units: { prbc: 0, ffp: 0, plt: 0, cryo: 0 }
    };
  }
  
  // Calculate units for each event.
  // BUG-146 FIX (v5.42.0): calculateResupplyUnits() only does a PUSH_PACKAGES catalog
  // lookup and returns all-zero when the package key is custom, blank, or absent.
  // supplyChainModule.js format-4 path reads event.supplies.blood_products directly —
  // that drives the actual stock simulation correctly.
  // Mirror that logic here so resupply_summary.events_by_day[].units matches the
  // simulation values shown in Tab 6 renderTab6ResupplyAnalysis.
  // Priority: (1) PUSH_PACKAGES catalog, (2) event.supplies.blood_products, (3) zero.
  const eventsWithUnits = resupplyEvents.map(event => {
    const modifier = event.quantity_modifier ?? 1.0;

    // BUG-TAB4-002 FIX: Drone events have no push_package — resolve via DRONE_DELIVERY_SPECS
    // to mirror the logic in supplyChainModule.js buildDailySimulation() DRONE branch.
    if (event.delivery_method === 'DRONE') {
      const droneType = event.drone_type || 'CARGO_DRONE';
      const spec = DRONE_DELIVERY_SPECS?.[droneType] ?? DRONE_DELIVERY_SPECS?.CARGO_DRONE ?? {};
      const adThreat = (event.ad_threat_override || 'MEDIUM').toUpperCase();
      const availability = spec.ad_threat_availability?.[adThreat]
                        ?? spec.ad_threat_availability?.MEDIUM
                        ?? 0.75;
      const bloodQty = spec.cold_chain_viable && (spec.blood_units_per_flight > 0)
        ? Math.round(spec.blood_units_per_flight * availability * 0.95)
        : 0;
      return { ...event, units: { prbc: bloodQty, ffp: 0, plt: 0, cryo: 0 } };
    }

    // BUG-TAB4-001 FIX: prefer event.supplies.blood_products (user-configured values) over
    // the catalog when the user has explicitly set blood product quantities.
    // The catalog only wins when supplies.blood_products is absent (auto-generated events).
    const bp = event.supplies?.blood_products;
    if (bp != null) {
      // User-configured event — use their explicit values, scaled by modifier
      return {
        ...event,
        units: {
          prbc: Math.round((bp.prbc_units      || 0) * modifier),
          ffp:  Math.round((bp.ffp_units        || 0) * modifier),
          plt:  Math.round((bp.platelets_units  || 0) * modifier),
          cryo: Math.round((bp.cryo_units       || 0) * modifier),
        }
      };
    }

    // Auto-generated / catalog event — fall back to PUSH_PACKAGES catalog
    const catalogUnits = calculateResupplyUnits(event.push_package, modifier);
    return { ...event, units: catalogUnits };
  });
  
  // Group by day for easy lookup
  const byDay = {};
  for (const event of eventsWithUnits) {
    if (!byDay[event.day]) {
      byDay[event.day] = [];
    }
    byDay[event.day].push(event);
  }
  
  // Calculate total units across all events
  const totalUnits = eventsWithUnits.reduce((total, event) => ({
    prbc: total.prbc + (event.units.prbc || 0),
    ffp: total.ffp + (event.units.ffp || 0),
    plt: total.plt + (event.units.plt || 0),
    cryo: total.cryo + (event.units.cryo || 0)
  }), { prbc: 0, ffp: 0, plt: 0, cryo: 0 });
  
  return {
    configured: true,
    events: eventsWithUnits,
    by_day: byDay,
    total_units: totalUnits
  };
}

/**
 * Calculate blood product units from push package code
 * USES PUSH_PACKAGES from classVIII.js - NO HARDCODED VALUES
 * 
 * @param {string} packageType - Package code (e.g. 'EMERGENCY_BLOOD')
 * @param {number} modifier - Quantity multiplier (default 1.0)
 * @returns {Object} Blood product units { prbc, ffp, plt, cryo }
 */
function calculateResupplyUnits(packageType, modifier = 1.0) {
  // CRITICAL: Import PUSH_PACKAGES from classVIII.js at top of file
  const pkg = PUSH_PACKAGES[packageType];
  
  if (!pkg || !pkg.contents?.blood_products) {
    return { prbc: 0, ffp: 0, plt: 0, cryo: 0 };
  }
  
  const bloodProducts = pkg.contents.blood_products;
  
  return {
    prbc: Math.round((bloodProducts.prbc_units || 0) * modifier),
    ffp: Math.round((bloodProducts.ffp_units || 0) * modifier),
    plt: Math.round((bloodProducts.platelets_units || 0) * modifier),
    cryo: Math.round((bloodProducts.cryo_units || 0) * modifier)
  };
}

// ============================================================================
// PHASE 4: MARCH/MOVEMENT HELPER FUNCTIONS
// ============================================================================

/**
 * PHASE 4 TASK 4.1: Check if posture is a march/movement type
 * 
 * @param {string} posture - Operational posture
 * @returns {boolean} True if march/movement posture
 */
function isMarchPosture(posture) {
  if (!posture || typeof posture !== 'string') {
    return false;
  }
  
  const upperPosture = posture.toUpperCase();
  return upperPosture.includes('MARCH') || 
         upperPosture.includes('TRANSIT') || 
         upperPosture.includes('DISPLACEMENT');
}

/**
 * PHASE 4 TASK 4.3: Get march battle casualty rate
 * 
 * March operations have VERY LOW battle casualty rates
 * but units are vulnerable to ambush, mines, air attack
 * 
 * @param {string} posture - March posture type
 * @returns {number} Daily casualty rate (fraction, e.g., 0.001 = 0.1%)
 */
function getMarchBattleRate(posture) {
  const upperPosture = posture?.toUpperCase() || '';
  
  // MARCH_TACTICAL: Combat-ready movement (0.1% per day)
  if (upperPosture.includes('MARCH_TACTICAL') || (upperPosture.includes('MARCH') && upperPosture.includes('TACTICAL'))) {
    return 0.001;
  }
  
  // MARCH_ADMINISTRATIVE: Peacetime movement (0.01% per day)
  if (upperPosture.includes('MARCH_ADMINISTRATIVE') || (upperPosture.includes('MARCH') && upperPosture.includes('ADMINISTRATIVE'))) {
    return 0.0001;
  }
  
  // TRANSIT: Between theaters (0.005% per day)
  if (upperPosture.includes('TRANSIT')) {
    return 0.00005;
  }
  
  // DISPLACEMENT: Repositioning (0.05% per day)
  if (upperPosture.includes('DISPLACEMENT')) {
    return 0.0005;
  }
  
  // Generic MARCH: Default to tactical march rate
  if (upperPosture.includes('MARCH')) {
    return 0.001;
  }
  
  // Not a march posture
  return null;
}

/**
 * PHASE 4 TASK 4.3: Get march DNBI rate
 * 
 * Movement INCREASES DNBI due to:
 * - Cramped conditions
 * - Fatigue from travel
 * - Hygiene challenges
 * - Disrupted rest schedules
 * 
 * @param {string} posture - March posture type
 * @returns {number} DNBI rate per 1000 per day (e.g., 30 = 3%)
 */
function getMarchDNBIRate(posture, operationalState) {
  const upperPosture = posture?.toUpperCase() || '';
  // 2026-10-13: tied to the normal DNBI rate of the operational state (was 30 / 25 per 1,000 per day, about six times a
  // combat-engaged brigade); see MARCH_DNBI_FACTOR in constants/dnbiRates.js.
  if (upperPosture.includes('TRANSIT')) return getDNBIRate(operationalState) * MARCH_DNBI_FACTOR.transit;
  if (upperPosture.includes('MARCH') || upperPosture.includes('DISPLACEMENT')) return getDNBIRate(operationalState) * MARCH_DNBI_FACTOR.march;
  // Not a march posture
  return null;
}

// ============================================================================
// SECTION 6: UI SUPPORT - HELPER FUNCTIONS & METADATA
// ============================================================================

/**
 * UI Color Scheme - Consistent colors throughout application
 */
const UI_COLOR_SCHEME = {
  operational_states: {
    COMBAT_ENGAGED: '#d32f2f',     // Red
    SUPPORT_ACTIVE: '#ffa726',     // Orange/Yellow
    TRUE_RESERVE: '#66bb6a'        // Green
  },
  
  posture_categories: {
    ATTACK: '#d32f2f',             // Red
    DEFENSE: '#2196f3',            // Blue
    MARCH: '#9e9e9e',              // Grey
    CONSOLIDATION: '#66bb6a'       // Green
  },
  
  mission_roles: {
    MAIN_EFFORT: '#d32f2f',        // Red
    SUPPORTING: '#2196f3',         // Blue
    RESERVE: '#66bb6a',            // Green
    ECONOMY_OF_FORCE: '#ffa726',   // Orange
    EXPLOITATION: '#9c27b0'        // Purple
  },
  
  severity: {
    LOW: '#66bb6a',                // Green (< 1.5x)
    MODERATE: '#ffa726',           // Orange (1.5-2.0x)
    HIGH: '#ff5722',               // Deep Orange (2.0-2.5x)
    VERY_HIGH: '#d32f2f',          // Red (> 2.5x)
    CRITICAL: '#b71c1c'            // Dark Red (> 3.0x)
  }
};

/**
 * SECTION 6: Get role badge metadata for UI display
 * 
 * @param {string} missionRole - Mission role (MAIN_EFFORT, SUPPORTING, etc)
 * @param {number} missionMultiplier - Casualty multiplier (1.5, 1.0, etc)
 * @returns {Object} Badge metadata with text, color, label
 */
function getRoleBadge(missionRole, missionMultiplier) {
  const badges = {
    MAIN_EFFORT: { text: 'ME', label: 'Main Effort', color: UI_COLOR_SCHEME.mission_roles.MAIN_EFFORT },
    SUPPORTING: { text: 'SE', label: 'Supporting Effort', color: UI_COLOR_SCHEME.mission_roles.SUPPORTING },
    RESERVE: { text: 'RES', label: 'Reserve', color: UI_COLOR_SCHEME.mission_roles.RESERVE },
    ECONOMY_OF_FORCE: { text: 'EF', label: 'Economy of Force', color: UI_COLOR_SCHEME.mission_roles.ECONOMY_OF_FORCE },
    EXPLOITATION: { text: 'EXP', label: 'Exploitation', color: UI_COLOR_SCHEME.mission_roles.EXPLOITATION }
  };
  
  const badge = badges[missionRole] || { text: 'SE', label: 'Supporting', color: UI_COLOR_SCHEME.mission_roles.SUPPORTING };
  
  return {
    ...badge,
    multiplier: missionMultiplier,
    tooltip: `${badge.label} (${missionMultiplier}x multiplier)`
  };
}

/**
 * SECTION 6: Get operational state badge metadata
 * 
 * @param {string} operationalState - Operational state
 * @returns {Object} State badge with text, color, short form
 */
function getStateBadge(operationalState) {
  const states = {
    COMBAT_ENGAGED: { 
      text: 'COMBAT_ENGAGED', 
      short: 'COMBAT', 
      color: UI_COLOR_SCHEME.operational_states.COMBAT_ENGAGED,
      tooltip: 'Actively engaged in combat operations'
    },
    SUPPORT_ACTIVE: { 
      text: 'SUPPORT_ACTIVE', 
      short: 'SUPPORT', 
      color: UI_COLOR_SCHEME.operational_states.SUPPORT_ACTIVE,
      tooltip: 'Support operations, not in direct combat'
    },
    TRUE_RESERVE: { 
      text: 'TRUE_RESERVE', 
      short: 'RESERVE', 
      color: UI_COLOR_SCHEME.operational_states.TRUE_RESERVE,
      tooltip: 'Held in reserve, minimal activity'
    }
  };
  
  return states[operationalState] || states.COMBAT_ENGAGED;
}

/**
 * SECTION 6: Get phase indicator metadata
 * 
 * @param {string} phaseSource - BRIGADE, DIVISION, or CORPS
 * @param {number} phaseCount - Number of phases
 * @returns {Object} Phase indicator with icon, tooltip
 */
function getPhaseIndicator(phaseSource, phaseCount) {
  const sourceIcons = {
    BRIGADE:  GENERAL_ICONS.GEAR,          // Custom phases
    DIVISION: ARROW_ICONS.DOWN,            // Inherited from division
    CORPS:    ARROW_ICONS.DOUBLE_DOWN      // Inherited from corps (⇊)
  };
  
  const sourceLabels = {
    BRIGADE: 'custom',
    DIVISION: 'inherited from Division',
    CORPS: 'inherited from Corps'
  };
  
  return {
    icon: `${phaseCount}${sourceIcons[phaseSource] || GENERAL_ICONS.GEAR}`,
    source: phaseSource,
    count: phaseCount,
    tooltip: `${phaseCount} phase${phaseCount > 1 ? 's' : ''} ${sourceLabels[phaseSource] || 'custom'}`
  };
}

/**
 * SECTION 6: Get casualty multiplier severity classification
 * 
 * @param {number} multiplier - Combined casualty multiplier
 * @returns {Object} Severity with level, color, label
 */
function getCasualtyMultiplierSeverity(multiplier) {
  let level, color, label;
  
  if (multiplier < 1.5) {
    level = 'LOW';
    color = UI_COLOR_SCHEME.severity.LOW;
    label = 'Low casualties expected';
  } else if (multiplier < 2.0) {
    level = 'MODERATE';
    color = UI_COLOR_SCHEME.severity.MODERATE;
    label = 'Moderate casualties expected';
  } else if (multiplier < 2.5) {
    level = 'HIGH';
    color = UI_COLOR_SCHEME.severity.HIGH;
    label = 'High casualties expected';
  } else if (multiplier < 3.0) {
    level = 'VERY_HIGH';
    color = UI_COLOR_SCHEME.severity.VERY_HIGH;
    label = 'Very high casualties expected';
  } else {
    level = 'CRITICAL';
    color = UI_COLOR_SCHEME.severity.CRITICAL;
    label = 'Critical casualty rates';
  }
  
  return { level, color, label, multiplier };
}

/**
 * SECTION 6: Get posture color based on category
 * 
 * @param {string} posture - Operational posture
 * @returns {string} Hex color code
 */
function getPostureColor(posture) {
  if (!posture) return UI_COLOR_SCHEME.posture_categories.DEFENSE;
  
  const upper = posture.toUpperCase();
  
  if (upper.includes('ATTACK') || upper.includes('ASSAULT') || upper.includes('EXPLOIT')) {
    return UI_COLOR_SCHEME.posture_categories.ATTACK;
  } else if (upper.includes('MARCH') || upper.includes('TRANSIT') || upper.includes('DISPLACEMENT')) {
    return UI_COLOR_SCHEME.posture_categories.MARCH;
  } else if (upper.includes('CONSOL')) {
    return UI_COLOR_SCHEME.posture_categories.CONSOLIDATION;
  } else {
    return UI_COLOR_SCHEME.posture_categories.DEFENSE;
  }
}

// ============================================================================
// SECTION 5: VALIDATION & WARNING FUNCTIONS
// ============================================================================

/**
 * SECTION 5 VALIDATION 1: Detect all units moving simultaneously
 * 
 * Critical problem: If all brigades in MARCH on same day, NO medical facilities operational
 * 
 * @param {Array} brigadeResults - Array of brigade calculation results
 * @param {number} totalDays - Total operation duration
 * @returns {Array} Array of critical warnings (one per day where all units marching)
 */
function detectAllUnitsMarching(brigadeResults, totalDays) {
  const warnings = [];
  
  for (let day = 1; day <= totalDays; day++) {
    let marchingCount = 0;
    let totalBrigades = 0;
    
    for (const brigade of brigadeResults) {
      const dayData = brigade.by_day.find(d => d.day === day);
      if (dayData && dayData.is_combat_effective !== false) {
        totalBrigades++;
        if (dayData.is_march_operation) {
          marchingCount++;
        }
      }
    }
    
    // If ALL effective brigades are marching on this day
    if (totalBrigades > 0 && marchingCount === totalBrigades) {
      warnings.push({
        level: 'CRITICAL',
        type: 'ALL_UNITS_MARCHING',
        day: day,
        brigades_marching: marchingCount,
        message: `Day ${day}: ALL units in movement - NO medical treatment facilities operational`,
        details: 'All Role 1, Role 2, and Role 3 facilities packed for movement. Only ambulances available.',
        impact: 'All casualties MUST be evacuated to higher echelon. Massive strain on evacuation system.',
        recommendations: [
          'Stagger movement (units move on different days)',
          'Designate at least one brigade to remain static for medical support',
          'Accept risk if emergency withdrawal situation',
          'Ensure robust ambulance/MEDEVAC availability',
          'Pre-position medical support at destination'
        ]
      });
    }
  }
  
  return warnings;
}

/**
 * SECTION 5 VALIDATION 2: Validate medical brigade coverage
 * 
 * Large forces need dedicated Medical Brigade for Role 2E/Role 3 support
 * 
 * @param {Object} force - Force configuration
 * @param {Array} brigadeConfigs - Array of brigade configurations
 * @returns {Object|null} Warning object if validation fails
 */
function validateMedicalBrigadeCoverage(force, brigadeConfigs) {
  const totalStrength = force?.forceBuilderResult?.total_strength || 
                        force?.total_strength || 0;
  
  // BUG-084 FIX: Medical Brigade is excluded from force.brigades by ForceService
  // (it goes into a separate medicalBrigades array, not the combat brigades array).
  // brigadeConfigs therefore never contains unit_type === 'MEDICAL', causing a false
  // NO_MEDICAL_BRIGADE warning even when a Medical Brigade is present.
  // Primary check: use ForceService flags set at division and corps level.
  // Secondary check: iterate brigadeConfigs (catches edge cases and legacy structures).
  const fbResult = force?.forceBuilderResult;
  const hasMedicalBrigade =
    fbResult?.has_medical_brigade === true ||
    fbResult?.has_medical_brigades === true ||
    (Array.isArray(fbResult?.medical_brigades) && fbResult.medical_brigades.length > 0) ||
    brigadeConfigs.some(b =>
      b.unit_type === 'MEDICAL' ||
      b.type === 'MEDICAL' ||
      b.is_medical_brigade === true ||
      (b.brigade_name && b.brigade_name.toLowerCase().includes('medical')) ||
      (b.name && b.name.toLowerCase().includes('medical'))
    );
  
  // Threshold: 9000 troops = 2 brigades
  if (totalStrength > 9000 && !hasMedicalBrigade) {
    return {
      level: 'WARNING',
      type: 'NO_MEDICAL_BRIGADE',
      force_size: totalStrength,
      message: `Large force (${totalStrength.toLocaleString()} troops) with no Medical Brigade`,
      details: 'Combat brigades provide only Role 1 (aid station) and Role 2B (basic surgery)',
      gaps: [
        'No Role 2E (enhanced surgery with specialty capabilities)',
        'No Role 3 (combat support hospital)',
        'Limited blood banking capability',
        'No specialty surgical teams (neuro, ortho, maxillofacial)',
        'Reduced forward resuscitation capacity'
      ],
      impact: 'Higher DOW rates due to limited surgical capability. Longer evacuation chains.',
      recommendation: 'Consider adding Medical Brigade for comprehensive medical support'
    };
  }
  
  return null;
}

/**
 * SECTION 5 VALIDATION 4: Validate phase configuration
 * 
 * Detect gaps, overlaps, and incomplete phase coverage
 * 
 * @param {Array} phases - Array of phase configurations
 * @param {number} totalDuration - Total operation duration in days
 * @returns {Array} Array of warning objects
 */
function validatePhaseConfiguration(phases, totalDuration) {
  const warnings = [];
  
  if (!phases || phases.length === 0) {
    return warnings;  // Single phase operation, no validation needed
  }
  
  let currentDay = 1;
  
  for (let i = 0; i < phases.length; i++) {
    const phase = phases[i];
    const phaseDuration = phase.duration_days || 0;
    const phaseStart = phase.start_day || currentDay;
    const phaseEnd = phaseStart + phaseDuration - 1;
    
    // Check for gap before this phase
    if (phaseStart > currentDay) {
      const gapStart = currentDay;
      const gapEnd = phaseStart - 1;
      const gapDays = gapEnd - gapStart + 1;
      
      warnings.push({
        level: 'WARNING',
        type: 'PHASE_GAP',
        gap_start: gapStart,
        gap_end: gapEnd,
        gap_days: gapDays,
        before_phase: phase.name || `Phase ${i + 1}`,
        message: `Phase gap: Days ${gapStart}-${gapEnd} (${gapDays} day${gapDays > 1 ? 's' : ''}) have no defined phase`,
        impact: 'Default phase parameters will be used for gap period',
        recommendation: 'Adjust phase start days to eliminate gap or add transition phase'
      });
    }
    
    // Check for overlap with next phase
    if (i < phases.length - 1) {
      const nextPhase = phases[i + 1];
      const nextStart = nextPhase.start_day || (phaseEnd + 1);
      
      if (phaseEnd >= nextStart) {
        const overlapDays = phaseEnd - nextStart + 1;
        
        warnings.push({
          level: 'WARNING',
          type: 'PHASE_OVERLAP',
          phase1: phase.name || `Phase ${i + 1}`,
          phase2: nextPhase.name || `Phase ${i + 2}`,
          overlap_days: overlapDays,
          message: `Phase overlap: "${phase.name || 'Phase ' + (i + 1)}" extends ${overlapDays} day${overlapDays > 1 ? 's' : ''} into "${nextPhase.name || 'Phase ' + (i + 2)}"`,
          impact: 'Ambiguous phase assignment for overlapping days',
          recommendation: 'Adjust phase durations to eliminate overlap'
        });
      }
    }
    
    currentDay = phaseEnd + 1;
  }
  
  // Check if phases cover full operation duration
  const lastPhase = phases[phases.length - 1];
  const lastPhaseEnd = (lastPhase.start_day || 1) + (lastPhase.duration_days || 0) - 1;
  
  if (lastPhaseEnd < totalDuration) {
    const uncoveredDays = totalDuration - lastPhaseEnd;
    
    warnings.push({
      level: 'WARNING',
      type: 'INCOMPLETE_PHASE_COVERAGE',
      covered_days: lastPhaseEnd,
      total_days: totalDuration,
      uncovered_days: uncoveredDays,
      message: `Incomplete phase coverage: Phases cover ${lastPhaseEnd} of ${totalDuration} days (${uncoveredDays} day${uncoveredDays > 1 ? 's' : ''} uncovered)`,
      impact: 'Final days will use last phase parameters',
      recommendation: 'Extend final phase or add consolidation phase to cover full operation'
    });
  }
  
  return warnings;
}

/**
 * Apply resupply events to blood stock for a specific day
 * 
 * @param {Object} currentStock - Current blood stock levels
 * @param {Array} todayResupplies - Resupply events for today
 * @returns {Object} Updated stock after resupply
 */
function applyResupplyToBloodStock(currentStock, todayResupplies) {
  if (!todayResupplies || todayResupplies.length === 0) {
    return {
      ...currentStock,
      resupply_received: false,
      units_received: { prbc: 0, ffp: 0, plt: 0, cryo: 0 }
    };
  }
  
  let totalReceived = { prbc: 0, ffp: 0, plt: 0, cryo: 0 };
  
  for (const resupply of todayResupplies) {
    totalReceived.prbc += resupply.units?.prbc || 0;
    totalReceived.ffp += resupply.units?.ffp || 0;
    totalReceived.plt += resupply.units?.plt || 0;
    totalReceived.cryo += resupply.units?.cryo || 0;
  }
  
  return {
    prbc: (currentStock.prbc || 0) + totalReceived.prbc,
    ffp: (currentStock.ffp || 0) + totalReceived.ffp,
    plt: (currentStock.plt || 0) + totalReceived.plt,
    cryo: (currentStock.cryo || 0) + totalReceived.cryo,
    resupply_received: true,
    units_received: totalReceived,
    resupply_count: todayResupplies.length
  };
}

/**
 * Calculate DOS projection over operation with resupply
 * 
 * @param {Object} initialStock - Starting blood stock
 * @param {Object} dailyConsumption - Daily consumption rate
 * @param {Object} resupplyConfig - Resupply configuration
 * @param {number} duration - Operation duration in days
 * @returns {Object} DOS projection by day
 */
function calculateDOSProjection(initialStock, dailyConsumption, resupplyConfig, duration) {
  const projection = [];
  let currentStock = { 
    prbc: initialStock.prbc || 0,
    ffp: initialStock.ffp || 0,
    plt: initialStock.plt || 0,
    cryo: initialStock.cryo || 0
  };
  
  for (let day = 1; day <= duration; day++) {
    // Apply resupply for today
    const todayResupply = resupplyConfig.by_day[day] || [];
    const stockAfterResupply = applyResupplyToBloodStock(currentStock, todayResupply);
    
    // Calculate DOS before consumption
    const dailyPRBCConsumption = dailyConsumption.prbc || 0;
    const dosBeforeConsumption = dailyPRBCConsumption > 0 
      ? stockAfterResupply.prbc / dailyPRBCConsumption 
      : 999;
    
    // Apply consumption
    currentStock = {
      prbc: Math.max(0, stockAfterResupply.prbc - dailyPRBCConsumption),
      ffp: Math.max(0, stockAfterResupply.ffp - (dailyConsumption.ffp || 0)),
      plt: Math.max(0, stockAfterResupply.plt - (dailyConsumption.plt || 0)),
      cryo: Math.max(0, stockAfterResupply.cryo - (dailyConsumption.cryo || 0))
    };
    
    // Calculate DOS after consumption
    const dosAfterConsumption = dailyPRBCConsumption > 0 
      ? currentStock.prbc / dailyPRBCConsumption 
      : 999;
    
    projection.push({
      day,
      stock_start: Math.round(dosBeforeConsumption * 10) / 10,
      stock_end: Math.round(dosAfterConsumption * 10) / 10,
      resupply_received: stockAfterResupply.resupply_received || false,
      units_received: stockAfterResupply.units_received || { prbc: 0, ffp: 0, plt: 0, cryo: 0 },
      stockout: currentStock.prbc <= 0
    });
  }
  
  // Find first stockout day
  const stockoutIndex = projection.findIndex(p => p.stockout);
  
  return {
    projection,
    stockout_day: stockoutIndex >= 0 ? stockoutIndex + 1 : null,
    minimum_dos: Math.min(...projection.map(p => p.stock_end)),
    final_dos: projection[projection.length - 1]?.stock_end || 0
  };
}

// 2026-10-13 (Tab 2 plan 7.6): the brigade support battalion is inside brigade.strength and already counted as rear battalion casualties of the
// brigade; the corps totals added it a second time through the support pool (about +10 percent). false = counted once.
const SUPPORT_POOL_COUNTS_BRIGADE_SUPPORT = false;

// 2026-10-14 (Dr. Tatar): the patients still in the medical system at D-day (day-0 load of the pre-operation assembly) occupy Role 2
// Basic beds on day 1: they start the running bed census (occupancy feedback on died of wounds) and the Role 2 Basic bed timeline of
// Tab 5. false = they only reduce the starting strength (former behaviour).
const ASSEMBLY_DAY0_BEDS = true;

// 2026-10-18 (Tab 3 review T3-01): Tab 3 saves the blood stock as blood_config.current_stock.prbc_units; the calculator read
// blood_config.stock.prbc, a key nobody writes, so the stock the planner typed never reached it (the planning default 100 was
// used). true = read the Tab 3 key when the old key is absent, and a planner's stock of 0 is a real value (no blood), not 'missing'.
const BLOOD_STOCK_FROM_TAB3 = true;

function bloodStockPrbc(medicalConfig) {
  const bc = medicalConfig?.blood_config;
  let v = bc?.stock?.prbc;
  if (BLOOD_STOCK_FROM_TAB3 && (v === undefined || v === null)) v = bc?.current_stock?.prbc_units;
  if (BLOOD_STOCK_FROM_TAB3 && v === 0) return 0;
  return resolveInput(v, 'blood_stock_prbc', { zeroIsMissing: true });
}

/** Blood on hand as a percentage of the objective (the stock at the start); 0 when there is no stock at all. */
function bloodStockPercent(onHand, objective) {
  return objective > 0 ? (onHand / objective) * 100 : 0;
}

// 2026-10-18 (Dr. Tatar: Tab 4 owns the blood supply): the blood stock, the resupply and the days of supply are Tab 4 (Logistics)
// inputs: Tab 4 saves the initial stock (logistics.medical_supplies.initial_stock) and the resupply events. The calculator now
//   - takes the initial PRBC stock from Tab 4 (the Tab 3 field is a read-only mirror),
//   - keeps the blood ON HAND day by day: initial stock (shared equally by the brigades) - the PRBC need of each day + the resupply
//     of the day (the supply chain events of Tab 4),
//   - derives the blood alert level of each day from the days of supply left at the rate of the day (getBloodAlertLevel); the alert
//     of the day before reaches the survival model (resuscitation and field care are lost in a shortage), and the stock as a share of
//     the start drives the walking blood bank.
// The effects apply only when Tab 4 gave a stock (an explicit input); with the planning default (an assumption, not a plan) the
// stock never runs out in the calculation, as before. false = the former behaviour (static stock, alert level saved by Tab 3).
const BLOOD_FROM_TAB4_DAILY = true;

// 2026-10-18 (Dr. Tatar): the T4 expectant share rises when the care a T1 casualty needs is missing. T1 casualties who need blood
// are held in T4 while the blood alert is RED (half) or BLACK (all); T1 casualties whom no operating team can take (the Role 2 Basic
// queue beyond its limit that no other facility takes, after the lower priorities) are held in T4. The hold ends the day the need is met
// again; the T4 death rate leaves the part still alive. Values and sources: constants/dynamicTriage.js. Blood trigger only with a Tab 4
// stock (explicit supply plan). false = fixed 5 percent T4 as before. Not built: Role 1 physician overwhelmed, evacuation closed.
export const DYNAMIC_T4_ON = true;
// The surgeon trigger can be switched off alone (the queue wait in the survival curve already penalises a long queue: see docs/planning/TAB3_REVIEW.md section 12).
export const DYNAMIC_T4_SURGEON_ON = true;

// 2026-10-19 (Dr. Tatar, R2-23): the wait before the first movement of a casualty is DERIVED from the operation (road availability, air,
// drones, artillery, fighting) as a spread, and prolonged field care acts on it, instead of the 0 / 3 / 12 hour paradigm default and the
// flat PFC multiplier. Planner's typed hold hours (operation_parameters.evacuation_hold_hours) still win when set. Model and numbers:
// evacuationWindowModel.js, docs/research/EVACUATION_DELAY_FACTOR_TABLE.md. false = the former paradigm hold and the flat PFC multiplier.
export const EVAC_WAIT_DERIVED_ON = true;
// 2026-10-20 (Dr. Tatar, R2-28): the evacuation environment (PERMISSIVE / CONTESTED / DENIED) of the DAY is derived from the open share of
// the evacuation window, not typed once for the operation. It feeds the daily tactical capacity and the daily MEDEVAC capacity; the extra
// transit delay of the typed label is dropped (the window and the transit legs already carry it: it counted the same conditions twice).
// false = the typed label everywhere, as before.
export const EVAC_ENV_DERIVED_ON = true;
// 2026-10-20 (Dr. Tatar, R2-29): the daily evacuation capacity of the Tab 3 fleet limits evacuation: casualties above it wait, the backlog
// carries to the next day and its wait is added to the derived wait (evacuationQueueModel.js). Needs EVAC_WAIT_DERIVED_ON. Applies only when
// Tab 3 has configured the evacuation fleet. false = capacity is informational only, as before.
export const EVAC_CAPACITY_QUEUE_ON = true;
// 2026-10-21 (Dr. Tatar, R2-29b): ground ambulances belong to the brigade (organic), the helicopters are one pool of the force. true = each brigade
// gets its own share of the Tab 3 ground fleet (in proportion to the standard fleet of its national standard) and the air pool is allocated by the
// daily demand of the brigades (two passes of the calculation); the other brigades' idle capacity is reported. false = capacity shared equally.
export const EVAC_FLEET_BY_BRIGADE_ON = true;
// 2026-10-21 (Dr. Tatar, R2-05): the night operations card (level, illumination, night vision goggles) acts on the evacuation: ground speed and helicopter
// availability of the days (modifiers computed by getNightOpsModifiers, weighted by the night share of the level). Before, only the casualty and treatment
// parts of the card were read. false = as before.
export const NIGHT_EVAC_MODIFIERS_ON = true;
// 2026-10-21 (Dr. Tatar): the weekly ambulance losses of the drone threat (5 percent a week contested, 15 percent saturated; threatAssessment.js) reach the
// ground ambulance fleet of the capacity that limits evacuation (R2-29). They only multiplied the informational daily MEDEVAC capacity before. Evidence: medical
// transport is a priority target of strike drones in Ukraine (docs/research/RESEARCH_DRONE_FACILITIES.md). false = as before.
export const DRONE_FLEET_ATTRITION_ON = true;
const droneAttritionOf = (weeklyLoss, day) => (weeklyLoss > 0 ? Math.max(0.30, Math.pow(1 - weeklyLoss, Math.floor((day - 1) / 7))) : 1.0);
// 2026-10-20 (R2-31): with the derived wait the drone and route conditions are already in the wound curve (the window carries drone
// threat, road state and artillery; the transit legs carry the route speed), so the flat drone DOW multiplier (x1.3 saturated: 'evacuation
// delays caused by drone saturation') and the route DOW multiplier would count the same delay twice. true = both are left out of the flat
// chain while the derived wait is active. Switched ON 2026-10-20 after Dr. Tatar approved the before / after numbers (docs/TODO_CURRENT.md R2-31).
export const HAZARD_WINDOW_OVERLAP_SKIP_ON = true;

function resolveBloodStock(scenario) {
  if (BLOOD_FROM_TAB4_DAILY) {
    const t4 = scenario?.logistics?.medical_supplies?.initial_stock?.prbc_units;
    if (typeof t4 === 'number' && Number.isFinite(t4) && t4 >= 0) return { prbc: t4, source: 'TAB4' };
  }
  // no stock from Tab 4: the former reading (Tab 3 key or the planning default, listed as an assumption); the daily effects stay off
  return { prbc: bloodStockPrbc(scenario?.medical_configuration), source: 'LEGACY' };
}

// 2026-10-18 (Tab 3 review T3-02): Tab 3 saves surgical_config.teams as an object {FST, FRT, SURG_CO} with counts; the resource
// module read teams.length (undefined for an object), so the number of surgical teams was always the planning default (2) and the
// planner's counts did nothing. true = surgical teams of the force from the Tab 3 counts, in team equivalents of a Forward
// Surgical Team (operating tables of the team type / tables of an FST: FST 1, Surgical Company 2), shared equally by the
// brigades of the scenario. The resuscitation teams (FRT) are not surgical teams; their effect is read elsewhere (7574).
const SURGICAL_TEAMS_FROM_TAB3 = true;

function surgicalTeamsFromTab3(medicalConfig, brigadeCount) {
  const sc = medicalConfig?.surgical_config;
  if (!SURGICAL_TEAMS_FROM_TAB3 || !sc || sc.enabled === false || !sc.teams || typeof sc.teams !== 'object' || Array.isArray(sc.teams)) return null;
  const tablesFst = SURGICAL_TEAM_COMPOSITION?.FORWARD_SURGICAL_TEAM?.capability?.simultaneous_tables;
  const tablesCo = SURGICAL_TEAM_COMPOSITION?.SURGICAL_COMPANY?.capability?.simultaneous_tables;
  if (!(tablesFst > 0) || !(tablesCo > 0)) {
    throw new Error('casualtyCalculator.surgicalTeamsFromTab3: operating tables of FORWARD_SURGICAL_TEAM / SURGICAL_COMPANY are missing in SURGICAL_TEAM_COMPOSITION');
  }
  const fst = Number(sc.teams.FST?.count);
  // The Surgical Company is a Role 2 Enhanced asset (Tab 3 card: 'Role 2 Enhanced | Full Capability'); without access to the
  // Role 2 Enhanced (Tab 3 switch) it is not available to the brigade.
  const r2eOpen = medicalConfig?.r2e_accessible !== false;
  // with the surgical regulating on the Surgical Company is capacity of the Role 2 Enhanced (surgicalRegulatingModule), not of the Role 2 Basic
  const co = (r2eOpen && !SURGICAL_REGULATING_ON) ? Number(sc.teams.SURG_CO?.count) : 0;
  if (!Number.isFinite(fst) && !Number.isFinite(co)) return null;
  const equivalents = (Number.isFinite(fst) ? fst : 0) + (Number.isFinite(co) ? co : 0) * (tablesCo / tablesFst);
  return equivalents / Math.max(1, brigadeCount || 1);
}

// 2026-10-13 (Tab 2 plan 7.10): a damaged, overwhelmed or destroyed local medical system (infrastructure card) cannot support the host nation beds.
function hnsConfigAfterInfrastructure(hnsConfig, scenario) {
  const infra = infrastructureModel(scenario?.operation_parameters?.infrastructure_config);
  if (hnsConfig && infra && infra.hns_available === false) return { ...hnsConfig, enabled: false };
  return hnsConfig;
}

// 2026-10-13 (Tab 2 plan 7.1): the Threat Assessment enemy air defense levels, in the vocabulary of the resupply simulation (AD_THREAT).
const ENEMY_AD_TO_RESUPPLY_AD = { NONE: 'NONE', LIMITED: 'LOW', MODERATE: 'MEDIUM', DENSE: 'HIGH', SOPHISTICATED: 'EXTREME' };

/**
 * PHASE 4.6.4: Calculate DOS projection with Monte Carlo simulation
 * Accounts for route conditions, weather, and delivery failures
 * 
 * @param {Object} initialStock - Starting blood stock
 * @param {Object} dailyConsumption - Daily consumption rate
 * @param {Object} resupplyConfig - Resupply configuration
 * @param {number} duration - Operation duration in days
 * @param {Object} scenario - Full scenario for route/environmental data
 * @returns {Object} DOS projection with probability bands
 */
function calculateDOSProjectionWithMonteCarlo(initialStock, dailyConsumption, resupplyConfig, duration, scenario) {
  // First calculate baseline (deterministic) projection
  const baselineProjection = calculateDOSProjection(initialStock, dailyConsumption, resupplyConfig, duration);
  
  // Check if route modeling should be enabled
  const routesConfigured = scenario?.medical_configuration?.evacuation_routes?.configured || false;
  const hasResupply = resupplyConfig.configured && resupplyConfig.events.length > 0;
  
  // If no routes configured OR no resupply events, return baseline only
  if (!routesConfigured || !hasResupply) {
    return {
      ...baselineProjection,
      monte_carlo: {
        enabled: false,
        reason: !routesConfigured ? 'Routes not configured' : 'No resupply events'
      }
    };
  }
  
  // Extract route and environmental conditions
  const routeConditions = scenario.medical_configuration?.evacuation_routes || {};
  const environmental = {
    // R2-18 (2026-10-20): the operation weather reaches the convoys (it read scenario.environment.weather, which no tab writes: always clear)
    weather: WEATHER_TO_RESUPPLY_WEATHER[resolveScenarioEnvironment(scenario).weather],
    // 2026-10-13 (Tab 2 plan 7.1): the panel saves threat_assessment.enemy_ad; the old key threat.enemy_ad was never written.
    ad_threat: ENEMY_AD_TO_RESUPPLY_AD[scenario.operation_parameters?.threat_assessment?.enemy_ad] || scenario.operation_parameters?.threat?.enemy_ad || 'NONE'
  };
  
  // Run Monte Carlo simulation
  const monteCarloResults = runMonteCarloResupplySimulation(
    resupplyConfig.events,
    initialStock,
    dailyConsumption,
    duration,
    routeConditions,
    environmental,
    100  // iterations
  );
  
  // Calculate delivery risk for each event
  const eventsWithRisk = resupplyConfig.events.map(event => {
    const deliveryTime = calculateDeliveryTime(
      event.source || 'ROLE_3',
      event.delivery_method || 'GROUND',
      routeConditions,
      environmental
    );
    
    const deliveryProb = calculateDeliveryProbability(
      event.delivery_method || 'GROUND',
      routeConditions,
      environmental,
      event.source || 'ROLE_3'
    );
    
    return {
      ...event,
      delivery_analysis: {
        time: deliveryTime,
        probability: deliveryProb
      }
    };
  });
  
  return {
    ...baselineProjection,
    monte_carlo: {
      enabled: true,
      iterations: monteCarloResults.iterations,
      final_dos: monteCarloResults.final_dos,
      minimum_dos: monteCarloResults.minimum_dos,
      stockout_probability: monteCarloResults.stockout_probability,
      average_stockout_day: monteCarloResults.average_stockout_day,
      success_rate: monteCarloResults.success_rate,
      events_with_risk: eventsWithRisk,
      environmental_conditions: environmental
    }
  };
}

// ============================================================================
// MAIN CALCULATOR CLASS
// ============================================================================

/**
 * CasualtyCalculator v4.10
 * 
 * Main orchestrator class for casualty calculations
 * v4.10: Added MSK-001 surge, FH-001 forward FH, TCCC-2025 placeholder
 */
// ============================================================================
// IMP-007: COMPOSITE EVACUATION FACTOR — module-level helper
// ============================================================================

/**
 * Compute a strength-weighted composite evacuation factor from an array of brigade configs.
 *
 * Each brigade's unit_type drives its wheeled/tracked/helicopter/stretcher demand via
 * EVACUATION_ASSET_FACTORS (evacuationAssetFactors.js). The composite is weighted by
 * normalised strength so larger brigades dominate the corps-level MEDEVAC sizing.
 *
 * Design notes:
 * - Uses getEvacuationFactorForBrigade() which applies UNIT_TYPE_TO_EVAC_KEY adapter
 *   (resolves ARMOR->ARMORED, MOUNTAIN, STRYKER, etc.) and throws on truly unknown types.
 * - Unknown/missing unit_type is handled gracefully: logs warning and uses MECHANIZED (1.0)
 *   so a single bad brigade does not crash the entire calculation.
 * - Returns MECHANIZED baseline (all 1.0) if brigadeConfigs is empty.
 *
 * @param {Array} brigadeConfigs - Brigade array from force.brigades
 * @returns {{ wheeled_factor, tracked_factor, helicopter_factor, stretcher_factor }}
 */
function computeCompositeEvacFactor(brigadeConfigs) {
  const BASELINE = { wheeled_factor: 1.0, tracked_factor: 1.0, helicopter_factor: 1.0, stretcher_factor: 1.0 };

  if (!Array.isArray(brigadeConfigs) || brigadeConfigs.length === 0) {
    return BASELINE;
  }

  let totalWeight = 0;
  let weighted = { wheeled_factor: 0, tracked_factor: 0, helicopter_factor: 0, stretcher_factor: 0 };

  for (const brigade of brigadeConfigs) {
    const weight = normalizeStrength(brigade.strength, 4500);
    if (weight <= 0) continue;

    let factors = BASELINE;
    try {
      // Use unit_type, fall back to type (field name variant — see casualtyCalculator line 723)
      const unitType = normalizeUnitTypeKey(brigade.unit_type || brigade.type);
      if (unitType) {
        factors = getEvacuationFactorForBrigade(unitType);
      }
    } catch (_err) {
      // Unknown unit_type — silently use MECHANIZED baseline (1.0 all factors).
      // Individual brigade resolution failure must not abort the corps calculation.
      // The try/catch is the safety net; JSDoc documents this behaviour.
      // 2026-10-24 FINDING (naming audit step 4): a brigade as the force builder returns it has brigade_type (MECHANIZED ...) and type
      // 'BRIGADE' but no unit_type, so for such a brigade the lookup throws on 'BRIGADE' and the baseline (1.0) is used. Whether the
      // brigades Tab 2 hands over (force.brigades) carry unit_type has to be checked in the running app; if not, the unit-type factors
      // are never applied (they feed the informational casevac.medevac_capacity only). Kept as it is: no number moves in step 4.
    }

    weighted.wheeled_factor    += factors.wheeled_factor    * weight;
    weighted.tracked_factor    += factors.tracked_factor    * weight;
    weighted.helicopter_factor += factors.helicopter_factor * weight;
    weighted.stretcher_factor  += factors.stretcher_factor  * weight;
    totalWeight += weight;
  }

  if (totalWeight === 0) return BASELINE;

  return {
    wheeled_factor:    weighted.wheeled_factor    / totalWeight,
    tracked_factor:    weighted.tracked_factor    / totalWeight,
    helicopter_factor: weighted.helicopter_factor / totalWeight,
    stretcher_factor:  weighted.stretcher_factor  / totalWeight
  };
}

// ============================================================================
// END IMP-007 helper
// ============================================================================

class CasualtyCalculator {
  constructor() {
    this.version = '5.56.0'; // BUG-VER-001 FIX: updated from stale 5.51.1 — April 5 2026 build (V4-TAB6-004 + BUG-R2E-MARCH-001)
    this.buildDate = '2026-04-05';
    this.modules = {
      survival: true,
      dnbi: true,
      medevac: true,
      battleCasualty: true,
      resources: true,
      monteCarlo: true,
      casevac: true,        // CC-007
      fightPhase: true,     // CC-008
      autoMethod: true,     // CC-009
      rearAreaDecay: true,  // CC-012
      bloodMechanism: true, // CC-011
      forceSuperiority: true,     // FIX-CC-014
      medicalPersonnel: true,     // FIX-CC-015
      perFacilityMascal: true,    // FIX-CC-016
      unitTypeCasualties: true,   // FIX-CC-017
      mskSurge: true,             // MSK-001: Blood surge at high casualty load
      forwardFH: true,            // FH-001: Forward Field Hospital detection
      tccc2025: true,             // TCCC-2025: ÃƒÆ’Ã‚Â¢Ãƒâ€¦Ã¢â‚¬Å“ÃƒÂ¢Ã¢â€šÂ¬Ã‚Â¦ ENABLED (CoTCCC April 2025 guidelines)
      supplyChain: true,          // CC-004: G4 supply chain tracking
      strengthProjection: true,   // CC-005: G1 strength projection
      timeToSurgery: true,        // CC-006: Golden Hour POIÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã‚Â ÃƒÂ¢Ã¢â€šÂ¬Ã¢â€žÂ¢Surgery
      methodBasedAssets: true     // CC-007: PUSH/PULL/AUTO asset allocation
    };
  }

  /**
   * Calculate casualties for a complete scenario
   * 
   * @param {Object} scenario - Complete scenario configuration
   * @returns {Object} Comprehensive casualty analysis
   */
  calculateCasualties(scenario, options = {}) {
    const startTime = Date.now();
    // 2026-10-25: one owner per leg of the medical chain (placed facilities, planner override); the engine keys are set from it
    if (scenario?.medical_configuration) scenario = { ...scenario, medical_configuration: applyChainDistances(scenario.medical_configuration, scenario?.force?.forceBuilderResult?.medical_facilities) };

    // BUG-057-NOISE: reset one-time warn flag before each full calculation run
    survivalModule.resetWarnings();
    assumptionLedger.reset();   // CALC-010: "assumptions used" starts empty for every run

    // ========================================================================
    // PHASE 1.1: Monte Carlo Integration - Check if Monte Carlo should run
    // ========================================================================
    const { final = false } = options;
    const mcConfig = scenario?.operation_parameters?.monte_carlo_config;
    
    // Run Monte Carlo ONLY if:
    // 1. Monte Carlo is enabled in Tab 2 configuration
    // 2. This is a FINAL calculation (Tab 5 display), not preview/validation
    if (mcConfig?.enabled && final) {
      const iterations = mcConfig.iterations || 1000;
      // Run deterministic FIRST - this becomes p50 (guaranteed match with summary cards)
      const deterministicResult = this.calculateCasualties(scenario, { final: false });
      // R2-29b: the Monte Carlo iterations reuse the air shares of the deterministic run (no second pass per iteration)
      const _mcScenario = deterministicResult?.air_allocation
        ? { ...scenario, operation_parameters: { ...scenario.operation_parameters, air_demand_shares: deterministicResult.air_allocation } }
        : scenario;
      return this.calculateWithMonteCarlo(_mcScenario, iterations, deterministicResult);
    }
    
    // Deterministic calculation for:
    // - Tab 3 validation/preview (final=false)
    // - Tab 5 when Monte Carlo disabled
    // - Any other validation checks
    if (mcConfig?.enabled && !final) {
    }

    // Validate inputs
    const validation = this.validateScenario(scenario);
    if (!validation.valid) {
      return {
        success: false,
        errors: validation.errors,
        version: this.version
      };
    }

    // R2-28b: the evacuation environment of the set-up calculations is derived from the operation, not typed
    scenario = withDerivedEvacuationEnvironment(scenario);

    // R2-29b: the air pool of the force is allocated by the daily demand of the brigades. Pass one (equal shares) gives the demand of each brigade and day;
    // pass two (this run) uses the shares. Only for a force of several brigades with a Tab 3 air pool and the capacity queue on.
    let _airAllocation = scenario?.operation_parameters?.air_demand_shares || null;
    {
      const _brigs = scenario?.force?.forceBuilderResult?.brigades || scenario?.force?.brigades || [];
      const _tec = scenario?.medical_configuration?.tactical_evac_config;
      const _hasAir = !!(_tec && (_tec.air_pools?.length || _tec.air_pool));
      if (EVAC_FLEET_BY_BRIGADE_ON && EVAC_CAPACITY_QUEUE_ON && !options._passOne && !_airAllocation && _brigs.length > 1 && _hasAir) {
        const _one = this.calculateCasualties(scenario, { ...options, final: false, _passOne: true });
        if (_one && Array.isArray(_one.by_brigade)) {
          const _shares = {};
          const _days = {};
          _one.by_brigade.forEach((r) => (r.by_day || []).forEach((d) => {
            const dem = d?.resources?.evac_queue?.demand_day;
            if (typeof dem === 'number') { _days[d.day] = _days[d.day] || {}; _days[d.day][r.brigade_id] = dem; }
          }));
          _one.by_brigade.forEach((r) => { _shares[r.brigade_id] = {}; });
          Object.entries(_days).forEach(([dayKey, perB]) => {
            const total = Object.values(perB).reduce((a, v) => a + v, 0);
            Object.entries(perB).forEach(([bid, v]) => { _shares[bid][dayKey] = total > 0 ? v / total : 1 / Object.keys(perB).length; });
          });
          _airAllocation = _shares;
          scenario = { ...scenario, operation_parameters: { ...scenario.operation_parameters, air_demand_shares: _shares } };
        }
      }
    }

    // Extract configuration
    const {
      force,
      operation_parameters,
      medical_configuration
    } = scenario;

    // PHASE 1.1: Extract WBB configuration once
    const wbbConfig = getWBBConfiguration(scenario);

    // PHASE 2.1: Extract evacuation routes configuration
    const routesConfig = getEvacuationRoutesConfiguration(scenario);
    const routeImpact = calculateRouteImpactOnEvacuation(routesConfig, {
      weather: scenario?.environment?.weather,
      threat: scenario?.operation_parameters?.threat_level
    });

    // PHASE 4.1: Extract resupply configuration
    let resupplyConfig = {
      configured: false,
      events: [],
      by_day: {},
      total_units: { prbc: 0, ffp: 0, plt: 0, cryo: 0 }
    };
    
    try {
      resupplyConfig = getResupplyConfiguration(scenario);
    } catch (error) {
      console.error(STATUS_ICONS.RED_X + ' Error getting resupply config:', error);
    }

    // v5.9.0 FRT-DOW: Extract Forward Resuscitation Team count once for entire operation.
    // FRT count from Tab3 Section 3D: medical_configuration.surgical_config.teams.FRT.count
    // Binary modifier: ≥1 FRT deployed = 7% DOW reduction (Ukraine-validated, AJMedP-1 Role 1+).
    // Applied per-day in DOW chain before TCCC modifiers (FRT acts upstream at Role 1).
    const _frtCount = medical_configuration?.surgical_config?.teams?.FRT?.count ?? 0;
    const _frtDOWModifier = _frtCount > 0
      ? (1 - FRT_DOW_REDUCTION_PERCENT / 100)
      : 1.0;
    // _frtDOWModifier applied below — no logging needed

    // ECHELON-DUAL: Read R3 accessibility system state from medical_configuration (Tab 3 domain).
    // false = R3 hospital not in theatre — triggers DOW +35%, STRATEVAC drain disabled,
    // blood DOS consumption +15%, and TPRP requirements computed.
    // Reads with ?? true so all pre-ECHELON-DUAL saved scenarios are unaffected.
    const _r3Accessible = medical_configuration?.r3_accessible ?? true;
    const _r3AbsentDOWMod = _r3Accessible ? 1.0 : 1.35; // ECHELON-DUAL: no definitive surgery

    // Phase 6.1: Extract airfield class + FARP once per scenario for stratevac_capacity.
    // (MTP, shelf life, surgeon hours extracted inside calculateBrigadeCasualties from medicalConfig.)
    const _airfieldClass    = medical_configuration?.strategic_evac_config?.airfield_class ?? 'CLASS_B';
    const _farpEnabled      = medical_configuration?.strategic_evac_config?.farp_enabled   ?? false;

    // SECTION 12: Validate operation duration - NO FALLBACK
    let duration = operation_parameters?.duration_days;
    
    if (!duration || duration <= 0) {
      console.error(`[SECTION 12] CRITICAL: Operation missing duration_days (got: ${duration})`);
      duration = 1;  // Obviously wrong - will expose missing Tab2 data
    }
    
    // ========================================================================
    // v5.1.0 ARCHITECTURAL INTEGRATION: Read from SINGLE SOURCE OF TRUTH
    // ========================================================================
    // v5.4.0 UPDATE: Defensive brigade lookup - check multiple locations
    // PRIMARY: force.brigades (flattened - Tab1/Tab2 v3.0)
    // FALLBACK: force.forceBuilderResult.brigades (nested - backward compatibility)
    // OLD (v5.0.0): operation_parameters.brigade_configs (deprecated)
    // REASON: Tab1 flattens brigades for calculator, Tab2 keeps both synchronized
    // RESULT: Calculator finds brigades regardless of data structure
    
    // F2-ARCH-010: ForceService now always outputs brigades[] array for all echelons.
    // Brigade resolution order: force.brigades (Tab2 flattened) → forceBuilderResult.brigades[] (array).
    // Singular forceBuilderResult.brigade fallback retained for any pre-F2 persisted data.
    const _fbrCalc = force?.forceBuilderResult;
    const brigadeConfigs =
      Array.isArray(force?.brigades) && force.brigades.length > 0
        ? force.brigades
        : Array.isArray(_fbrCalc?.brigades) && _fbrCalc.brigades.length > 0
          ? _fbrCalc.brigades
          : _fbrCalc?.brigade
            ? [_fbrCalc.brigade]   // legacy: pre-F2 persisted BRIGADE echelon data
            : [];

    // Initialize results
    const results = {
      version: this.version,
      calculation_timestamp: new Date().toISOString(),
      scenario_summary: this.buildScenarioSummary(scenario),
      air_allocation: _airAllocation,   // R2-29b: share of the air pool of each brigade by day (null when not used)
      
      by_brigade: [],
      by_corps: {
        total_kia: 0,
        total_dow: 0,
        total_wia: 0,
        total_dnbi: 0,
        total_rtd: 0,
        total_prehospital_deaths: 0,   // R2-24d: killed before arrival (wound hazard model), for the deaths-before-surgery range of the Monte Carlo
        total_all_sources: 0,
        total_evacuated: 0
      },
      
      by_day: [],
      
      medical_system_analysis: {},
      medical_requirements: {},
      
      recommendations: [],
      warnings: []
    };

    // V4-002: resupply_success — logistics delivery shortfall scales initial blood stock.
    // resupply_success < 1.0 = supply chain delivered less than planned (LOC interdiction, vehicle loss).
    // Directly reduces blood_dos from day 1 → earlier Class VIII degradation → higher DOW rate cascade.
    // No-op at 1.0 (deterministic runs, modifier absent).
    const _mcResupplyMod = scenario?.operation_parameters?.monte_carlo_modifiers?.resupply_success ?? 1.0;

    // Initialize medical system state (for cascade effects)
    let medicalSystemState = {
      surgical_backlog: 0,
      blood_dos: Math.round(resolveBloodStock(scenario).prbc * _mcResupplyMod),
      blood_initial_total: Math.round(resolveBloodStock(scenario).prbc * _mcResupplyMod),
      blood_source: resolveBloodStock(scenario).source,
      cumulative_fatigue: 0
    };

    // Calculate HQ and support casualties first
    const hqCasualties = this.calculateHQCasualtiesMethod(force, operation_parameters, duration);
    const supportCasualties = this.calculateSupportUnitCasualties(force, operation_parameters, duration);

    // IMP-007: Compute strength-weighted composite evacuation factor from brigade array.
    // Applies unit-type-specific wheeled/tracked/helicopter/stretcher demand scalars.
    // HEAVY brigade tracked=1.8x; INFANTRY stretcher=2.0x; AIR_ASSAULT helicopter=3.0x, etc.
    // Weighted by normalised strength so large brigades dominate the corps-level MEDEVAC sizing.
    // Result passed to calculateComprehensiveMEDEVAC() which applies it inside the module.
    const compositeEvacFactor = computeCompositeEvacFactor(brigadeConfigs);

    // ENH-035 / IMP-S107-001: Build medevacConfig.pools from tactical_evac_config.
    // Supports both new air_pools array and legacy single air_pool object.
    // Sum nominal sorties across all pool entries; use primary pool's aircraft_type for
    // litter capacity model. calculateComprehensiveMEDEVAC() receives combined aircraft count.
    const _tec2 = medical_configuration?.tactical_evac_config || {};
    const _medevacAirPools = _tec2.air_pools
      ? _tec2.air_pools
      : _tec2.air_pool
        ? [_tec2.air_pool]
        : [];
    const _medevacNomSorties = _medevacAirPools.reduce(
      (s, p) => s + (typeof p.nominal_daily_sorties === 'number' ? p.nominal_daily_sorties : 0), 0
    );
    const _medevacAircraftCount = _medevacNomSorties > 0
      ? Math.max(1, Math.round(_medevacNomSorties / 6))
      : 0;
    // ENH-037: Use pool_code → MEDEVAC_POOLS[].primary_aircraft when aircraft_type not explicit.
    // Primary pool drives the litter capacity model (most capable / national asset first).
    const _primaryMedevacPool  = _medevacAirPools[0] || {};
    const _medevacPoolCode     = _primaryMedevacPool.pool_code?.toUpperCase() || '';
    const _poolPrimaryAircraft = MEDEVAC_POOLS[_medevacPoolCode]?.primary_aircraft || null;
    const _medevacAircraftType = (_primaryMedevacPool.aircraft_type || _poolPrimaryAircraft || 'IAR330')
      .toUpperCase().replace(/-/g, '_');
    const medevacConfigBuilt = {
      pools: _medevacAircraftCount > 0 ? {
        primary_pool: {
          enabled: true,
          aircraft_count: _medevacAircraftCount,
          aircraft_type: _medevacAircraftType,
          pool_code: _medevacPoolCode || null,
          pool_requires_mou: MEDEVAC_POOLS[_medevacPoolCode]?.requires_mou ?? null,
          // IMP-S107-001: expose pool count for Tab5 display
          pool_count: _medevacAirPools.length
        }
      } : {}
    };

    // Get MEDEVAC availability
    // AFTER (Phase 2.5 enhanced):
let medevacAnalysis;

// Try Phase 2.5 per-facility capacity first
const phase25Analysis = prepareForCasualtyCalculator(scenario);
if (phase25Analysis) {
  medevacAnalysis = {
    ...calculateComprehensiveMEDEVAC({
      medevacConfig: medevacConfigBuilt,  // ENH-035: was medical_configuration?.medevac_config (always null)
      ambulanceConfig: medical_configuration?.ambulance_config,
      scenario,
      dailyCasualties: brigadeConfigs.reduce((sum, b) => sum + normalizeStrength(b.strength, 4500) * 0.02, 0),
      medicalConfig: medical_configuration,
      compositeUnitFactors: compositeEvacFactor  // IMP-007: unit-type fleet scaling
    }),
    // Overlay Phase 2.5 enhancements
    chain_analysis: phase25Analysis.chain_analysis,
    golden_hour_analysis: phase25Analysis.golden_hour_analysis || undefined,
    capacity_utilization_percent: phase25Analysis.saturation?.saturation_percent || 50,
    per_facility_capacity: phase25Analysis.facilities_detail,
    blue_light: phase25Analysis.blue_light
  };
} else {
  // Fallback to legacy calculation
  const legacyMedevac = calculateComprehensiveMEDEVAC({
    medevacConfig: medical_configuration?.medevac_config,
    ambulanceConfig: medical_configuration?.ambulance_config,
    scenario,
    dailyCasualties: brigadeConfigs.reduce((sum, b) => sum + normalizeStrength(b.strength, 4500) * 0.02, 0),
    medicalConfig: medical_configuration,
    compositeUnitFactors: compositeEvacFactor  // IMP-007: unit-type fleet scaling
  });
  
  // EA-003: Try to add chain analysis even in legacy mode if facilities available
  let chainAnalysis = null;
  const facilities = medical_configuration?.facilities || 
                    medical_configuration?.per_facility_evacuation_assets || [];
  
  if (facilities.length > 0) {
    chainAnalysis = calculateEvacuationChainCapacity({
      facilities,
      evacuationChain: null,
      environment: resolveEvacuationParadigm(scenario).paradigm,  // CALC-005: was threat_level === 'HIGH' (never a real key)
      weather: scenario?.environment?.weather || 'CLEAR',
      terrain: resolveScenarioEnvironment(scenario).terrain,  // CALC-011: planner's terrain (Tab 2)
      timeOfDay: scenario?.operation_parameters?.time_of_day || 'DAY',
      dailyCasualties: brigadeConfigs.reduce((sum, b) => sum + normalizeStrength(b.strength, 4500) * 0.02, 0)
    });
  }
  
  medevacAnalysis = {
    ...legacyMedevac,
    chain_analysis: chainAnalysis
  };
}
    // Assess initial MASCAL status
    let mascalStatus = this.assessMASCALStatus(medical_configuration, medevacAnalysis);

    // HNS-001: Compute HNS modifiers once — scenario-level, not per-brigade.
    // getHNSModifiers returns reliability-adjusted values; returns all-zero inert
    // object when HNS is disabled or level is NONE (backward compatible).
    // dnbi_absorption: fraction of per-brigade DNBI absorbed by civilian system.
    // bed_augmentation_percent: additional R3 beds as fraction of organic R3 capacity.
    const _hnsRawConfig = scenario?.operation_parameters?.hns_config;
    const hnsModifiers = getHNSModifiers(hnsConfigAfterInfrastructure(_hnsRawConfig, scenario));

    // ── V4-TAB6-004: Pre-combat build-up phase DNBI pre-computation ──────────
    // Runs before the brigade loop. Uses existing calculateComprehensiveDNBI with
    // SUPPORT_ACTIVE operational state (staging/deployment) plus 1.2 deployment
    // stress multiplier. Day-0 load = last 3 days' DNBI x non-RTD fraction (~15%).
    // IMP-BUILDUP-001: dnbi_held_rear is distributed proportionally to each brigade
    // and subtracted from initialStrength — soldiers in the medical system at H-Hour
    // do not step off the line of departure.
    // Backward compat: if buildup_config absent, result is null (Tab5 panel hidden).
    const _buildupPhase = (() => {
      const _bc = scenario?.operation_parameters?.buildup_config;
      if (!_bc?.enabled || !(_bc.duration_days > 0)) return null;

      const _buildupDuration = Math.min(Math.max(1, _bc.duration_days), 90);
      const _DEPLOYMENT_STRESS_MOD = 1.2; // 20% elevation over SUPPORT_ACTIVE baseline
      const _DAY0_HOLD_WINDOW = 3;        // days still in system at D-Day (R1+R2B DNBI hold)
      const _EVAC_FRACTION     = 0.15;    // ~15% DNBI requiring evacuation (non-RTD)

      // Total force strength — mirrors the pattern used in support unit calculation
      const _buildupStrength =
        force?.forceBuilderResult?.total_strength ||
        force?.total_strength ||
        brigadeConfigs.reduce((s, b) => s + normalizeStrength(b.strength, 4500), 0) ||
        1;

      // Climate from scenario — same source used by the brigade DNBI loop
      const _buildupClimate = scenario?.operation_parameters?.climate_config;
      const _buildupEnv = {
        climate:  _buildupClimate?.theater_region || 'TEMPERATE',
        season:   seasonForDay(scenario, 1, _buildupClimate?.season || 'summer'),
      };

      let _buildupTotal = 0;
      const _lastDays = []; // circular buffer for day0 window

      for (let _d = 0; _d < _buildupDuration; _d++) {
        const _dayResult = calculateComprehensiveDNBI({
          strength:          _buildupStrength,
          operationalState:  'SUPPORT_ACTIVE', // staging/deployment — below combat rate
          environment:       _buildupEnv,
          day:               _d + 1,
          cumulativeBattleCasualties: 0,       // no combat yet
        });
        const _adjusted = Math.round((_dayResult.total_dnbi || 0) * _DEPLOYMENT_STRESS_MOD);
        _buildupTotal += _adjusted;
        _lastDays.push(_adjusted);
        if (_lastDays.length > _DAY0_HOLD_WINDOW) _lastDays.shift();
      }

      // Patients still in system at D-Day: last window days, non-RTD fraction
      const _lastWindowTotal = _lastDays.reduce((s, v) => s + v, 0);
      const _day0Load     = Math.round(_lastWindowTotal * _EVAC_FRACTION);
      const _heldRear     = _day0Load;
      const _effectiveStr = Math.max(0, _buildupStrength - _heldRear);
      const _strengthRedPct = _buildupStrength > 0
        ? Math.round((_heldRear / _buildupStrength) * 1000) / 10
        : 0;

      return {
        enabled:                    true,
        duration_days:              _buildupDuration,
        buildup_dnbi_total:         _buildupTotal,
        day0_medical_load:          _day0Load,
        effective_starting_strength: _effectiveStr,
        dnbi_held_rear:             _heldRear,
        strength_reduction_pct:     _strengthRedPct,
        total_strength_reference:   _buildupStrength,
        avg_daily_dnbi:             Math.round((_buildupTotal / _buildupDuration) * 10) / 10,
      };
    })();

    // OBS-S96-006: Compute effective R1 fleet once before the brigade loop
    // (force-structure property — same for every brigade/day)
    const _r1Fleet = computeEffectiveR1Fleet(
      brigadeConfigs,
      medical_configuration?.per_facility_evacuation_assets
    );
    const _r1HeloCapacity = _r1Fleet.total.helo * 6 * 2; // 6 sorties/day × 2 litter

    // Process each brigade
    for (let brigadeIndex = 0; brigadeIndex < brigadeConfigs.length; brigadeIndex++) {
      const brigadeConfig = brigadeConfigs[brigadeIndex];
      // BUG-045 FIX: Normalize posture at entry point BEFORE passing to any function
      // Tab 2 saves posture as object {value: 'X', label: 'Y'} but calculations expect string 'X'
      // This ensures all downstream code receives a string, not an object
      // BUG-047 FIX: Also normalize strength (Tab 1/2 may save as {value: 4500})
      // BUG-061 FIX: Pass index for unique brigade IDs
      const normalizedBrigadeConfig = {
        ...brigadeConfig,
        _index: brigadeIndex,  // BUG-061: For unique ID generation
        strength: normalizeStrength(brigadeConfig.strength, 4500),  // BUG-047 FIX
        operational_posture: normalizePosture(brigadeConfig.operational_posture, 'DELIBERATE_DEFENSE'),
        // Also normalize posture in nested operation object if present
        operation: brigadeConfig.operation ? {
          ...brigadeConfig.operation,
          operational_posture: normalizePosture(
            brigadeConfig.operation?.operational_posture || brigadeConfig.operational_posture, 
            'DELIBERATE_DEFENSE'
          )
        } : undefined
      };
      
      const brigadeResult = this.calculateBrigadeCasualties({
        brigade: normalizedBrigadeConfig,
        duration,
        scenario,
        forceQuality: applyTab2TechnologyEnvironment(resolvePerBrigadeQuality(normalizedBrigadeConfig, force), normalizedBrigadeConfig, operation_parameters), // ENH-043: per-brigade cascade + Tab 2 technology choice
        enemyQuality: operation_parameters?.enemy_force_quality || {},
        medicalConfig: medical_configuration,
        medevacAnalysis,
        medicalSystemState,
        wbbConfig,        // PHASE 1.1: Pass WBB config
        routeImpact,      // PHASE 2.1: Pass route impact
        resupplyConfig,   // PHASE 4.1: Pass resupply config
        forceStructure: force?.forceBuilderResult,  // TASK 3.2: Pass force structure for phase resolution
        frtDOWModifier: _frtDOWModifier,  // v5.9.0: Forward Resuscitation Team DOW modifier
        r3AbsentDOWMod: _r3AbsentDOWMod,  // ECHELON-DUAL: R3-absent DOW multiplier (1.0 or 1.35)
        orbatSendingAssets: _r1Fleet.total.all,  // OBS-S96-006: ORBAT-derived R1 fleet total
        buildupPhase: _buildupPhase               // IMP-BUILDUP-001: Day-0 held load for strength reduction
      });

      results.by_brigade.push(brigadeResult);

      // Update medical system state for cascade effects
      medicalSystemState.surgical_backlog = brigadeResult.end_state.surgical_backlog;
      // ECHELON-DUAL: R3 absent → patients linger longer in R2E → 15% more blood consumed per brigade-period
      const _bloodConsumed = brigadeResult.totals.blood_consumed || 0;
      medicalSystemState.blood_dos -= _r3Accessible ? _bloodConsumed : Math.round(_bloodConsumed * 1.15);
      medicalSystemState.cumulative_fatigue += 0.02 * duration;

      // Aggregate to corps level
      results.by_corps.total_kia += brigadeResult.totals.kia;
      results.by_corps.total_dow += brigadeResult.totals.dow;
      results.by_corps.total_wia += brigadeResult.totals.wia;
      results.by_corps.total_dnbi += brigadeResult.totals.dnbi;
      results.by_corps.total_rtd += brigadeResult.totals.rtd;
      results.by_corps.total_prehospital_deaths += brigadeResult.totals.prehospital_deaths || 0;
    }

    // ========================================================================
    // F-05 FIX: Re-evaluate MASCAL and Blue Light with actual computed surgical backlog
    // ========================================================================
    // assessMASCALStatus() and evaluateBlueLightProtocol() are both called BEFORE the
    // brigade loop (lines ~5444 and ~5397). At that point surgical_backlog is always 0
    // because the brigade loop has not run yet — Tab3 does not populate
    // surgical_config.backlog (it saves only teams/shift_mode/fatigue_level).
    // The correct value is now in medicalSystemState.surgical_backlog (accumulated
    // by the loop at line 5487). Re-evaluate both here using that actual value.
    if (medicalSystemState.surgical_backlog > 0) {
      // Re-evaluate MASCAL status with real surgical backlog
      const medConfigWithBacklog = {
        ...medical_configuration,
        surgical_config: {
          ...(medical_configuration?.surgical_config || {}),
          backlog: {
            total: medicalSystemState.surgical_backlog,  // read path in medevacModule
            hours: medicalSystemState.surgical_backlog   // read path in assessMASCALStatus
          }
        }
      };
      mascalStatus = this.assessMASCALStatus(medConfigWithBacklog, medevacAnalysis);

      // Re-evaluate Blue Light only when phase25Analysis is not overriding blue_light.
      // When phase25Analysis is present it overlays medevacAnalysis.blue_light already —
      // that overlay used phase25Analysis data which also doesn't have backlog, but the
      // surgical backlog trigger is only one of five Blue Light triggers and the
      // phase25Analysis path is the higher-fidelity calculation, so we leave it alone.
      if (!phase25Analysis && medevacAnalysis.helicopter && medevacAnalysis.ground) {
        const updatedBlueLightAnalysis = evaluateBlueLightProtocol({
          helicopterCapacity: medevacAnalysis.helicopter,
          groundCapacity: medevacAnalysis.ground,
          dailyCasualties: medevacAnalysis.expected_casualties ||
            brigadeConfigs.reduce((sum, b) => sum + normalizeStrength(b.strength, 4500) * 0.02, 0),
          bloodStatus: medical_configuration?.blood_config?.alert_level || 'GREEN',
          surgicalBacklog: medicalSystemState.surgical_backlog
        });
        medevacAnalysis = { ...medevacAnalysis, blue_light: updatedBlueLightAnalysis };
      }
    }
    // ========================================================================

    // Add HQ and support casualties
    results.by_corps.total_kia += hqCasualties.kia + supportCasualties.kia;
    results.by_corps.total_dow += hqCasualties.dow + supportCasualties.dow;
    results.by_corps.total_wia += hqCasualties.wia + supportCasualties.wia;
    results.by_corps.total_dnbi += hqCasualties.dnbi + supportCasualties.dnbi;

    // Calculate totals
    results.by_corps.total_all_sources = 
      results.by_corps.total_kia +
      results.by_corps.total_dow +
      results.by_corps.total_wia +
      results.by_corps.total_dnbi;

    // DNBI-03 FIX: Remove hardcoded 0.30 DNBI evacuation literal.
    // total_rtd already includes both WIA RTD and DNBI RTD (Feb 7 2026 bugfix —
    // brigadeResult.totals.rtd accumulates daySummary.battle.rtd + daySummary.dnbi.rtd).
    // Algebraic proof:
    //   total_evacuated = wia_evacuated + dnbi_evacuated
    //                   = (total_wia − wia_rtd) + (total_dnbi − dnbi_rtd)
    //                   = total_wia − (wia_rtd + dnbi_rtd) + total_dnbi
    //                   = total_wia − total_rtd + total_dnbi   [dnbi_rtd cancels]
    // The state-specific evacuation fraction is already encoded in dnbiModule via
    // DNBI_CATEGORY_BREAKDOWN evacuation rates and surfaces through dnbi.rtd per day.
    // No constants lookup required — the correct DNBI evacuated count = total_dnbi − dnbi_rtd,
    // and dnbi_rtd is already deducted via total_rtd.
    results.by_corps.total_evacuated =
      results.by_corps.total_wia - results.by_corps.total_rtd +
      results.by_corps.total_dnbi;

    // OBS-S96-006: Effective R1 fleet (ORBAT organic + attached − detached)
    results.by_corps.r1_fleet = _r1Fleet;
    results.by_corps.r1_helo_capacity = _r1HeloCapacity;

    // BUG-S97-002: Add total_strength to by_corps so Tab5 sustainabilityIndicators
    // can compute Remaining Strength without depending on scenario.force fallback
    // (which is null in CoA/approved-plan context).
    // BUG-S102-028 FIX: Previous formula only summed brigadeConfigs (combat brigades),
    // omitting Corps HQ + Division HQs + support troops (~1,780 for a Romanian CORPS).
    // Prefer forceBuilderResult.total_strength which includes all personnel categories;
    // fall back to the brigade-only sum for legacy scenarios missing that field.
    results.by_corps.total_strength = (_fbrCalc?.total_strength > 0)
      ? _fbrCalc.total_strength
      : brigadeConfigs.reduce((sum, b) => sum + normalizeStrength(b.strength, 4500), 0);

    // IMP-005: Planned MTF displacement gaps — surfaced to Tab 5
    results.by_corps.planned_displacement_gaps = (() => {
      const gaps = [];
      const _pdPhases = operation_parameters?.corps_config?.phases || [];
      (medical_configuration?.per_facility_evacuation_assets || [])
        .filter(fa => fa.phase_displacements?.length)
        .forEach(fa => {
          fa.phase_displacements.forEach(disp => {
            const ph = _pdPhases[disp.phase_index];
            if (!ph) return;
            gaps.push({
              facility_type: fa.facility_type,
              facility_name: fa.facility_name || fa.facility_type,
              phase_label: `Ph.${ph.phase_number || disp.phase_index + 1} D+${ph.start_day}`,
              cause: disp.cause,
              gap_hrs: disp.gap_hrs,
              day: ph.start_day,
              // IMP-005 enrichment: setup ramp time and brigade linkage for Tab 5 display
              setup_ramp_hrs: fa.facility_type === 'ROLE2_BASIC'
                ? (requireNumber(MTF_SETUP_TIMES_HOURS.ROLE_2B, 'casualtyCalculator.js:7709', 'MTF_SETUP_TIMES_HOURS.ROLE_2B'))
                : (requireNumber(MTF_SETUP_TIMES_HOURS.ROLE_2E, 'casualtyCalculator.js:7710', 'MTF_SETUP_TIMES_HOURS.ROLE_2E')),
              brigade_id: fa.allocated_to?.unit_id || null,
            });
          });
        });
      return gaps;
    })();

    // v5.4.2 FIX: Aggregate brigade daily data into results.by_day
    // Supply chain module needs daily totals across all brigades
    // Each brigade has by_day array, we need to sum them by day number
    for (let day = 1; day <= duration; day++) {
      const dayTotals = {
        day,
        kia: 0,
        dow: 0,
        wia: 0,
        dnbi: 0,
        rtd: 0,
        triage_1: 0,
        triage_2: 0,
        triage_3: 0,
        total_casualties: 0
      };
      
      // Sum all brigades for this day
      results.by_brigade.forEach(brigade => {
        const brigadeDay = brigade.by_day?.find(d => d.day === day);
        if (brigadeDay?.totals) {
          dayTotals.kia += brigadeDay.totals.kia || 0;
          dayTotals.dow += brigadeDay.totals.dow || 0;
          dayTotals.wia += brigadeDay.totals.wia || 0;
          dayTotals.dnbi += brigadeDay.totals.dnbi || 0;
          dayTotals.rtd += brigadeDay.totals.rtd || 0;
        }
        
        // v5.4.4 FIX: Triage data is in brigadeDay.triage object (T1/T2/T3), not totals
        // v5.5.0 FIX: calculateTimeAdjustedTriageDistribution() returns t1_immediate/t2_delayed/t3_minimal,
        // not T1/T2/T3. Using the same three-way fallback chain already established at line ~6960.
        // All three variants attempted: uppercase (legacy), lowercase-with-qualifier (current), short lowercase.
        if (brigadeDay?.triage) {
          dayTotals.triage_1 += brigadeDay.triage.T1 || brigadeDay.triage.t1_immediate || brigadeDay.triage.t1 || 0;
          dayTotals.triage_2 += brigadeDay.triage.T2 || brigadeDay.triage.t2_delayed   || brigadeDay.triage.t2 || 0;
          dayTotals.triage_3 += brigadeDay.triage.T3 || brigadeDay.triage.t3_minimal   || brigadeDay.triage.t3 || 0;
        }
      });
      
      // Add HQ/support casualties (distribute evenly across days)
      const dailyHqKia = (hqCasualties.kia + supportCasualties.kia) / duration;
      const dailyHqDow = (hqCasualties.dow + supportCasualties.dow) / duration;
      const dailyHqWia = (hqCasualties.wia + supportCasualties.wia) / duration;
      const dailyHqDnbi = (hqCasualties.dnbi + supportCasualties.dnbi) / duration;
      
      dayTotals.kia += dailyHqKia;
      dayTotals.dow += dailyHqDow;
      dayTotals.wia += dailyHqWia;
      dayTotals.dnbi += dailyHqDnbi;
      
      dayTotals.total_casualties = dayTotals.kia + dayTotals.dow + dayTotals.wia + dayTotals.dnbi;
      
      results.by_day.push({ day, totals: dayTotals });
    }

    // ========================================================================
    // MASCAL-WHEN-WHERE (v5.8.0): Per-brigade R2B bed occupancy timelines.
    // Identifies which brigade's R2B goes CRITICAL first and on what day.
    // Uses same R2B bed counting as personnelIntelligence.js Priority-1 path
    // to stay consistent with Tab4 Section 4D system-wide display.
    // ========================================================================

    // --- Derive total R2B beds from medical_configuration ---
    // Helper predicates — same definitions as personnelIntelligence.js v2.11.
    const _isRole1FacCC  = (f) => ['ROLE1', 'ROLE_1'].includes(f.type || f.role || f.role_type || '');
    const _isRole2BFacCC = (f) => ['ROLE2_BASIC','ROLE_2_BASIC','ROLE2B','Role2Basic','role2_basic']
      .includes(f.role || f.type || f.role_type || f.mtf_role || '');
    const _getBedCC      = (f) => f.beds || f.bed_count || f.capacity || 0;

    let _totalR2BBeds = 0;
    const _mcFacilities = medical_configuration?.facilities;
    if (Array.isArray(_mcFacilities) && _mcFacilities.length > 0) {
      // Priority 1: same path as personnelIntelligence Priority 1
      _mcFacilities.forEach(f => {
        if (!_isRole1FacCC(f) && _isRole2BFacCC(f)) _totalR2BBeds += _getBedCC(f);
      });
    } else {
      // Fallback: mtf_structure (same as personnelIntelligence Priority 4)
      const _mtfStr = medical_configuration?.mtf_structure;
      if (_mtfStr) {
        _totalR2BBeds = (_mtfStr.role2?.beds || 30) * (_mtfStr.role2?.count || 0);
      }
    }

    // PHA-BED (renamed v5.9.0 CSU-BED): Add Patient Holding Area beds to forward holding capacity.
    // PHAs (Section 3G) expand R2B-level holding when use_advanced_staging is enabled.
    // Source: staging_facilities[].capacity_patients where facility_type === 'PHA'.
    // PHA = Role 2B forward holding (AJP-4.10). Renamed from CSU per AJMedP-1 para 3.18.6
    // doctrinal correction: CSU = Casualty Staging Unit at APOD; PHA = forward holding at R2B.
    let _phaBeds_r2b = 0;
    if (medical_configuration?.use_advanced_staging === true) {
      const _stagingFacilities = medical_configuration?.staging_facilities;
      if (Array.isArray(_stagingFacilities)) {
        _stagingFacilities.forEach(sf => {
          if (sf.facility_type === 'PHA') {
            _phaBeds_r2b += sf.capacity_patients || 0;
          }
        });
      }
    }
    _totalR2BBeds += _phaBeds_r2b;

    // --- Non-medical brigade count (medical brigades need no R2B beds) ---
    const _nonMedBrigadeCount = results.by_brigade.filter(b =>
      !(b.unit_type    && b.unit_type.toLowerCase().includes('medical')) &&
      !(b.brigade_name && b.brigade_name.toLowerCase().includes('medical'))
    ).length;

    // Doctrinal FST minimum (30 beds) used when no beds found or no brigades.
    const _r2bBedsPerBrigade = (_nonMedBrigadeCount > 0 && _totalR2BBeds > 0)
      ? Math.round(_totalR2BBeds / _nonMedBrigadeCount)
      : 30;

    // --- Derive THP key for AVG_LOS_BY_THP lookup ---
    const _mascalThpCfg  = scenario?.logistics?.theatre_holding_policy;
    const _mascalThpDays = _mascalThpCfg?.policy_days ||
      (_mascalThpCfg?.policy?.match(/THP_(\d+)_DAY/)?.[1]
        ? parseInt(_mascalThpCfg.policy.match(/THP_(\d+)_DAY/)[1], 10)
        : 7);
    let _mascalThpKey;
    if      (_mascalThpDays <= 3)  _mascalThpKey = 'THP_3_DAY';
    else if (_mascalThpDays <= 5)  _mascalThpKey = 'THP_5_DAY';
    else if (_mascalThpDays <= 7)  _mascalThpKey = 'THP_7_DAY';
    else if (_mascalThpDays <= 15) _mascalThpKey = 'THP_15_DAY';
    else if (_mascalThpDays <= 30) _mascalThpKey = 'THP_30_DAY';
    else                           _mascalThpKey = 'NO_THP';

    const _mascalAvgLOS = AVG_LOS_BY_THP[_mascalThpKey];
    if (_mascalAvgLOS === undefined) {
      throw new Error(
        `AVG_LOS_BY_THP missing key '${_mascalThpKey}'. Check theatreHoldingPolicy.js AVG_LOS_BY_THP structure.`
      );
    }

    // IMP-005: Build per-brigade planned displacement capacity overrides
    // { brigade_id: { day_number: capacity_fraction } }
    const _brigadeDisplacementDays = {};
    const _dispPhases = operation_parameters?.corps_config?.phases || [];
    (medical_configuration?.per_facility_evacuation_assets || [])
      .filter(fa => fa.facility_type === 'ROLE2_BASIC' && fa.phase_displacements?.length)
      .forEach(fa => {
        const brigadeId = fa.allocated_to?.unit_id;
        if (!brigadeId) return;
        fa.phase_displacements.forEach(disp => {
          const ph = _dispPhases[disp.phase_index];
          if (!ph) return;
          const day = ph.start_day;
          const fraction = Math.max(0, 1 - (disp.gap_hrs || 0) / 24);
          if (!_brigadeDisplacementDays[brigadeId]) _brigadeDisplacementDays[brigadeId] = {};
          _brigadeDisplacementDays[brigadeId][day] = Math.min(
            _brigadeDisplacementDays[brigadeId][day] ?? 1, fraction
          );
        });
      });

    // --- Build per-brigade timelines ---
    const _mascalBrigades = results.by_brigade.map(br => {
      const { timeline, first_critical_day, peak_day, peak_pct } =
        this.buildBrigadeR2BBedTimeline(br, _r2bBedsPerBrigade, _mascalAvgLOS, _brigadeDisplacementDays);
      return {
        brigade_id:         br.brigade_id,
        brigade_name:       br.brigade_name,
        r2b_beds:           _r2bBedsPerBrigade,
        timeline,
        first_critical_day,
        peak_day,
        peak_pct
      };
    });

    // --- System summary ---
    const _criticalsOnly = _mascalBrigades.filter(b => b.first_critical_day !== null);
    const _firstCritical = _criticalsOnly.length > 0
      ? _criticalsOnly.reduce((earliest, b) =>
          b.first_critical_day < earliest.first_critical_day ? b : earliest,
          _criticalsOnly[0]
        )
      : null;

    results.mascal_analysis = {
      r2b_beds_per_brigade:  _r2bBedsPerBrigade,
      total_r2b_beds:        _totalR2BBeds,
      csu_beds_added:        _phaBeds_r2b,
      avg_los_days:          _mascalAvgLOS,
      thp_key:               _mascalThpKey,
      brigades:              _mascalBrigades,
      system_summary: {
        first_critical_day:      _firstCritical?.first_critical_day  ?? null,
        first_critical_brigade:  _firstCritical?.brigade_name        ?? null,
        brigades_ever_critical:  _criticalsOnly.length,
        total_brigades:          _mascalBrigades.length
      }
    };

    // ========================================================================
    // v5.10.0 MASCAL-R2E-R3: Per-division R2E and corps R3 MASCAL timelines.
    // Uses same _mascalAvgLOS and HOSPITALIZATION_RATE as R2B block above.
    // ========================================================================

    // --- Extract R2E beds ---
    const _isRole2EFacCC = (f) => [
      'ROLE2_ENHANCED','ROLE_2_ENHANCED','ROLE2E','Role2Enhanced','role2_enhanced'
    ].includes(f.role || f.type || f.role_type || f.mtf_role || '');

    // --- Extract R3 beds ---
    const _isRole3FacCC = (f) => [
      'ROLE3','ROLE_3','Role3','role3'
    ].includes(f.role || f.type || f.role_type || f.mtf_role || '');

    let _totalR2EBeds = 0;
    let _totalR3Beds  = 0;

    if (Array.isArray(_mcFacilities) && _mcFacilities.length > 0) {
      _mcFacilities.forEach(f => {
        if (_isRole2EFacCC(f)) _totalR2EBeds += _getBedCC(f);
        if (_isRole3FacCC(f))  _totalR3Beds  += _getBedCC(f);
      });
    } else {
      const _mtfStr2 = medical_configuration?.mtf_structure;
      if (_mtfStr2) {
        // BUG-R2E-DEFAULT-001 FIX: was `|| 100` — silently replaced 0/null with 100 beds.
        // `?? 0` preserves the configured value (even 0). 100-bed template should never
        // override actual Tab3 configuration. R2E absent → 0 beds × 0 count = correct 0.
        _totalR2EBeds = (_mtfStr2.role2e?.beds ?? 0) * (_mtfStr2.role2e?.count || 0);
        _totalR3Beds  = (_mtfStr2.role3?.beds  ?? 0)   * (_mtfStr2.role3?.count  || 0);
      }
    }

    // BUG-NEXT-015: When r2e_accessible=true but no R2E in force structure (brigade echelon —
    // R2E is a division asset), inject doctrinal default capacity (40 beds —
    // ROLE_2_ENHANCED_DEFINITION.holding_capacity.beds, mtfCapabilities.js).
    // Brigade planners cannot add division R2E to their ORBAT. The toggle is authoritative.
    const _r2eAccessible    = medical_configuration?.r2e_accessible ?? true;
    const _r2eUsingDefaults = _r2eAccessible && _totalR2EBeds === 0;
    if (_r2eUsingDefaults) {
      _totalR2EBeds = 40; // ROLE_2_ENHANCED_DEFINITION.holding_capacity.beds
    }

    // CSU-BED (renamed v5.13.0 PHA-BED): Add Casualty Staging Unit beds to R3/APOD holding capacity.
    // CSUs at APOD/Airfield expand R3-layer holding for STRATEVAC staging buffer.
    // Source: staging_facilities[].capacity_patients where facility_type === 'CSU'.
    // CSU = Casualty Staging Unit at APOD (AJMedP-1 para 3.18.6). Renamed from PHA per
    // doctrinal correction: old 'PHA' label was wrong; this is the STRATEVAC staging function.
    let _csuBeds_r3 = 0;
    if (medical_configuration?.use_advanced_staging === true) {
      const _stagingFacsR3 = medical_configuration?.staging_facilities;
      if (Array.isArray(_stagingFacsR3)) {
        _stagingFacsR3.forEach(sf => {
          if (sf.facility_type === 'CSU') {
            _csuBeds_r3 += sf.capacity_patients || 0;
          }
        });
      }
    }
    _totalR3Beds += _csuBeds_r3;

    // HNS-001: Apply HNS R3 bed augmentation.
    // Host nation civilian hospitals augment R3 holding capacity
    // (AJP-4.10C §5.4; bed_augmentation_percent reliability-adjusted in getHNSModifiers).
    // Applied after PHA beds are added — HNS augments the total organic+PHA pool.
    // Tracked in mascal_analysis for Tab5 HNS panel transparency.
    const _hnsR3BedAugmentation = hnsModifiers.is_active
      ? Math.round(_totalR3Beds * hnsModifiers.bed_augmentation_percent)
      : 0;
    _totalR3Beds += _hnsR3BedAugmentation;

    // BUG-NEXT-014: When r3_accessible=true but no R3 in force structure (ORBAT),
    // inject doctrinal default capacity (150 beds — ROLE_3_DEFINITION.holding_capacity.beds).
    // Division planners cannot/should not add corps R3 to their ORBAT — the toggle is
    // the authoritative input. "R3 accessible" means corps confirmed it exists and is
    // reachable; the model applies NATO doctrinal CSH defaults for overflow routing.
    // Track via r3_using_defaults flag for Tab5 transparency note.
    const _r3UsingDefaults = _r3Accessible && _totalR3Beds === 0;
    if (_r3UsingDefaults) {
      _totalR3Beds = 150; // ROLE_3_DEFINITION.holding_capacity.beds (mtfCapabilities.js)
    }

    // BUG-159 FIX: _totalR3Beds is now fully computed (organic + PHA + HNS augmentation).
    // Declare _r3Beds / _r3Configured here — after all mutations — to satisfy
    // no-use-before-define. Assign r3_configured to the already-initialised mascal_analysis
    // object (created above before _totalR3Beds was available).
    const _r3Beds       = _totalR3Beds;
    const _r3Configured = _totalR3Beds > 0;
    results.mascal_analysis.r3_configured    = _r3Configured;
    results.mascal_analysis.r3_using_defaults = _r3UsingDefaults;

    // ENH-019: MROE / EPW & civilian patient load.
    // getMRoEModifiers() runs in Tab2 buildUpdates() and saves pre-computed values to
    // scenario.operation_parameters.mroe_config.modifiers — the calculator reads them,
    // it does not re-derive them. This mirrors the HNS pattern exactly.
    //
    // EPW hold beds 40% longer than friendly: no RTD curve applies (GC III Art.12 —
    // EPW held until repatriation/transfer, not returned to duty).
    // Civilian patients stabilised and transferred to host-nation system — no LOS multiplier.
    //
    // EPW load does NOT modify _totalR3Beds — it is emitted as a separate planning row
    // (mroe_load) so Tab5 can display it without contaminating the MASCAL threshold math.
    // Doctrinally correct: EPW beds must be a planned augmentation, not a silent reduction
    // of the friendly bed buffer. Source: AJMedP-1 §3.21, GC III Art.12.
    const _mroeRawCfg  = scenario?.operation_parameters?.mroe_config;
    const _mroeModifs  = _mroeRawCfg?.modifiers;
    const _mroeActive  = !!(_mroeRawCfg?.enabled && _mroeModifs);
    // EPW_BED_MULT: EPW hold 40% longer than friendly avg LOS (no RTD; GC III Art.12).
    // Defined here for backward compat; canonical value now in mroeConfig.EPW_BED_MULT.
    const _EPW_BED_MULT = 1.4;

    let _mroeLoad;
    if (_mroeActive) {
      const _epwDaily      = _mroeModifs.epw_casualties_day     || 0;
      const _civDaily      = _mroeModifs.civilian_casualties_day || 0;
      const _epwBedPerDay  = Math.round(_epwDaily * _EPW_BED_MULT);

      // V4-CAL-001: two-stream objects — read from modifiers if pre-computed, else derive.
      // getMRoEModifiers (V4) writes epw_stream / civilian_stream into modifiers.
      // Legacy modifiers (V3.x) will not have these fields; fallback keeps backward compat.
      const _epwStream = _mroeModifs.epw_stream || {
        daily_patients:             _epwDaily,
        section_beds_daily:         _epwBedPerDay,
        collection_hold_days:       1,
        mp_escort_required:         _epwDaily > 0,
        dedicated_section_required: _mroeModifs.epw_dedicated_medical || false,
        additive:                   true,
      };
      const _civStream = _mroeModifs.civilian_stream || {
        daily_patients:       _civDaily,
        triage_profile:       null,
        t1_surgical_daily:    0,
        t2_ward_daily:        0,
        t3_hns_daily:         0,
        competes_with_military: _civDaily > 0,
        civ_active_fraction:  _mroeModifs.civ_active_fraction || 100,
      };

      _mroeLoad = {
        enabled:                      true,
        not_configured:               false,
        epw_level:                    _mroeRawCfg.epw_volume    || _mroeRawCfg.epw_level    || 'NONE',
        civilian_level:               _mroeRawCfg.civilian_density || _mroeRawCfg.civilian_level || 'NONE',
        epw_daily_patients:           _epwDaily,
        civilian_daily_patients:      _civDaily,
        epw_bed_occupancy_per_day:    _epwBedPerDay,
        total_additional_bed_per_day: _epwBedPerDay + _civDaily,
        mp_wing_required:             _epwDaily > 0,
        capacity_impact_pct:          Math.round((_mroeModifs.total_capacity_impact || 0) * 100),
        epw_dedicated_medical:        _mroeModifs.epw_dedicated_medical  || false,
        civilian_cmo_required:        _mroeModifs.civilian_cmo_required   || false,
        humanitarian_crisis:          _mroeModifs.humanitarian_crisis      || false,
        warnings:                     _mroeModifs.warnings                 || [],
        // V4-CAL-001: two-stream breakdown for Tab5 display
        epw_stream:                   _epwStream,
        civilian_stream:              _civStream,
      };
    } else {
      // MROE not configured -- emit sentinel so Tab5 can show the planning gap warning.
      _mroeLoad = {
        enabled:                      _mroeRawCfg?.enabled || false,
        not_configured:               true,
        epw_daily_patients:           0,
        civilian_daily_patients:      0,
        epw_bed_occupancy_per_day:    0,
        total_additional_bed_per_day: 0,
        mp_wing_required:             false,
        capacity_impact_pct:          0,
        epw_stream:                   null,
        civilian_stream:              null,
      };
    }

    // --- Build R2E per-division timelines ---
    // Group non-medical brigades by parent_division_id using force structure.
    // Fall back to splitting evenly if division structure unavailable.
    const _divisions = force?.forceBuilderResult?.divisions || [];
    const _nonMedBrigadeResults = results.by_brigade.filter(b =>
      !(b.unit_type    && b.unit_type.toLowerCase().includes('medical')) &&
      !(b.brigade_name && b.brigade_name.toLowerCase().includes('medical'))
    );

    let _r2eDivisions = [];

    if (_divisions.length > 0) {
      const _r2eBedsPerDivision = (_divisions.length > 0 && _totalR2EBeds > 0)
        ? Math.round(_totalR2EBeds / _divisions.length)
        : 100; // Doctrinal FST default

      _r2eDivisions = _divisions.map(div => {
        // Find brigade results belonging to this division
        const divBrigadeIds = new Set((div.brigades || []).map(b => b.id));
        const divBrigadeResults = _nonMedBrigadeResults.filter(
          br => divBrigadeIds.has(br.brigade_id)
        );

        // IMP-MASCAL-001: R2E drain capacity = sum of each brigade's tactical evac
        // capacity across the division per day. tactical_evac_analysis already computed
        // per brigade per day in calculateBrigadeCasualties(); no new sources needed.
        const _r2eDrainByDay = Array.from({ length: duration }, (_, di) =>
          divBrigadeResults.reduce((sum, br) => {
            const ds = br.by_day?.[di];
            return sum + (ds?.tactical_evac_analysis?.total_capacity_per_day ?? 0);
          }, 0)
        );

        const { timeline, first_critical_day, peak_day, peak_pct } =
          this.buildAggregatedBedTimeline(
            divBrigadeResults,
            _r2eBedsPerDivision,
            _mascalAvgLOS,
            ECHELON_ADMISSION_DISTRIBUTION.ROLE_2_ENHANCED,
            _r2eDrainByDay,
            MTF_SETUP_RAMP_R2E   // IMP-MARCH-001: R2E closes when all div brigades march
          );

        // V4-ECH-002: R2E-absent delta — second pass with 0 beds and no tactical drain.
        // Shows what happens to this division's patients when its R2E MTF is destroyed/overrun.
        // Overflow routes up to R3 or STRATEVAC — no evac drain (disrupted by R2E loss).
        const { timeline: _r2eAbsentTl } = this.buildAggregatedBedTimeline(
          divBrigadeResults,
          0,    // R2E MTF destroyed
          _mascalAvgLOS,
          ECHELON_ADMISSION_DISTRIBUTION.ROLE_2_ENHANCED,
          null, // tactical evac disrupted
          null  // no march ramp
        );
        let _r2eTotalUnmet = 0, _r2eFirstCritWithout = null, _r2eCritDaysWithout = 0;
        let _r2eT1FracSum = 0, _r2eT1FracCount = 0;
        const _r2eLen = Math.min(timeline.length, _r2eAbsentTl.length);
        for (let _di = 0; _di < _r2eLen; _di++) {
          const _base   = timeline[_di]       || {};
          const _absent = _r2eAbsentTl[_di]   || {};
          _r2eTotalUnmet += Math.max(0, (_absent.unmet_demand || 0) - (_base.unmet_demand || 0));
          if (_absent.status === 'CRITICAL' && _r2eFirstCritWithout === null) {
            _r2eFirstCritWithout = _absent.day;
          }
          if (_absent.status === 'CRITICAL') _r2eCritDaysWithout++;
          if (_base.admissions > 0) {
            _r2eT1FracSum   += (_base.icu_admissions || 0) / _base.admissions;
            _r2eT1FracCount += 1;
          }
        }
        const _r2eAvgT1Frac      = _r2eT1FracCount > 0 ? _r2eT1FracSum / _r2eT1FracCount : 0.40;
        const _r2eExcessDeaths   = Math.round(_r2eTotalUnmet * _r2eAvgT1Frac);
        const _r2eBaselineCritDays = timeline.filter(d => d.status === 'CRITICAL').length;
        const r2e_absent_delta = {
          r2e_beds_removed:               _r2eBedsPerDivision,
          first_critical_day_without_r2e: _r2eFirstCritWithout,
          baseline_critical_days:         _r2eBaselineCritDays,
          critical_days_without_r2e:      _r2eCritDaysWithout,
          total_unmet_patient_increase:   Math.round(_r2eTotalUnmet),
          excess_t1_deaths_estimate:      _r2eExcessDeaths,
          avg_t1_fraction:                Math.round(_r2eAvgT1Frac * 100),
          r3_overflow_increase:           Math.round(_r2eTotalUnmet),
        };

        return {
          division_id:        div.id,
          division_name:      div.name || `Division ${div.id}`,
          r2e_beds:           _r2eBedsPerDivision,
          brigade_count:      divBrigadeResults.length,
          timeline,
          first_critical_day,
          peak_day,
          peak_pct,
          r2e_absent_delta,   // V4-ECH-002
        };
      });
    } else if (_nonMedBrigadeResults.length > 0) {
      // No division structure — treat all brigades as one division
      const _r2eBedsPerDivision = _totalR2EBeds > 0 ? _totalR2EBeds : 100;
      const _r2eDrainByDayAll = Array.from({ length: duration }, (_, di) =>
        _nonMedBrigadeResults.reduce((sum, br) => {
          const ds = br.by_day?.[di];
          return sum + (ds?.tactical_evac_analysis?.total_capacity_per_day ?? 0);
        }, 0)
      );
      const { timeline, first_critical_day, peak_day, peak_pct } =
        this.buildAggregatedBedTimeline(
          _nonMedBrigadeResults,
          _r2eBedsPerDivision,
          _mascalAvgLOS,
          ECHELON_ADMISSION_DISTRIBUTION.ROLE_2_ENHANCED,
          _r2eDrainByDayAll,
          MTF_SETUP_RAMP_R2E   // IMP-MARCH-001
        );
      // V4-ECH-002: fallback path delta (no division structure)
      const { timeline: _r2eAbsentTlAll } = this.buildAggregatedBedTimeline(
        _nonMedBrigadeResults, 0, _mascalAvgLOS,
        ECHELON_ADMISSION_DISTRIBUTION.ROLE_2_ENHANCED, null, null
      );
      let _r2eUAll = 0, _r2eFCAll = null, _r2eCDAll = 0, _r2eT1SAll = 0, _r2eT1CAll = 0;
      const _r2eLenAll = Math.min(timeline.length, _r2eAbsentTlAll.length);
      for (let _di = 0; _di < _r2eLenAll; _di++) {
        const _b = timeline[_di] || {}, _a = _r2eAbsentTlAll[_di] || {};
        _r2eUAll += Math.max(0, (_a.unmet_demand || 0) - (_b.unmet_demand || 0));
        if (_a.status === 'CRITICAL' && _r2eFCAll === null) _r2eFCAll = _a.day;
        if (_a.status === 'CRITICAL') _r2eCDAll++;
        if (_b.admissions > 0) { _r2eT1SAll += (_b.icu_admissions || 0) / _b.admissions; _r2eT1CAll++; }
      }
      const _r2eT1FAll = _r2eT1CAll > 0 ? _r2eT1SAll / _r2eT1CAll : 0.40;
      _r2eDivisions = [{
        division_id:        'div_all',
        division_name:      'All Divisions',
        r2e_beds:           _r2eBedsPerDivision,
        brigade_count:      _nonMedBrigadeResults.length,
        timeline,
        first_critical_day,
        peak_day,
        peak_pct,
        r2e_absent_delta: {    // V4-ECH-002
          r2e_beds_removed:               _r2eBedsPerDivision,
          first_critical_day_without_r2e: _r2eFCAll,
          baseline_critical_days:         timeline.filter(d => d.status === 'CRITICAL').length,
          critical_days_without_r2e:      _r2eCDAll,
          total_unmet_patient_increase:   Math.round(_r2eUAll),
          excess_t1_deaths_estimate:      Math.round(_r2eUAll * _r2eT1FAll),
          avg_t1_fraction:                Math.round(_r2eT1FAll * 100),
          r3_overflow_increase:           Math.round(_r2eUAll),
        },
      }];
    }

    // --- Build R3 corps timeline ---

    // IMP-MASCAL-001: R3 drain = STRATEVAC air + train. Compute per departure day.
    // All helpers (computeDepartureDays, STRATEVAC_AIRCRAFT, computeTrainDepartureDays,
    // getTrainCapacityPerDeparture) are already imported. Each try/catch falls back to 0
    // capacity for that asset — graceful degradation with no crash.
    const _r3DrainByDay = (() => {
      const drain = new Array(duration).fill(0);
      const _secCfg = scenario?.medical_configuration?.strategic_evac_config;
      if (!_secCfg) return drain;

      // --- STRATEVAC air drain ---
      if (_secCfg.air_enabled !== false) {
        const _activeAf = (_secCfg.airfields ?? []).filter(af =>
          af != null &&
          af.is_active === true &&
          af.schedule?.aircraft_type &&
          af.schedule?.frequency_mode
        );
        for (const _af of _activeAf) {
          // Primary schedule
          try {
            const _ac       = STRATEVAC_AIRCRAFT[_af.schedule.aircraft_type];
            const _litCap   = _ac?.litter_capacity ?? 40;
            const _fpd      = _af.schedule.flights_per_day ?? 1;
            const _daysCap  = _litCap * _fpd;
            const _depDays  = new Set(computeDepartureDays(_af.schedule, duration));
            for (let _d = 0; _d < duration; _d++) {
              if (_depDays.has(_d + 1)) drain[_d] += _daysCap;
            }
          } catch (_e) { /* skip this airfield primary */ }

          // Secondary schedule (BUG-079 mixed-fleet)
          if (_af.schedule_b?.aircraft_type && _af.schedule_b?.frequency_mode) {
            try {
              const _acB      = STRATEVAC_AIRCRAFT[_af.schedule_b.aircraft_type];
              const _litCapB  = _acB?.litter_capacity ?? 40;
              const _fpdB     = _af.schedule_b.flights_per_day ?? 1;
              const _dayCapB  = _litCapB * _fpdB;
              const _depDaysB = new Set(computeDepartureDays(_af.schedule_b, duration));
              for (let _d = 0; _d < duration; _d++) {
                if (_depDaysB.has(_d + 1)) drain[_d] += _dayCapB;
              }
            } catch (_e) { /* skip this airfield secondary */ }
          }
        }
      }

      // --- Train drain ---
      if (_secCfg.train_enabled && _secCfg.train_schedule?.frequency_mode) {
        try {
          const _capPerDep = getTrainCapacityPerDeparture({
            train_type:           _secCfg.train_type ?? 'STANDARD',
            trains_per_departure: _secCfg.train_schedule.trains_per_departure ?? 1
          });
          if (_secCfg.train_schedule.frequency_mode === 'ON_DEMAND') {
            // ON_DEMAND: distribute capacity evenly (~every 3 days conservative estimate)
            const _dailyEst = _capPerDep / 3;
            for (let _d = 0; _d < duration; _d++) drain[_d] += _dailyEst;
          } else {
            const _trainDays = new Set(computeTrainDepartureDays(_secCfg.train_schedule, duration));
            for (let _d = 0; _d < duration; _d++) {
              if (_trainDays.has(_d + 1)) drain[_d] += _capPerDep;
            }
          }
        } catch (_e) { /* skip train — no drain contribution */ }
      }

      return drain;
    })();

    // ECHELON-DUAL: when R3 not accessible, zero the STRATEVAC drain (no evacuation staging).
    const _effectiveR3Drain = _r3Accessible ? _r3DrainByDay : null;

    const { timeline: _r3Timeline, first_critical_day: _r3FirstCritical,
            peak_day: _r3PeakDay, peak_pct: _r3PeakPct } =
      this.buildAggregatedBedTimeline(
        _nonMedBrigadeResults,
        _r3Beds,
        _mascalAvgLOS,
        ECHELON_ADMISSION_DISTRIBUTION.ROLE_3,
        _effectiveR3Drain,
        null   // IMP-MARCH-001: R3 stays open during tactical movement.
               // MTF_SETUP_RAMP_R3 reserved for future ENH-006 Phase 0 corpus retrograde.
               // When implemented: replace null with MTF_SETUP_RAMP_R3.
      );

    // V4-ECH-001: R3-absent delta — lightweight second pass with echelonBeds=0.
    // Runs ONLY when R3 is configured (delta is meaningful only against a baseline).
    // Uses buildAggregatedBedTimeline with same inputs but 0 beds — shows what happens
    // to patients when the Role 3 MTF is lost (destroyed / inaccessible / overrun).
    // Output: r3_absent_delta in mascal_analysis for Tab5 "What-if: R3 lost" card.
    const _r3AbsentDelta = (() => {
      if (!_r3Configured) return null;

      const { timeline: _r3AbsentTimeline } = this.buildAggregatedBedTimeline(
        _nonMedBrigadeResults,
        0,   // R3 beds = 0 (MTF absent/destroyed)
        _mascalAvgLOS,
        ECHELON_ADMISSION_DISTRIBUTION.ROLE_3,
        null, // no STRATEVAC drain either — R3 loss assumed to disrupt air evac staging
        null
      );

      let totalUnmetIncrease = 0;
      let firstCriticalWithout = null;
      let criticalDaysWithout = 0;
      let t1FracSum = 0;
      let t1FracCount = 0;

      const len = Math.min(_r3Timeline.length, _r3AbsentTimeline.length);
      for (let i = 0; i < len; i++) {
        const base   = _r3Timeline[i]       || {};
        const absent = _r3AbsentTimeline[i] || {};
        const unmetDelta = Math.max(0, (absent.unmet_demand || 0) - (base.unmet_demand || 0));
        totalUnmetIncrease += unmetDelta;
        if (absent.status === 'CRITICAL' && firstCriticalWithout === null) {
          firstCriticalWithout = absent.day;
        }
        if (absent.status === 'CRITICAL') criticalDaysWithout++;
        if (base.admissions > 0) {
          t1FracSum   += (base.icu_admissions || 0) / base.admissions;
          t1FracCount += 1;
        }
      }

      const avgT1Frac        = t1FracCount > 0 ? t1FracSum / t1FracCount : 0.40;
      const excessT1Deaths   = Math.round(totalUnmetIncrease * avgT1Frac);
      const baselineCritDays = _r3Timeline.filter(d => d.status === 'CRITICAL').length;

      return {
        r3_beds_removed:               _r3Beds,
        first_critical_day_without_r3: firstCriticalWithout,
        baseline_critical_days:        baselineCritDays,
        critical_days_without_r3:      criticalDaysWithout,
        total_unmet_patient_increase:  Math.round(totalUnmetIncrease),
        excess_t1_deaths_estimate:     excessT1Deaths,
        avg_t1_fraction:               Math.round(avgT1Frac * 100),
        r2e_overflow_increase:         Math.round(totalUnmetIncrease),
      };
    })();

    const _r3Corps = {
      r3_beds:           _r3Beds,
      brigade_count:     _nonMedBrigadeResults.length,
      timeline:          _r3Timeline,
      first_critical_day: _r3FirstCritical,
      peak_day:          _r3PeakDay,
      peak_pct:          _r3PeakPct
    };

    // --- R2E system summary ---
    const _r2eCriticals = _r2eDivisions.filter(d => d.first_critical_day !== null);
    const _r2eFirstCritical = _r2eCriticals.length > 0
      ? _r2eCriticals.reduce((earliest, d) =>
          d.first_critical_day < earliest.first_critical_day ? d : earliest,
          _r2eCriticals[0]
        )
      : null;

    // Append R2E and R3 to mascal_analysis
    results.mascal_analysis.total_r2e_beds    = _totalR2EBeds;
    results.mascal_analysis.r2e_using_defaults = _r2eUsingDefaults; // BUG-NEXT-015
    results.mascal_analysis.total_r3_beds     = _totalR3Beds;
    results.mascal_analysis.pha_beds_added   = _csuBeds_r3;
    results.mascal_analysis.hns_r3_beds_added = _hnsR3BedAugmentation;
    results.mascal_analysis.mroe_load          = _mroeLoad;
    results.mascal_analysis.r2e_divisions    = _r2eDivisions;
    results.mascal_analysis.r3_corps         = _r3Corps;
    results.mascal_analysis.r2e_system_summary = {
      first_critical_day:      _r2eFirstCritical?.first_critical_day ?? null,
      first_critical_division: _r2eFirstCritical?.division_name      ?? null,
      divisions_ever_critical: _r2eCriticals.length,
      total_divisions:         _r2eDivisions.length
    };
    results.mascal_analysis.r3_system_summary = {
      first_critical_day: _r3FirstCritical,
      ever_critical:      _r3FirstCritical !== null
    };
    // V4-ECH-001: R3-absent delta (null when R3 not configured — delta is undefined in that case)
    // ECHELON-DUAL: is_active_mode=true when r3_accessible=false (this IS the planned scenario)
    results.mascal_analysis.r3_absent_delta = _r3AbsentDelta
      ? { ..._r3AbsentDelta, is_active_mode: !_r3Accessible }
      : null;

    // ECHELON-DUAL: TPRP (Treatment Planning Requirements Profile) — required additional R2E
    // organic surgical capacity when R3 absent. 1 surgical team handles ~14 T3 patients/day (NATO norm).
    // r3_accessible=true → zeroed fields (no additional requirement). Always present in output.
    const _tprpR3Overflow = results.mascal_analysis.r3_absent_delta?.r2e_overflow_increase ?? 0;
    results.tprp = {
      r3_accessible:                  _r3Accessible,
      additional_r2e_teams_required:  _r3Accessible ? 0 : Math.ceil(_tprpR3Overflow / 14),
      additional_blood_prbc_required: _r3Accessible ? 0 : Math.round(_tprpR3Overflow * 2.5),
      mascal_threshold_effect:        _r3Accessible ? 'baseline' : 'accelerated -- R3 drain removed, occupancy rises faster',
      dow_multiplier_applied:         _r3Accessible ? 1.0 : 1.35,
      stratevac_drain_active:         _r3Accessible,
      note: _r3Accessible
        ? 'R3 accessible -- standard echelon support, no additional requirement'
        : 'ECHELON-DUAL -- DOW +35%, STRATEVAC drain disabled, blood consumption +15%',
    };
    // V4-TAB6-004: Pre-combat build-up phase (null when not configured — Tab5 panel hidden)
    results.mascal_analysis.buildup_phase = _buildupPhase;
    // ========================================================================

    // Medical system analysis
    results.medical_system_analysis = {
  medevac: medevacAnalysis,
  mascal_status: mascalStatus,
  golden_hour_analysis: this.calculateGoldenHourSummary(results.by_brigade),
  overall_golden_hour_percent: medevacAnalysis.golden_hour_analysis?.blended_gh_achievement_percent ||
                              this.calculateOverallGoldenHour(results.by_brigade),
  
  // Phase 2.5: Per-facility evacuation chain
  evacuation_chain: medevacAnalysis.chain_analysis || null,
  per_facility_capacity: medevacAnalysis.per_facility_capacity || null,
  bottlenecks: medevacAnalysis.bottlenecks || []
};

    // Medical requirements
    // Calculate medical requirements first (contains blood totals)
    results.medical_requirements = this.calculateTotalMedicalRequirements(results.by_brigade);

    // Phase 6.1: Append STRATEVAC capacity by airfield class + FARP.
    // Computed once per scenario (not per-day). Used by Tab 5 Phase 6.2 display.
    results.medical_requirements.stratevac_capacity = calculateStratevacCapacityByClass(
      _airfieldClass,
      _farpEnabled
    );

    // INC-011: Cross-modal load split — when both air and rail STRATEVAC are enabled,
    // divide the patient pool proportionally by modal holding-area capacity so patients
    // are not double-counted across modes. Proxy: PHA beds (air) vs station beds (rail).
    // Single-mode scenarios receive modalLoadFraction = 1.0 — no change in behaviour.
    const _secCfgForSplit = scenario?.medical_configuration?.strategic_evac_config;
    const _airEnabled  = _secCfgForSplit?.air_enabled !== false;
    const _railEnabled = _secCfgForSplit?.train_enabled === true;
    let _airModalFraction  = 1.0;
    let _railModalFraction = 1.0;
    if (_airEnabled && _railEnabled) {
      const _airBeds = (_secCfgForSplit?.airfields ?? [])
        .filter(af => af?.is_active && af?.schedule?.aircraft_type && af?.schedule?.frequency_mode)
        .reduce((s, af) => s + (af.pha_beds ?? 50), 0);
      const _stationType = _secCfgForSplit?.station_type ?? 'MEDIUM';
      const _railBeds = TRAIN_STATION_STANDARDS[_stationType]?.capacity?.holding_area_beds ?? 50;
      const _totalBeds = _airBeds + _railBeds;
      if (_totalBeds > 0) {
        _airModalFraction  = _airBeds  / _totalBeds;
        _railModalFraction = _railBeds / _totalBeds;
      }
    }

    // ENH-003: STRATEVAC schedule analysis — PHA queue model per airfield.
    // Runs after by_day is fully populated. Requires results.by_day with triage_1 per day.
    // Appended to results.mascal_analysis so Tab5 can surface PHA MASCAL alongside R2B/R2E/R3.
    results.mascal_analysis.stratevac_schedule_analysis = computeStratevacScheduleAnalysis(
      scenario,
      duration,
      results.by_day,
      _airModalFraction
    );

    // ENH-004: Train schedule analysis — station queue model.
    // Mirrors STRATEVAC air model. Returns enabled:false if train not configured.
    results.mascal_analysis.train_schedule_analysis = computeTrainScheduleAnalysis(
      scenario,
      duration,
      results.by_day,
      _railModalFraction
    );
    
    // PHASE 4.1: Add resupply summary to medical requirements
    if (resupplyConfig.configured) {
      // Calculate initial blood stock from Tab 4 (not medical_configuration)
      // CRITICAL FIX: Tab 4 stores stock in logistics.medical_supplies.initial_stock
      const tab4InitialStock = scenario?.logistics?.medical_supplies?.initial_stock || {};
      const initialBloodStock = {
        prbc: tab4InitialStock.prbc_units || 0,
        ffp: tab4InitialStock.ffp_units || 0,
        plt: tab4InitialStock.platelets_units || 0,
        cryo: tab4InitialStock.cryo_units || 0
      };
      
      
      // Calculate daily blood consumption from medical requirements total
      // FIXED: Use results.medical_requirements.blood (already calculated)
      const totalBloodConsumed = results.medical_requirements.blood;
      const avgDailyConsumption = {
        prbc: totalBloodConsumed.prbc / duration,
        ffp: totalBloodConsumed.ffp / duration,
        plt: totalBloodConsumed.plt / duration,
        cryo: totalBloodConsumed.cryo / duration
      };
      
      
      // PHASE 4.6.4: Calculate DOS projection with Monte Carlo simulation
      const dosProjection = calculateDOSProjectionWithMonteCarlo(
        initialBloodStock,
        avgDailyConsumption,
        resupplyConfig,
        duration,
        scenario  // Pass full scenario for route/environmental data
      );
      
      // Organize events by day for Tab 5 display
      const eventsByDay = {};
      for (const [day, events] of Object.entries(resupplyConfig.by_day)) {
        eventsByDay[day] = events.map(event => ({
          day: parseInt(day),
          push_package: event.push_package,
          package_label: PUSH_PACKAGES[event.push_package]?.label || event.push_package,
          delivery_method: event.delivery_method,
          mtf_target: event.mtf_target || 'ALL',
          units: event.units,
          // BUG-TAB6-003 FIX: use ALL blood items (PRBC+FFP+PLT+Cryo) to match how
          // buildDailySimulation() computes bloodDOS = totalBloodStock / totalBloodBase.
          // Using only PRBC gave ~0.1 days while Tab 5 showed ~1.3 days for the same event.
          dos_impact: (() => {
            const totalDelivered = (event.units.prbc || 0) + (event.units.ffp || 0)
                                 + (event.units.plt  || 0) + (event.units.cryo || 0);
            const totalDaily     = (avgDailyConsumption.prbc || 0) + (avgDailyConsumption.ffp || 0)
                                 + (avgDailyConsumption.plt  || 0) + (avgDailyConsumption.cryo || 0);
            return totalDelivered > 0 && totalDaily > 0
              ? Math.round((totalDelivered / totalDaily) * 10) / 10
              : 0;
          })()
        }));
      }
      
      results.medical_requirements.resupply_summary = {
        configured: true,
        total_events: resupplyConfig.events.length,
        total_units: resupplyConfig.total_units,
        events_by_day: eventsByDay,
        dos_projection: dosProjection,
        // TAB4-BADGE-001: expose daily rates so Tab4 getConsumptionDataSource() can detect
        // that the calculator has run and use real rates (not estimates).
        daily_consumption: avgDailyConsumption
      };
      
    } else {
      results.medical_requirements.resupply_summary = {
        configured: false,
        total_events: 0,
        total_units: { prbc: 0, ffp: 0, plt: 0, cryo: 0 },
        events_by_day: {},
        dos_projection: null,
        // TAB4-BADGE-001: expose daily rates even when resupply not configured
        daily_consumption: {
          prbc: results.medical_requirements.blood.prbc / duration,
          ffp:  results.medical_requirements.blood.ffp  / duration,
          plt:  results.medical_requirements.blood.plt  / duration,
          cryo: results.medical_requirements.blood.cryo / duration
        }
      };
    }

    // Generate recommendations
    results.recommendations = this.generateRecommendations(results, medevacAnalysis, mascalStatus);

    // Generate warnings
    results.warnings = this.generateWarnings(results, medevacAnalysis);

    // Add HQ and support details
    results.hq_casualties = hqCasualties;
    results.support_casualties = supportCasualties;

    // ========================================================================
    // CC-004 to CC-007: NEW MODULE INTEGRATION
    // ========================================================================
    
    // Prepare daily casualties array for modules
    const dailyCasualtiesForModules = results.by_day.map((day, index) => ({
      day: index + 1,
      t1: day.totals?.triage_1 || 0,
      t2: day.totals?.triage_2 || 0,
      t3: day.totals?.triage_3 || 0,
      triage_1: day.totals?.triage_1 || 0,
      triage_2: day.totals?.triage_2 || 0,
      triage_3: day.totals?.triage_3 || 0,
      kia: day.totals?.kia || 0,
      dow: day.totals?.dow || 0,
      wia: day.totals?.wia || 0,
      dnbi: day.totals?.dnbi || 0,
      // wound mix of the day (WIA-weighted blend of the brigades): weights the dressing consumption (2026-10-11)
      wound_distribution: (() => {
        const parts = results.by_brigade
          .map((b) => ({ w: b.by_day?.[index]?.battle?.wia || 0, d: b.by_day?.[index]?.wound_distribution?.distribution }))
          .filter((x) => x.d && x.w > 0);
        const total = parts.reduce((sum, x) => sum + x.w, 0);
        if (total <= 0) return null;
        const blend = {};
        ['extremity', 'thorax', 'abdomen', 'head_neck', 'burns', 'multi'].forEach((r) => {
          blend[r] = parts.reduce((sum, x) => sum + x.w * x.d[r], 0) / total;
        });
        return blend;
      })()
    }));

    // CC-004: Supply Chain Module - G4 logistics tracking
    try {
      const supplyChainRaw = calculateSupplyChain(scenario, dailyCasualtiesForModules);
      
      // v5.4.5: Map supply keys to match Tab5 expectations
      // Supply module returns: prbc, ffp, platelets
      // Tab5 expects: prbc_units, ffp_units, platelets_units
      const supplyKeyMap = {
        'prbc': 'prbc_units',
        'ffp': 'ffp_units',
        'platelets': 'platelets_units',
        'cryoprecipitate': 'cryoprecipitate_units',
        'whole_blood': 'whole_blood_units'
      };
      
      // Enhance with status colors and formatted requests using imported helpers
      const enhancedDaysRemaining = {};
      Object.entries(supplyChainRaw.days_remaining || {}).forEach(([item, days]) => {
        const enhancedKey = supplyKeyMap[item] || item;
        // Sanitize: Infinity not valid JSON — becomes null after localStorage round-trip
        const safeDays = isFinite(days) ? days : 9999;
        enhancedDaysRemaining[enhancedKey] = {
          days: safeDays,
          status: getSupplyStatusText(safeDays),
          color: getSupplyStatusColor(safeDays),
          critical: safeDays < DOS_THRESHOLDS.LOW
        };
      });
      
      // Format G4 requests for display
      const formattedG4Requests = (supplyChainRaw.g4_requests || []).map(req => ({
        ...req,
        formatted: formatG4Request(req)
      }));
      
      results.supply_chain = {
        ...supplyChainRaw,
        days_remaining_enhanced: enhancedDaysRemaining,
        g4_requests: formattedG4Requests,
        thresholds: DOS_THRESHOLDS // Include thresholds for UI reference
      };
    } catch (error) {
      results.supply_chain = {
        error: error.message,
        daily_stock: [],
        days_remaining: {},
        days_remaining_enhanced: {},
        critical_items: [],
        g4_requests: [],
        thresholds: DOS_THRESHOLDS
      };
    }

    // CC-005: Strength Projection Module - G1 personnel tracking
    try {
      const strengthRaw = calculateStrengthProjection(scenario, dailyCasualtiesForModules);
      
      // Enhance corps summary with effectiveness rating
      const corpsFinalPercent = strengthRaw.corps_summary?.final_percent || 100;
      const corpsEffectiveness = getEffectivenessRating(corpsFinalPercent);
      
      // Format G1 requests for display
      const formattedG1Requests = (strengthRaw.g1_requests || []).map(req => ({
        ...req,
        formatted: formatG1Request(req)
      }));
      
      // Enhance by_unit with effectiveness ratings
      const enhancedByUnit = (strengthRaw.by_unit || []).map(unit => ({
        ...unit,
        effectiveness_rating: getEffectivenessRating(unit.final_percent || 100)
      }));
      
      results.strength_projection = {
        ...strengthRaw,
        by_unit: enhancedByUnit,
        g1_requests: formattedG1Requests,
        corps_summary: {
          ...strengthRaw.corps_summary,
          effectiveness_rating: corpsEffectiveness
        },
        // Include constants for UI reference
        effectiveness_levels: STRENGTH_EFFECTIVENESS,
        replacement_thresholds: REPLACEMENT_THRESHOLDS
      };
    } catch (error) {
      results.strength_projection = { 
        error: error.message, 
        by_unit: [], 
        threshold_warnings: [], 
        g1_requests: [],
        effectiveness_levels: STRENGTH_EFFECTIVENESS,
        replacement_thresholds: REPLACEMENT_THRESHOLDS
      };
    }

    // CC-006: Time to Surgery Module - Golden Hour POIÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã‚Â ÃƒÂ¢Ã¢â€šÂ¬Ã¢â€žÂ¢Surgery
    try {
      const allFacilities = medical_configuration?.mtf_network || 
                            medical_configuration?.facilities || 
                            [];
      if (allFacilities.length > 0) {
        const timeToSurgeryResults = calculateAllTimesToSurgery(allFacilities, {
          include_treatment_times: true,
          include_fst_detection: true
        });
        
        // Also calculate individual facility times for detailed analysis
        const detailedFacilityTimes = allFacilities
          .filter(f => f.type === 'ROLE1' || f.type === 'ROLE_1' || f.role === 'ROLE1')
          .map(facility => ({
            facility_id: facility.id,
            facility_name: facility.name,
            ...calculateTimeToSurgery(facility, allFacilities, { include_treatment_times: true })
          }));
        
        results.time_to_surgery = {
          by_facility: timeToSurgeryResults,
          detailed_analysis: detailedFacilityTimes,
          summary: getGoldenHourSummary(timeToSurgeryResults),
          fst_recommendations: recommendFSTAttachments(timeToSurgeryResults, allFacilities),
          // Include constants for UI reference
          thresholds: GOLDEN_HOUR_THRESHOLDS,
          surgical_team_specs: SURGICAL_TEAMS
        };
      } else {
        results.time_to_surgery = { 
          warning: 'No MTF network configured', 
          by_facility: [], 
          summary: null,
          thresholds: GOLDEN_HOUR_THRESHOLDS,
          surgical_team_specs: SURGICAL_TEAMS
        };
      }
    } catch (error) {
      results.time_to_surgery = { 
        error: error.message, 
        by_facility: [], 
        summary: null,
        thresholds: GOLDEN_HOUR_THRESHOLDS,
        surgical_team_specs: SURGICAL_TEAMS
      };
    }

    // CC-007: Method-Based Assets Module - PUSH/PULL/AUTO evacuation
    try {
      const allFacilities = medical_configuration?.mtf_network || 
                            medical_configuration?.facilities || 
                            [];
      if (allFacilities.length > 0) {
        const assetAllocations = calculateAllFacilityAssets(allFacilities, 'PULL');
        
        // Calculate individual facility allocations with method details
        const detailedAssetAllocations = allFacilities.map(facility => {
          const method = facility.evacuation_config?.method || 
                         facility.evacuation?.method || 
                         'PULL';
          const distance = facility.evacuation?.distance_km || 
                           facility.distance_to_receiving || 
                           20;
          
          const allocation = calculateMethodBasedAssets(facility, method, distance);
          
          // Calculate DOW impact if using PUSH
          const dowImpact = allocation.method_used === 'PUSH' 
            ? calculatePushDOWImpact(allocation, 5) // Assume 5 casualties/hour
            : null;
          
          // Check if AUTO would recommend PUSH
          const autoRecommendation = shouldAutoPush({
            receiving_load_percent: 70, // Default estimate
            collection_queue: 10,
            casualty_rate_per_hour: 5,
            distance_km: distance
          });
          
          return {
            facility_id: facility.id,
            facility_name: facility.name,
            ...allocation,
            dow_impact: dowImpact,
            auto_recommendation: autoRecommendation
          };
        });
        
        results.method_based_assets = {
          by_facility: assetAllocations,
          detailed_allocations: detailedAssetAllocations,
          echelon_summary: getEchelonCapacitySummary(assetAllocations),
          // Include constants for UI reference
          methods: EVACUATION_METHODS,
          auto_thresholds: AUTO_THRESHOLDS
        };
      } else {
        results.method_based_assets = { 
          warning: 'No facilities configured', 
          by_facility: [], 
          echelon_summary: null,
          methods: EVACUATION_METHODS,
          auto_thresholds: AUTO_THRESHOLDS
        };
      }
    } catch (error) {
      results.method_based_assets = { 
        error: error.message, 
        by_facility: [], 
        echelon_summary: null,
        methods: EVACUATION_METHODS,
        auto_thresholds: AUTO_THRESHOLDS
      };
    }

    // TCCC-2025 Summary for reports
    results.tccc2025_summary = getTCCC2025Summary();

    // ========================================================================
    // SECTION 5: RUN VALIDATION CHECKS
    // ========================================================================
    
    results.warnings = [];
    
    // VALIDATION 1: Detect all units marching simultaneously
    const marchWarnings = detectAllUnitsMarching(results.by_brigade, duration);
    if (marchWarnings.length > 0) {
      results.warnings.push(...marchWarnings);
      marchWarnings.forEach(w => {
      });
    }
    
    // VALIDATION 2: Validate medical brigade coverage
    const medicalCoverageWarning = validateMedicalBrigadeCoverage(force, brigadeConfigs);
    if (medicalCoverageWarning) {
      results.warnings.push(medicalCoverageWarning);
    }
    
    // VALIDATION 4: Validate phase configuration (if phases exist)
    // Check first brigade for phase structure (all should use same phases if inherited)
    if (results.by_brigade.length > 0 && results.by_brigade[0].phases_used) {
      const phaseWarnings = validatePhaseConfiguration(
        results.by_brigade[0].phases_used,
        duration
      );
      if (phaseWarnings.length > 0) {
        results.warnings.push(...phaseWarnings);
        phaseWarnings.forEach(w => {
        });
      }
    }
    
    // INC-002: Propagate paradigm-fallback flag from survivalModule to visible warnings
    if (survivalModule._paradigmMissing) {
      results.warnings.push({
        level: 'WARNING',
        type: 'PARADIGM_FALLBACK',
        message: `Evacuation paradigm "${survivalModule._paradigmMissing}" not recognised — CONTESTED used as fallback`,
        details: 'The configured evacuation paradigm could not be found in the constants file. Survival, DOW, and golden-hour calculations used CONTESTED defaults instead.',
        impact: 'DoW estimates, golden-hour analysis, and blood requirements may differ from the intended paradigm.',
        recommendations: ['Check the evacuation paradigm setting in Tab 2 / Tab 3 and correct the value.']
      });
    }

    // CALC-005: scenario has no evacuation paradigm (saved before Tab 2 wrote it) - say so
    if (survivalModule._paradigmDefaulted) {
      results.warnings.push({
        level: 'WARNING',
        type: 'PARADIGM_DEFAULTED',
        message: 'No evacuation paradigm set in Tab 2 - CONTESTED default applied',
        details: 'The scenario has no operation_parameters.evacuation_paradigm. The documented default (CONTESTED, the most common Ukraine 2022-2024 condition) was used for survival, DOW and evacuation capacity.',
        impact: 'DoW estimates, golden-hour analysis and evacuation capacity follow the CONTESTED paradigm.',
        recommendations: ['Open Tab 2, choose the evacuation paradigm explicitly and save.']
      });
    }

    // Summary
    const criticalCount = results.warnings.filter(w => w.level === 'CRITICAL').length;
    const warningCount = results.warnings.filter(w => w.level === 'WARNING').length;
    
    if (criticalCount > 0 || warningCount > 0) {
    } else {
    }

    // ========================================================================
    // SECTION 6: GENERATE UI-READY DATA STRUCTURES
    // ========================================================================
    
    // Add UI color scheme to results
    results.ui_color_scheme = UI_COLOR_SCHEME;
    
    // Generate phase timeline (use first brigade's phases as reference)
    if (results.by_brigade.length > 0 && results.by_brigade[0].phases_used) {
      const referencePhases = results.by_brigade[0].phases_used;
      
      results.phase_timeline = {
        total_days: duration,
        phase_count: referencePhases.length,
        phases: referencePhases.map((phase, idx) => ({
          index: idx,
          name: phase.name || `Phase ${idx + 1}`,
          start_day: phase.start_day || (idx > 0 ? referencePhases[idx - 1].end_day + 1 : 1),
          end_day: phase.end_day || (phase.start_day + phase.duration_days - 1),
          duration: phase.duration_days,
          posture: phase.posture,
          operational_state: phase.operational_state,
          color: getPostureColor(phase.posture),
          short_name: phase.name ? phase.name.substring(0, 6).toUpperCase() : `PH${idx + 1}`,
          is_march: isMarchPosture(phase.posture)
        })),
        
        // Day-to-phase mapping for quick lookups
        day_to_phase: {}
      };
      
      // Build day-to-phase mapping
      referencePhases.forEach((phase, idx) => {
        const start = phase.start_day || 1;
        const end = phase.end_day || (start + phase.duration_days - 1);
        for (let day = start; day <= end && day <= duration; day++) {
          results.phase_timeline.day_to_phase[day] = phase.name || `Phase ${idx + 1}`;
        }
      });
      
    } else {
      results.phase_timeline = {
        total_days: duration,
        phase_count: 0,
        phases: [],
        day_to_phase: {},
        note: 'Single-phase operation or phases not configured'
      };
    }
    
    // Generate division summaries (if force structure available)
    results.division_summaries = [];
    if (force?.forceBuilderResult?.divisions && Array.isArray(force.forceBuilderResult.divisions)) {
      for (const division of force.forceBuilderResult.divisions) {
        const divisionBrigades = results.by_brigade.filter(b => 
          b.brigade_id && division.brigades && 
          division.brigades.some(db => db.id === b.brigade_id)
        );
        
        const inheritedCount = divisionBrigades.filter(b => 
          b.phase_source === 'DIVISION' || b.phase_source === 'CORPS'
        ).length;
        
        const customCount = divisionBrigades.filter(b => 
          b.phase_source === 'BRIGADE'
        ).length;
        
        results.division_summaries.push({
          division_id: division.id,
          division_name: division.name || `Division ${division.id}`,
          
          phase_status: {
            total_phases: division.phases ? division.phases.length : 0,
            source: division.uses_corps_phases ? 'CORPS' : 'DIVISION',
            inherited: division.uses_corps_phases || false
          },
          
          brigade_phase_status: {
            total_brigades: divisionBrigades.length,
            inherited_count: inheritedCount,
            custom_count: customCount,
            details: divisionBrigades.map(b => ({
              brigade_id: b.brigade_id,
              brigade_name: b.brigade_name,
              phase_source: b.phase_source
            }))
          },
          
          casualties_summary: {
            total_kia: divisionBrigades.reduce((sum, b) => sum + (b.totals?.kia || 0), 0),
            total_dow: divisionBrigades.reduce((sum, b) => sum + (b.totals?.dow || 0), 0),
            total_wia: divisionBrigades.reduce((sum, b) => sum + (b.totals?.wia || 0), 0),
            total_all: divisionBrigades.reduce((sum, b) => sum + (b.totals?.total_all || 0), 0)
          }
        });
      }
      
    }
    
    // Enhance warnings with UI metadata
    results.warnings = results.warnings.map(warning => {
      const uiMetadata = {
        icon: warning.level === 'CRITICAL' ? STATUS_ICONS.RED : warning.level === 'WARNING' ? STATUS_ICONS.YELLOW : STATUS_ICONS.INFO,
        color: warning.level === 'CRITICAL' ? UI_COLOR_SCHEME.severity.CRITICAL :
               warning.level === 'WARNING' ? UI_COLOR_SCHEME.severity.MODERATE :
               UI_COLOR_SCHEME.severity.LOW,
        priority: warning.level === 'CRITICAL' ? 1 :
                  warning.level === 'WARNING' ? 2 : 3,
        category: warning.type.includes('MEDICAL') || warning.type.includes('BRIGADE') ? 'MEDICAL' :
                  warning.type.includes('PHASE') ? 'PLANNING' : 'GENERAL',
        show_in_summary: true,
        dismissible: warning.level !== 'CRITICAL'
      };
      
      return { ...warning, ui: uiMetadata };
    });
    

    // CALC-010: inputs the planner did not provide and the calculation filled with a documented default.
    // Resolver defaults that are decided outside assumptionLedger.resolveInput are noted here.
    if (survivalModule._paradigmDefaulted) {
      assumptionLedger.note('evacuation_paradigm');
    }
    const _envForLedger = resolveScenarioEnvironment(scenario);
    if (_envForLedger.defaulted.terrain) assumptionLedger.note('terrain');
    if (_envForLedger.defaulted.weather) assumptionLedger.note('weather');
    results.assumptions = assumptionLedger.list();

    const endTime = Date.now();
    results.calculation_time_ms = endTime - startTime;


    return results;
  }

  /**
   * TASK 3.2: Resolve which phases a brigade should use
   * 
   * Handles phase inheritance:
   * - Brigade can inherit from Division (uses_division_phases = true)
   * - Division can inherit from Corps (uses_corps_phases = true)
   * - Follows inheritance chain: Corps → Division → Brigade
   * 
   * @param {Object} brigade - Brigade configuration
   * @param {Object} division - Parent division (if exists)
   * @param {Object} corps - Parent corps (if exists)
   * @param {number} duration - Operation duration in days (for fallback phase)
   * @returns {Array} Array of phases to use for this brigade
   */
  getBrigadeActualPhases(brigade, division, corps, duration) {
    // BUG-NEXT-013 FIX: Tab 2 stores phase posture as `operational_posture` and
    // operational state as `mission_type`, but the calculator reads `phase.posture`
    // and `phase.operational_state`. Without this normalization every phase silently
    // falls back to DELIBERATE_DEFENSE regardless of what the user configured.
    // 2026-10-13 (step 1 of the Tab 2 plan): `mission_type` (ATTACK, DEFEND, MOVEMENT ...) is the TACTICAL task, not an
    // operational state (COMBAT_ENGAGED, SUPPORT_ACTIVE, TRUE_RESERVE). It was copied into operational_state, no state
    // matched, and the battle and DNBI lookups fell to x0.2: casualties of every editor phase were about 5 times too low.
    // The phase state is the phase's own value, else the brigade's state set in Tab 2; with neither it is an error.
    const normalizePhase = (p) => {
      const state = p.operational_state ?? brigade.operational_state ?? null;
      if (!state) {
        throw new Error(
          `getBrigadeActualPhases: phase '${p.name || p.phase_name || '?'}' of brigade '${brigade.name || brigade.id}' has no operational state ` +
          `and the brigade has none either. Set the brigade operational state in Tab 2 (Combat engaged, Support active or True reserve).`
        );
      }
      return {
        ...p,
        posture: p.posture ?? p.operational_posture ?? null,
        operational_state: state,
      };
    };

    // CASE 1: Brigade has its own phases (not inheriting)
    if (!brigade.uses_division_phases && brigade.phases && brigade.phases.length > 0) {
      return brigade.phases.map(normalizePhase);
    }

    // CASE 2: Brigade inherits from Division
    if (brigade.uses_division_phases && division) {
      // Check if Division inherits from Corps
      if (division.uses_corps_phases && corps && corps.phases && corps.phases.length > 0) {
        return corps.phases.map(normalizePhase);
      }

      // Division has its own phases
      if (division.phases && division.phases.length > 0) {
        return division.phases.map(normalizePhase);
      }
    }
    
    // CASE 3: Fallback - brigade should have phases but doesn't
    // This indicates data integrity issue
    
    // SECTION 12: Use brigade's actual values, don't hide missing data with defaults
    const fallbackPosture = brigade.operational_posture;
    const fallbackState = brigade.operational_state;
    
    if (!fallbackPosture) {
      console.error(`[SECTION 12] CRITICAL: Brigade "${brigade.name || brigade.id}" has no operational_posture (no phases AND no brigade value)`);
    }
    if (!fallbackState) {
      console.error(`[SECTION 12] CRITICAL: Brigade "${brigade.name || brigade.id}" has no operational_state (no phases AND no brigade value)`);
    }
    
    // SECTION 12: Validate duration for fallback phase
    if (!duration || duration <= 0) {
      console.error(`[SECTION 12] CRITICAL: Fallback phase for brigade "${brigade.name || brigade.id}" has invalid duration (got: ${duration})`);
    }
    
    // Return a single default phase covering the operation
    return [{
      name: 'Full Operation',
      duration_days: duration,  // Will be undefined if not set - this exposes the problem
      posture: fallbackPosture,  // Will be undefined if not set - this exposes the problem
      operational_state: fallbackState,  // Will be undefined if not set - this exposes the problem
      notes: 'Fallback phase - brigade has no defined phases'
    }];
  }

  // ============================================================================
  // MASCAL-WHEN-WHERE: Per-brigade R2B bed occupancy rolling census
  // v5.8.0 — March 4, 2026
  //
  // Algorithm: sliding window of length ceil(avgLOS) over per-day admissions.
  //   For day d: census = sum of admissions in [d - ceil(avgLOS) + 1 .. d]
  //   Admissions on day d = (battle.total + dnbi.total) × HOSPITALIZATION_RATE
  //                         × ECHELON_ADMISSION_DISTRIBUTION.ROLE_2_BASIC
  //
  // Status thresholds mirror calculateBedOccupancy() in theatreHoldingPolicy.js:
  //   CRITICAL ≥100% / HIGH ≥85% / ELEVATED ≥70% / GREEN <70%
  //
  // @param {Object} brigadeResult  - completed brigadeResult (by_day populated)
  // @param {number} r2bBedsForBrigade - R2B beds allocated to this brigade
  // @param {number} avgLOS         - average LOS in days (from AVG_LOS_BY_THP)
  // @returns {{ timeline, first_critical_day, peak_day, peak_pct }}
  // ============================================================================
  // IMP-MASCAL-001 (March 13, 2026) — Active drain model.
  //
  // Previous model: passive sliding window of width ceil(avgLOS). Patients exited
  // automatically after LOS days regardless of evacuation asset availability.
  // Result: 455% sustained R2B occupancy — clinically impossible (MASCAL lasts 1–3 days
  // in exercises per Dr. Tatar; consistent with SABRE peak-to-average ratio ~3.9×).
  //
  // Corrected model (per CURRENT_STATE §32 — research complete session 26):
  //   census_N = census_(N-1)
  //              + admitted_today          [phase-varying: from by_day, NOT planning average]
  //              - rtd_today               [passive: RTD fraction of today's admissions]
  //              - min(evac_capacity,      [active drain: commander evacuation pump]
  //                    census_(N-1),
  //                    fit_to_move)
  //
  // Drain assets at R2B: ground ambulance + MEDEVAC helicopter (tactical_evac_analysis).
  // Surge cap: physical_beds × 1.10 — overflow → unmet_demand field (DOW risk flag).
  // LOS/TPRP role: maximum holding ceiling, NOT drain mechanism. Retained in signature
  // for backward-compatible call sites but no longer used as window width.
  //
  // New output fields: rtd_drain, evac_drain, unmet_demand.
  // Existing fields (day, admissions, icu_admissions, ward_admissions, census,
  //   icu_census, ward_census, occupancy_pct, status) — unchanged for Tab5 backward compat.
  // ============================================================================
  buildBrigadeR2BBedTimeline(brigadeResult, r2bBedsForBrigade, avgLOS, brigadeDisplacementDays = null) { // avgLOS: ceiling ref only
    // IMP-MASCAL-001: R2B surge cap per AJMedP-1 — 10% above nominal beds.
    const SURGE_TOLERANCE = 1.10;
    const surgeCap = Math.floor(r2bBedsForBrigade * SURGE_TOLERANCE);

    // IMP-MARCH-001: Track days since last march day for setup ramp.
    // daysSinceLastMarch = 0 on halt day, 1 next day, etc.
    // capacity_fraction applies to both available beds and admissions gating.
    //
    // Ramp derivation from MTF_SETUP_TIMES_HOURS (Rule 3.2 — wired, not just imported):
    //   R2B setup = MTF_SETUP_TIMES_HOURS.ROLE_2B (12h) => ceil(12/24) = 1 ramp step
    //   MTF_SETUP_RAMP_R2B[0]=0.0 (halt day), [1]=0.5 (day 1, 50%), [2]=1.0 (day 2+)
    //   R2E setup = MTF_SETUP_TIMES_HOURS.ROLE_2E (24h) => ceil(24/24) = 2 ramp steps
    //   MTF_SETUP_RAMP_R2E[0]=0.0, [1]=0.0, [2]=1.0
    //   R3  setup = MTF_SETUP_TIMES_HOURS.ROLE_3  (48h) => ceil(48/24) = 3 ramp steps
    //   MTF_SETUP_RAMP_R3[0]=0.0, [1]=0.0, [2]=0.0, [3]=1.0 — not applied at corps level
    //   (R3 does not displace during tactical movement — null passed to buildAggregatedBedTimeline)
    if (process.env.NODE_ENV === 'development') {
      // Runtime cross-check: ramp length must equal ceil(setup_hours/24) + 2 for each echelon.
      // +2 because the ramp always has a day-0 entry (halt day = 0% capacity) AND a final
      // entry at 100%, with ceil(hours/24) intermediate days in between.
      // BUG-212 FIX: was + 1, causing false warnings for R2B/R2E/R3 (all off by 1).
      // MTF_SETUP_RAMP_R3 is validated here even though null is passed to buildAggregatedBedTimeline
      // for R3 (corps does not displace during tactical movement). When ENH-006 Phase 0 implements
      // R3 displacement, replace null with MTF_SETUP_RAMP_R3 at the R3 call site.
      const _rampChecks = [
        { name: 'R2B', hours: MTF_SETUP_TIMES_HOURS.ROLE_2B, ramp: MTF_SETUP_RAMP_R2B },
        { name: 'R2E', hours: MTF_SETUP_TIMES_HOURS.ROLE_2E, ramp: MTF_SETUP_RAMP_R2E },
        { name: 'R3',  hours: MTF_SETUP_TIMES_HOURS.ROLE_3,  ramp: MTF_SETUP_RAMP_R3  }
      ];
      for (const { name, hours, ramp } of _rampChecks) {
        const _expected = Math.ceil(hours / 24) + 2;
        if (ramp.length !== _expected) {
          console.warn(
            `MTF ramp mismatch: MTF_SETUP_RAMP_${name} has ${ramp.length} steps ` +
            `but MTF_SETUP_TIMES_HOURS.ROLE_${name.replace('2B','2_BASIC').replace('2E','2_ENHANCED')} ` +
            `(${hours}h) implies ${_expected}. Update in casualtyCalculatorConstants.js.`
          );
        }
      }
    }
    let daysSinceLastMarch = Infinity; // Infinity = never marched = full capacity
    let movementBacklogTotal = 0;      // Accumulated patients who could not be admitted during march

    const timeline = [];
    let prevCensus     = ASSEMBLY_DAY0_BEDS ? (brigadeResult.assembly_held_patients || 0) : 0;   // day-0 load of the assembly
    let prevIcuCensus  = 0;
    for (const daySummary of brigadeResult.by_day) {
      // ── IMP-MARCH-001: Update march tracking ─────────────────────────────
      const isMarch = daySummary.is_march_operation === true;
      if (isMarch) {
        daysSinceLastMarch = 0;
      } else {
        // Day after march: increment counter (clamped so ramp index stays valid)
        if (daysSinceLastMarch < Infinity) {
          daysSinceLastMarch = Math.min(daysSinceLastMarch + 1, MTF_SETUP_RAMP_R2B.length - 1);
        }
      }

      // ── IMP-MARCH-001: Capacity fraction from setup ramp ─────────────────
      // During march (daysSinceLastMarch === 0): capacity = 0 (MTF packed for movement).
      // Day 1 post-march: capacity = 50% (one section open, one still setting up).
      // Day 2+ post-march: capacity = 100% (fully operational).
      // Never marched (daysSinceLastMarch === Infinity): capacity = 100%.
      let capacityFraction;
      if (daysSinceLastMarch === Infinity) {
        capacityFraction = 1.0;
      } else {
        const rampIdx = Math.min(daysSinceLastMarch, MTF_SETUP_RAMP_R2B.length - 1);
        capacityFraction = MTF_SETUP_RAMP_R2B[rampIdx];
      }

      // IMP-005: Planned displacement override (user-configured, non-march days only)
      const _bId = brigadeResult.brigade_id ?? brigadeResult.id;
      const _plannedFraction = brigadeDisplacementDays?.[_bId]?.[daySummary.day];
      if (_plannedFraction !== undefined && !isMarch) {
        capacityFraction = Math.min(capacityFraction, _plannedFraction);
      }

      // Effective available beds this day (setup ramp applied to denominator too)
      const effectiveBeds = r2bBedsForBrigade * capacityFraction;
      const effectiveSurgeCap = Math.floor(effectiveBeds * SURGE_TOLERANCE);

      const dailyCas   = (daySummary.battle?.total || 0) + (daySummary.dnbi?.total || 0);

      // ── IMP-MARCH-001: Sub B — Admission gate ────────────────────────────
      // During march (capacityFraction === 0): MTF is not operational.
      // Casualties cannot be admitted — they accumulate as movement_backlog.
      // When MTF opens (capacityFraction > 0): backlog admitted first at ramp capacity,
      // then fresh casualties. This models the Day 1 assault spike being partly backlog.
      let rawAdmissions = dailyCas * HOSPITALIZATION_RATE * ECHELON_ADMISSION_DISTRIBUTION.ROLE_2_BASIC;

      let backlog_admitted = 0;
      if (capacityFraction === 0) {
        // MTF closed — accumulate backlog, zero admissions to census
        movementBacklogTotal += rawAdmissions;
        rawAdmissions = 0;
      } else if (movementBacklogTotal > 0) {
        // MTF partially or fully open — admit backlog up to capacity headroom
        // Headroom = effectiveSurgeCap - prevCensus (available space)
        const headroom = Math.max(0, effectiveSurgeCap - prevCensus);
        backlog_admitted = Math.min(movementBacklogTotal, headroom * 0.5); // 50% headroom for backlog
        movementBacklogTotal = Math.max(0, movementBacklogTotal - backlog_admitted);
        rawAdmissions += backlog_admitted;
      }

      const admissions = rawAdmissions;

      // ENH-011: ICU vs ward split (unchanged).
      const t1 = daySummary.triage?.T1
              ?? daySummary.triage?.t1_immediate
              ?? daySummary.triage?.t1
              ?? 0;
      const icuAdmissions  = t1 * HOSPITALIZATION_RATE * ECHELON_ADMISSION_DISTRIBUTION.ROLE_2_BASIC;
      const wardAdmissions = Math.max(0, admissions - icuAdmissions);

      // RTD drain — passive self-discharge.
      const rtdTotal  = (daySummary.battle?.rtd || 0) + (daySummary.dnbi?.rtd || 0);
      const rtd_drain = Math.min(
        rtdTotal * ECHELON_ADMISSION_DISTRIBUTION.ROLE_2_BASIC,
        prevCensus
      );

      // Active drain — reduced during march (no assets) and setup ramp.
      // During march: evac_capacity from tactical_evac_analysis may still move
      // existing patients forward even if MTF cannot admit new ones.
      const evac_capacity = daySummary.tactical_evac_analysis?.total_capacity_per_day ?? 0;

      const triage = daySummary.triage || {};
      const totalTriage = (triage.T1 || 0) + (triage.T2 || 0)
                        + (triage.T3 || 0) + (triage.T4 || 0);
      const t4Frac      = totalTriage > 0 ? (triage.T4 || 0) / totalTriage : 0;
      const fit_to_move = Math.max(0, prevCensus * (1 - t4Frac));

      const evac_drain  = Math.min(evac_capacity, prevCensus, fit_to_move);

      // New census — capped at effective surge capacity.
      const rawCensus    = Math.max(0, prevCensus + admissions - rtd_drain - evac_drain);
      const census       = Math.min(rawCensus, effectiveSurgeCap > 0 ? effectiveSurgeCap : surgeCap);
      const unmet_demand = Math.max(0, rawCensus - (effectiveSurgeCap > 0 ? effectiveSurgeCap : surgeCap));

      // ICU/ward split — proportional to prior ICU fraction.
      const totalPool = prevCensus + admissions;
      const icuFrac   = totalPool > 0
        ? (prevIcuCensus + icuAdmissions) / totalPool
        : 0;
      const icuCensus  = Math.round(census * icuFrac * 10) / 10;
      const wardCensus = Math.max(0, Math.round((census - icuCensus) * 10) / 10);

      // Occupancy against full nominal beds (planning reference denominator unchanged)
      const occupancy_pct = r2bBedsForBrigade > 0
        ? Math.round((census / r2bBedsForBrigade) * 100)
        : 0;

      let status;
      if (occupancy_pct >= 100)     status = 'CRITICAL';
      else if (occupancy_pct >= 85) status = 'HIGH';
      else if (occupancy_pct >= 70) status = 'ELEVATED';
      else                          status = 'GREEN';

      timeline.push({
        day:              daySummary.day,
        admissions:       Math.round(admissions     * 10) / 10,
        icu_admissions:   Math.round(icuAdmissions  * 10) / 10,
        ward_admissions:  Math.round(wardAdmissions * 10) / 10,
        census:           Math.round(census         * 10) / 10,
        icu_census:       icuCensus,
        ward_census:      wardCensus,
        rtd_drain:        Math.round(rtd_drain      * 10) / 10,
        evac_drain:       Math.round(evac_drain     * 10) / 10,
        unmet_demand:     Math.round(unmet_demand   * 10) / 10,
        occupancy_pct,
        status,
        // IMP-MARCH-001: new march-awareness fields
        is_march:               isMarch,
        capacity_fraction:      Math.round(capacityFraction * 100) / 100,
        movement_backlog:       Math.round(movementBacklogTotal * 10) / 10,
        backlog_admitted:       Math.round(backlog_admitted   * 10) / 10,
        days_since_last_march:  daysSinceLastMarch === Infinity ? null : daysSinceLastMarch
      });

      prevCensus     = census;
      prevIcuCensus  = icuCensus;
    }

    const firstCriticalDay = timeline.find(d => d.status === 'CRITICAL')?.day ?? null;
    const peak = timeline.length > 0
      ? timeline.reduce((best, d) => d.occupancy_pct > best.occupancy_pct ? d : best, timeline[0])
      : null;

    return {
      timeline,
      first_critical_day:    firstCriticalDay,
      peak_day:              peak?.day           ?? null,
      peak_pct:              peak?.occupancy_pct ?? 0,
      // IMP-MARCH-001: summary fields
      had_march_phases:      timeline.some(d => d.is_march),
      peak_movement_backlog: timeline.reduce((m, d) => Math.max(m, d.movement_backlog || 0), 0)
    };
  }

  // ============================================================================
  // v5.10.0 MASCAL-R2E-R3: Aggregated bed timeline for R2E (division) and R3 (corps).
  //
  // Algorithm mirrors buildBrigadeR2BBedTimeline but aggregates daily casualties
  // across multiple brigade results before applying the echelon admission fraction.
  //
  // Daily admissions = Σ(battle.total + dnbi.total across brigades on day D)
  //                    × HOSPITALIZATION_RATE × admissionFraction
  //
  // @param {Array}  brigadeResultsArray  - subset of results.by_brigade for this echelon
  // @param {number} echelonBeds          - total beds at this echelon (R2E per-div or R3 total)
  // @param {number} avgLOS               - from AVG_LOS_BY_THP (same key as R2B)
  // @param {number} admissionFraction    - ECHELON_ADMISSION_DISTRIBUTION.ROLE_2_ENHANCED or ROLE_3
  // @returns {{ timeline, first_critical_day, peak_day, peak_pct }}
  // ============================================================================
  // IMP-MASCAL-001: Active drain model for R2E (division) and R3 (corps).
  // Mirrors the corrected buildBrigadeR2BBedTimeline algorithm but aggregates
  // casualties across multiple brigade results before applying echelon fraction.
  //
  // @param {Array}  brigadeResultsArray  - subset of results.by_brigade for this echelon
  // @param {number} echelonBeds          - total beds at this echelon
  // @param {number} avgLOS               - retained in signature (ceiling ref), not used as window
  // @param {number} admissionFraction    - ECHELON_ADMISSION_DISTRIBUTION.ROLE_2_ENHANCED or ROLE_3
  // @param {number[]|null} drainCapacityByDay - evac capacity per day (0-indexed). null = 0 drain.
  //   R2E: pass aggregated tactical_evac capacity across division brigades.
  //   R3:  pass STRATEVAC (air) + train capacity per departure day.
  // ============================================================================
  buildAggregatedBedTimeline(brigadeResultsArray, echelonBeds, avgLOS, admissionFraction, drainCapacityByDay = null, marchRamp = null) {
    if (!brigadeResultsArray || brigadeResultsArray.length === 0) {
      return { timeline: [], first_critical_day: null, peak_day: null, peak_pct: 0 };
    }

    // IMP-MASCAL-001: R2E/R3 surge cap — 10% above nominal beds.
    const SURGE_TOLERANCE = 1.10;
    const surgeCap = Math.floor(echelonBeds * SURGE_TOLERANCE);

    // Determine operation duration from longest brigade timeline
    const durationAgg = brigadeResultsArray.reduce(
      (max, br) => Math.max(max, br.by_day?.length || 0), 0
    );

    // BUG-R2E-MARCH-001: Backlog counter — mirrors movementBacklogTotal in buildBrigadeR2BBedTimeline.
    // Accumulates patients who cannot be admitted when echelonCapacityFraction===0 (MTF closed
    // during march). Drained on post-march days up to 50% of available headroom. Without this,
    // patients generated during march phases were permanently lost.
    let movementBacklogAgg = 0;

    const timeline = [];
    let prevCensus     = 0;
    let prevIcuCensus  = 0;
    for (let i = 0; i < durationAgg; i++) {
      const day = i + 1;

      // Aggregate daily casualties, T1, RTD, and T4 across all brigades for this day
      let dailyCasAgg = 0, t1Agg = 0, rtdAgg = 0, t4Agg = 0, totalTriageAgg = 0;
      for (const br of brigadeResultsArray) {
        const ds = br.by_day?.[i];
        if (ds) {
          dailyCasAgg    += (ds.battle?.total || 0) + (ds.dnbi?.total || 0);
          t1Agg          += ds.triage?.T1 ?? ds.triage?.t1_immediate ?? ds.triage?.t1 ?? 0;
          rtdAgg         += (ds.battle?.rtd || 0) + (ds.dnbi?.rtd || 0);
          const tr = ds.triage || {};
          t4Agg          += (tr.T4 || 0);
          totalTriageAgg += (tr.T1 || 0) + (tr.T2 || 0) + (tr.T3 || 0) + (tr.T4 || 0);
        }
      }

      // IMP-MARCH-001: Gate admissions when all brigades in echelon are marching.
      // allMarching = true only when every ds in this day's aggregation has is_march_operation.
      const allMarching = brigadeResultsArray.length > 0 && brigadeResultsArray.every(br => {
        const ds = br.by_day?.[i];
        return ds && ds.is_march_operation === true;
      });

      // Apply march setup ramp to this echelon (null marchRamp = R3, always open)
      let echelonCapacityFraction = 1.0;
      if (marchRamp) {
        // Count consecutive days since last all-march day using the timeline built so far
        let daysSinceMarchAgg = Infinity;
        for (let k = timeline.length - 1; k >= 0; k--) {
          if (timeline[k].is_march) { daysSinceMarchAgg = timeline.length - k; break; }
        }
        if (allMarching) daysSinceMarchAgg = 0;
        if (daysSinceMarchAgg === Infinity) {
          echelonCapacityFraction = 1.0;
        } else {
          const rampIdx = Math.min(daysSinceMarchAgg, marchRamp.length - 1);
          echelonCapacityFraction = marchRamp[rampIdx];
        }
      }

      let rawAdmissions = dailyCasAgg * HOSPITALIZATION_RATE * admissionFraction;

      // BUG-R2E-MARCH-001: Backlog gate — same pattern as R2B admission gate.
      // When MTF closed (fraction=0): accumulate to backlog, zero admissions.
      // When MTF reopens and backlog exists: admit up to 50% of available headroom.
      let backlogAdmittedAgg = 0;
      if (echelonCapacityFraction === 0) {
        // MTF closed during march — patients cannot be admitted, accumulate in backlog
        movementBacklogAgg += rawAdmissions;
        rawAdmissions = 0;
      } else if (movementBacklogAgg > 0) {
        // MTF open (post-march or no-march) — drain backlog up to 50% of available headroom
        const headroomAgg = Math.max(0, surgeCap - prevCensus);
        backlogAdmittedAgg = Math.min(movementBacklogAgg, headroomAgg * 0.5);
        movementBacklogAgg = Math.max(0, movementBacklogAgg - backlogAdmittedAgg);
        rawAdmissions += backlogAdmittedAgg;
      }

      const admissions     = rawAdmissions;
      // ENH-011: ICU = T1-driven; ward = remainder
      const icuAdmissions  = t1Agg * HOSPITALIZATION_RATE * admissionFraction;
      const wardAdmissions = Math.max(0, admissions - icuAdmissions);

      // RTD drain — passive self-discharge (scale by echelon admission fraction)
      const rtd_drain = Math.min(
        rtdAgg * admissionFraction,
        prevCensus
      );

      // Active drain — capacity provided by caller (R2E: tactical evac; R3: STRATEVAC + train)
      const evac_capacity = drainCapacityByDay ? (drainCapacityByDay[i] ?? 0) : 0;
      const t4Frac        = totalTriageAgg > 0 ? t4Agg / totalTriageAgg : 0;
      const fit_to_move   = Math.max(0, prevCensus * (1 - t4Frac));
      const evac_drain    = Math.min(evac_capacity, prevCensus, fit_to_move);

      // New census — capped at surge capacity; overflow tracked as unmet_demand.
      const rawCensus    = Math.max(0, prevCensus + admissions - rtd_drain - evac_drain);
      const census       = Math.min(rawCensus, surgeCap);
      const unmet_demand = Math.max(0, rawCensus - surgeCap);

      // ICU/ward split — proportional to prior ICU fraction
      const totalPool = prevCensus + admissions;
      const icuFrac   = totalPool > 0
        ? (prevIcuCensus + icuAdmissions) / totalPool
        : 0;
      const icuCensus  = Math.round(census * icuFrac * 10) / 10;
      const wardCensus = Math.max(0, Math.round((census - icuCensus) * 10) / 10);

      const occupancy_pct = echelonBeds > 0
        ? Math.round((census / echelonBeds) * 100)
        : 0;

      let status;
      if (occupancy_pct >= 100)     status = 'CRITICAL';
      else if (occupancy_pct >= 85) status = 'HIGH';
      else if (occupancy_pct >= 70) status = 'ELEVATED';
      else                          status = 'GREEN';

      timeline.push({
        day,
        admissions:        Math.round(admissions     * 10) / 10,
        icu_admissions:    Math.round(icuAdmissions  * 10) / 10,
        ward_admissions:   Math.round(wardAdmissions * 10) / 10,
        census:            Math.round(census         * 10) / 10,
        icu_census:        icuCensus,
        ward_census:       wardCensus,
        rtd_drain:         Math.round(rtd_drain    * 10) / 10,
        evac_drain:        Math.round(evac_drain   * 10) / 10,
        unmet_demand:      Math.round(unmet_demand * 10) / 10,
        occupancy_pct,
        status,
        // IMP-MARCH-001
        is_march:          allMarching,
        capacity_fraction: Math.round(echelonCapacityFraction * 100) / 100,
        // BUG-R2E-MARCH-001: backlog tracking (mirrors R2B timeline fields)
        movement_backlog:  Math.round(movementBacklogAgg  * 10) / 10,
        backlog_admitted:  Math.round(backlogAdmittedAgg  * 10) / 10,
      });

      prevCensus     = census;
      prevIcuCensus  = icuCensus;
    }

    const firstCriticalDay = timeline.find(d => d.status === 'CRITICAL')?.day ?? null;
    const peak = timeline.length > 0
      ? timeline.reduce((best, d) => d.occupancy_pct > best.occupancy_pct ? d : best, timeline[0])
      : null;

    return {
      timeline,
      first_critical_day: firstCriticalDay,
      peak_day:  peak?.day           ?? null,
      peak_pct:  peak?.occupancy_pct ?? 0,
      // BUG-R2E-MARCH-001: summary fields (mirrors R2B return shape)
      had_march_phases:      timeline.some(d => d.is_march),
      peak_movement_backlog: timeline.reduce((m, d) => Math.max(m, d.movement_backlog || 0), 0)
    };
  }


  calculateBrigadeCasualties(config) {
    const {
      brigade: brigadeRaw,
      duration,  // Used in fallback phase when brigade has no phases configured
      scenario,
      forceQuality,
      enemyQuality,
      medicalConfig,
      medevacAnalysis,
      medicalSystemState,
      wbbConfig,        // PHASE 1.1: Receive WBB config as parameter
      routeImpact,      // PHASE 2.1: Receive route impact as parameter
      resupplyConfig,   // PHASE 4.1: Receive resupply config as parameter
      forceStructure,   // TASK 3.2: Receive force structure for phase resolution
      frtDOWModifier = 1.0,  // v5.9.0: FRT DOW modifier (default 1.0 = no effect)
      r3AbsentDOWMod = 1.0,  // ECHELON-DUAL: R3-absent DOW multiplier (default 1.0 = accessible)
      orbatSendingAssets = 4, // OBS-S96-006: ORBAT-derived R1 fleet total (default 4 if zero brigades)
      buildupPhase = null     // IMP-BUILDUP-001: Day-0 held load (null when build-up disabled)
    } = config;

    // v5.1.3: Ensure brigade has sub_units before ANY processing.
    // Handles old scenario data saved before ForceService Phase 2 was deployed.
    // ensureBrigadeSubUnits() generates doctrinal sub_units from UNIT_TYPES if missing.
    const brigade = ensureBrigadeSubUnits(brigadeRaw);

    // Phase 6.1: Extract per-brigade Phase 6.1 config from medicalConfig.
    // Declared here (not in calculateCasualties) because calculateBrigadeCasualties
    // is a separate method — variables do not cross method scope boundaries.
    const _mtpEnabled         = medicalConfig?.blood_config?.mtp_enabled        ?? false;
    const _mtpProtocol        = medicalConfig?.blood_config?.mtp_protocol        ?? '1:1:1';
    const _shelfLifeEnabled   = medicalConfig?.blood_config?.shelf_life_tracking ?? false;
    const _surgeonHoursWorked = medicalConfig?.surgical_config?.hours_worked     ?? 0;
    // T3-02: the surgical teams of this brigade (null = not configured: the resource module keeps its planning default)
    const _surgTeamsThisBrigade = surgicalTeamsFromTab3(
      medicalConfig,
      (scenario?.force?.forceBuilderResult?.brigades || scenario?.force?.brigades || []).length
    );
    const _resourceMedConfig = _surgTeamsThisBrigade === null ? medicalConfig : { ...medicalConfig, surgical_teams_per_brigade: _surgTeamsThisBrigade };

    // Blood on hand day by day (Tab 4 owns the stock, see resolveBloodStock): the effects apply when Tab 4 gave the stock.
    const _bloodActive = BLOOD_FROM_TAB4_DAILY && medicalSystemState?.blood_source === 'TAB4';
    const _bloodShare = Math.max(1, (scenario?.force?.forceBuilderResult?.brigades || scenario?.force?.brigades || []).length);
    let bloodOnHand = _bloodActive ? medicalSystemState.blood_initial_total / _bloodShare : null;
    const bloodInitialShare = bloodOnHand;
    let bloodAlertPrev = 'GREEN';   // the alert of the day before reaches the survival model

    // HNS-001: Read pre-computed HNS modifiers from scenario (saved by Tab2 buildUpdates via
    // getHNSModifiers). Cannot read the outer-scope hnsModifiers — different method boundary.
    // getHNSModifiers returns an inert all-zero object when HNS is disabled (backward compat).
    const hnsModifiers = getHNSModifiers(hnsConfigAfterInfrastructure(scenario?.operation_parameters?.hns_config, scenario));

    // V3.1-BLOOD-STREAM2: Read MRoE civilian stream config for this method's scope.
    // Cannot read outer-scope _mroeLoad — different method boundary (same pattern as HNS above).
    // civilian_stream.t1_surgical_daily is pre-computed by getMRoEModifiers() and saved to
    // scenario.operation_parameters.mroe_config.modifiers by Tab2 buildUpdates().
    const _mroeCfgBC   = scenario?.operation_parameters?.mroe_config;
    const _mroeModsBC  = _mroeCfgBC?.modifiers;
    const _mroeActiveBC = !!(_mroeCfgBC?.enabled && _mroeModsBC);
    const _civT1DailyBC = _mroeActiveBC
      ? (_mroeModsBC?.civilian_stream?.t1_surgical_daily || 0)
      : 0;
    // R2-08 (2026-10-21): the military takes only the share of the civilian cases that the rules of engagement oblige it to and the host nation hospitals
    // cannot take (civilianLoad.js): civilians needing surgery go to the Role 2 Enhanced and Role 3 first, the blood they use is scaled by the same share.
    const _hostStatusBC = scenario?.operation_parameters?.infrastructure_config?.enabled === true
      ? (scenario.operation_parameters.infrastructure_config.local_medical || 'FUNCTIONAL') : 'FUNCTIONAL';
    // every patient category of the matrix is a stream (civilians hurt by our fire, other civilians, HN forces, VIP, contractors, prisoners): civilianLoad.js
    const _eligBC = _mroeActiveBC
      ? eligibilityLoad({
          matrix: _mroeCfgBC.matrix,
          hostStatus: _hostStatusBC,
          civilianSurgicalDaily: _civT1DailyBC,
          civilianWardDaily: _mroeModsBC?.civilian_stream?.t2_ward_daily || 0,
          epwDailyPatients: _mroeModsBC?.epw_stream?.daily_patients || 0,
          volumes: _mroeCfgBC
        })
      : null;
    const _civT1Military = _eligBC ? _eligBC.military_cases : 0;
    const _civBrigadesBC = Math.max(1, (scenario?.force?.forceBuilderResult?.brigades || scenario?.force?.brigades || []).length);

    // TASK 3.2: Resolve brigade phases (may inherit from division or corps)
    let parentDivision = null;
    let parentCorps = null;
    
    if (forceStructure) {
      // Find parent division if brigade has parent_division_id
      if (brigade.parent_division_id && forceStructure.divisions) {
        parentDivision = forceStructure.divisions.find(d => d.id === brigade.parent_division_id);
      }
      
      // Get corps if available
      if (forceStructure.corps) {
        parentCorps = forceStructure.corps;
      }
    }
    
    // Resolve actual phases for this brigade
    const actualPhases = this.getBrigadeActualPhases(brigade, parentDivision, parentCorps, duration);

    // SECTION 12: Remove fallback, validate required data
    let initialStrength = brigade.strength;

    if (!initialStrength || initialStrength <= 0) {
      const brigadeNameTemp = brigade.name || brigade.brigade_name || brigade.unit_name || 'Unknown';
      console.error(`[SECTION 12] CRITICAL: Brigade "${brigadeNameTemp}" missing or invalid strength (got: ${initialStrength})`);
      initialStrength = 1;
    }

    // IMP-BUILDUP-001: Soldiers held in the medical system at H-Hour (Day-0 load)
    // do not step off the line of departure — subtract their proportional share
    // from this brigade's starting strength.
    let assemblyHeldPatients = 0;   // this brigade's share of the day-0 load (patients in the medical system at D-day)
    if (buildupPhase?.enabled && buildupPhase.dnbi_held_rear > 0 && buildupPhase.total_strength_reference > 0) {
      const brigadeHeldRear = Math.round(
        buildupPhase.dnbi_held_rear * (initialStrength / buildupPhase.total_strength_reference)
      );
      assemblyHeldPatients = brigadeHeldRear;
      initialStrength = Math.max(1, initialStrength - brigadeHeldRear);
    }
    
    // BUG-060/061 FIX: Generate unique brigade ID and name
    // Tab 1/2 may not provide id/name, causing duplicate React keys and "Unknown Brigade"
    const brigadeIndex = brigade._index ?? Math.floor(Math.random() * 1000);
    const brigadeId = brigade.id || brigade.brigade_id || `brigade_${brigadeIndex}`;
    const brigadeName = brigade.name || brigade.brigade_name || brigade.unit_name || `Brigade ${brigadeIndex + 1}`;
    
    // SECTION 12: Remove unit_type fallback, validate required data
    let brigadeUnitType = normalizeUnitTypeKey(brigade.unit_type || brigade.type);  // Keep field name variant; 2026-10-24: an old spelling is translated
    
    if (!brigadeUnitType) {
      console.error(`[SECTION 12] CRITICAL: Brigade "${brigadeName}" missing unit_type`);
      brigadeUnitType = 'UNKNOWN';  // Obviously wrong value to expose the issue
    }


    // Initialize brigade results
    const brigadeResult = {
      brigade_id: brigadeId,
      // BUG-060 FIX: Include name and type for Tab 5 display
      brigade_name: brigadeName,
      unit_type: brigadeUnitType,
      initial_strength: initialStrength,
      assembly_held_patients: assemblyHeldPatients,   // day-0 load of this brigade (beds on day 1 when ASSEMBLY_DAY0_BEDS)
      
      // TASK 3.2: Store resolved phases for reporting
      phases_used: actualPhases,
      phase_source: brigade.uses_division_phases 
        ? (parentDivision?.uses_corps_phases ? 'CORPS' : 'DIVISION')
        : 'BRIGADE',
      
      // TASK 3.3: Per-phase casualty breakdown
      by_phase: [],  // Will store casualties per phase
      
      totals: {
        kia: 0,
        dow: 0,
        wia: 0,
        rtd: 0,
        dnbi: 0,
        rear_area: 0,
        total_battle: 0,
        total_all: 0,
        blood_consumed: 0,
        surgical_hours: 0
      },
      
      by_day: [],
      
      golden_hour: {
        days_achieved: 0,
        days_not_achieved: 0,
        average_evac_time_minutes: 0
      },
      
      // CC-007: CASEVAC tracking
      casevac: {
        days_overwhelmed: 0,
        total_medevac_evacuated: 0,
        total_casevac_evacuated: 0,
        total_unevacuated: 0,
        total_preventable_deaths: 0,
        average_capacity_utilization: 0
      },
      
      // FIX-CC-015: Medical personnel casualties tracking
      medical_personnel: {
        initial_count: brigade.medical_personnel || Math.round(initialStrength * 0.03), // ~3% medical staff
        initial_count_source: brigade.medical_personnel ? 'BRIGADE_FIELD' : 'ASSUMED_3_PERCENT_OF_STRENGTH',   // R1-08: the assumption is shown in Tab 5
        total_casualties: 0,
        remaining: brigade.medical_personnel || Math.round(initialStrength * 0.03),
        capacity_reduction_percent: 0,
        days_with_casualties: 0
      },
      
      // FIX-CC-014: Force superiority tracking
      force_superiority: {
        average_ratio: 0,
        total_modifier_applied: 0,
        casualties_prevented: 0
      },
      
      // FIX-CC-016: Per-facility MASCAL tracking
      facility_mascal: {
        days_in_mascal: 0,
        facilities_overwhelmed: []
      },
      
      end_state: {
        final_strength: initialStrength,
        strength_percent: 100,
        combat_effective: true,
        surgical_backlog: 0
      }
    };

    // Track cumulative casualties
    let cumulativeCasualties = 0;
    const returnSchedule = {};   // day -> number of soldiers returning to duty that day (out-of-action pool)
    let totalReturned = 0;
    let totalEvacTime = 0;
    let surgicalBacklog = medicalSystemState?.surgical_backlog || 0;
    let dynT4Prev = { capacity: 0, t1: 0 };   // dynamic T4: what the surgeons could not take yesterday
    let evacBacklogPrev = { urgent: 0, other: 0 };   // R2-29: casualties waiting for evacuation at the end of the day before
    let surgicalTempoState = null;   // 2026-10-18 operations tempo of the surgical teams: days in a non-normal tempo

    // ENH-039: Running R2B bed census for occupancy→DOW feedback (bedOccupancyPct per day).
    // Derives per-brigade R2B capacity from medicalConfig facilities — same predicates used
    // by MASCAL-WHEN-WHERE post-loop (lines 7221-7238) for consistency.
    // Falls back to doctrinal FST minimum (30 beds per AJMedP-1) when no facilities found.
    const _r2bIsR2BFac = (f) => ['ROLE2_BASIC','ROLE_2_BASIC','ROLE2B','Role2Basic','role2_basic']
      .includes(f.facility_type || f.role || f.type || f.role_type || f.mtf_role || '');   // ForceBuilder facilities carry facility_type (it was not read, so real bed counts were ignored)
    const _r2bGetBeds  = (f) => f.beds || f.bed_count || f.capacity || 0;
    const _r2bFacs = medicalConfig?.facilities;
    let   _r2bBedCap = 0;
    if (Array.isArray(_r2bFacs) && _r2bFacs.length > 0) {
      _r2bFacs.forEach(f => { if (_r2bIsR2BFac(f)) _r2bBedCap += _r2bGetBeds(f); });
    } else {
      const _mtfStr = medicalConfig?.mtf_structure;
      if (_mtfStr) _r2bBedCap = (_mtfStr.role2?.beds || 30) * (_mtfStr.role2?.count || 1);
    }
    const r2bBedCapacity = _r2bBedCap > 0 ? _r2bBedCap : 30;  // NATO FST minimum per AJMedP-1
    // P1-03 FIX: Derive THP timeframe for RTD rate lookup.
    // RTD rates in theatreHoldingPolicy.js are keyed by timeframe (within_72h/7d/15d/30d).
    // Must derive timeframe from the configured THP before the day loop.
    // BUG-FIX (2026-03-28): Moved thpConfig/thpDays declarations above _r2bAvgLOS which reads
    // thpDays — was a const temporal dead zone ReferenceError crashing calculateBrigadeCasualties.
    const thpConfig = scenario?.logistics?.theatre_holding_policy;
    const thpDays = thpConfig?.policy_days ||
      (thpConfig?.policy?.match(/THP_(\d+)_DAY/)?.[1]
        ? parseInt(thpConfig.policy.match(/THP_(\d+)_DAY/)[1], 10)
        : 7);
    // avgLOS from THP: shorter hold (THP_3: 2.5d) → faster turnover → less occupancy accumulation.
    const _r2bAvgLOS = thpDays <= 3 ? (requireNumber(AVG_LOS_BY_THP.THP_3_DAY, 'casualtyCalculator.js:9745', 'AVG_LOS_BY_THP.THP_3_DAY'))
                     : thpDays <= 7 ? (requireNumber(AVG_LOS_BY_THP.THP_7_DAY, 'casualtyCalculator.js:9746', 'AVG_LOS_BY_THP.THP_7_DAY'))
                     : requireNumber(AVG_LOS_BY_THP.THP_15_DAY, 'casualtyCalculator.js:9747', 'AVG_LOS_BY_THP.THP_15_DAY');
    let bedCensus = 0;  // running R2B patient census, updated each operation day (wounded)
    const _restCarry = {};   // fractions of the other daily counts (triage, died of wounds, return to duty ...)
    const _rc = (value, key) => roundWithCarry(value, CASUALTY_FRACTION_CARRY_ON ? _restCarry : null, key);
    const _dnbiCarry = { nonCsr: 0, csr: 0, march: 0, hns: 0, rtd: 0 };   // fractions of non-battle cases not yet counted (CASUALTY_FRACTION_CARRY_ON)
    const _casualtyCarry = { front: 0, rear: 0, kia: 0 };   // fractions of casualties not yet counted (CASUALTY_FRACTION_CARRY_ON)
    // R2-08 (2026-10-21): patients of the MedROE streams (civilians, prisoners, HN forces, contractors, VIP; surgical and ward) fill the beds of the Role 2 Enhanced and
    // Role 3 first (this brigade's slice, same shares as the surgery), the rest of them the Role 2B census above.
    let medroeCensusEnh = 0;
    const _medroeAdmissions = (_eligBC ? _eligBC.bed_admissions : 0) / _civBrigadesBC;
    const _enhBeds = (() => {
      const facs = medicalConfig?.facilities;
      const isE = (f) => ['ROLE2_ENHANCED', 'ROLE_2_ENHANCED', 'ROLE2E', 'Role2Enhanced', 'role2_enhanced'].includes(f.facility_type || f.role || f.type || f.role_type || f.mtf_role || '');
      const isR3 = (f) => ['ROLE3', 'ROLE_3', 'Role3', 'role3'].includes(f.facility_type || f.role || f.type || f.role_type || f.mtf_role || '');
      const beds = (f) => f.beds || f.bed_count || f.capacity || 0;
      let r2e = 0; let r3 = 0;
      if (Array.isArray(facs) && facs.length > 0) facs.forEach((f) => { if (isE(f)) r2e += beds(f); if (isR3(f)) r3 += beds(f); });
      else { const m = medicalConfig?.mtf_structure; if (m) { r2e = (m.role2e?.beds ?? 0) * (m.role2e?.count || 0); r3 = (m.role3?.beds ?? 0) * (m.role3?.count || 0); } }
      if (r2e === 0 && medicalConfig?.r2e_accessible !== false) r2e = 40;   // brigade echelon: the division Role 2E at its doctrinal 40 beds (as the MASCAL analysis)
      if (medicalConfig?.r2e_accessible === false) r2e = 0;
      if (medicalConfig?.r3_accessible === false) r3 = 0;
      const cfg = medicalConfig?.surgical_regulating || {};
      const pc = (v, d) => (typeof v === 'number' && v >= 0 ? v : d) / 100;
      return r2e * pc(cfg.r2e_share_percent, 33) + r3 * pc(cfg.r3_share_percent, 33);
    })();
    let bedCensusDnbi = ASSEMBLY_DAY0_BEDS ? assemblyHeldPatients : 0;  // running R2B census of DNBI that needs holding (shorter stay than the wounded); starts with the day-0 load of the assembly
    // Psychiatric support level (Tab 3). Absent = the reference level (STANDARD), listed in the assumptions.
    const _psychSupport = resolvePsychiatricSupport(medicalConfig);
    if (_psychSupport.defaulted) assumptionLedger.note('psychiatric_support_level');
    let totalCapacityUtilization = 0;  // CC-007: For averaging
    let thpTimeframe;
    if (thpDays <= 3) thpTimeframe = 'within_72h';
    else if (thpDays <= 7) thpTimeframe = 'within_7d';
    else if (thpDays <= 15) thpTimeframe = 'within_15d';
    else thpTimeframe = 'within_30d';

    // ENH-025 Cat-25: Read drone threat once per brigade call — all three axes.
    // Path: scenario.operation_parameters.threat_assessment.drone_threat (set by Tab2 EnemyAssessmentPanel).
    // casualty_modifier: raw WIA/KIA scale factor (1.0–1.8).
    // dow_modifier:      delayed-evacuation DOW penalty (1.0–1.3).
    // medevac_vehicle_losses: weekly fleet attrition fraction (0.0–0.15).
    // Planner input by day (Dr. Tatar, 2026-10-10): the level, its DOW multiplier and a day-by-day schedule come from
    // Tab 2 (threat_assessment.drone_threat / drone_dow_modifier / drone_schedule). Re-read at the start of each day.
    const _droneDay1 = resolveDroneForDay(scenario?.operation_parameters?.threat_assessment, 1);
    let _droneThreatLevel = _droneDay1.level;
    let _droneThreatData  = _droneDay1.data;
    let _droneCasualtyMod = _droneThreatData.casualty_modifier ?? 1.0;
    let _droneDOWMod      = _droneDay1.dowModifier;
    let _droneDOWSource   = _droneDay1.source;
    let _droneWeeklyLoss  = _droneThreatData.medevac_vehicle_losses ?? 0;

    // ENH-016: Read friendly air situation once per brigade — casualty modifier only.
    // MEDEVAC AD override is handled in medevacModule.calculateComprehensiveMEDEVAC.
    // Default PARITY = 1.0 (no effect — backward compatible with saved scenarios lacking this field).
    const _airSituationLevel = scenario?.operation_parameters?.air_situation_config?.level ?? 'PARITY';
    const _airSituationMod   = AIR_SITUATION_LEVELS[_airSituationLevel]?.casualty_modifier ?? 1.0;
    
    // P1-03 FIX: Pre-read RTD rates from constants for the configured THP timeframe.
    // Old code used 0.05/0.15/0.60/0.02 literals regardless of THP.
    // APP_RULES 6.1/6.3: No fallback ?? literals — throw if constants missing.
    // If RTD_RATES structure is wrong, the error surfaces here, not as silent wrong output.
    const _wiaRtdT1 = THP_RTD_RATES?.BY_TRIAGE_CATEGORY?.T1_IMMEDIATE?.rtd_rates?.[thpTimeframe];
    const _wiaRtdT2 = THP_RTD_RATES?.BY_TRIAGE_CATEGORY?.T2_DELAYED?.rtd_rates?.[thpTimeframe];
    const _wiaRtdT3 = THP_RTD_RATES?.BY_TRIAGE_CATEGORY?.T3_MINIMAL?.rtd_rates?.[thpTimeframe];
    const _wiaRtdT4 = THP_RTD_RATES?.BY_TRIAGE_CATEGORY?.T4_EXPECTANT?.rtd_rates?.[thpTimeframe];
    if (_wiaRtdT1 === undefined || _wiaRtdT2 === undefined || _wiaRtdT3 === undefined || _wiaRtdT4 === undefined) {
      throw new Error(
        `RTD_RATES constants missing for THP timeframe '${thpTimeframe}'. ` +
        `Check theatreHoldingPolicy.js BY_TRIAGE_CATEGORY structure. ` +
        `T1=${_wiaRtdT1} T2=${_wiaRtdT2} T3=${_wiaRtdT3} T4=${_wiaRtdT4}`
      );
    }
    const wiaRtdT1 = _wiaRtdT1;
    const wiaRtdT2 = _wiaRtdT2;
    const wiaRtdT3 = _wiaRtdT3;
    const wiaRtdT4 = _wiaRtdT4;
    
    // CALC-011: terrain/weather the planner chose in Tab 2 (operation_parameters.environmental_factors).
    // Previously read from scenario.environment, which no tab writes.
    const _scenarioEnv = resolveScenarioEnvironment(scenario);

    // FIX-CC-015: Track medical personnel state
    let remainingMedicalPersonnel = brigadeResult.medical_personnel.initial_count;
    
    // FIX-CC-014: Track force superiority modifiers
    let totalForceRatio = 0;
    let totalForceSuperiorityModifier = 0;
    let totalCasualtiesPrevented = 0;

    // Wound-type hazard model: mode from operation_parameters.wound_hazard_mode (OFF when absent).
    // Antibiotics and dressings availability follow Tab 4 stock, push packages and daily consumption.
    const _woundHazardMode = resolveWoundHazardMode(scenario?.operation_parameters?.wound_hazard_mode);
    // Wait in the surgical queue (hours) carried from the previous day's backlog (resourceModule queue cascade).
    let _hazardQueueWaitHours = 0;
    // 2026-10-13 (Tab 2 plan, step 5): days since the supporting medical units last packed (march, displacement); Infinity = never
    let _daysSincePacked = Infinity;
    // R2-30 (2026-10-21): advance per brigade and the Role 2 sawtooth (advanceModel.js): positions of the front and of the Role 2B and Role 2E along the axis
    const _axis = createAxisState();
    // 2026-10-13 (Tab 2 plan, step 7): road state, helicopter availability, rear factor, DOW factor of the threat; one read of the Tab 2 inputs
    // R2-18 (2026-10-20): built for one weather; the day loop takes the model of the day's weather (phase weather or the operation's)
    const _buildThreatModel = (weatherKey) => THREAT_EFFECTS_USE ? (() => {
      const op = scenario?.operation_parameters || {};
      const ta = op.threat_assessment || {};
      const infra = infrastructureModel(op.infrastructure_config);
      return {
        infrastructure: infra,
        // no LOC configuration and no IED or insurgent threat: no road effect (a scenario that never opened Tab 2 keeps its numbers)
        roadState: (op.loc_config || ta.ied_threat || ta.insurgent_activity) ? resolveRoadState({ locConfig: op.loc_config || {}, threatAssessment: ta, weather: weatherKey }) : null,
        helicopter: helicopterAvailability({ airSituationLevel: op.air_situation_config?.level || 'PARITY', enemyAd: ta.enemy_ad || 'NONE', weather: weatherKey, coordination: infra ? infra.medevac_coordination : 1 }),
        rear: rearThreatFactor(ta),
        dow: threatDowFactor(ta),
        iedMarch: iedMarchFactor(ta)
      };
    })() : null;
    const _threatModelByWeather = { [_scenarioEnv.weather]: _buildThreatModel(_scenarioEnv.weather) };
    let _threatModel = _threatModelByWeather[_scenarioEnv.weather];
    // R2-05: night operations modifiers on the evacuation (computed by Tab 2 with getNightOpsModifiers and saved in night_ops_config.modifiers)
    const _nightCfgE = scenario?.operation_parameters?.night_ops_config;
    const _nightEvac = (NIGHT_EVAC_MODIFIERS_ON && _nightCfgE?.enabled && _nightCfgE.modifiers)
      ? { medevac: _nightCfgE.modifiers.medevac_modifier, ground: _nightCfgE.modifiers.ground_evac_modifier }
      : { medevac: 1, ground: 1 };
    [_nightEvac.medevac, _nightEvac.ground].forEach((v) => {
      if (typeof v !== 'number' || Number.isNaN(v) || v <= 0) throw new Error(`calculateCasualties: night_ops_config.modifiers medevac_modifier and ground_evac_modifier must be numbers > 0 (got: ${v})`);
    });
    let _dayEnvironment = { ..._scenarioEnv, night_ground_speed: _nightEvac.ground };   // terrain, weather and night ground speed of the day
    const _hazardSupply = _woundHazardMode === WOUND_HAZARD_MODES.OFF ? null : createSupplyTracker({
      initialStock: scenario?.logistics?.medical_supplies?.initial_stock,
      resupplyEvents: scenario?.logistics?.medical_supplies?.resupply_events || []
    });

    // TASK 3.3: Calculate casualties per phase instead of single duration
    let currentDay = 1;  // Track absolute day number across all phases
    
    
    for (let phaseIndex = 0; phaseIndex < actualPhases.length; phaseIndex++) {
      const phase = actualPhases[phaseIndex];
      const phaseDuration = phase.duration_days || 1;
      
      
      // PHASE 4 TASK 4.1: Detect march/movement posture
      const isMarch = isMarchPosture(phase.posture);
      if (isMarch) {
      }

      // ENH-046: Phase transition surge detection.
      // A posture change between consecutive phases creates a predictable demand spike on
      // the first day of the new phase (AJMedP-1: transition DEFENSE→OFFENSE = heaviest
      // initial casualty load; OFFENSE→DEFENSE = counter-attack / consolidation surge).
      // Surge applied as a multiplier to battle casualties on dayInPhase === 1 only.
      // Helper: read posture strings, classify by keyword, return surge multiplier.
      const _prevPhasePosture = phaseIndex > 0 ? (actualPhases[phaseIndex - 1]?.posture || '') : '';
      const _curPhasePosture  = phase.posture || '';
      const _isPhaseTransition = phaseIndex > 0 && _prevPhasePosture !== _curPhasePosture;

      const _getPhaseTransitionSurge = (fromP, toP) => {
        if (!fromP || !toP || fromP === toP) return 1.0;
        const from = fromP.toUpperCase();
        const to   = toP.toUpperCase();
        const fromDef   = from.includes('DEFENSE') || from.includes('DEFENSIVE');
        const fromOff   = from.includes('OFFENSE') || from.includes('OFFENSIVE') || from.includes('ATTACK');
        const fromMarch = from.includes('MARCH')   || from.includes('MOVEMENT') || from.includes('TRANSIT');
        const toDef     = to.includes('DEFENSE')   || to.includes('DEFENSIVE');
        const toOff     = to.includes('OFFENSE')   || to.includes('OFFENSIVE')  || to.includes('ATTACK');
        if (fromDef && toOff)          return 1.50; // DEFENSE→OFFENSE: initial assault — peak demand
        if (fromOff && toDef)          return 1.25; // OFFENSE→DEFENSE: consolidation, counter-attacks
        if (fromMarch && (toOff || toDef)) return 1.40; // MARCH→COMBAT: first contact casualty spike
        return 1.15;                                // Any other posture change — minor surge
      };

      const _phaseTransitionSurgeMod = _isPhaseTransition
        ? _getPhaseTransitionSurge(_prevPhasePosture, _curPhasePosture)
        : 1.0;
      
      // Initialize phase breakdown
      const phaseBreakdown = {
        phase_index: phaseIndex,
        phase_name: phase.name || `Phase ${phaseIndex + 1}`,
        duration_days: phaseDuration,
        posture: phase.posture,
        operational_state: phase.operational_state,
        start_day: currentDay,
        end_day: currentDay + phaseDuration - 1,
        is_march: isMarch,  // PHASE 4: Track if this is a march phase
        medical_facilities_operational: !isMarch,  // PHASE 4: Medical facilities unavailable during march
        days: [],
        totals: {
          kia: 0,
          dow: 0,
          wia: 0,
          rtd: 0,
          dnbi: 0,
          total_battle: 0,
          total_all: 0,
          blood_consumed: 0,
          surgical_hours: 0
        }
      };
      
      // V4-013: Track previous sub-phase mission for intra-phase transition surge.
      // Reset to null at the start of each phase (transition only within a phase).
      let _prevSubPhaseMission = null;

      // Calculate for each day in this phase
      for (let dayInPhase = 1; dayInPhase <= phaseDuration; dayInPhase++) {
        const day = currentDay;  // Absolute day number
        {
          const _droneToday = resolveDroneForDay(scenario?.operation_parameters?.threat_assessment, day);
          _droneThreatLevel = _droneToday.level;
          _droneThreatData  = _droneToday.data;
          _droneCasualtyMod = _droneThreatData.casualty_modifier ?? 1.0;
          _droneDOWMod      = _droneToday.dowModifier;
          _droneDOWSource   = _droneToday.source;
          _droneWeeklyLoss  = _droneThreatData.medevac_vehicle_losses ?? 0;
        }
      // Check if unit still effective
      // BUG-048 FIX: Ensure currentStrength is never NaN (break cascade)
      // Out-of-action pool: those whose treatment ends today are back before the day starts.
      const returningToday = OUT_OF_ACTION_POOL ? (returnSchedule[day] || 0) : 0;
      if (returningToday > 0) {
        cumulativeCasualties = Math.max(0, cumulativeCasualties - returningToday);
        totalReturned += returningToday;
      }
      const safeCumulativeCasualties = isNaN(cumulativeCasualties) ? 0 : cumulativeCasualties;
      const currentStrength = Math.max(0, initialStrength - safeCumulativeCasualties);
      
      if (currentStrength / initialStrength < 0.30) {
        brigadeResult.by_day.push({
          day,
          is_combat_effective: false,
          note: 'Unit combat ineffective'
        });
        continue;
      }

      // R2-18 (2026-10-20): the weather of the day is the phase's own (Tab 2 phase table) or the operation weather.
      {
        const _phaseWeather = phase.weather;
        const _dayWeatherKey = (_phaseWeather === undefined || _phaseWeather === null || _phaseWeather === '') ? _scenarioEnv.weather : _phaseWeather;
        if (!Object.prototype.hasOwnProperty.call(WEATHER_TO_CAPACITY_WEATHER, _dayWeatherKey)) {
          throw new Error(`calculateCasualties: unknown phase weather '${_dayWeatherKey}' (valid: ${Object.keys(WEATHER_TO_CAPACITY_WEATHER).join(', ')}). Path: phase.weather (Tab 2 phase table)`);
        }
        _dayEnvironment = { ..._scenarioEnv, weather: _dayWeatherKey, night_ground_speed: _nightEvac.ground };
        if (THREAT_EFFECTS_USE) {
          if (!_threatModelByWeather[_dayWeatherKey]) _threatModelByWeather[_dayWeatherKey] = _buildThreatModel(_dayWeatherKey);
          _threatModel = _threatModelByWeather[_dayWeatherKey];
        }
      }

      // IMP-008: Cohesion modifier — computed from current strength % at start of this day.
      // Placed after the <30% guard: units reaching this point are combat-effective (≥30%).
      // strengthPercent uses initialStrength (not brigade.strength) to track cumulative attrition.
      // cohesionDef resolved for daySummary.cohesion emission (fm_color, description).
      const strengthPercent = (currentStrength / initialStrength) * 100;
      const cohesionModifier = getCohesionModifier(strengthPercent);
      const cohesionBand = getCohesionBand(strengthPercent);
      const cohesionDef = STRENGTH_COHESION_MODIFIERS[cohesionBand] || STRENGTH_COHESION_MODIFIERS.FULL_EFFECTIVE;
      const isNight = scenario?.operation_parameters?.time_of_day === 'NIGHT' ||
                      scenario?.operation_parameters?.night_operations_percent > 0.5;

      // Calculate TCCC analysis - FIX-004: Use per-facility distances when available
      const evacTimeToRole2 = getEvacuationTimeToRole2(
        medicalConfig, 
        brigade.primary_role1_id || null,  // Use brigade's primary Role 1 if available
        'ROLE1'
      );
      
      const tcccAnalysis = calculateTCCCInterventionEffects({
        timeToIntervention: 5 + Math.random() * 10,
        timeToRole2: evacTimeToRole2,  // FIX-004: Now uses per-facility or falls back to generic
        clsTraining: resolveClsQuality({ ...forceQuality, cls_training_percentage: resolveClsLevel(medicalConfig, forceQuality).level }),   // CLS training: Tab 3 card, Tab 1 value as the failsafe (R1-07)
        ambientTempC: resolveInput(scenario?.environment?.temperature_c, 'ambient_temperature_c', { zeroIsMissing: true }),
        woundMechanism: 'PENETRATING'
      });

      // Calculate battle casualties
      // BUG-002 FIX: Now includes mission_role for brigade-level mission role modifier
      // TASK 3.3: Use phase-specific posture and operational_state
      // TASK 3.4: Use pre-calculated mission_multiplier from Tab 1
      // V5-012: Per-phase brigade role takes priority over static Tab1 role.
      //   brigade.phase_overrides[phaseIndex].brigade_role (set in BrigadePhaseTab per-phase
      //   override panel) overrides the static tactical_in_parent.mission_multiplier for this
      //   phase only. null = not set → fall through to Tab1 value (backward compatible).
      // V5-012: phase_overrides[phaseIndex].brigade_role → inherited phases (ENH-023 pattern)
      // phase.brigade_role                             → custom phases (stored directly in phase obj)
      const _phaseRole = brigade.phase_overrides?.[phaseIndex]?.brigade_role
                      ?? phase.brigade_role
                      ?? null;
      const missionMultiplier = _phaseRole
        ? getMissionCasualtyMultiplier(_phaseRole)          // V5-012: phase-level role
        : (brigade.tactical_in_parent?.mission_multiplier   // Tab1 static role
           || brigade.mission_multiplier
           || 1.0);  // Fallback to 1.0 (Supporting)

      // V5-013: Corps-level division Main Effort modifier.
      //   When echelon is CORPS, one division per phase may be designated Main Effort
      //   (stored in operation_parameters.division_configs[j].phase_overrides[phaseIndex].division_role).
      //   MAIN_EFFORT division: ×1.15 (corps pushes harder through this division).
      //   ECONOMY_OF_FORCE division: ×0.70 (minimise commitment).
      //   Applied AFTER missionMultiplier is baked into battleResult (see below, ~line 9956).
      //   No-op (1.0) when no corps echelon, no division_role set, or brigade has no parent_division_id.
      const _divisionConfigs = scenario?.operation_parameters?.division_configs || [];
      // BUG-NEXT-005: guard — skip .find() when brigade has no parent_division_id (BRIGADE
      // echelon, standalone unit, or legacy scenario without division assignment).
      // Without guard, .find() compares dc.division_id === undefined which always fails
      // silently and defaults to 1.0 — correct behaviour, but intent is ambiguous.
      const _parentDivConfig = brigade.parent_division_id
        ? _divisionConfigs.find(dc => dc.division_id === brigade.parent_division_id)
        : null;
      const _divPhaseRole   = _parentDivConfig?.phase_overrides?.[phaseIndex]?.division_role ?? null;
      const divisionRoleMod = _divPhaseRole === 'MAIN_EFFORT'      ? 1.15
                            : _divPhaseRole === 'ECONOMY_OF_FORCE' ? 0.70
                            : 1.0;

      // V4-013: Sub-phase variables declared here (outer scope) so they survive the
      // isMarch if/else block and are available for the modifier application below.
      let _enemyDay = null;     // enemy of this brigade and day when enemy forces are entered (report)
      let _dayProfile = null;   // casualty table profile of the day (set below on non-march days); read by the evacuation profile
      let _subPhaseMissionMod     = 1.0;
      let _subPhaseTransitionMod  = 1.0;

      // PHASE 4 TASK 4.3: Check if march posture - use special calculation
      let battleResult;

      if (isMarch) {
        // PHASE 4: March operations have very different casualty profile
        // 2026-10-13 (Tab 2 plan 7.5): IED threat is a hazard of movement: march, transit and convoy days take the IED casualty factor
        // (x1.05 to x1.5); resistance does not apply on a march (separate march rate)
        const marchBattleRate = getMarchBattleRate(phase.posture) * (_threatModel ? _threatModel.iedMarch : 1.0);
        const marchBattleCasualties = CASUALTY_FRACTION_CARRY_ON ? currentStrength * marchBattleRate : Math.round(currentStrength * marchBattleRate);
        
        // During march, operational_state typically SUPPORT_ACTIVE (0.2x)
        const opStateMultiplier = phase.operational_state === 'COMBAT_ENGAGED' ? 1.0 :
                                  phase.operational_state === 'SUPPORT_ACTIVE' ? 0.2 : 0.08;
        
        // 2026-10-21: with the carry the march casualties are rounded once with their fractions carried (a march day of 0.4 casualties is not lost)
        const _marchFinal = marchBattleCasualties * opStateMultiplier;
        const finalBattleCasualties = CASUALTY_FRACTION_CARRY_ON ? _rc(_marchFinal, 'march_total') : Math.round(_marchFinal);
        const _marchPart = (share, key) => (CASUALTY_FRACTION_CARRY_ON ? _rc(_marchFinal * share, key) : Math.round(finalBattleCasualties * share));
        
        // Create simplified battle result for march
        battleResult = {
          battle_casualties: {
            total: finalBattleCasualties,
            kia: _marchPart(0.15, 'march_kia'),  // 15% KIA
            dow: _marchPart(0.10, 'march_dow'),  // 10% DOW
            wia: _marchPart(0.65, 'march_wia'),  // 65% WIA
            rtd: _marchPart(0.10, 'march_rtd')   // 10% RTD
          },
          analysis: {
            is_march_operation: true,
            march_type: phase.posture,
            march_base_rate: marchBattleRate,
            note: 'March operations: Low battle casualties, vulnerable to ambush/mines/air'
          }
        };
      } else {
        // V4-013: Resolve sub-phase for this day and update outer-scoped modifiers
        const _activeSub = _getSubPhaseForDay(phase.sub_phases, dayInPhase, phaseDuration);
        let _subPhaseTerrainOverride = null;

        if (_activeSub) {
          // OBS-S102-007: new 2-field combined multiplier (role × operation_type, cap 2.50).
          // BC fallback: if role field absent (old data), use single mission_type lookup.
          if (USE_PHASE_CASUALTY_TABLE) {
            _subPhaseMissionMod = 1.0;   // 2026-10-13: the sub-phase role and operation type feed the casualty table profile instead
          } else if (_activeSub.role !== undefined) {
            _subPhaseMissionMod = getSubPhaseCombinedMultiplier(_activeSub.role, _activeSub.operation_type);
          } else {
            _subPhaseMissionMod = getMissionCasualtyMultiplier(_activeSub.mission_type);
          }
          _subPhaseTerrainOverride = _activeSub.terrain ?? null;  // null = use scenario terrain
          // Transition surge tracks operation_type changes (new data) or mission_type (old data)
          const _activeSubKey = _activeSub.role !== undefined
            ? (_activeSub.operation_type || _activeSub.role || 'SUPPORTING')
            : (_activeSub.mission_type || 'SUPPORTING');
          if (_prevSubPhaseMission && _prevSubPhaseMission !== _activeSubKey) {
            _subPhaseTransitionMod = _getSubPhaseTransitionSurge(_prevSubPhaseMission, _activeSubKey);
          } else {
            _subPhaseTransitionMod = 1.0;
          }
          _prevSubPhaseMission = _activeSubKey;
        } else {
          _subPhaseMissionMod    = 1.0;
          _subPhaseTransitionMod = 1.0;
          _prevSubPhaseMission   = null;
        }
        // Effective terrain for this day: sub-phase override takes priority
        const _effectiveTerrain = _subPhaseTerrainOverride ?? _scenarioEnv.terrain;

        // 2026-10-13 (Tab 2 plan, step 2): ONE casualty table. Form of battle at the phase resistance x maneuver group x
        // special condition replaces operation type x posture x role; the sub-phase role x operation type multiplier is
        // replaced too (the sub-phase role and operation type feed the profile), the tactical transition surge stays.
        const _phaseProfile = USE_PHASE_CASUALTY_TABLE
          ? resolvePhaseProfile({
              posture: normalizePosture(phase.posture || brigade.operational_posture, 'DELIBERATE_DEFENSE'),
              phase: { ...phase, brigade_role: _phaseRole },   // the per-phase role set in the brigade override panel
              subPhase: _activeSub,
              scenarioOperationType: resolveOperationType(scenario),
              dayInPhase,
              resistanceIndex: resolveResistanceIndex(phase, scenario, { brigadeId: brigade.id || brigade.brigade_id, day, friendlyStrength: currentStrength })
            })
          : null;
        _dayProfile = _phaseProfile;
        if (scenario?.operation_parameters?.enemy_forces && !(phase.resistance !== undefined && phase.resistance !== null && phase.resistance !== '' && phase.resistance !== 'AUTO')) {
          const _ef = brigadeEnemyRatio({ phaseRatio: phase.enemy_strength_ratio, cardRatio: scenario.operation_parameters.enemy_strength_ratio, forces: scenario.operation_parameters.enemy_forces, brigadeId: brigade.id || brigade.brigade_id, day, friendlyStrength: currentStrength });
          if (_ef.ratio !== undefined) _enemyDay = { ratio: Math.round(_ef.ratio * 1000) / 1000, source: _ef.source, initial: _ef.initial, reinforcement: _ef.reinforcement, units: _ef.entries };
        }

        // Normal combat operations - use standard calculation
        battleResult = calculateDailyBattleCasualties({
        brigade: {
          ...brigade,
          phase_profile: _phaseProfile,
          environment: {
            terrain: _effectiveTerrain,
            weather: _dayEnvironment.weather,
            threat_level: scenario?.operation_parameters?.threat_level || 'PEER'
          },
          operation: {
            ...(brigade.operation || {
              mission_type: scenario?.operation_parameters?.mission_type || 'MAIN_DEFENSE',
              // TASK 3.3: Use phase posture instead of brigade posture
              operational_posture: normalizePosture(phase.posture || brigade.operational_posture, 'DELIBERATE_DEFENSE')
            }),
            // 2026-10-11: Tab 2's engagement level and operation type now reach the base casualty rate
            engagement_level: resolveEngagementLevel(brigade, scenario),
            operation_type: resolveOperationType(scenario)
          },
          // 2026-10-11: the base rate module reads `brigade.posture`, which nothing set, so every brigade ran as
          // DELIBERATE_DEFENSE (x0.6). The phase posture (Tab 2) now reaches it.
          posture: normalizePosture(phase.posture || brigade.operational_posture, 'DELIBERATE_DEFENSE'),
          // TASK 3.3: Use phase operational_state instead of brigade operational_state
          // SECTION 12: Phase overrides brigade, but no hardcoded fallback
          operational_state: phase.operational_state || brigade.operational_state,
          // BUG-002 FIX: Pass mission role for MAIN_EFFORT/SUPPORTING/RESERVE differentiation
          mission_role: brigade.mission_role || brigade.missionRole,
          // TASK 3.4: Pass pre-calculated mission_multiplier from Tab 1
          mission_multiplier: missionMultiplier
        },
        ...(BASE_RATE_WEATHER_ON ? { weather: _dayEnvironment.weather } : {}),
        scenario,
        forceQuality,
        // 2026-10-11: the battle module reads `friendlyQuality`; the calculator passed only `forceQuality`, so training,
        // equipment, morale, experience, technology and force superiority never reached the base casualty rate (the module
        // ran on its defaults).
        friendlyQuality: forceQuality,
        enemyQuality,
        day,
        cumulativeCasualties,
        tcccAnalysis,
        isNight,  // NIGHT-001 FIX: night modifier (0.85x) now applied to battle casualties
        armorLevel: forceQuality?.body_armor_level || 'LEVEL_III'  // ENH-BAP: reads from force_quality (Tab1)
      });
      }  // End march vs normal combat if/else

      // 2026-10-21: unrounded copy of the day's killed and wounded (rate behind the module's rounded total x strength), scaled by
      // every step of the chain below (scaleBattleCasualties); absent for a march, which keeps the integers.
      if (CASUALTY_FRACTION_CARRY_ON && typeof battleResult.casualty_rate_unrounded === 'number' && typeof battleResult.battle_casualties.rates?.kia_rate === 'number') {
        const _totalUnrounded = battleResult.casualty_rate_unrounded * currentStrength;
        battleResult._unrounded = {
          kia: _totalUnrounded * battleResult.battle_casualties.rates.kia_rate,
          wia: _totalUnrounded * (1 - battleResult.battle_casualties.rates.kia_rate)
        };
      }

      // IMP-008: Apply cohesion modifier to battle casualties.
      // Applied BEFORE force superiority: cohesion is a unit-internal degradation factor
      // (mutual fire support, command continuity, medical carry capacity) — it precedes
      // the external force-ratio comparison per Dupuy TNDM sequencing logic.
      // modifier 1.00 (GREEN, ≥85%): no-op, branch skipped entirely.
      if (cohesionModifier !== 1.0) {
        scaleBattleCasualties(battleResult, cohesionModifier);
      }

      // ========================================================================
      // FIX-CC-014: Apply Force Superiority Modifier (Dupuy TNDM methodology)
      // ========================================================================
      // Force ratio significantly affects casualty rates:
      // - 3:1 advantage: ~40% fewer friendly casualties
      // - 1:1 parity: no modification
      // - 1:3 disadvantage: ~40% more friendly casualties
      const friendlyStrength = currentStrength;
      const enemyStrength = scenario?.operation_parameters?.enemy_strength || 
                           (friendlyStrength * (scenario?.operation_parameters?.enemy_strength_ratio || scenario?.operation_parameters?.enemy_ratio || 1.0));
      const forceRatio = friendlyStrength / Math.max(enemyStrength, 1);
      
      const forceSuperiorityResult = applyForceSuperiorityModifier({
        friendlyStrength,
        enemyStrength,
        baseCasualties: battleResult.battle_casualties.total,
        friendlyQuality: forceQuality,
        enemyQuality: enemyQuality
      });
      
      // Apply force superiority adjustment to battle casualties
      const originalCasualties = battleResult.battle_casualties.total;
      const adjustedTotal = forceSuperiorityResult.adjusted_casualties || originalCasualties;
      const casualtiesPrevented = originalCasualties - adjustedTotal;
      
      // Update battle result with force superiority adjustment
      // 2026-10-13 (Tab 2 plan, step 3): the enemy assessment now sets the resistance of the phase; the force superiority
      // multiplier is not applied again (switch FORCE_SUPERIORITY_MULTIPLIER_IN_CHAIN). The analysis is still computed and reported.
      const forceSuperiorityModifier = FORCE_SUPERIORITY_MULTIPLIER_IN_CHAIN ? (forceSuperiorityResult.modifier || 1.0) : 1.0;
      scaleBattleCasualties(battleResult, forceSuperiorityModifier);

      // ENH-012: Apply geographic theater scalar.
      // Reads theater_region + season from the same climate_config block Tab2 already saves.
      // Applied AFTER force superiority — this is theater-level environmental adjustment.
      // getTheaterScalar() returns 1.0 for null/unknown key (full backward compat).
      const _theaterRegion = scenario?.operation_parameters?.climate_config?.theater_region ?? null;
      const _theaterSeason = seasonForDay(scenario, day, scenario?.operation_parameters?.climate_config?.season ?? 'summer');
      const theaterScalar  = getTheaterScalar(_theaterRegion, _theaterSeason);
      if (theaterScalar !== 1.0) {
        scaleBattleCasualties(battleResult, theaterScalar);
      }
      
      // ENH-025 Cat-25: Apply drone warfare casualty modifier.
      // Applied last in the battle casualty chain (after force superiority and theater scalar).
      // SATURATED = 1.8× reflects Ukraine 2024 peer-adversary drone saturation.
      // Modifier 1.0 (NONE) = no-op: branch skipped entirely.
      if (_droneCasualtyMod !== 1.0) {
        scaleBattleCasualties(battleResult, _droneCasualtyMod);
      }

      // ENH-016: Apply friendly air situation casualty modifier.
      // Applied after drone mod — air situation is a theater-level environmental parameter.
      // AIR_SUPERIORITY (0.85×): CAS available, reduced friendly casualties.
      // PARITY (1.0×): no-op, branch skipped.
      // CONTESTED (1.15×): reduced CAS, partial enemy air activity.
      // ENEMY_AIR_SUPERIORITY (1.30×): no CAS, enemy air interdiction.
      if (_airSituationMod !== 1.0) {
        scaleBattleCasualties(battleResult, _airSituationMod);
      }

      // Track force superiority statistics
      totalForceRatio += forceRatio;
      totalForceSuperiorityModifier += forceSuperiorityModifier;
      totalCasualtiesPrevented += Math.max(0, casualtiesPrevented);

      // ========================================================================
      // FIX-CC-015: Calculate Medical Personnel Casualties
      // ========================================================================
      // Medical personnel face unique risks including deliberate targeting (Ukraine pattern)
      const threatLevel = scenario?.operation_parameters?.threat_level || 'PEER';
      // CALC-004: medic losses follow the brigade's own casualty rate (threat, posture and
      // fatigue are already inside it); the function throws if any required input is missing.
      const medicalPersonnelResult = calculateMedicalPersonnelCasualties({
        medicalPersonnel: remainingMedicalPersonnel,
        roleType: 'combat_medic_role1',
        brigadeDailyCasualtyRate: battleResult.battle_casualties.total / currentStrength,
        isDeliberatelyTargeted: scenario?.operation_parameters?.medical_targeting === true,
        dayOfOperation: day
      });
      
      // Update remaining medical personnel
      remainingMedicalPersonnel = medicalPersonnelResult.remaining_medical_personnel;
      brigadeResult.medical_personnel.total_casualties += medicalPersonnelResult.medical_casualties;
      brigadeResult.medical_personnel.remaining = remainingMedicalPersonnel;
      if (medicalPersonnelResult.medical_casualties > 0) {
        brigadeResult.medical_personnel.days_with_casualties++;
      }
      
      // Calculate capacity reduction from medical personnel losses
      const medicalCapacityReduction = 1 - (remainingMedicalPersonnel / brigadeResult.medical_personnel.initial_count);
      brigadeResult.medical_personnel.capacity_reduction_percent = Math.round(medicalCapacityReduction * 100);

      // ========================================================================
      // FIX-CC-016: Per-Facility MASCAL Assessment
      // ========================================================================
      // Assess MASCAL condition at each medical facility
      const dailyCasualtyLoad = battleResult.battle_casualties.total;
      const facilityMascalResult = assessMASCALCondition({
        casualties: dailyCasualtyLoad,
        capacity: resolveInput(medicalConfig?.surgical_capacity, 'surgical_capacity_per_day', { zeroIsMissing: true }),
        timeframe: 'daily',
        facilityType: 'ROLE2_BASIC'
      });
      
      if (facilityMascalResult?.is_mascal) {
        brigadeResult.facility_mascal.days_in_mascal++;
        if (!brigadeResult.facility_mascal.facilities_overwhelmed.includes('ROLE2_BASIC')) {
          brigadeResult.facility_mascal.facilities_overwhelmed.push('ROLE2_BASIC');
        }
      }

      // ========================================================================
      // v3.0 PHASE 3: SUB-UNIT COMPOSITION CASUALTY CALCULATION (NO FALLBACKS)
      // ========================================================================
      // v3.0: Calculate casualties using actual sub-unit composition
      // Each sub-unit (battalion/squadron/company) gets its own casualty modifier
      // Example: MECHANIZED Brigade = 2×Mech (1.0) + 1×Armor (0.85) + 1×Artillery (0.6) + ...
      
      let subUnitCalculationResult;
      let unitTypeModifier; // For backwards compatibility with reporting
      let unitTypeResult; // v3.0 FIX: Declare at proper scope for reporting code access

      // BUG-142 FIX: Apply Monte Carlo casualty_rate_modifier to battle casualties.
      // monteCarloSimulator sets scenario.operation_parameters.monte_carlo_modifiers
      // .casualty_rate_modifier per iteration (±20%), but the battle calculation path
      // never read it — only dnbiModule received scenario. Result: 1000 iterations
      // produced only ~7-casualty spread (DNBI only) instead of ~300+ (battle + DNBI).
      // Applied AFTER all other modifiers (cohesion, force superiority) and BEFORE
      // sub-unit recalculation so it scales the final battle total proportionally.
      // No-op when modifier = 1.0 (deterministic runs, modifier absent).
      const _mcBattleMod = scenario?.operation_parameters?.monte_carlo_modifiers
        ?.casualty_rate_modifier ?? 1.0;
      if (_mcBattleMod !== 1.0) {
        scaleBattleCasualties(battleResult, _mcBattleMod);
      }

      // V4-002: enemy_effectiveness — lethality shift. More effective enemy = higher KIA fraction,
      // same total casualties. Redistributes kia/wia within already-scaled total; does not change total.
      // Cap KIA fraction at 0.40 (historical upper bound for sustained conventional combat).
      const _mcEnemyEffMod = scenario?.operation_parameters?.monte_carlo_modifiers?.enemy_effectiveness ?? 1.0;
      if (_mcEnemyEffMod !== 1.0) {
        const _u0 = battleResult._unrounded;
        const _battleTotal = _u0 && CASUALTY_FRACTION_CARRY_ON ? _u0.kia + _u0.wia : battleResult.battle_casualties.kia + battleResult.battle_casualties.wia;
        const _baseFrac    = (_u0 && CASUALTY_FRACTION_CARRY_ON ? _u0.kia : battleResult.battle_casualties.kia) / Math.max(1e-9, _battleTotal);
        setKilledShare(battleResult, Math.min(0.40, _baseFrac * _mcEnemyEffMod));
      }

      // V4-002: force_quality — friendly protection uncertainty. Higher quality = fewer casualties from
      // identical fire (dispersion, buddy-aid, PPE). Inverse multiplier. Stacks on ENH-043 per-brigade
      // quality cascade (ENH-043 = configured baseline; this = MC uncertainty around that baseline).
      const _mcForceQualMod = scenario?.operation_parameters?.monte_carlo_modifiers?.force_quality ?? 1.0;
      if (_mcForceQualMod !== 1.0) {
        scaleBattleCasualties(battleResult, 1 / _mcForceQualMod);
      }

      // V4-002: technology_environment — drone/EW lethality on KIA only. Precision munitions achieve
      // kills, not wounds. WIA unchanged. technology_environment > 1.0 = more drone/EW dominance = more KIA.
      const _mcTechEnvMod = scenario?.operation_parameters?.monte_carlo_modifiers?.technology_environment ?? 1.0;
      if (_mcTechEnvMod !== 1.0) {
        scaleBattleCasualties(battleResult, 1, _mcTechEnvMod);   // killed only
      }

      // ENH-046: Phase transition surge — applied to day 1 of a posture-changing phase.
      // Multiplier computed per phase above (_phaseTransitionSurgeMod > 1.0 only on transitions).
      // Applied AFTER cohesion, force superiority, and Monte Carlo so it is purely additive.
      // Logs transition metadata into daySummary.phase_transition for Tab5/Tab6 display.
      let _phaseTransitionLog = null;
      if (dayInPhase === 1 && _isPhaseTransition && _phaseTransitionSurgeMod > 1.0) {
        const _surgeBefore = { kia: battleResult.battle_casualties.kia, wia: battleResult.battle_casualties.wia };
        if (CASUALTY_FRACTION_CARRY_ON && battleResult._unrounded) {
          scaleBattleCasualties(battleResult, _phaseTransitionSurgeMod);
        } else {
          battleResult.battle_casualties.kia   += Math.round(_surgeBefore.kia * (_phaseTransitionSurgeMod - 1));
          battleResult.battle_casualties.wia   += Math.round(_surgeBefore.wia * (_phaseTransitionSurgeMod - 1));
          battleResult.battle_casualties.total  = battleResult.battle_casualties.kia + battleResult.battle_casualties.wia;
        }
        const _surgeExtra = {
          kia: battleResult.battle_casualties.kia - _surgeBefore.kia,
          wia: battleResult.battle_casualties.wia - _surgeBefore.wia
        };
        _phaseTransitionLog = {
          from_posture:  _prevPhasePosture,
          to_posture:    _curPhasePosture,
          surge_factor:  _phaseTransitionSurgeMod,
          surge_kia:     _surgeExtra.kia,
          surge_wia:     _surgeExtra.wia,
          surge_total:   _surgeExtra.kia + _surgeExtra.wia,
          note: 'Phase transition demand spike — AJMedP-1 §5.4 teaching point'
        };
      }

      // V4-013: Sub-phase tactical differentiation multipliers.
      // Applied AFTER phase transition surge, BEFORE discrete events.
      // _subPhaseMissionMod: multiplier from MISSIONS.casualty_multiplier for this day's tactical mission.
      //   URBAN=1.65x, RIVER_CROSSING=1.45x, MAIN_EFFORT=1.5x, RESERVE=0.2x, etc.
      //   No-op (1.0) when no sub_phases defined on this phase — backward compat.
      // _subPhaseTransitionMod: tactical transition surge (lighter than posture transition).
      //   Fires only when sub-phase mission changes day-to-day within the same parent phase.
      //   No-op (1.0) when no transition, same mission, or no sub_phases.
      // Note: these variables are declared in the isMarch else-branch above (always initialized).
      if (!isMarch && (_subPhaseMissionMod !== 1.0 || _subPhaseTransitionMod !== 1.0)) {
        const _spCombined = _subPhaseMissionMod * _subPhaseTransitionMod;
        scaleBattleCasualties(battleResult, _spCombined);
      }

      // V5-013: Corps-level division role modifier.
      // Applied AFTER sub-phase multipliers, BEFORE discrete events.
      // divisionRoleMod was computed above from division_configs[j].phase_overrides[phaseIndex].division_role.
      // Stacks with brigade missionMultiplier intentionally:
      //   Main Effort brigade (×1.5) in Main Effort division (×1.15) → ×1.725 combined.
      // No-op when divisionRoleMod === 1.0 (SUPPORTING, null, or no corps echelon).
      if (divisionRoleMod !== 1.0) {
        scaleBattleCasualties(battleResult, divisionRoleMod);
      }

      // ENH-041: Discrete event overlay — point-in-time MASCAL surge.
      // Reads scenario.operation_parameters.discrete_events[]. Matches on day + brigade_id.
      // Events applied AFTER Monte Carlo modifier so rate-based and event casualties are additive.
      // Re-assesses MASCAL with combined load if any events fired this day.
      const _discreteEvts = (scenario?.operation_parameters?.discrete_events || [])
        .filter(ev => ev.day === day && (ev.brigade_id === brigade.brigade_id || !ev.brigade_id));
      let _discreteEvtTotal = 0;
      const _discreteEvtLog = [];
      for (const ev of _discreteEvts) {
        const added = applyDiscreteEvent(battleResult, ev);  // mutates battleResult in-place
        if (added > 0) {
          _discreteEvtTotal += added;
          _discreteEvtLog.push({
            id:               ev.id || null,
            event_type:       ev.event_type || 'UNSPECIFIED',
            label:            DISCRETE_EVENT_TYPES[ev.event_type]?.label || ev.event_type || 'Event',
            casualty_count:   added,
            duration_minutes: ev.duration_minutes || DISCRETE_EVENT_TYPES[ev.event_type]?.typical_duration_minutes || 60,
            description:      ev.description || DISCRETE_EVENT_TYPES[ev.event_type]?.description || ''
          });
        }
      }
      if (_discreteEvtTotal > 0 && battleResult._unrounded) {
        // events add whole casualties to the integers: the same amounts are added to the unrounded copy
        battleResult._unrounded.kia += battleResult.battle_casualties.kia - Math.round(battleResult._unrounded.kia);
        battleResult._unrounded.wia += battleResult.battle_casualties.wia - Math.round(battleResult._unrounded.wia);
      }
      // Re-assess MASCAL with combined (rate + discrete) casualty load if any events fired
      let _discreteEventsMASCAL = null;
      if (_discreteEvtTotal > 0) {
        _discreteEventsMASCAL = assessMASCALCondition({
          casualties: battleResult.battle_casualties.total,
          capacity:   resolveInput(medicalConfig?.surgical_capacity, 'surgical_capacity_per_day', { zeroIsMissing: true }),
          timeframe:  'daily',
          facilityType: 'ROLE2_BASIC'
        });
        if (_discreteEventsMASCAL?.is_mascal) {
          brigadeResult.facility_mascal.days_in_mascal++;
          if (!brigadeResult.facility_mascal.facilities_overwhelmed.includes('ROLE2_BASIC')) {
            brigadeResult.facility_mascal.facilities_overwhelmed.push('ROLE2_BASIC');
          }
        }
      }

      // Check if brigade has v3.0 sub_units
      if (brigade.sub_units && Array.isArray(brigade.sub_units) && brigade.sub_units.length > 0) {
        // v3.0 PATH: Use sub-unit composition for accurate calculation
        try {
          // Calculate base rate from battleResult
          const baseCasualtyRate = battleResult.base_rate ||
                                   (battleResult._unrounded
                                     ? (battleResult._unrounded.kia + battleResult._unrounded.wia) / Math.max(currentStrength, 1)
                                     : (battleResult.battle_casualties.total / Math.max(currentStrength, 1)));
          
          // Additional modifiers (terrain, posture, etc.) - already in battleResult
          // We'll recalculate using sub_units
          const unitTypeResultOld = calculateUnitTypeCasualties({
            unitType: brigadeUnitType,
            strength: currentStrength,
            baseCasualtyRate: baseCasualtyRate,
            posture: normalizePosture(brigade.operational_posture, 'DELIBERATE_DEFENSE'),
            terrain: _scenarioEnv.terrain
          });
          
          const terrainModifier = unitTypeResultOld.rates?.terrain_modifier || 1.0;
          const postureModifier = unitTypeResultOld.rates?.posture_modifier || 1.0;
          const additionalModifiers = terrainModifier * postureModifier;
          
          // Set unitTypeResult for backwards compatibility with reporting code
          unitTypeResult = unitTypeResultOld;
          
          // Calculate using sub-unit composition
          // Technology environment on rear units: rear_area_multiplier replaces the general drone casualty multiplier
          // (already inside the rate), so drones count once.
          const _techEnv = TECHNOLOGY_ENVIRONMENT[forceQuality?.technology_env];
          if (!_techEnv) {
            throw new Error(`calculateBrigadeCasualties: unknown technology environment '${forceQuality?.technology_env}' (valid: ${Object.keys(TECHNOLOGY_ENVIRONMENT).join(', ')})`);
          }
          const _technologyRearFactor = _techEnv.rear_area_multiplier / _techEnv.casualty_multiplier;
          const _nationalRearModifier = resolveInput(
            NATIONAL_STANDARDS[brigade.nationalStandard || brigade.national_standard]?.rear_area_security_modifier,
            'rear_area_security_modifier'
          );
          subUnitCalculationResult = calculateSubUnitCasualties({
            brigade: brigade,
            baseCasualtyRate: baseCasualtyRate,
            additionalModifiers: additionalModifiers,
            currentStrength,
            technologyRearFactor: _technologyRearFactor,
            nationalRearModifier: _nationalRearModifier,
            threatRearFactor: _threatModel ? _threatModel.rear.value : 1.0,
            carry: CASUALTY_FRACTION_CARRY_ON ? _casualtyCarry : null,
            engineerModifier: (ENGINEER_TASK_EXPOSURE_ON && _dayProfile)
              ? engineerExposure({ family: PHASE_FORMS[_dayProfile.form].family, condition: _dayProfile.condition, dayInPhase })
              : null
          });
          
          // Calculate weighted average modifier for reporting
          const _reportSubUnits = subUnitCalculationResult.sub_unit_breakdown;
          const totalStrength = _reportSubUnits.reduce((sum, su) => sum + su.strength, 0);
          unitTypeModifier = _reportSubUnits.reduce((sum, su) => {
            return sum + (su.casualty_modifier * su.strength / totalStrength);
          }, 0) * terrainModifier * postureModifier;
          
          // Apply sub-unit calculated casualties to battle result
          const casualtyRatio = subUnitCalculationResult.total_casualties / 
                                Math.max(battleResult.battle_casualties.total, 1);
          
          // R1-09 (switch EQUIPMENT_KILL_EFFECT_ON): killed-share factor of the equipment level on the mounted part of the brigade (1 when off)
          let _equipmentKillFactor = 1;
          if (EQUIPMENT_KILL_EFFECT_ON) {
            const _eqLevel = EQUIPMENT_LEVELS[forceQuality?.equipment_level];
            if (!_eqLevel) {
              throw new Error(`calculateBrigadeCasualties: unknown equipment level '${forceQuality?.equipment_level}' (valid: ${Object.keys(EQUIPMENT_LEVELS).join(', ')})`);
            }
            _equipmentKillFactor = killedShareFactor(subUnitCalculationResult.sub_unit_breakdown, _eqLevel.kia_multiplier);
          }
          if (CASUALTY_FRACTION_CARRY_ON) {
            // the killed share of the unrounded numbers (the rate of the module, not the share of its rounded integers)
            const _u = battleResult._unrounded;
            const _kiaShareBase = _u
              ? _u.kia / Math.max(_u.kia + _u.wia, 1e-9)
              : battleResult.battle_casualties.kia / Math.max(battleResult.battle_casualties.total, 1);
            // R1-09: the equipment level changes the killed share of the mounted part of the units (vehicle protection); the total is unchanged
            const _kiaShare = _kiaShareBase * _equipmentKillFactor;
            const _total = subUnitCalculationResult.total_casualties;
            const _kiaToday = _total * _kiaShare + _casualtyCarry.kia;
            const _kia = Math.min(_total, Math.max(0, Math.round(_kiaToday)));
            _casualtyCarry.kia = _kiaToday - _kia;
            battleResult.battle_casualties.kia = _kia;
            battleResult.battle_casualties.wia = _total - _kia;
            battleResult.battle_casualties.total = _total;
          } else {
            battleResult.battle_casualties.kia = Math.round(battleResult.battle_casualties.kia * casualtyRatio * _equipmentKillFactor);
            battleResult.battle_casualties.wia = _equipmentKillFactor === 1
              ? Math.round(battleResult.battle_casualties.wia * casualtyRatio)
              : subUnitCalculationResult.total_casualties - battleResult.battle_casualties.kia;   // R1-09: the killed that the equipment saves are wounded
            battleResult.battle_casualties.total = subUnitCalculationResult.total_casualties;
          }
          
          
        } catch (error) {
          // v3.0 validation failed - this is INTENTIONAL to expose missing data
          console.error(`[v3.0] Sub-unit calculation failed: ${error.message}`);
          throw error; // Re-throw to stop calculation - force data to be fixed
        }
        
      } else {
        // v5.1.3: This branch should NEVER be reached - ensureBrigadeSubUnits() at
        // calculateBrigadeCasualties entry guarantees sub_units exist.
        // If somehow reached, log a warning and skip sub-unit calculation for this phase.
      }

      // CC-012: Apply rear area casualty decay based on FEBA distance
      const brigadeFebaDistance = resolveInput(brigade.position_feba_km || 
                                  medicalConfig?.mtf_distances?.role2_basic_km, 'brigade_position_feba_km', { zeroIsMissing: true });
      // threatLevel already defined above in FIX-CC-015
      const rearAreaDecayAnalysis = calculateRearAreaDecay(
        brigadeFebaDistance,
        threatLevel,
        normalizeUnitTypeKey(brigade.unit_type) || 'DEFAULT'
      );
      // 2026-10-11: rear-area casualties are the casualties of the rear battalions (calculateSubUnitCasualties); they are
      // already decayed by the distance of each battalion. The old component read battleResult.rear_area, which nothing
      // ever set (always 0). Rear casualties stay inside the battle total (they need care); this is the reported subset.
      const _rearTotal = subUnitCalculationResult?.rear_casualties || 0;
      const adjustedRearArea = { original: _rearTotal, adjusted: _rearTotal, reduction: 0 };

      // Get climate configuration from correct path (Tab2 saves to operation_parameters.climate_config)
      const climateConfig = scenario?.operation_parameters?.climate_config || {};
      const climateEnabled = climateConfig.enabled !== false;
      
      // PHASE 4 TASK 4.3: Calculate DNBI (march has HIGHER DNBI rates)
      let dnbiResult;
      
      if (isMarch) {
        // PHASE 4: March operations have ELEVATED DNBI
        // Cramped conditions, fatigue, hygiene challenges, disrupted rest
        const marchDNBIRate = getMarchDNBIRate(phase.posture, phase.operational_state || brigade.operational_state);  // Returns per 1000 per day
        const marchDNBI = roundWithCarry(currentStrength * (marchDNBIRate / 1000), CASUALTY_FRACTION_CARRY_ON ? _dnbiCarry : null, 'march');
        
        
        dnbiResult = {
          total_dnbi: marchDNBI,
          dnbi_rate_used: marchDNBIRate,
          is_march_operation: true,
          march_type: phase.posture,
          breakdown: {
            base: marchDNBI,
            disease: Math.round(marchDNBI * 0.3),
            injury: Math.round(marchDNBI * 0.4),
            fatigue: Math.round(marchDNBI * 0.2),
            other: Math.round(marchDNBI * 0.1)
          },
          note: 'March DNBI elevated: cramped conditions, fatigue, hygiene challenges',
          // 2026-10-13: the march result lacked the fields every consumer reads (rtd, csr_analysis), so any march phase
          // (Tactical March, Administrative March, Transit) stopped the calculator. The share of cases that return to duty is
          // the category-weighted share (1 minus the evacuated share); no combat stress is modelled on a march.
          rtd: (() => {
            const cats = Object.values(DNBI_CATEGORIES);
            const weight = cats.reduce((sum, c) => sum + c.percentage, 0);
            const evacuated = cats.reduce((sum, c) => sum + c.percentage * c.evacuation_rate, 0) / weight;
            const expectedRtd = Math.round(marchDNBI * (1 - evacuated));
            return { expected_rtd: expectedRtd, expected_evacuation: marchDNBI - expectedRtd, rtd_rate: Math.round((1 - evacuated) * 100) };
          })(),
          csr_analysis: { total_csr_casualties: 0, rtd_expectations: { expected_rtd_count: 0 } }
        };
      } else {
        // Normal operations - use comprehensive DNBI calculation
        dnbiResult = calculateComprehensiveDNBI({
        psychiatricSupport: _psychSupport.level,   // Tab 3 psychiatric support planning assumption
        strength: currentStrength,
        operationalState: phase.operational_state || brigade.operational_state || 'COMBAT_ENGAGED',
        unitType: brigadeUnitType,
        echelon: brigade.echelon || 'BRIGADE',
        environment: {
          climate: climateEnabled ? (climateConfig.climate_zone || 'TEMPERATE') : 'TEMPERATE',
          season: seasonForDay(scenario, day, climateConfig.season || 'summer'),
          month: climateConfig.month || null,
          altitude_m: climateConfig.altitude_m || 500,
          endemic_disease: climateConfig.endemic_disease || 'NONE',
          prophylaxis_compliance: climateConfig.prophylaxis_compliance ?? 1.0,   // R2-06
          water_food_safety: climateConfig.water_food_safety || 'SAFE',
          terrain: brigade.environment?.terrain ||
                   scenario?.operation_parameters?.environmental_factors?.terrain || 'MIXED',
          threat_level: scenario?.operation_parameters?.threat_level || 'PEER',
          theater_region: climateConfig.theater_region || null
        },
        climateEnabled,
        day,
        cumulativeBattleCasualties: cumulativeCasualties,
        woundedToday: battleResult.battle_casualties.wia,
        unitCohesion: forceQuality?.morale_level || 'GOOD',
        isNight,
        scenario,  // CRITICAL FIX: Pass scenario so dnbiModule can read monte_carlo_modifiers
        // GROUP-1-A: Wojcik three-phase DNBI model — pass current phase posture so dnbiModule
        // can select the correct phase modifier (GROUND_COMBAT/BUILD_UP/POST_COMBAT).
        operationalPosture: phase.posture || null,
        carry: CASUALTY_FRACTION_CARRY_ON ? _dnbiCarry : null
      });
      }  // End march vs normal DNBI if/else

      // W5 (2026-10-20): cold injuries caused by the weather of the day (frostbite, trench foot, hypothermia), anchored to the wounded; added to the
      // DNBI before the host nation absorption. Protection of the troops is a Tab 2 input (default STANDARD, listed in the assumptions).
      {
        // the default is recorded in the assumptions only when the weather has a cold risk at all
        const _coldProtection = coldIndexOfWeather(_dayEnvironment.weather) > 0
          ? resolveInput(scenario?.operation_parameters?.cold_protection, 'cold_protection')
          : (scenario?.operation_parameters?.cold_protection || 'STANDARD');
        const _cold = coldInjuryOfDay({ weather: _dayEnvironment.weather, wounded: battleResult.battle_casualties.wia || 0, protection: _coldProtection });
        dnbiResult.cold_injury_weather = _cold;
        if (_cold.cases > 0) {
          dnbiResult.total_dnbi = (dnbiResult.total_dnbi || 0) + _cold.cases;
          dnbiResult.rtd = dnbiResult.rtd || {};
          dnbiResult.rtd.expected_rtd = (dnbiResult.rtd.expected_rtd || 0) + _cold.cases * COLD_INJURY_RTD_SHARE;
        }
      }

      // W5 heat (2026-10-20): heat injuries of extreme heat, by strength, contact and acclimatization (the climate setting acclimatization_complete,
      // which nothing read before); added to the DNBI before the host nation absorption.
      {
        const _heatOn = heatIndexOfWeather(_dayEnvironment.weather) > 0;
        const _accl = _heatOn
          ? resolveInput(climateConfig.acclimatization_complete, 'acclimatization_complete')
          : (climateConfig.acclimatization_complete ?? true);
        const _heat = heatInjuryOfDay({
          weather: _dayEnvironment.weather,
          strength: currentStrength,
          engaged: (phase.operational_state || brigade.operational_state) === 'COMBAT_ENGAGED',
          acclimatized: _accl
        });
        dnbiResult.heat_injury_weather = _heat;
        if (_heat.cases > 0) {
          dnbiResult.total_dnbi = (dnbiResult.total_dnbi || 0) + _heat.cases;
          dnbiResult.rtd = dnbiResult.rtd || {};
          dnbiResult.rtd.expected_rtd = (dnbiResult.rtd.expected_rtd || 0) + _heat.cases * HEAT_INJURY_RTD_SHARE;
        }
      }

      // HNS-001: Apply HNS DNBI absorption.
      // Host nation civilian medical system absorbs a fraction of military DNBI
      // (AJP-4.10C §5.4 — civilian DNBI/accident absorption reducing military load).
      // Applied after both march and normal DNBI paths — same formula for both.
      // hns_absorbed tracked for Tab5 HNS panel display.
      // Backward compat: hnsModifiers.dnbi_absorption === 0.00 when HNS disabled → no-op.
      if (hnsModifiers.is_active && hnsModifiers.dnbi_absorption > 0) {
        const hnsAbsorbed = roundWithCarry(dnbiResult.total_dnbi * hnsModifiers.dnbi_absorption, CASUALTY_FRACTION_CARRY_ON ? _dnbiCarry : null, 'hns');
        dnbiResult.total_dnbi    = Math.max(0, dnbiResult.total_dnbi - hnsAbsorbed);
        dnbiResult.hns_absorbed  = hnsAbsorbed;
        dnbiResult.rtd           = dnbiResult.rtd || {};
        // RTD scales proportionally with absorbed load reduction
        if (dnbiResult.rtd.expected_rtd !== undefined) {
          dnbiResult.rtd.expected_rtd = Math.max(
            0,
            roundWithCarry(dnbiResult.rtd.expected_rtd * (1 - hnsModifiers.dnbi_absorption), CASUALTY_FRACTION_CARRY_ON ? _dnbiCarry : null, 'hns_rtd')
          );
        }
      } else {
        dnbiResult.hns_absorbed = 0;
      }
      // ENH-039: Update running R2B bed census and derive occupancy % for DOW feedback.
      // Admissions = (battle WIA + DNBI) × HOSPITALIZATION_RATE × ROLE_2_BASIC fraction.
      // Drain = census / avgLOS (patients leave via RTD or evacuation after avgLOS days on avg).
      // bedOccupancyPct passed to calculateComprehensiveDOW → step 11.6 in DOW chain.
      // Wounded: (WIA) x hospitalization rate x the share admitted to Role 2B. DNBI (2026-10-10, Dr. Tatar): only DNBI that needs
      // holding goes to Role 2B beds = the cases NOT returned to duty at the unit, by category evacuation rate; behavioural
      // health (combat stress, psychiatric) stays forward and takes no Role 2B bed. Stay of a DNBI admission: DNBI_HOLDING.
      // R2-08: the MedROE patients first fill the beds of the Role 2 Enhanced and Role 3 (this brigade's slice); the overflow is admitted to the Role 2B
      const _medroeFree = Math.max(0, _enhBeds - medroeCensusEnh);
      const _medroeAdmittedEnh = Math.min(_medroeAdmissions, _medroeFree);
      const _medroeOverflow = _medroeAdmissions - _medroeAdmittedEnh;
      const _medroeDrain = medroeCensusEnh > 0 ? medroeCensusEnh / _r2bAvgLOS : 0;
      medroeCensusEnh = Math.max(0, medroeCensusEnh + _medroeAdmittedEnh - _medroeDrain);
      // bed census (2026-10-21, carry): admissions and drain are kept as they are (not rounded to whole patients each day)
      const _bedRound = CASUALTY_FRACTION_CARRY_ON ? (v) => v : Math.round;
      const _bedAdmissions = _bedRound(
        (battleResult?.battle_casualties?.wia || 0) * HOSPITALIZATION_RATE * ECHELON_ADMISSION_DISTRIBUTION.ROLE_2_BASIC
      ) + _bedRound(_medroeOverflow);
      const _dnbiByCategory = dnbiResult?.breakdown?.by_category || {};
      const _dnbiHeld = Object.entries(_dnbiByCategory).reduce((sum, [cat, n]) => {
        if (cat === 'BEHAVIORAL_HEALTH' && !DNBI_HOLDING.behavioral_health_uses_role2_beds) return sum;
        const rate = DNBI_CATEGORIES[cat]?.evacuation_rate;
        return sum + (typeof rate === 'number' ? n * rate : 0);
      }, 0);
      const _bedAdmissionsDnbi = _bedRound(_dnbiHeld);
      const _bedDrain = bedCensus > 0 ? _bedRound(bedCensus / _r2bAvgLOS) : 0;
      bedCensus = Math.max(0, bedCensus + _bedAdmissions - _bedDrain);
      const _bedDrainDnbi = bedCensusDnbi > 0 ? _bedRound(bedCensusDnbi / DNBI_HOLDING.mean_los_days) : 0;
      bedCensusDnbi = Math.max(0, bedCensusDnbi + _bedAdmissionsDnbi - _bedDrainDnbi);
      const bedOccupancyPct = r2bBedCapacity > 0
        ? Math.round(((bedCensus + bedCensusDnbi) / r2bBedCapacity) * 100)
        : 0;

      // 2026-10-11: the wound table follows the operation type (Tab 2), the posture of the phase and the unit type; before, it
      // was looked up with mission_type ('DEFENSE' ...), which matched no table key, so STANDARD_BASELINE was always used.
      const opType = woundDistributionKeyFor({
        operationType: resolveOperationType(scenario),
        posture: normalizePosture(phase.posture || brigade.operational_posture, 'DELIBERATE_DEFENSE'),
        unitType: brigadeUnitType
      });
      // 2026-10-11: body armor changes the TYPE of wounds (fewer thorax wounds); the tables are the mix without modern armor.
      // The unit types of the battalions change it too (armored crews burn: UNIT_TYPE_WOUND_MIX).
      const woundDistribution = applyArmorToWoundMix(
        blendWoundMixByUnitTypes(getWoundDistribution(opType), subUnitCalculationResult?.sub_unit_breakdown),
        forceQuality?.body_armor_level || DEFAULT_BODY_ARMOR_LEVEL
      );

      // ENH-044: Wire Class VIII days_of_supply → MTF surgical capability degradation.
      // When operation day exceeds the planned blood supply DOS (configured in Tab3 blood_config),
      // the MTF is projected to lack critical surgical consumables (blood products, IV fluids).
      // This degrades surgical_capacity passed to calculateComprehensiveDOW, increasing DOW rate.
      // DOS thresholds align with DOS_THRESHOLDS from classVIII.js:
      //   DOS_THRESHOLDS.LOW=3  → <3 days remaining  → 75% capability (−25%)
      //   DOS_THRESHOLDS.CRITICAL=1 → <1 day remaining → 50% capability (−50%)
      // Only applies when blood_config.days_of_supply is configured (non-zero).
      // Default (0/absent) → full capability (no change from pre-ENH-044 behavior).
      const _bloodDOS = medicalConfig?.blood_config?.days_of_supply || 0;
      let _class8CapMod = 1.0;
      if (_bloodDOS > 0) {
        const _daysRemaining = _bloodDOS - day;
        if (_daysRemaining < 1)       _class8CapMod = 0.50;  // CRITICAL: <1 day DOS
        else if (_daysRemaining < 3)  _class8CapMod = 0.75;  // LOW: <3 days DOS
      }

      // ENH-048b: Monte Carlo surgical_capacity modifier stacked with Class VIII degradation.
      // surgical_capacity > 1.0 = more throughput = better T1 survival = lower DOW.
      // surgical_capacity < 1.0 = degraded (staffing, equipment) = more T1 die = higher DOW.
      // _effectiveSurgMod combines both sources of surgical capacity variation.
      // No-op at 1.0 — deterministic runs never set monte_carlo_modifiers.
      const _mcSurgCapMod = scenario?.operation_parameters?.monte_carlo_modifiers?.surgical_capacity ?? 1.0;

      // GROUP-1-D: Gielerak bo factor — medical structure protection level.
      // bo = operation_parameters.medical_protection_level (0-100%).
      // Maps to surgical capacity factor: _boCapMod = 0.50 + (bo/100) * 0.50
      //   bo=100% -> _boCapMod=1.00 (fully protected; no surgical degradation)
      //   bo= 70% -> _boCapMod=0.85 (default LSCO; 15% degradation)
      //   bo=  0% -> _boCapMod=0.50 (fully exposed; 50% degradation)
      // This models deliberate enemy targeting of medical structures as per
      // Gielerak & Murawski 2024 (Lekarz Wojskowy Vol. 102, DOI: 10.53301/lw/177410).
      const _bo = scenario?.operation_parameters?.medical_protection_level ?? MEDICAL_PROTECTION_DEFAULT;
      const _boCapMod = MEDICAL_PROTECTION_BO_FACTOR_MIN + (_bo / 100) * (1 - MEDICAL_PROTECTION_BO_FACTOR_MIN);

      // V4-002: equipment_readiness — OR equipment mechanical state (anaesthesia machines, diathermy,
      // imaging). Distinct from surgical_capacity (staffing) and class8 (blood supply).
      // equipment_readiness < 1.0 = deferred maintenance = degraded OR throughput.
      const _mcEquipReadSurgMod = scenario?.operation_parameters?.monte_carlo_modifiers?.equipment_readiness ?? 1.0;

      // Stack all four surgical capacity modifiers:
      //   _class8CapMod        — blood DOS depletion (ENH-044)
      //   _mcSurgCapMod        — Monte Carlo staffing/fatigue variation (ENH-048b)
      //   _mcEquipReadSurgMod  — Monte Carlo OR equipment readiness (V4-002)
      //   _boCapMod            — medical structure protection (GROUP-1-D / Gielerak)
      // 2026-10-13 (Tab 2 plan 7.10): power and water of the infrastructure card stack with the other surgical capacity modifiers
      const _infraSurgMod = _threatModel?.infrastructure ? _threatModel.infrastructure.surgical_capacity : 1.0;
      // R2-03 / R2-04 (2026-10-21): protection of the facilities (exposure to enemy artillery) and power and water of the infrastructure card now reach the
      // surgical capacity of the resource model (facilityCapacityFactor below); they left this stack, which only fed a configuration the survival model does not
      // use for capacity.
      const _protection = protectionCapacityFactor({ protectionLevel: _bo, artilleryLevel: scenario?.operation_parameters?.threat_assessment?.artillery_threat });
      const _facilityCapFactor = _protection.factor * _infraSurgMod;
      const _effectiveSurgMod = _class8CapMod * _mcSurgCapMod * _mcEquipReadSurgMod;
      const _dowMedConfig = _effectiveSurgMod !== 1.0 ? {
        ...medicalConfig,
        surgical_capacity: Math.max(1, Math.round((resolveInput(medicalConfig?.surgical_capacity, 'surgical_capacity_per_day', { zeroIsMissing: true })) * _effectiveSurgMod)),
        class8_degraded: _class8CapMod < 1.0,      // true only for actual Class VIII depletion
        class8_capability_factor: _class8CapMod,
        class8_days_remaining: _bloodDOS > 0 ? Math.max(0, _bloodDOS - day) : null,
        bo_protection_level: _bo,                   // GROUP-1-D: persisted for Tab5/6 display
        bo_capability_factor: _boCapMod             // GROUP-1-D: stacked modifier value
      } : medicalConfig;

      // CALC-001/002: cumulative time from injury to surgery (collection, Role 1 treatment, transport,
      // forward surgery where present). Computed first because the T1 survival curve in
      // calculateComprehensiveDOW is driven by it (previously a fixed 90 minutes).
      const _ghBase = analyzeGoldenHourCompliance(
        medicalConfig,
        brigade,
        medevacAnalysis,
        _dayEnvironment
      );

      // 2026-10-13 (Tab 2 plan, step 5): evacuation profiles by cause (constants/evacuationProfiles.js). TRANSPORT-limited delay
      // (river crossing, amphibious, airborne, urban, breach, march) lengthens the time to ARRIVAL; FACILITY-limited delay (medical
      // units packed on march or displacement, setup ramp after the halt) lengthens the time to SURGERY. THREAT-limited delay
      // (evacuation paradigm, hold hours, drones) is unchanged.
      // R2-30: advance of the day (km) and the positions of the medical chain along the axis; march days: the units move together, nothing grows
      const _advForm = _dayProfile?.form;
      const _advKm = (ADVANCE_LEGS_ON && !isMarch && _dayProfile)
        ? phaseAdvanceKm({ form: _advForm, resistanceLevel: _dayProfile.resistance_level, group: _dayProfile.group, condition: _dayProfile.condition, dayInPhase, footholdDay: phase.foothold_day, plannerKm: phase.advance_km_day })
        : 0;
      const _axisDay = (ADVANCE_LEGS_ON && !isMarch) ? stepAxis(_axis, _advKm) : null;
      // the km model replaces the fixed displacement interval for forms that advance or while legs are still grown
      const _advDrives = !!(_axisDay && (_axisDay.active || Object.prototype.hasOwnProperty.call(ADVANCE_KM_PER_DAY, _advForm)));
      const _surgFacility = facilityOf(_ghBase.surgical_destination).name;
      const _packedToday = _advDrives
        ? (_surgFacility === 'R2B' ? _axisDay.r2b_jump : _surgFacility === 'R2E' ? _axisDay.r2e_jump : false)
        : isMedicalPackedToday({ isMarch, form: _dayProfile?.form, dayInPhase });
      _daysSincePacked = _packedToday ? 0 : _daysSincePacked + 1;
      const _evacProfileBase = USE_EVACUATION_PROFILES
        ? evacuationAdjustment({
            condition: _dayProfile?.condition ?? null,
            dayInPhase,
            footholdDay: phase.foothold_day,
            isMarch,
            hasHelo: _ghBase.has_helicopter_evac,
            baseArrivalMin: arrivalHoursFromSegments(_ghBase.segments) * 60,
            surgicalDestination: _ghBase.surgical_destination,
            packedToday: _packedToday,
            daysSincePacked: _daysSincePacked,
            r2eMovesWithForce: (scenario?.force?.brigades?.length ?? 0) === 1 || _advDrives,   // the division R2E packs only when no other brigade stays, or when it follows the advance (R2-30)
            // 2026-10-13 (Tab 2 plan 7.2, 7.9): one road state for the legs above Role 2, one helicopter availability
            helicopterFactor: _threatModel ? _threatModel.helicopter.value * _nightEvac.medevac : 1,
            roadState: _threatModel ? _threatModel.roadState : null,   // null = no road effect
            rearLegMin: rearLegMinutes(_ghBase.segments),
            evacDelayFactor: _threatModel?.infrastructure ? _threatModel.infrastructure.evac_delay_modifier : 1
          })
        : null;
      // R2-30: the grown legs cost minutes at the speed of the existing evacuation chain (arrival on the Role 1 -> Role 2B leg, surgery on the leg behind)
      let _evacProfile = _evacProfileBase;
      if (_evacProfileBase && _axisDay && _axisDay.active) {
        const _gsf = TAB2_TERRAIN_TYPES[_dayEnvironment.terrain].medical.evacuation_speed * TAB2_WEATHER_CONDITIONS[_dayEnvironment.weather].evacuation_speed_modifier * _nightEvac.ground;
        const _legMin = legGrowthMinutes({ leg1ExtraKm: _axisDay.leg1_extra_km, leg2ExtraKm: _axisDay.leg2_extra_km, surgeryAtR2B: _surgFacility === 'R2B', groundSpeedFactor: _gsf });
        const _xa = Math.round(_legMin.extra_arrival_min);
        const _xs = Math.round(_legMin.extra_surgery_min);
        _evacProfile = {
          ..._evacProfileBase,
          extra_arrival_min: _evacProfileBase.extra_arrival_min + _xa,
          extra_surgery_min: _evacProfileBase.extra_surgery_min + _xs,
          extra_total_min: _evacProfileBase.extra_total_min + _xa + _xs,
          advance_legs: { arrival_min: _xa, surgery_min: _xs }
        };
      }
      const cumulativeGHAnalysis = _evacProfile
        ? {
            ..._ghBase,
            cumulative_time_to_surgery_min: _ghBase.cumulative_time_to_surgery_min + _evacProfile.extra_total_min,
            golden_hour_compliant: _ghBase.golden_hour_compliant && _ghBase.cumulative_time_to_surgery_min + _evacProfile.extra_total_min <= 60,
            evacuation_profile: _evacProfile
          }
        : _ghBase;

      // Wound-type hazard model inputs (null when OFF: the call below is then identical to before).
      let _woundHazardInput = null;
      let _evacWait = null;   // derived evacuation wait of the day (window, spread, PFC level)
      let _dayEnv = null;     // R2-28: evacuation environment of the day, derived from the open share of the window
      let _tacCapDay = null;  // R2-29: tactical evacuation capacity of the day (reused by tactical_evac_analysis)
      let _evacQueue = null;  // R2-29: evacuation queue of the day (waits, backlog)
      const _dynT4Hold = { blood: 0, surgeon: 0, share: 0 };
      if (DYNAMIC_T4_ON) {
        _dynT4Hold.blood = _bloodActive ? t1HeldForBlood({ alert: bloodAlertPrev, woundDistribution }) : 0;
        _dynT4Hold.surgeon = !DYNAMIC_T4_SURGEON_ON ? 0 : t1HeldForSurgeon({ t1Cases: dynT4Prev.t1, capacity: dynT4Prev.capacity });
        _dynT4Hold.share = combineHolds(_dynT4Hold.blood, _dynT4Hold.surgeon);
      }
      if (_hazardSupply) {
        _hazardSupply.startDay(day);
        // Walking blood bank: when it is active (stock below its threshold or MASCAL) fresh whole blood is at hand, so
        // resuscitation stays in place in the hazard model even if the stock alert is red. Same inputs as the daily
        // WBB step below (yesterday's blood state); its DOW reduction was computed in resourceModule and never applied.
        const _wbbObjective = _bloodActive ? bloodInitialShare : bloodStockPrbc(medicalConfig);
        const _wbbEarly = calculateWBBContribution(
          wbbConfig,
          bloodStockPercent(_bloodActive ? bloodOnHand : (medicalSystemState.blood_dos ?? 100), _wbbObjective),
          medicalSystemState.mascal_level || 'NORMAL'
        );
        const _wbbActive = _wbbEarly.activated === true && _wbbEarly.availableUnits > 0;
        // dynamic T4: with the walking blood bank active fresh whole blood is at hand, nobody is held for blood
        if (DYNAMIC_T4_ON && _wbbActive) {
          _dynT4Hold.blood = 0;
          _dynT4Hold.share = combineHolds(0, _dynT4Hold.surgeon);
        }
        if (EVAC_WAIT_DERIVED_ON && _threatModel) {
          const _taW = scenario?.operation_parameters?.threat_assessment || {};
          const _winW = evacuationWindowFromParts({
            roadAvailability: _threatModel.roadState ? _threatModel.roadState.availability : null,
            helicopterValue: _threatModel.helicopter.value * _nightEvac.medevac,   // R2-05: night operations
            droneLevel: _droneThreatLevel,
            artilleryLevel: _taW.artillery_threat,
            resistanceLevel: levelOfIndex(resolveResistanceIndex(phase, scenario, { brigadeId: brigade.id || brigade.brigade_id, day, friendlyStrength: currentStrength })),
            closureScale: scenario?.operation_parameters?.monte_carlo_modifiers?.evac_closure_scale ?? 1,   // R2-24d
            weather: _dayEnvironment.weather   // W3: the weather lowers what drones can do
          });
          // R1-07 (2026-10-25): the level follows the Role 1 facilities and the blood setting of Tab 3 when PFC_FROM_TAB3_ON; the Tab 1 value is the failsafe
          const _pfcDerived = derivePfcLevelCached(medicalConfig, pfcLevelOf(forceQuality?.medical_capability || 'GOOD'));
          const _pfcKeyW = _pfcDerived.level;
          // R1-08: the share of the brigade's medical personnel still alive takes the same share off the prolonged field care benefit
          const _medicShareW = MEDIC_LOSS_REDUCES_PFC_ON ? medicShareOf(remainingMedicalPersonnel, brigadeResult.medical_personnel.initial_count) : 1;
          const _pfcScaleW = (scenario?.operation_parameters?.monte_carlo_modifiers?.pfc_effect_scale ?? 1) * _medicShareW;
          _evacWait = { window: _winW, pfc_level: _pfcKeyW, pfc_source: _pfcDerived.source, pfc_role1_count: _pfcDerived.role1_count, medic_share: _medicShareW, points_eff: waitSpreadPoints(_winW).map((w) => pfcEffectiveWait(w, _pfcKeyW, _pfcScaleW)) };
          if (EVAC_ENV_DERIVED_ON && _woundHazardMode === WOUND_HAZARD_MODES.ON) _dayEnv = suggestParadigm(_winW.p_open);   // SHADOW changes no result
          // R2-29: the Tab 3 fleet's capacity of the day limits evacuation; the backlog's wait is added to the wait of the window.
          const _tecCfg = medicalConfig?.tactical_evac_config;
          const _tacticalConfigured = !!(_tecCfg && (_tecCfg.fleet || _tecCfg.air_pool || _tecCfg.air_pools));
          if (EVAC_CAPACITY_QUEUE_ON && _tacticalConfigured) {
            // R2-29b: ground ambulances are the brigade's own (its share of the Tab 3 fleet by the standard fleet of its nation); the helicopters are one pool of
            // the force, allocated by the daily demand of the brigades (shares of the first pass); false = everything shared equally
            const _brigList = scenario?.force?.forceBuilderResult?.brigades || scenario?.force?.brigades || [];
            const _nBrig = Math.max(1, _brigList.length);
            const _byBrigade = EVAC_FLEET_BY_BRIGADE_ON && _brigList.length > 1;
            const _groundShares = _byBrigade ? groundFleetShares(_brigList, brigade) : null;
            _tacCapDay = tacticalCapacityOfDay({ medicalConfig, scenario, brigade, day, environmentOverride: _dayEnv, weatherKey: _dayEnvironment.weather, groundShares: _groundShares });
            // drone attrition of the ambulance fleet (weeks elapsed, floor 30 percent of the fleet)
            const _fleetAttrition = DRONE_FLEET_ATTRITION_ON ? droneAttritionOf(_droneWeeklyLoss, day) : 1;
            const _groundCap = (_tacCapDay.gCap?.total_patients_per_day || 0) * _fleetAttrition;
            const _airPool = _tacCapDay.aCap?.total_patients_per_day || 0;
            const _airShare = _byBrigade ? (scenario?.operation_parameters?.air_demand_shares?.[brigade.id || brigade.brigade_id]?.[day] ?? 1 / _nBrig) : 1 / _nBrig;
            const _capacityDay = _byBrigade ? _groundCap + _airPool * _airShare : (_groundCap + _airPool) / _nBrig;
            const _wiaDay = battleResult.battle_casualties.wia || 0;
            const _t1Early = (calculateTriageDistribution
              ? calculateTriageDistribution(_wiaDay, scenario?.operation_parameters?.mission_type || 'DEFENSE')
              : calculateTimeAdjustedTriageDistribution(_wiaDay, scenario?.operation_parameters?.mission_type || 'DEFENSE', day));
            const _t1Cases = _t1Early.t1_immediate || _t1Early.t1 || 0;
            const _q = evacuationQueueDay({ backlogIn: evacBacklogPrev, demand: _wiaDay, urgentShare: _wiaDay > 0 ? Math.min(1, _t1Cases / _wiaDay) : 0, capacity: _capacityDay });
            _evacQueue = { ..._q, capacity_day: _capacityDay, demand_day: _wiaDay, backlog_in: { ...evacBacklogPrev }, ground_capacity: _byBrigade ? _groundCap : _groundCap / _nBrig, fleet_attrition: _fleetAttrition, air_capacity: _byBrigade ? _airPool * _airShare : _airPool / _nBrig, air_share: _airShare, by_brigade: _byBrigade };
            evacBacklogPrev = _q.backlog_out;
            _evacWait.points_eff = waitSpreadPoints(_winW).map((w) => pfcEffectiveWait(w + _q.urgent_wait_hours, _pfcKeyW, _pfcScaleW));
            _evacWait.points_eff_other = waitSpreadPoints(_winW).map((w) => pfcEffectiveWait(w + _q.other_wait_hours, _pfcKeyW, _pfcScaleW));   // T2 and below wait behind the urgent
          }
        }
        _woundHazardInput = {
          mode: _woundHazardMode,
          arrivalHours: arrivalHoursFromSegments(cumulativeGHAnalysis.segments) + (_evacProfile ? _evacProfile.extra_arrival_min / 60 : 0),
          surgicalQueueWaitHours: _hazardQueueWaitHours + (_evacProfile ? _evacProfile.extra_surgery_min / 60 : 0),
          walkingBloodBankActive: _wbbActive,
          t1ToT4Share: DYNAMIC_T4_ON ? _dynT4Hold.share : undefined,
          bloodNeedHeld: DYNAMIC_T4_ON && _dynT4Hold.blood > 0,
          t1PriorityQueue: DYNAMIC_T4_ON && DYNAMIC_T4_SURGEON_ON,
          waitSpreadHours: _evacWait ? _evacWait.points_eff : undefined,
          waitSpreadHoursOther: _evacWait ? _evacWait.points_eff_other : undefined,
          paradigmTransitDelayOff: !!(EVAC_ENV_DERIVED_ON && _evacWait),   // R2-28: the typed label's extra transit counted the window's conditions twice
          antibioticsInStock: _hazardSupply.inStock('antibiotics_doses'),
          dressingsInStock: _hazardSupply.inStock('dressings_sets')
        };
      }

      // Calculate DOW with comprehensive factors - USE medevacAnalysis!
      const dowResult = calculateComprehensiveDOW({
        brigade: {
          ...brigade,
          environment: {
            terrain: _scenarioEnv.terrain,
            threat_level: scenario?.operation_parameters?.threat_level || 'PEER'
          },
          cls_training: resolveClsQuality({ ...forceQuality, cls_training_percentage: resolveClsLevel(medicalConfig, forceQuality).level })
        },
        scenario,
        medicalConfig: _bloodActive
          ? { ..._dowMedConfig, blood_config: { ...(_dowMedConfig.blood_config || {}), alert_level: bloodAlertPrev } }
          : _dowMedConfig,  // ENH-044: Class VIII degraded config when DOS < threshold; with Tab 4 blood the alert of the day before
        medevacAnalysis,  // Pass MEDEVAC analysis (recommended_method)
        timeToSurgeryMinutes: cumulativeGHAnalysis.cumulative_time_to_surgery_min,
        dailyCasualties: battleResult.battle_casualties.total,
        day,
        tcccAnalysis,     // Pass TCCC analysis for intervention effects
        // WTD-01: Wound distribution for wound-type DOW refinement (JTS JTTR rates)
        woundDistribution,
        operationType: opType,
        woundHazard: _woundHazardInput,
        bedOccupancyPct  // ENH-039: running R2B bed census % → step 11.6 DOW multiplier
      });

      // Calculate triage distribution - CC-010: Now adjusts based on operation day
      let triageDistribution = calculateTriageDistribution ? 
        calculateTriageDistribution(battleResult.battle_casualties.wia, 
          scenario?.operation_parameters?.mission_type || 'DEFENSE') :
        calculateTimeAdjustedTriageDistribution(
          battleResult.battle_casualties.wia,
          scenario?.operation_parameters?.mission_type || 'DEFENSE',
          day  // CC-010: Pass day for time-based adjustment
        );
      // 2026-10-21: the triage split of the wounded is rounded per category with the carry (a category of 0.4 a day is not lost); the
      // percentages are those of the function itself (called on a large number), the counts of the day are their share of the wounded
      if (CASUALTY_FRACTION_CARRY_ON) {
        const _triageMission = scenario?.operation_parameters?.mission_type || 'DEFENSE';
        const _triageRef = calculateTriageDistribution
          ? calculateTriageDistribution(1e6, _triageMission)
          : calculateTimeAdjustedTriageDistribution(1e6, _triageMission, day);
        const _wiaToday = battleResult.battle_casualties.wia;
        const _carriedTriage = { ...triageDistribution };
        Object.keys(_triageRef).forEach((k) => {
          if (typeof _triageRef[k] === 'number' && typeof triageDistribution[k] === 'number' && k !== 'day') {
            _carriedTriage[k] = _rc(_wiaToday * _triageRef[k] / 1e6, 'triage_' + k);
          }
        });
        triageDistribution = _carriedTriage;
      }

      // CC-011: Calculate blood requirements by injury mechanism
      const terrain = _scenarioEnv.terrain;   // CALC-011: planner's terrain (Tab 2)
      const missionType = scenario?.operation_parameters?.mission_type || 'DEFENSE';
      const mechanismDistribution = getInjuryMechanismDistribution(terrain, missionType);
      // v4.9 BUGFIX: Use t1_immediate (not t1) - calculateTimeAdjustedTriageDistribution returns t1_immediate
      // INC-010: pass MSK surge threshold from Tab3 medical config (falls back to 12 if not set)
      const bloodByMechanism = calculateBloodByMechanism(
        triageDistribution.t1_immediate || triageDistribution.t1 || 0,
        mechanismDistribution,
        scenario?.medical_configuration?.msk_surge_threshold_per_day
      );

      // Calculate resource requirements
      // CC-013: Calculate fatigue effect on surgical capacity
      const dayFatigueEffect = calculateFatigueEffect(
        medicalSystemState?.cumulative_fatigue || 0,
        day
      );
      // Phase 6.1: Apply surgeon shift-hours performance multiplier on top of day-based fatigue.
      // getSurgeonPerformance(0) returns { performance: 1.0 } — no change when hours_worked = 0.
      const surgeonPerfResult = getSurgeonPerformance
        ? getSurgeonPerformance(_surgeonHoursWorked)
        : { performance: 1.0 };
      const surgeonPerfModifier = surgeonPerfResult?.performance ?? 1.0;
      const fatigueEffect = Math.max(0.50, dayFatigueEffect * surgeonPerfModifier);
      
      const resourceResult = calculateComprehensiveResources({
        triageDistribution,
        dailyCasualties: battleResult.battle_casualties.total,
        medicalConfig: _resourceMedConfig,
        day,
        previousDayState: { surgical_backlog: surgicalBacklog, surgical_tempo_state: surgicalTempoState },
        brigadeCount: (scenario?.force?.forceBuilderResult?.brigades || scenario?.force?.brigades || []).length,
        mascalLevel: _discreteEventsMASCAL && _discreteEventsMASCAL.is_mascal ? (_discreteEventsMASCAL.level || 'MASCAL_1') : (medicalSystemState.mascal_level || 'NORMAL'),
        fatigueModifier: fatigueEffect,  // CC-013: Pass fatigue to resource module
        civilianSurgicalCases: _civT1Military,   // R2-08: civilian cases the military takes (force), to the Role 2 Enhanced and Role 3 first
        facilityCapacityFactor: _facilityCapFactor,   // R2-03 / R2-04: surgical capacity left after enemy fire on the facilities and the power and water they run on
        exactCounts: CASUALTY_FRACTION_CARRY_ON   // blood and surgical counts of the day are not rounded per triage category
      });

      if (DYNAMIC_T4_ON) {
        const _byCat = resourceResult.surgical?.requirements?.demand?.by_category;
        const _reg = resourceResult.surgical?.regulating;
        dynT4Prev = {
          // capacity of the day for operations: Role 2 Basic up to its crisis limit + what the accessible facilities can take
          capacity: _reg
            ? _reg.limit + (_reg.destinations || []).reduce((c, d) => c + Math.max(0, d.capacity), 0)
            : (resourceResult.surgical?.requirements?.capacity?.effective_capacity || 0),
          t1: _byCat?.t1_immediate?.cases || 0
        };
      }

      // Wound hazard model: tomorrow's wait in the surgical queue follows today's end-of-day backlog.
      if (_hazardSupply) {
        _hazardQueueWaitHours = (resourceResult.surgical?.cascade?.output?.avg_wait_hours ?? 0) +
          (resourceResult.surgical?.regulating ? resourceResult.surgical.regulating.extra_delay_hours : 0);   // diverted cases pay the extra transit
      }

      // PHASE 1.1: Calculate WBB contribution
      // Check current blood stock level as % of objective
      const currentBloodStock = _bloodActive ? bloodOnHand : (medicalSystemState.blood_dos ?? 100);
      const bloodObjective = _bloodActive ? bloodInitialShare : bloodStockPrbc(medicalConfig);
      const currentStockPercent = bloodStockPercent(currentBloodStock, bloodObjective);
      
      // Get current MASCAL level for WBB activation
      const currentMascalLevel = medicalSystemState.mascal_level || 'NORMAL';
      
      // Calculate WBB contribution
      const wbbAnalysis = calculateWBBContribution(wbbConfig, currentStockPercent, currentMascalLevel);
      
      // Apply WBB to blood requirements (reduces PRBC need if activated)
      const wbbAdjustedBlood = applyWBBToBloodCalculations(
        resourceResult.blood?.requirements?.totals || { prbc_units: 0, ffp_units: 0 },
        wbbAnalysis
      );
      
      // Update resourceResult with WBB-adjusted blood requirements
      if (resourceResult.blood && resourceResult.blood.requirements) {
        resourceResult.blood.requirements.totals = wbbAdjustedBlood;
      }

      // Phase 6.1: Apply MTP ratio adjustment on top of WBB-adjusted blood.
      // Only modifies FFP and PLT upward to enforce the configured ratio.
      // mtpPatients comes from bloodByMechanism (already computed above).
      const mtpPatientsToday = bloodByMechanism?.totals?.mtp_patients ?? 0;
      if (_mtpEnabled && mtpPatientsToday > 0 && resourceResult.blood?.requirements) {
        resourceResult.blood.requirements.totals = applyMTPAdjustment(
          resourceResult.blood.requirements.totals,
          mtpPatientsToday,
          _mtpProtocol
        );
      }

      // Phase 6.1: Shelf life tracking — PLT wastage estimate (display only).
      const pltOnHand = resourceResult.blood?.requirements?.totals?.plt_units
                     || resourceResult.blood?.requirements?.totals?.plt
                     || 0;
      const shelfLifeTracking = computeShelfLifeTracking(pltOnHand, _shelfLifeEnabled);

      // PHASE 4.1: Apply resupply events for today
      const todayResupply = resupplyConfig.by_day[day] || [];
      let resupplyReceived = false;
      let resupplyUnitsReceived = { prbc: 0, ffp: 0, plt: 0, cryo: 0 };
      
      if (todayResupply.length > 0) {
        // Apply resupply to blood stock
        for (const resupplyEvent of todayResupply) {
          resupplyUnitsReceived.prbc += resupplyEvent.units?.prbc || 0;
          resupplyUnitsReceived.ffp += resupplyEvent.units?.ffp || 0;
          resupplyUnitsReceived.plt += resupplyEvent.units?.plt || 0;
          resupplyUnitsReceived.cryo += resupplyEvent.units?.cryo || 0;
        }
        
        // Update medicalSystemState blood stock
        if (_bloodActive) {
          bloodOnHand += resupplyUnitsReceived.prbc / _bloodShare;   // the delivery is shared by the brigades like the stock
        } else {
          const currentBloodPRBC = medicalSystemState.blood_dos ?? bloodObjective;
          medicalSystemState.blood_dos = currentBloodPRBC + resupplyUnitsReceived.prbc;
        }
        resupplyReceived = true;
        
      }

      // Operations tempo of the surgical teams (surge / crisis from the load); replaces the fatigue division below when present.
      const _surgTempo = resourceResult.surgical?.tempo || null;
      if (_surgTempo) surgicalTempoState = resourceResult.surgical.tempo_state;

      // CC-013: Apply fatigue effect to surgical backlog if module doesn't handle it
      // This increases backlog when teams are fatigued (can't process as many cases)
      const fatigueAdjustedBacklog = resourceResult.surgical.end_of_day_backlog || 0;
      const effectiveBacklog = Math.round(fatigueAdjustedBacklog / (_surgTempo ? 1 : fatigueEffect));   // the tempo already carries the fatigue
      
      // Update surgical backlog with fatigue-adjusted value
      surgicalBacklog = effectiveBacklog;

      // Apply DOW rate to get actual DOW
      const dowFromRate = Math.round(battleResult.battle_casualties.wia * dowResult.dow_rate);
      // Note: adjustedWIA calculation moved to finalWIA below after CASEVAC analysis

      // CC-004: Calculate cumulative Golden Hour (POI ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã‚Â ÃƒÂ¢Ã¢â€šÂ¬Ã¢â€žÂ¢ Surgery)
      // EA-003: Check helicopter availability for evacuation method options
      const hasHeloAvailable = medevacAnalysis?.available_helicopters > 0 ||
                      medicalConfig?.evacuation_assets?.air?.iar330_puma?.count > 0 ||
                      medicalConfig?.evacuation_assets?.air?.uh60_blackhawk?.count > 0;
      
      // CALC-001/002: cumulativeGHAnalysis is computed before the DOW call above. Its dow_modifier is now
      // the forward field hospital effect only; time to surgery acts through the T1 survival curve.
      const cumulativeDOWModifier = cumulativeGHAnalysis.dow_modifier;

      // ENH-024: DCS blood supplement — when forward surgical capability exists,
      // T1 patients who reach the OR undergo Damage Control Surgery (Phase 1).
      // DCS Phase 1 consumes significantly more blood than the mechanism-based
      // baseline (which reflects pre-surgical / non-DCS care patterns).
      // Supplement = (DCS Phase 1 per-patient) − (mechanism baseline per T1).
      // Applied AFTER WBB and MTP so it represents the true additive demand that
      // neither WBB supply nor MTP ratio management eliminates.
      // Placed here — after cumulativeGHAnalysis (has_forward_surgery, surgical_destination)
      // and after triageDistribution — all three dependencies now satisfied.
      const _hasDCS = cumulativeGHAnalysis?.has_forward_surgery === true;
      const _dcsFacilityType = cumulativeGHAnalysis?.surgical_destination || null;
      const _dcsSurgicalPatients =
        triageDistribution?.t1_immediate ||
        triageDistribution?.t1 ||
        0;
      let _dcsBloodSupplementPRBC = 0;
      let _dcsBloodSupplementFFP  = 0;

      if (_hasDCS && _dcsSurgicalPatients > 0) {
        const _dcsBloodReq = calculateDCSBloodRequirements('PHASE_1', false);
        // mechanism baseline average (prbc & ffp per T1 patient already included in resourceResult)
        const _baselinePRBC = bloodByMechanism?.average_per_casualty?.prbc || 0;
        const _baselineFFP  = bloodByMechanism?.average_per_casualty?.ffp  || 0;
        const _extraPRBC = Math.max(0, (_dcsBloodReq?.prbc || 0) - _baselinePRBC);
        const _extraFFP  = Math.max(0, (_dcsBloodReq?.ffp  || 0) - _baselineFFP);
        _dcsBloodSupplementPRBC = CASUALTY_FRACTION_CARRY_ON ? _dcsSurgicalPatients * _extraPRBC : Math.round(_dcsSurgicalPatients * _extraPRBC);
        _dcsBloodSupplementFFP  = CASUALTY_FRACTION_CARRY_ON ? _dcsSurgicalPatients * _extraFFP : Math.round(_dcsSurgicalPatients * _extraFFP);

        // Add to live totals so downstream blood_dos deduction and Tab4 badge see DCS demand
        if (resourceResult.blood?.requirements?.totals) {
          resourceResult.blood.requirements.totals.prbc =
            (resourceResult.blood.requirements.totals.prbc || 0) + _dcsBloodSupplementPRBC;
          resourceResult.blood.requirements.totals.ffp =
            (resourceResult.blood.requirements.totals.ffp || 0) + _dcsBloodSupplementFFP;
        }
      }

      // V3.1-BLOOD-STREAM2: Add civilian T1 surgical blood demand to daily consumption.
      // Civilian T1 patients reaching R2B/R2E surgical capability consume blood at the
      // same rate as military T1 (same surgical protocol, same physiology).
      // _civT1DailyBC is read at method top from scenario (cannot use outer-scope _mroeLoad).
      // Applied AFTER WBB, MTP, and DCS adjustments — additive to all military demands.
      // R2-08: only the military share of the civilian surgical cases, and this brigade's part of the force load
      const _civT1BloodCases = _civT1Military / _civBrigadesBC;
      if (_civT1BloodCases > 0 && resourceResult.blood?.requirements?.totals) {
        const _civT1PrcbPerPt = bloodByMechanism?.average_per_casualty?.prbc || 6;
        const _civT1FfpPerPt  = bloodByMechanism?.average_per_casualty?.ffp  || 5;
        resourceResult.blood.requirements.totals.prbc =
          (resourceResult.blood.requirements.totals.prbc || 0) +
          (CASUALTY_FRACTION_CARRY_ON ? _civT1BloodCases * _civT1PrcbPerPt : Math.round(_civT1BloodCases * _civT1PrcbPerPt));
        resourceResult.blood.requirements.totals.ffp =
          (resourceResult.blood.requirements.totals.ffp || 0) +
          (CASUALTY_FRACTION_CARRY_ON ? _civT1BloodCases * _civT1FfpPerPt : Math.round(_civT1BloodCases * _civT1FfpPerPt));
      }

      // Blood on hand: today's PRBC need (after the walking blood bank, MTP, DCS and civilian supplements; +15 percent without a
      // Role 3 as in the former deduction) comes off the stock; the days of supply left at today's rate give the alert of the day.
      let _bloodShortfallToday = 0;
      let _bloodDaysLeft = null;
      if (_bloodActive) {
        const _reqPrbc = resourceResult.blood?.requirements?.totals?.prbc || 0;
        const _need = medicalConfig?.r3_accessible === false ? _reqPrbc * 1.15 : _reqPrbc;
        _bloodShortfallToday = Math.max(0, _need - bloodOnHand);
        bloodOnHand = Math.max(0, bloodOnHand - _need);
        _bloodDaysLeft = _need > 0 ? bloodOnHand / _need : null;
        bloodAlertPrev = _bloodDaysLeft === null ? 'GREEN' : getBloodAlertLevel(_bloodDaysLeft).level;
      }

      const ghAdjustedDOW = Math.round(dowFromRate * cumulativeDOWModifier);

      // ENH-023: PFC modifier — partially offsets the time penalty when the force
      // has PFC-capable medics. Auto-derived from medical_capability (never a user
      // input — PCC is a contingency, not a primary planning capability per JTS CPG-91).
      // Only activates when golden hour is not achieved (modifier = 1.0 otherwise).
      const pfcResult = getPFCModifier(
        cumulativeGHAnalysis.golden_hour_compliant,
        forceQuality?.medical_capability || 'GOOD',
        derivePfcLevelCached(medicalConfig, pfcLevelOf(forceQuality?.medical_capability || 'GOOD')).level   // R1-07: Tab 3 facilities when PFC_FROM_TAB3_ON
      );
      // derived wait: prolonged field care already acted on the wait of the wound model, so the flat multiplier is not applied on top
      // the derived wait replaces these multipliers only when it enters the result (ON); in SHADOW the flat rate keeps them
      const _evacWaitApplied = !!_evacWait && _woundHazardMode === WOUND_HAZARD_MODES.ON;
      // HOUSEKEEPING (Dr. Tatar 2026-10-25): REMOVE this flat PFC multiplier with the older (non-ON) wound modes; docs/DEAD_CODE_REGISTER.md
      const _pfcFlatModifier = _evacWaitApplied ? 1 : pfcResult.modifier;
      const pfcAdjustedDOW = Math.round(ghAdjustedDOW * _pfcFlatModifier);

      // PHASE 2.1: Apply route impact DOW modifier
      // Route conditions (DAMAGED, CONTESTED) increase DOW risk
      const routeDOWModifier = routeImpact?.dow_modifier || 1.0;
      const _overlapSkip = HAZARD_WINDOW_OVERLAP_SKIP_ON && _evacWaitApplied;   // R2-31: drone and route delay already in the derived wait
      const _routeModEff = _overlapSkip ? 1 : routeDOWModifier;
      // the level's default multiplier is left out; a multiplier the planner typed in Tab 2 (source PLANNER) is a deliberate input and still applies
      const _droneModEff = (_overlapSkip && _droneDOWSource !== 'PLANNER') ? 1 : _droneDOWMod;
      const routeAdjustedDOW = Math.round(pfcAdjustedDOW * _routeModEff);
      // Tired surgeons: died of wounds +2 percent for every 10 percent of lost performance (SURGICAL_FATIGUE_MODEL.FATIGUE_IMPACT)
      const tempoAdjustedDOW = _surgTempo ? Math.round(routeAdjustedDOW * _surgTempo.fatigue_mortality_factor) : routeAdjustedDOW;

      // ENH-025 Cat-25: Drone DOW modifier — evacuation delays caused by drone saturation.
      // SATURATED = 1.3× (Ukraine 2024: median evacuation time 85-120 min; golden hour 38%).
      // Modifier 1.0 (NONE/RECON) = no-op. Positioned after route modifier, before TCCC —
      // drone threat is an external environment factor, not a medical protocol improvement.
      // 2026-10-13 (Tab 2 plan 7.5): IED threat (blast injuries) and CBRN risk (NO DATA, guess) raise the died-of-wounds rate; the larger of the
      // two applies (not both). The drone DOW modifier above stays separate.
      const _threatDOWMod = _threatModel ? _threatModel.dow.value : 1.0;
      const droneAdjustedDOW = Math.round(tempoAdjustedDOW * _droneModEff * _threatDOWMod);

      // ENH-048c: Monte Carlo golden_hour_success modifier applied as inverse to DOW rate.
      // golden_hour_success > 1.0 = better GH compliance (faster evacuation) = lower DOW rate.
      // Applied as (1 / golden_hour_success): 1.3 success → ×0.77 DOW rate (−23%); 0.7 → ×1.43 (+43%).
      // Positioned with FRT and drone — pre-TCCC, Role 1 chain. No-op at 1.0.
      const _mcGoldenHourMod = scenario?.operation_parameters?.monte_carlo_modifiers?.golden_hour_success ?? 1.0;
      const _mcGoldenHourDOWMod = _mcGoldenHourMod > 0 ? (1.0 / _mcGoldenHourMod) : 1.0;

      // ========================================================================
      // TCCC-2025: Apply TCCC 2025 guideline improvements
      // Reduces DOW through improved TXA protocols, hemorrhage control, PCC
      // v5.9.0 FRT-DOW: Apply Forward Resuscitation Team modifier to baseDOWRate
      // BEFORE TCCC — FRT acts at Role 1 (upstream of TCCC-captured protocols).
      // _frtDOWModifier = 0.93 when ≥1 FRT deployed, 1.0 when none.
      // ENH-025: _droneDOWMod added to baseDOWRate chain after routeDOWModifier.
      // ENH-048c: _mcGoldenHourDOWMod = 1/golden_hour_success applied last in pre-TCCC chain.
      // ========================================================================
      // Wound hazard model ON: a forward resuscitation team always helps, but with blood short a clinician's options
      // change the outcome only slightly, so only a share of its benefit is realised (Dr. Tatar, 2026-10-10).
      const _hazardOnCalc = _woundHazardInput?.mode === 'ON';
      const _bloodShortCalc = dowResult.analysis?.modifiers_applied?.blood_shortage_modifier > 1;
      const _frtEff = (_hazardOnCalc && _bloodShortCalc)
        ? 1 - (1 - frtDOWModifier) * FRT_BENEFIT_WITHOUT_BLOOD.share
        : frtDOWModifier;
      const _tccc2025Computed = applyTCCC2025Modifiers({
        baseDOWRate: dowResult.dow_rate * cumulativeDOWModifier * _pfcFlatModifier * _routeModEff * _droneModEff * _threatDOWMod * _frtEff * _mcGoldenHourDOWMod * r3AbsentDOWMod,
        evacuationTimeHours: cumulativeGHAnalysis.cumulative_time_to_surgery_min / 60,
        txaAdministered: true,  // Assume TXA given per simplified 2g protocol
        hasTBI: false,          // Could be enhanced to check injury type distribution
        hemorrhageType: 'extremity'  // Most common type
      });
      // Wound hazard model ON: tourniquet, TXA and control time are already in the curve (care flags and control
      // time), so the TCCC 2025 factors are reported but not applied again (docs/planning/HAZARD_OVERLAP_CHECK.md).
      const tccc2025Result = _hazardOnCalc
        ? {
            ..._tccc2025Computed,
            modified_dow_rate: _tccc2025Computed.original_dow_rate,
            tccc2025_applied: false,
            reference_modified_dow_rate: _tccc2025Computed.modified_dow_rate
          }
        : _tccc2025Computed;
      
      // Apply TCCC-2025 survival improvements
      // v5.9.0: frtDOWModifier already baked into baseDOWRate above → propagates through TCCC
      // ENH-025: non-TCCC path uses droneAdjustedDOW (was routeAdjustedDOW) so both paths include drone.
      const tccc2025AdjustedDOW = TCCC_2025_CONFIG.enabled
        ? _rc(battleResult.battle_casualties.wia * tccc2025Result.modified_dow_rate, 'dow')
        : Math.round(droneAdjustedDOW * _frtEff * _mcGoldenHourDOWMod); // FRT + ENH-048c GH modifier, non-TCCC path

      // ENH-025 Cat-25: MEDEVAC attrition — drone warfare degrades fleet over time.
      // Weekly loss rate compounds: CONTESTED = 5%/week, SATURATED = 15%/week.
      // Floor at 0.30 (fleet never falls below 30% — residual armored / night capacity).
      // Stateless: computed from day number only, consistent across all brigades.
      const _droneAttritionFactor = droneAttritionOf(_droneWeeklyLoss, day);

      // CC-007 + EA-003: Calculate CASEVAC requirements and adjusted DOW
      // EA-003: Pass terrain/threat options for per-facility asset calculations
      // ENH-025: Multiply by _droneAttritionFactor — fleet degradation reduces daily capacity.
      const dailyMEDEVACCapacity = calculateDailyMEDEVACCapacity(medevacAnalysis, medicalConfig, {
        terrain: _scenarioEnv.terrain,   // CALC-011: planner's terrain (Tab 2); was a ghost read defaulting to ROLLING_HILLS
        threat: scenario?.operation_parameters?.threat_level || 'LOW',
        weather: _dayEnvironment.weather,   // R2-18: the operation weather key of the day (was the ghost scenario.environment.weather: always clear)
        routesConfig: getEvacuationRoutesConfiguration(scenario),   // Tab 3 B.3: leg distances and surfaces
        environment: _dayEnv || resolveEvacuationParadigm(scenario).paradigm,   // sorties per aircraft per day by environment (R2-28: the day's derived environment)
        nationalStandard: resolveForceNation(scenario)               // default helicopter type
      }) * _droneAttritionFactor;
      const casevacAvailability = medicalConfig?.casevac_config?.availability || 
                                   CASEVAC_CONFIG.default_availability;
      
      // CC-008: Get phase modifiers for current operational phase
      const currentPhase = scenario?.operation_parameters?.current_phase ||
                          brigade?.operation?.current_phase ||
                          scenario?.phases?.[Math.min(day - 1, (scenario?.phases?.length || 1) - 1)]?.type ||
                          'DEFENSE';
      const phaseModifiers = getPhaseCASEVACModifiers(currentPhase, medicalConfig?.casevac_config);
      
      // Base CASEVAC analysis
      let casevacAnalysis = analyzeCASEVACRequirements({
        dailyCasualties: battleResult.battle_casualties.wia,
        medevacCapacity: dailyMEDEVACCapacity,
        casevacAvailability,
        medicalConfig,
        exact: CASUALTY_FRACTION_CARRY_ON
      });
      
      // CC-008: Apply phase modifiers to CASEVAC analysis
      casevacAnalysis = applyPhaseToCASEVAC(casevacAnalysis, phaseModifiers, CASUALTY_FRACTION_CARRY_ON);
      
      // CC-009: Determine AUTO evacuation method for R1ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã‚Â ÃƒÂ¢Ã¢â€šÂ¬Ã¢â€žÂ¢R2B link
      const r2bCapacityPercent = medicalConfig?.role2b_capacity_percent || 50;
      const r1ToR2bDistance = medicalConfig?.mtf_distances?.role2_basic_km || 20;
      const autoMethodAnalysis = determineAutoMethod({
        receivingCapacityPercent: r2bCapacityPercent,
        distanceKm: r1ToR2bDistance,
        casualtyLoad: battleResult.battle_casualties.wia,
        fromType: 'ROLE1',
        toType: 'ROLE2B',
        config: medicalConfig?.evac_method_config
      });
      
      // CC-009: Calculate effective capacity based on determined method
      const methodCapacity = calculateMethodAdjustedCapacity({
        method: autoMethodAnalysis.method,
        sendingAssets: orbatSendingAssets,  // OBS-S96-006: ORBAT-derived R1 fleet (passed from calculate())
        receivingAssets: medevacAnalysis?.ground_ambulances || 8,
        patientsPerTripPerAsset: 2,
        tripsPerDay: 4,
        distanceKm: r1ToR2bDistance  // BUG-005 FIX: Pass distance for dynamic asset split
      });
      
      // CC-007: Calculate final DOW with CASEVAC penalties
      let finalDOW, finalWIA, casevacDOWBreakdown;
      
      // BUG-057 FIX: Store original WIA for fallback calculations
      const originalWIA = battleResult.battle_casualties.wia || 0;
      
      if (casevacAnalysis.is_overwhelmed) {
        // ARCH-010 FIX: calculateOverflowDOW replaces calculateCASEVACAdjustedDOW.
        // All overflow (casevac_evacuated + unevacuated) treated as unevacuated —
        // CASEVAC is unplanned/opportunistic and not a planning factor (AJMedP-2).
        casevacDOWBreakdown = calculateOverflowDOW({
          baseWIA: originalWIA,
          baseDOWRate: dowResult.dow_rate * cumulativeDOWModifier * _droneModEff * _threatDOWMod * r3AbsentDOWMod, // Include GH + drone modifiers
          casevacAnalysis,
          exact: CASUALTY_FRACTION_CARRY_ON
        });
        // the day's total is rounded once, with the carry (same series as the normal path)
        finalDOW = CASUALTY_FRACTION_CARRY_ON ? _rc(casevacDOWBreakdown.total_dow, 'dow') : casevacDOWBreakdown.total_dow;
        finalWIA = originalWIA - finalDOW;
      } else {
        // All evacuated by MEDEVAC - use TCCC-2025 adjusted DOW
        finalDOW = tccc2025AdjustedDOW;
        finalWIA = originalWIA - tccc2025AdjustedDOW;
        casevacDOWBreakdown = {
          total_dow: tccc2025AdjustedDOW,
          medevac_dow: tccc2025AdjustedDOW,
          casevac_dow: 0,
          unevacuated_dow: 0,
          effective_dow_rate: tccc2025Result.modified_dow_rate,
          preventable_deaths: 0,
          tccc2025: tccc2025Result  // Include TCCC-2025 analysis
        };
      }
      
      // BUG-057 FIX: Guard against NaN in DOW/WIA calculations
      if (isNaN(finalDOW) || finalDOW === undefined || finalDOW === null) {
        // Fallback: 12% DOW rate (NATO standard)
        finalDOW = Math.round(originalWIA * 0.12);
      }
      
      // DOW cannot exceed 85% of WIA (some must survive evacuation)
      const maxDOW = Math.floor(originalWIA * 0.85);
      if (finalDOW > maxDOW) {
        finalDOW = maxDOW;
      }
      
      // Recalculate finalWIA after DOW guards
      finalWIA = originalWIA - finalDOW;
      
      // Final guard: WIA must be non-negative
      if (isNaN(finalWIA) || finalWIA < 0) {
        finalWIA = Math.max(0, originalWIA - finalDOW);
      }

      // Wound hazard model ON: deaths between injury and arrival come out of the wounded and into KIA
      // (Dr. Tatar, 2026-10-10). SHADOW only reports them. The hazard rates are shares of the original WIA,
      // so DOW (arrival to surgery) and these deaths never overlap.
      const _hazardReport = dowResult.analysis?.modifiers_applied?.wound_hazard || null;
      const _preDeaths = _hazardReport
        ? Math.min(_rc(originalWIA * _hazardReport.prehospital_rate / 100, 'prehospital'), Math.max(0, finalWIA))
        : 0;
      const _preApplied = _hazardReport?.applied === true;

      const _wxToday = _dayEnvironment.weather;   // copy for the closure of tactical_evac_analysis below
      const _fleetAttrToday = DRONE_FLEET_ATTRITION_ON ? droneAttritionOf(_droneWeeklyLoss, day) : 1;   // same copy: drone losses of the ambulance fleet

      // Day summary
      const daySummary = {
        day,
        is_combat_effective: battleResult.is_combat_effective,
        is_march_operation: isMarch,  // PHASE 4: Track if this day is during march
        
        battle: {
          total: battleResult.battle_casualties.total,
          kia: battleResult.battle_casualties.kia + (_preApplied ? _preDeaths : 0),
          dow: finalDOW,  // CC-007: Now includes CASEVAC-adjusted DOW
          wia: finalWIA - (_preApplied ? _preDeaths : 0),  // CC-007: Adjusted for CASEVAC DOW (and hazard prehospital deaths when ON)
          // P1-03 FIX: RTD rates now read from RTD_RATES.BY_TRIAGE_CATEGORY via theatreHoldingPolicy.js,
          // keyed by the configured THP timeframe (within_72h / within_7d / within_15d / within_30d).
          // Previous code used hardcoded 0.05/0.15/0.60/0.02 which ignored the THP configuration.
          rtd: _rc(
            (triageDistribution?.T1 || triageDistribution?.t1_immediate || triageDistribution?.t1 || 0) * wiaRtdT1 +
            (triageDistribution?.T2 || triageDistribution?.t2_delayed || triageDistribution?.t2 || 0) * wiaRtdT2 +
            (triageDistribution?.T3 || triageDistribution?.t3_minimal || triageDistribution?.t3 || 0) * wiaRtdT3 +
            (triageDistribution?.T4 || triageDistribution?.t4_expectant || triageDistribution?.t4 || 0) * wiaRtdT4,
            'battle_rtd'
          )
        },
        
        dnbi: {
          total: dnbiResult.total_dnbi,
          // P1-02 FIX: Use dnbiResult.rtd.expected_rtd which is computed correctly by dnbiModule
          // from DNBI_CATEGORY_BREAKDOWN evacuation_rate constants per category.
          // Old code: Math.round(dnbiResult.total_dnbi * 0.85) — hardcoded 85% ignores
          // category-specific rates (MUSCULOSKELETAL 85%, BEHAVIORAL_HEALTH 70%, etc.).
          rtd: dnbiResult.rtd.expected_rtd,
          csr: dnbiResult.csr_analysis.total_csr_casualties,
          csr_rtd: dnbiResult.csr_analysis.rtd_expectations.expected_rtd_count,   // combat stress cases back to duty within 72 h
          other: dnbiResult.total_dnbi - dnbiResult.csr_analysis.total_csr_casualties,
          // BUG-5H-001 FIX: hns_absorbed was computed but never stored in daySummary.
          // Tab5 HNS panel aggregates d.dnbi?.hns_absorbed — without this field it was always 0.
          hns_absorbed: dnbiResult.hns_absorbed || 0
        },
        
        // CC-012: Rear area with FEBA distance decay
        rear_area: adjustedRearArea.adjusted,
        rear_area_analysis: {
          original: adjustedRearArea.original,
          adjusted: adjustedRearArea.adjusted,
          reduction: adjustedRearArea.reduction,
          feba_distance_km: brigadeFebaDistance,
          threat_band: rearAreaDecayAnalysis.distance_band,
          modifier: rearAreaDecayAnalysis.final_modifier,
          // Reference only (never applied): the earlier single-distance step decay of the whole brigade (REAR_AREA_DECAY_CONFIG).
          // Each rear battalion now has its own distance curve (rearDistanceFactor).
          step_decay_reference: applyRearAreaDecay(adjustedRearArea.original, rearAreaDecayAnalysis).adjusted
        },
        
        ...(_enemyDay ? { enemy_forces: _enemyDay } : {}),
        triage: triageDistribution,
        
        golden_hour: {
          // Legacy single-leg tracking (for backward compatibility)
          achieved: dowResult.analysis.evacuation.golden_hour_achieved,
          evac_time_minutes: dowResult.analysis.evacuation.time_minutes,
          method: dowResult.analysis.evacuation.method,
          // CC-004: Cumulative time tracking (POI ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã‚Â ÃƒÂ¢Ã¢â€šÂ¬Ã¢â€žÂ¢ Surgery)
          cumulative_time_to_surgery_min: cumulativeGHAnalysis.cumulative_time_to_surgery_min,
          evacuation_profile: cumulativeGHAnalysis.evacuation_profile || null,
          ukraine_time_band_dow_modifier: cumulativeGHAnalysis.ukraine_time_band_modifier,
          cumulative_golden_hour_compliant: cumulativeGHAnalysis.golden_hour_compliant,
          surgical_destination: cumulativeGHAnalysis.surgical_destination,
          has_forward_surgery: cumulativeGHAnalysis.has_forward_surgery,
          time_category: cumulativeGHAnalysis.time_category,
          estimated_achievement_percent: cumulativeGHAnalysis.estimated_achievement_percent,
          dow_modifier_applied: cumulativeDOWModifier,
          // EA-003: Helicopter availability for evacuation
          helicopter_available: hasHeloAvailable,
          // FH-001: Forward Field Hospital tracking
          forward_fh: cumulativeGHAnalysis.forward_fh || { applied: false }
        },

        // ENH-023: PFC (Prolonged Field Care / Prolonged Casualty Care) tracking
        // Activated when golden hour not achievable. Modifier auto-derived from
        // medical_capability — never a user input per JTS PCC CPG-91 doctrine.
        pfc: {
          triggered: pfcResult.triggered,
          pfc_level: pfcResult.pfc_level,
          label: pfcResult.label,
          modifier: _pfcFlatModifier,
          dow_before_pfc: ghAdjustedDOW,
          dow_after_pfc: pfcAdjustedDOW,
          lives_saved_by_pfc: pfcResult.triggered
            ? Math.max(0, ghAdjustedDOW - pfcAdjustedDOW)
            : 0,
          warning: pfcResult.warning
        },

        // ENH-024: Damage Control Surgery (Cat-24 Research Inventory Audit)
        // Applicable when forward surgical capability exists (Role 2E or Role 3).
        // DOW benefit already captured via time-to-surgery pathway — this block
        // records the blood consumption impact for logistics planning transparency.
        dcs: {
          applicable: _hasDCS,
          facility_type: _dcsFacilityType,
          surgical_patients: _hasDCS ? _dcsSurgicalPatients : 0,
          phase: 'PHASE_1',  // Role 2E = Phase 1 only; Role 3 acute demand = Phase 1 per day
          blood_supplement_prbc: _dcsBloodSupplementPRBC,
          blood_supplement_ffp:  _dcsBloodSupplementFFP,
          // Survival rates from DCS_PHASES (read-only reference — DOW NOT re-applied here)
          survival_rate_with_dcs:
            requireNumber(DCS_PHASES?.PHASE_1_ABBREVIATED_SURGERY?.outcomes?.survival_to_icu_if_phase1_done, 'casualtyCalculator.js:11042', 'DCS_PHASES.PHASE_1_ABBREVIATED_SURGERY.outcomes.survival_to_icu_if_phase1_done'),
          survival_rate_without_dcs:
            requireNumber(DCS_PHASES?.PHASE_1_ABBREVIATED_SURGERY?.outcomes?.survival_if_attempted_definitive, 'casualtyCalculator.js:11044', 'DCS_PHASES.PHASE_1_ABBREVIATED_SURGERY.outcomes.survival_if_attempted_definitive'),
          note: _hasDCS
            ? null
            : 'No forward surgical capability — DCS not applicable for this brigade today'
        },

        // ENH-025: Drone Warfare Environment (Cat-25 Research Inventory Audit)
        // Three axes: casualty modifier applied to raw KIA/WIA, DOW modifier in
        // evacuation chain, MEDEVAC attrition reducing fleet capacity week-by-week.
        drone: {
          threat_level:        _droneThreatLevel,
          label:               _droneThreatData.label              ?? _droneThreatLevel,
          casualty_modifier:   _droneCasualtyMod,
          dow_modifier:        _droneDOWMod,
          dow_modifier_source: _droneDOWSource,
          dow_modifier_level_default: DRONE_THREAT[_droneThreatLevel]?.dow_modifier ?? null,   // the level's own value, next to the planner's
          dow_before_drone:    routeAdjustedDOW,
          dow_after_drone:     droneAdjustedDOW,
          additional_dow_from_drone: Math.max(0, droneAdjustedDOW - routeAdjustedDOW),
          medevac_weekly_loss: _droneWeeklyLoss,
          medevac_attrition_factor: _droneAttritionFactor,
          medevac_capacity_before_attrition: _droneAttritionFactor > 0 && _droneAttritionFactor < 1
            ? Math.round(dailyMEDEVACCapacity / _droneAttritionFactor)
            : dailyMEDEVACCapacity,
          medevac_capacity_after_attrition: dailyMEDEVACCapacity
        },

        // ENH-016: Friendly Air Situation (theater-level air environment modifier)
        // Applied to battle casualties after drone modifier in the chain.
        // MEDEVAC AD override is applied in medevacModule (see calculateComprehensiveMEDEVAC).
        air_situation: {
          level:             _airSituationLevel,
          label:             AIR_SITUATION_LEVELS[_airSituationLevel]?.label ?? _airSituationLevel,
          casualty_modifier: _airSituationMod,
          medevac_ad_override: AIR_SITUATION_LEVELS[_airSituationLevel]?.medevac_ad_threat_override ?? null,
          modifier_applied:  _airSituationMod !== 1.0
        },
        
        // TCCC-2025: Tactical Combat Casualty Care 2025 guideline improvements
        tccc2025: {
          enabled: TCCC_2025_CONFIG.enabled,
          version: TCCC_2025_CONFIG.version || null,
          improvement_applied: tccc2025Result.tccc2025_applied,
          original_dow_rate: tccc2025Result.original_dow_rate,
          modified_dow_rate: tccc2025Result.modified_dow_rate,
          improvement_percent: tccc2025Result.improvement_percent || 0,
          modifiers_applied: tccc2025Result.modifiers || {},
          lives_saved_estimate: TCCC_2025_CONFIG.enabled 
            ? Math.max(0, ghAdjustedDOW - tccc2025AdjustedDOW) 
            : 0
        },

        // v5.9.0 FRT-DOW: Forward Resuscitation Team modifier tracking
        // frtDOWModifier received as parameter; reduction_percent back-calculated from modifier.
        frt: {
          modifier_applied: _frtEff,
          reduction_percent: _frtEff < 1.0 ? Math.round((1 - _frtEff) * 100) : 0
        },
        
        // CC-007: CASEVAC analysis
        // ARCH-010: All fields below are INFORMATIONAL ONLY per AJMedP-2.
        // CASEVAC is unplanned/opportunistic and does not influence DOW calculation.
        // DOW is computed by calculateOverflowDOW() which treats all overflow as unevacuated.
        casevac: {
          arch010_informational_only: true,  // ARCH-010: UI display only — not a DOW factor
          medevac_capacity: casevacAnalysis.medevac_capacity,
          capacity_utilization_percent: casevacAnalysis.capacity_utilization_percent,
          is_overwhelmed: casevacAnalysis.is_overwhelmed,
          medevac_evacuated: casevacAnalysis.medevac_evacuated,
          casevac_evacuated: casevacAnalysis.casevac_evacuated,
          unevacuated: casevacAnalysis.unevacuated,
          casevac_availability: casevacAnalysis.casevac_availability,
          dow_breakdown: {
            medevac_dow: casevacDOWBreakdown.medevac_dow,
            casevac_dow: casevacDOWBreakdown.casevac_dow,
            unevacuated_dow: casevacDOWBreakdown.unevacuated_dow,
            preventable_deaths: casevacDOWBreakdown.preventable_deaths
          },
          // CC-008: Fight phase modifiers
          phase: {
            current: currentPhase,
            description: phaseModifiers.description,
            medevac_modifier: phaseModifiers.medevac_capacity_modifier,
            casevac_modifier: phaseModifiers.casevac_availability_modifier,
            original_capacity: casevacAnalysis.original_medevac_capacity || casevacAnalysis.medevac_capacity,
            adjusted_capacity: casevacAnalysis.adjusted_medevac_capacity || casevacAnalysis.medevac_capacity
          },
          warning: casevacAnalysis.warning
        },
        
        // CC-009: Evacuation method analysis
        evac_method: {
          r1_to_r2b: {
            method: autoMethodAnalysis.method,
            reason: autoMethodAnalysis.reason,
            confidence: autoMethodAnalysis.confidence,
            warning: autoMethodAnalysis.warning
          },
          capacity: {
            collection_capacity: methodCapacity.collection_capacity,
            forward_transport_capacity: methodCapacity.forward_transport_capacity,
            total_daily_capacity: methodCapacity.total_daily_capacity,
            assets_collecting: methodCapacity.assets_collecting,
            assets_pushing: methodCapacity.assets_pushing
          }
        },
        
        resources: {
          blood_prbc: Math.round(resourceResult.blood.requirements.totals.prbc * 100) / 100,   // counts are not whole numbers with the fraction carry: reported to 2 decimals
          blood_on_hand: _bloodActive ? Math.round(bloodOnHand * 10) / 10 : null,
          blood_shortfall: _bloodActive ? Math.round(_bloodShortfallToday * 10) / 10 : null,
          blood_days_left: _bloodActive && _bloodDaysLeft !== null ? Math.round(_bloodDaysLeft * 10) / 10 : null,
          blood_alert: _bloodActive ? bloodAlertPrev : null,
          blood_ffp: Math.round(resourceResult.blood.requirements.totals.ffp * 100) / 100,
          // PHASE 1.1: WBB tracking
          wbb_status: resourceResult.blood.requirements.totals.wbb_status || 'STANDBY',
          wbb_contribution: resourceResult.blood.requirements.totals.wbb_contribution || 0,
          wbb_universal_units: resourceResult.blood.requirements.totals.wbb_universal_units || 0,
          wbb_activation_reason: resourceResult.blood.requirements.totals.wbb_activation_reason || null,
          surgical_hours: Math.round(resourceResult.surgical.requirements.demand.total_hours * 100) / 100,
          surgical_backlog: surgicalBacklog,
          // CC-013: Track fatigue effect on medical capacity
          fatigue_effect: Math.round((_surgTempo ? _surgTempo.performance : fatigueEffect) * 100),  // As percentage (100% = no fatigue)
          evac_wait: _evacWait ? {
            p_open: Math.round(_evacWait.window.p_open * 1000) / 1000,
            mean_wait_hours: Math.round(_evacWait.window.mean_wait_hours * 100) / 100,
            median_wait_hours: Math.round(_evacWait.window.median_wait_hours * 100) / 100,
            p90_wait_hours: Math.round(_evacWait.window.p90_wait_hours * 100) / 100,
            share_beyond_6h: Math.round(_evacWait.window.share_beyond_6h * 1000) / 1000,
            pfc_level: _evacWait.pfc_level,
            pfc_source: _evacWait.pfc_source,           // TAB3_FACILITIES, TAB1_FALLBACK (no Role 1 facility passed) or TAB1_SETTING (switch off)
            pfc_role1_count: _evacWait.pfc_role1_count,
            medic_share: Math.round(_evacWait.medic_share * 1000) / 1000,   // R1-08: share of the medical personnel alive; the prolonged field care benefit is scaled by it
            mean_effective_wait_hours: Math.round(_evacWait.points_eff.reduce((a, b) => a + b, 0) / _evacWait.points_eff.length * 100) / 100
          } : null,
          evac_environment: { label: _dayEnv || resolveEvacuationParadigm(scenario).paradigm, source: _dayEnv ? 'DERIVED_FROM_WINDOW' : 'TYPED' },
          evac_queue: _evacQueue ? {
            capacity_day: Math.round(_evacQueue.capacity_day * 10) / 10,
            demand_day: Math.round(_evacQueue.demand_day * 10) / 10,
            backlog_in: Math.round((_evacQueue.backlog_in.urgent + _evacQueue.backlog_in.other) * 10) / 10,
            backlog_out: Math.round((_evacQueue.backlog_out.urgent + _evacQueue.backlog_out.other) * 10) / 10,
            urgent_wait_hours: Math.round(_evacQueue.urgent_wait_hours * 100) / 100,
            other_wait_hours: Math.round(_evacQueue.other_wait_hours * 100) / 100,
            overflow: Math.round(_evacQueue.overflow * 10) / 10,
            ground_capacity: Math.round(_evacQueue.ground_capacity * 10) / 10,
            fleet_attrition: Math.round(_evacQueue.fleet_attrition * 1000) / 1000,
            air_capacity: Math.round(_evacQueue.air_capacity * 10) / 10,
            air_share: Math.round(_evacQueue.air_share * 1000) / 1000,
            idle_capacity: Math.round(Math.max(0, _evacQueue.capacity_day - _evacQueue.demand_day) * 10) / 10,
            by_brigade: _evacQueue.by_brigade
          } : null,
          dynamic_t4: DYNAMIC_T4_ON ? {
            share_of_t1: Math.round(_dynT4Hold.share * 1000) / 1000,
            for_blood: Math.round(_dynT4Hold.blood * 1000) / 1000,
            for_surgeon: Math.round(_dynT4Hold.surgeon * 1000) / 1000,
            t1_held: Math.round(_dynT4Hold.share * (triageDistribution.T1 || triageDistribution.t1_immediate || triageDistribution.t1 || 0) * 10) / 10
          } : null,
          surgical_tempo: _surgTempo,
          surgical_regulating: resourceResult.surgical?.regulating || null,
          bed_census_medroe: _eligBC ? {
            admissions_per_day_brigade: Math.round(_medroeAdmissions * 10) / 10,
            enhanced_beds_slice: Math.round(_enhBeds * 10) / 10,
            enhanced_census: Math.round(medroeCensusEnh * 10) / 10,
            overflow_to_r2b: Math.round(_medroeOverflow * 10) / 10
          } : null,
          civilian_load: _eligBC ? {
            civilian_surgical_cases_force: Math.round(_civT1DailyBC * 10) / 10,
            host_status: _hostStatusBC,
            military_share: Math.round(_eligBC.civilian.military * 1000) / 1000,
            host_share: Math.round(_eligBC.civilian.host * 1000) / 1000,
            unmet_share: Math.round(_eligBC.civilian.unmet * 1000) / 1000,
            military_cases: Math.round((_eligBC.by_category.CIVCAS_OWN.military_cases + _eligBC.by_category.CIVCAS_OTHER.military_cases) * 10) / 10,
            unmet_cases: Math.round((_eligBC.by_category.CIVCAS_OWN.unmet_cases + _eligBC.by_category.CIVCAS_OTHER.unmet_cases) * 10) / 10,
            military_cases_all_categories: Math.round(_eligBC.military_cases * 10) / 10,
            unmet_cases_all_categories: Math.round(_eligBC.unmet_cases * 10) / 10,
            by_category: Object.fromEntries(Object.entries(_eligBC.by_category).map(([k, c]) => [k, { cases: Math.round(c.cases * 10) / 10, military_share: Math.round(c.military_share * 1000) / 1000, host_share: Math.round(c.host_share * 1000) / 1000, unmet_share: Math.round(c.unmet_share * 1000) / 1000, military_cases: Math.round(c.military_cases * 10) / 10 }]))
          } : null,
          facility_capacity: { protection_factor: Math.round(_protection.factor * 1000) / 1000, infrastructure_factor: Math.round(_infraSurgMod * 1000) / 1000, combined: Math.round(_facilityCapFactor * 1000) / 1000, hit_probability_per_day: _protection.p_hit },
          effective_surgical_capacity_percent: Math.round((_surgTempo ? _surgTempo.performance : fatigueEffect) * 100),
          // Phase 6.1: Surgeon performance from shift-hours model
          surgeon_performance_pct: Math.round(surgeonPerfModifier * 100),
          // Phase 6.1: MTP protocol tracking
          mtp_protocol_applied: resourceResult.blood.requirements.totals.mtp_protocol_applied || false,
          mtp_protocol: resourceResult.blood.requirements.totals.mtp_protocol || null,
          // Phase 6.1: Shelf life tracking (display only — does not affect consumption totals)
          shelf_life: shelfLifeTracking,
          // ENH-024: DCS blood supplement (additive over mechanism baseline; 0 when no forward surgery)
          dcs_blood_supplement_prbc: _dcsBloodSupplementPRBC,
          dcs_blood_supplement_ffp:  _dcsBloodSupplementFFP,
          // ENH-025: Drone attrition factor applied to MEDEVAC capacity this day
          drone_attrition_factor: _droneAttritionFactor
        },
        
        // CC-011 + MSK-001: Blood requirements by injury mechanism with surge tracking
        blood_by_mechanism: {
          category: bloodByMechanism.category,
          totals: bloodByMechanism.totals,
          average_per_t1: bloodByMechanism.average_per_casualty,
          mtp_patients: bloodByMechanism.totals.mtp_patients,
          by_mechanism: bloodByMechanism.by_mechanism,
          // MSK-001: Surge tracking
          surge: bloodByMechanism.surge || { active: false }
        },
        
        tccc_quality: tcccAnalysis.combined.tccc_quality_assessment,
        dow_rate_applied: Math.round(casevacDOWBreakdown.effective_dow_rate * 1000) / 10,
        // Surgical queue (resourceModule cascade): reference values. In the flat model its DOW was never applied; in
        // the hazard model the previous day's wait is added to the time to surgery (see wound_hazard).
        surgical_queue: {
          end_of_day_backlog: resourceResult.surgical?.cascade?.output?.end_of_day_backlog ?? 0,
          avg_wait_hours: resourceResult.surgical?.cascade?.output?.avg_wait_hours ?? 0,
          reference_additional_dow: resourceResult.surgical?.cascade?.mortality_impact?.additional_dow ?? 0,
          reference_delay_mortality_percent: resourceResult.surgical?.cascade?.mortality_impact?.delay_mortality_rate ?? 0
        },
        // Wound-type hazard model report (flat vs hazard base DOW, prehospital rate, per region). Present only when
        // the mode is SHADOW or ON, so an OFF run is identical to a run before the model existed.
        ...(dowResult.analysis?.modifiers_applied?.wound_hazard
          ? {
              wound_hazard: {
                ...dowResult.analysis.modifiers_applied.wound_hazard,
                // Deaths between injury and arrival (not DOW). Reported, NOT yet subtracted from the wounded or
                // moved into KIA: how they relate to the KIA constant is an open decision (PLAN_WOUND_HAZARD_IMPL 7).
                prehospital_deaths: _preDeaths,
                prehospital_deaths_applied: _preApplied
              }
            }
          : {}),
        
        // FIX-CC-014: Force superiority analysis
        force_superiority: {
          friendly_strength: friendlyStrength,
          enemy_strength: enemyStrength,
          force_ratio: Math.round(forceRatio * 100) / 100,
          modifier_applied: Math.round(forceSuperiorityModifier * 100) / 100,
          casualties_prevented: Math.round(casualtiesPrevented),
          analysis: forceSuperiorityResult
        },
        
        // FIX-CC-015: Medical personnel status
        medical_personnel: {
          casualties_today: medicalPersonnelResult.medical_casualties,
          remaining: remainingMedicalPersonnel,
          capacity_reduction_percent: Math.round(medicalCapacityReduction * 100),
          risk_analysis: medicalPersonnelResult.risk_analysis
        },
        
        // FIX-CC-016: Per-facility MASCAL status
        facility_mascal: {
          is_mascal: facilityMascalResult?.is_mascal || false,
          level: facilityMascalResult?.level || 'NORMAL',
          facilities_affected: facilityMascalResult?.facilities || []
        },

        // ENH-041: Discrete event summary for this day.
        // null when no events fired (zero overhead for normal days).
        discrete_events: _discreteEvtTotal > 0 ? {
          count:            _discreteEvtLog.length,
          total_casualties: _discreteEvtTotal,
          events:           _discreteEvtLog,
          triggered_mascal: _discreteEventsMASCAL?.is_mascal || false,
          mascal_level:     _discreteEventsMASCAL?.level || 'NORMAL'
        } : null,

        // ENH-046: Phase transition surge log — non-null only on day 1 of posture-changing phases.
        // Contains from/to posture, surge factor, and additional casualties generated.
        // Teaching point: displayed in Tab5 day-by-day view + Tab6 CASREP surge annotation.
        phase_transition: _phaseTransitionLog,

        // FIX-CC-017: Unit type analysis (v4.9: NOW APPLIED to casualties)
        unit_type_analysis: {
          unit_type: brigadeUnitType,
          casualty_modifier: unitTypeResult.rates?.unit_type_modifier || 1.0,
          terrain_modifier: unitTypeResult.rates?.terrain_modifier || 1.0,
          posture_modifier: unitTypeResult.rates?.posture_modifier || 1.0,
          // v4.9: Combined modifier that was actually applied
          combined_modifier_applied: Math.round(unitTypeModifier * 100) / 100
        },
        
        // 2026-10-13 (Tab 2 plan 7.11): what the casualty rate of the day was made of, for the end-of-tab summary card and the reports
        phase_name: phase.name || `Phase ${phaseIndex + 1}`,
        phase_posture: phase.posture,
        advance_profile: _axisDay ? {
          advance_km: Math.round(_axisDay.advance_km * 10) / 10,
          front_km: Math.round(_axisDay.front_km * 10) / 10,
          leg1_extra_km: Math.round(_axisDay.leg1_extra_km * 10) / 10,
          leg2_extra_km: Math.round(_axisDay.leg2_extra_km * 10) / 10,
          r2b_jump: _axisDay.r2b_jump,
          r2e_jump: _axisDay.r2e_jump,
          legs_minutes: _evacProfile?.advance_legs || null
        } : null,
        casualty_profile: _dayProfile ? {
          form: _dayProfile.form, group: _dayProfile.group, condition: _dayProfile.condition,
          resistance_level: _dayProfile.resistance_level, resistance_index: Math.round(_dayProfile.resistance_index * 100) / 100,
          form_factor: Math.round(_dayProfile.form_factor * 1000) / 1000, group_factor: _dayProfile.group_factor,
          condition_factor: _dayProfile.condition_factor, total_factor: Math.round(_dayProfile.total_factor * 1000) / 1000
        } : null,
        threat_model: _threatModel ? {
          helicopter_availability: Math.round(_threatModel.helicopter.value * 1000) / 1000,
          road_status: _threatModel.roadState ? _threatModel.roadState.status : null,
          road_route: _threatModel.roadState ? _threatModel.roadState.route : null,
          road_speed_factor: _threatModel.roadState ? Math.round(_threatModel.roadState.speed_factor * 1000) / 1000 : null,
          road_availability: _threatModel.roadState ? Math.round(_threatModel.roadState.availability * 1000) / 1000 : null,
          rear_factor: Math.round(_threatModel.rear.value * 1000) / 1000,
          dow_factor: _threatModel.dow.value,
          ied_march_factor: _threatModel.iedMarch
        } : null,

        // PHASE 2.1: Evacuation routes impact
        route_impact: {
          configured: routeImpact?.configured || false,
          segments_summary: routeImpact?.segments_summary || null,
          dow_modifier: routeImpact?.dow_modifier || 1.0,
          golden_hour_achievable: routeImpact?.golden_hour_achievable ?? true,
          golden_hour_margin_min: routeImpact?.golden_hour_margin_min || null,
          total_travel_time_min: routeImpact?.total_travel_time_min || null,
          total_travel_time_hours: routeImpact?.total_travel_time_hours || null,
          routes_closed: routeImpact?.routes_closed || 0,
          using_alternate: routeImpact?.using_alternate_count || 0,
          warnings: routeImpact?.warnings || []
        },
        
        // PHASE 4.1: Resupply analysis for this day
        resupply_analysis: {
          received_today: resupplyReceived,
          events: todayResupply.map(event => ({
            package: event.push_package,
            method: event.delivery_method,
            destination: event.mtf_target,
            units: event.units
          })),
          units_received: resupplyUnitsReceived,
          events_count: todayResupply.length
        },
        
        strength: {
          start: currentStrength,
          // P1-02 FIX: Use dnbiResult.rtd.expected_rtd (same source as dnbi.rtd above).
          // Old code: Math.round(dnbiResult.total_dnbi * 0.85) — hardcoded, ignores categories.
          end: currentStrength - battleResult.battle_casualties.total - 
               dnbiResult.total_dnbi + battleResult.battle_casualties.rtd + 
               dnbiResult.rtd.expected_rtd,  // DNBI RTD
          percent: Math.round(((currentStrength - battleResult.battle_casualties.total) / initialStrength) * 100)
        },

        // IMP-008: Cohesion band — FM 101-5-1 Gumball Chart (Dupuy TNDM supplement).
        // Worst-case aggregation across all brigades is performed in Tab5 dailyProjectionData.
        // strength_percent is start-of-day (before today's losses) per Dupuy sequencing.
        cohesion: {
          strength_percent: Math.round(strengthPercent),
          band:             cohesionBand,
          modifier:         cohesionModifier,
          fm_color:         cohesionDef.fm_color,
          description:      cohesionDef.description
        },

        // WTD-01: Anatomical wound distribution for this day.
        // Body region breakdown of T1/T2 WIA by operation type (woundTypeDistribution.js).
        // Used by Tab5 anatomical breakdown and dressing consumption context.
        wound_distribution: {
          operation_type: opType,
          distribution: woundDistribution,
          t1_by_wound: distributeCasualtiesByWound(
            triageDistribution?.T1 || triageDistribution?.t1_immediate || triageDistribution?.t1 || 0,
            opType,
            woundDistribution
          ),
          t2_by_wound: distributeCasualtiesByWound(
            triageDistribution?.T2 || triageDistribution?.t2_delayed || triageDistribution?.t2 || 0,
            opType,
            woundDistribution
          )
        },

        // Phase 5.1: Tactical evacuation capacity vs demand.
        // Uses calculateGroundCapacity / calculateAirCapacity from evacuationCapacityCalculator.js.
        // ARCH-010 COMPLIANT: arch010_dow_risk_informational is display only — never applied to DOW.
        // Feeds Tab5 Phase 5.2 Capacity vs Demand chart via projections.by_brigade[n].by_day[d].

        // ENH-012: Theater scalar applied this day (1.0 = no adjustment; >1.0 = theater inflation)
        theater_scalar: theaterScalar,

        tactical_evac_analysis: (() => {
          try {
            const { tec, gCap, aCap } = _tacCapDay || tacticalCapacityOfDay({ medicalConfig, scenario, brigade, day, environmentOverride: _dayEnv, weatherKey: _wxToday });
            const dailyTotal = (battleResult.battle_casualties.total || 0)
                             + (dnbiResult.total_dnbi || 0);
            const sufficiency = computeTacticalEvacSufficiency({ ...gCap, total_patients_per_day: (gCap.total_patients_per_day || 0) * _fleetAttrToday }, aCap, dailyTotal);
            return {
              ...sufficiency,
              environment: tec.environment,
              weather:     tec.weather
            };
          } catch (e) {
            console.warn('Phase 5.1: tactical_evac_analysis failed:', e.message);
            return { status: 'UNKNOWN', error: e.message };
          }
        })()
      };

      brigadeResult.by_day.push(daySummary);

      // Wound-type hazard model: today's antibiotic and dressing consumption lowers tomorrow's stock.
      if (_hazardSupply) {
        _hazardSupply.consume(calculateDailyConsumption({
          t1: triageDistribution?.T1 || triageDistribution?.t1_immediate || triageDistribution?.t1 || 0,
          t2: triageDistribution?.T2 || triageDistribution?.t2_delayed || triageDistribution?.t2 || 0,
          t3: triageDistribution?.T3 || triageDistribution?.t3_minimal || triageDistribution?.t3 || 0,
          wound_distribution: woundDistribution,
          t4: triageDistribution?.T4 || triageDistribution?.t4_expectant || triageDistribution?.t4 || 0,
          dnbi: dnbiResult?.total_dnbi || 0
        }));
      }

      // Update totals - BUG-050 FIX: Defensive NaN checks
      brigadeResult.totals.kia += isNaN(daySummary.battle?.kia) ? 0 : daySummary.battle.kia;
      brigadeResult.totals.dow += isNaN(daySummary.battle?.dow) ? 0 : daySummary.battle.dow;
      if (daySummary.wound_hazard) {
        brigadeResult.totals.prehospital_deaths = (brigadeResult.totals.prehospital_deaths || 0) + daySummary.wound_hazard.prehospital_deaths;
      }
      brigadeResult.totals.wia += isNaN(daySummary.battle?.wia) ? 0 : daySummary.battle.wia;
      // BUG FIX (Feb 7, 2026): Add both battle RTD and DNBI RTD
      brigadeResult.totals.rtd += isNaN(daySummary.battle?.rtd) ? 0 : daySummary.battle.rtd;
      brigadeResult.totals.rtd += isNaN(daySummary.dnbi?.rtd) ? 0 : daySummary.dnbi.rtd;
      brigadeResult.totals.dnbi += isNaN(daySummary.dnbi?.total) ? 0 : daySummary.dnbi.total;
      brigadeResult.totals.rear_area += isNaN(daySummary.rear_area) ? 0 : daySummary.rear_area;
      brigadeResult.totals.blood_consumed = Math.round((brigadeResult.totals.blood_consumed + daySummary.resources.blood_prbc) * 100) / 100;
      brigadeResult.totals.surgical_hours = Math.round((brigadeResult.totals.surgical_hours + daySummary.resources.surgical_hours) * 100) / 100;

      // WTD-01: Aggregate wound distribution counts across all days for brigade-level totals.
      if (!brigadeResult.wound_distribution) {
        brigadeResult.wound_distribution = {
          operation_type: opType,
          distribution: woundDistribution,
          t1_by_wound: { extremity: 0, thorax: 0, abdomen: 0, head_neck: 0, burns: 0, multi: 0 },
          t2_by_wound: { extremity: 0, thorax: 0, abdomen: 0, head_neck: 0, burns: 0, multi: 0 }
        };
      }
      const wdT1 = daySummary.wound_distribution.t1_by_wound;
      const wdT2 = daySummary.wound_distribution.t2_by_wound;
      const bwd = brigadeResult.wound_distribution;
      ['extremity','thorax','abdomen','head_neck','burns','multi'].forEach(region => {
        bwd.t1_by_wound[region] += (wdT1[region] || 0);
        bwd.t2_by_wound[region] += (wdT2[region] || 0);
      });

      // CC-007: Update CASEVAC totals
      if (daySummary.casevac.is_overwhelmed) {
        brigadeResult.casevac.days_overwhelmed++;
      }
      brigadeResult.casevac.total_medevac_evacuated += daySummary.casevac.medevac_evacuated;
      brigadeResult.casevac.total_casevac_evacuated += daySummary.casevac.casevac_evacuated;
      brigadeResult.casevac.total_unevacuated += daySummary.casevac.unevacuated;
      brigadeResult.casevac.total_preventable_deaths += daySummary.casevac.dow_breakdown.preventable_deaths;
      totalCapacityUtilization += daySummary.casevac.capacity_utilization_percent;

      // Update cumulative - BUG-048 FIX: Defensive NaN check to break cascade
      const dayBattleTotal = isNaN(daySummary.battle?.total) ? 0 : daySummary.battle.total;
      const dayDnbiTotal = isNaN(daySummary.dnbi?.total) ? 0 : daySummary.dnbi.total;
      const dayRtd = isNaN(daySummary.battle?.rtd) ? 0 : daySummary.battle.rtd;
      
      // BUG-048 DIAGNOSTIC: Log which value is NaN
      if (isNaN(daySummary.battle?.total)) {
      }
      if (isNaN(daySummary.dnbi?.total)) {
      }
      if (isNaN(daySummary.battle?.rtd)) {
      }
      
      if (OUT_OF_ACTION_POOL) {
        // Everyone who became a casualty today is out of action; the survivors who return are scheduled to come back later.
        cumulativeCasualties += dayBattleTotal + dayDnbiTotal;
        const _triage = {
          T1: triageDistribution?.T1 || triageDistribution?.t1_immediate || triageDistribution?.t1 || 0,
          T2: triageDistribution?.T2 || triageDistribution?.t2_delayed || triageDistribution?.t2 || 0,
          T3: triageDistribution?.T3 || triageDistribution?.t3_minimal || triageDistribution?.t3 || 0
        };
        const _wiaReturn = { T1: wiaRtdT1, T2: wiaRtdT2, T3: wiaRtdT3 };
        const _wiaCategory = { T1: 'T1_IMMEDIATE', T2: 'T2_DELAYED', T3: 'T3_MINIMAL' };
        Object.keys(_triage).forEach((t) => {
          const returning = _rc(_triage[t] * _wiaReturn[t], 'return_' + t);
          if (returning <= 0) return;
          const median = requireNumber(THP_RTD_RATES?.BY_TRIAGE_CATEGORY?.[_wiaCategory[t]]?.median_days_to_rtd,
            'casualtyCalculator.returnPool', `THP_RTD_RATES.BY_TRIAGE_CATEGORY.${_wiaCategory[t]}.median_days_to_rtd`);
          const delay = Math.max(1, Math.min(Math.round(median), thpDays));
          returnSchedule[day + delay] = (returnSchedule[day + delay] || 0) + returning;
        });
        const _dnbiReturning = _rc(isNaN(daySummary.dnbi?.rtd) ? 0 : (daySummary.dnbi?.rtd || 0), 'return_dnbi');
        if (_dnbiReturning > 0) {
          const dDay = day + Math.max(1, Math.round(DNBI_RETURN_TO_DUTY.delay_days));
          returnSchedule[dDay] = (returnSchedule[dDay] || 0) + _dnbiReturning;
        }
      } else {
        cumulativeCasualties += dayBattleTotal + dayDnbiTotal - dayRtd;
      }

      // Golden hour tracking - CC-004: Use cumulative compliance
      // Track both legacy single-leg and new cumulative for backward compatibility
      if (daySummary.golden_hour.achieved) {
        brigadeResult.golden_hour.days_achieved++;
      } else {
        brigadeResult.golden_hour.days_not_achieved++;
      }
      
      // CC-004: Track cumulative golden hour compliance
      if (daySummary.golden_hour.cumulative_golden_hour_compliant) {
        brigadeResult.golden_hour.cumulative_days_achieved = 
          (brigadeResult.golden_hour.cumulative_days_achieved || 0) + 1;
      } else {
        brigadeResult.golden_hour.cumulative_days_not_achieved = 
          (brigadeResult.golden_hour.cumulative_days_not_achieved || 0) + 1;
      }
      
      totalEvacTime += daySummary.golden_hour.evac_time_minutes;
      
      // CC-004: Track cumulative time for averaging
      brigadeResult.golden_hour.total_cumulative_time = 
        (brigadeResult.golden_hour.total_cumulative_time || 0) + 
        daySummary.golden_hour.cumulative_time_to_surgery_min;
      
      // TASK 3.3: Add day to phase breakdown
      phaseBreakdown.days.push(daySummary);
      
      // TASK 3.3: Update phase totals
      phaseBreakdown.totals.kia += daySummary.battle?.kia || 0;
      phaseBreakdown.totals.dow += daySummary.battle?.dow || 0;
      phaseBreakdown.totals.wia += daySummary.battle?.wia || 0;
      // BUG FIX (Feb 7, 2026): Add both battle RTD and DNBI RTD to phase totals
      phaseBreakdown.totals.rtd += daySummary.battle?.rtd || 0;
      phaseBreakdown.totals.rtd += daySummary.dnbi?.rtd || 0;
      phaseBreakdown.totals.dnbi += daySummary.dnbi?.total || 0;
      phaseBreakdown.totals.blood_consumed = Math.round((phaseBreakdown.totals.blood_consumed + daySummary.resources.blood_prbc) * 100) / 100;
      phaseBreakdown.totals.surgical_hours = Math.round((phaseBreakdown.totals.surgical_hours + daySummary.resources.surgical_hours) * 100) / 100;
      
      // TASK 3.3: Increment absolute day counter
      currentDay++;
    }  // End of dayInPhase loop
    
    // TASK 3.3: Finalize phase totals
    phaseBreakdown.totals.total_battle = phaseBreakdown.totals.kia + 
                                          phaseBreakdown.totals.dow + 
                                          phaseBreakdown.totals.wia;
    phaseBreakdown.totals.total_all = phaseBreakdown.totals.total_battle + 
                                       phaseBreakdown.totals.dnbi;
    
    // TASK 3.3: Add phase to brigade results
    brigadeResult.by_phase.push(phaseBreakdown);
    
  }  // End of phase loop
  

    // Finalize totals
    brigadeResult.totals.total_battle = brigadeResult.totals.kia + brigadeResult.totals.dow + 
                                         brigadeResult.totals.wia;
    brigadeResult.totals.total_all = brigadeResult.totals.total_battle + brigadeResult.totals.dnbi;

    // Golden hour average
    const effectiveDays = brigadeResult.by_day.filter(d => d.is_combat_effective !== false).length;
    brigadeResult.golden_hour.average_evac_time_minutes = effectiveDays > 0 ?
      Math.round(totalEvacTime / effectiveDays) : 0;
    
    // CC-004: Add cumulative golden hour metrics
    brigadeResult.golden_hour.average_cumulative_time_to_surgery = effectiveDays > 0 ?
      Math.round((brigadeResult.golden_hour.total_cumulative_time || 0) / effectiveDays) : 0;
    brigadeResult.golden_hour.cumulative_achievement_percent = 
      (brigadeResult.golden_hour.cumulative_days_achieved || 0) + 
      (brigadeResult.golden_hour.cumulative_days_not_achieved || 0) > 0 ?
        Math.round(((brigadeResult.golden_hour.cumulative_days_achieved || 0) / 
          ((brigadeResult.golden_hour.cumulative_days_achieved || 0) + 
           (brigadeResult.golden_hour.cumulative_days_not_achieved || 0))) * 100) : 0;

    // CC-007: Add CASEVAC summary
    brigadeResult.casevac.average_capacity_utilization = effectiveDays > 0 ?
      Math.round(totalCapacityUtilization / effectiveDays) : 0;
    brigadeResult.casevac.percent_days_overwhelmed = effectiveDays > 0 ?
      Math.round((brigadeResult.casevac.days_overwhelmed / effectiveDays) * 100) : 0;
    brigadeResult.casevac.total_evacuated = brigadeResult.casevac.total_medevac_evacuated +
                                            brigadeResult.casevac.total_casevac_evacuated;
    brigadeResult.casevac.percent_by_casevac = brigadeResult.casevac.total_evacuated > 0 ?
      Math.round((brigadeResult.casevac.total_casevac_evacuated / brigadeResult.casevac.total_evacuated) * 100) : 0;
    
    // CC-007: Assessment
    if (brigadeResult.casevac.total_unevacuated > 0) {
      brigadeResult.casevac.assessment = 'CRITICAL';
      brigadeResult.casevac.assessment_reason = `${brigadeResult.casevac.total_unevacuated} casualties could not be evacuated`;
    } else if (brigadeResult.casevac.percent_by_casevac > 30) {
      brigadeResult.casevac.assessment = 'STRAINED';
      brigadeResult.casevac.assessment_reason = `${brigadeResult.casevac.percent_by_casevac}% evacuated by CASEVAC (no en-route care)`;
    } else if (brigadeResult.casevac.days_overwhelmed > 0) {
      brigadeResult.casevac.assessment = 'ADEQUATE';
      brigadeResult.casevac.assessment_reason = `MEDEVAC overwhelmed on ${brigadeResult.casevac.days_overwhelmed} day(s)`;
    } else {
      brigadeResult.casevac.assessment = 'SUFFICIENT';
      brigadeResult.casevac.assessment_reason = 'MEDEVAC capacity sufficient for all casualties';
    }

    // FIX-CC-014: Force superiority summary
    brigadeResult.force_superiority.average_ratio = effectiveDays > 0 ?
      Math.round((totalForceRatio / effectiveDays) * 100) / 100 : 1.0;
    brigadeResult.force_superiority.total_modifier_applied = effectiveDays > 0 ?
      Math.round((totalForceSuperiorityModifier / effectiveDays) * 100) / 100 : 1.0;
    brigadeResult.force_superiority.casualties_prevented = Math.round(totalCasualtiesPrevented);
    brigadeResult.force_superiority.assessment = 
      brigadeResult.force_superiority.average_ratio >= 2.0 ? 'FAVORABLE' :
      brigadeResult.force_superiority.average_ratio >= 1.0 ? 'PARITY' : 'UNFAVORABLE';

    // NATO planning-norm DOW rate for this threat level, for comparison with the model's DOW
    brigadeResult.reference_dow_rate = calculateDOWRate(
      { environment: { threat_level: scenario?.operation_parameters?.threat_level } },
      scenario
    );

    // FIX-CC-015: Medical personnel summary (daily values are fractional expected losses; whole people for display)
    brigadeResult.medical_personnel.total_casualties = Math.round(brigadeResult.medical_personnel.total_casualties);
    brigadeResult.medical_personnel.remaining = Math.round(brigadeResult.medical_personnel.remaining);
    brigadeResult.medical_personnel.final_capacity_percent =
      Math.round((brigadeResult.medical_personnel.remaining / brigadeResult.medical_personnel.initial_count) * 100);
    brigadeResult.medical_personnel.assessment = 
      brigadeResult.medical_personnel.final_capacity_percent >= 80 ? 'ADEQUATE' :
      brigadeResult.medical_personnel.final_capacity_percent >= 50 ? 'DEGRADED' : 'CRITICAL';

    // FIX-CC-016: Facility MASCAL summary
    brigadeResult.facility_mascal.percent_days_mascal = effectiveDays > 0 ?
      Math.round((brigadeResult.facility_mascal.days_in_mascal / effectiveDays) * 100) : 0;

    // End state
    brigadeResult.end_state.final_strength = initialStrength - cumulativeCasualties;
    brigadeResult.end_state.returned_to_duty = totalReturned;
    brigadeResult.end_state.still_in_treatment = Object.entries(returnSchedule)
      .reduce((sum, [d, n]) => sum + n, 0) - totalReturned;
    brigadeResult.end_state.strength_percent = Math.round(
      (brigadeResult.end_state.final_strength / initialStrength) * 100
    );
    brigadeResult.end_state.combat_effective = brigadeResult.end_state.strength_percent >= 30;
    brigadeResult.end_state.surgical_backlog = surgicalBacklog;

    // ========================================================================
    // SECTION 6: ADD UI METADATA FOR DISPLAY COMPONENTS
    // ========================================================================
    const missionRole = brigade.mission_role || brigade.missionRole;
    const missionMultiplier = brigade.tactical_in_parent?.mission_multiplier || 
                              brigade.mission_multiplier || 1.0;
    const operationalState = actualPhases.length > 0 ? actualPhases[0].operational_state : 
                             (brigade.operational_state || 'COMBAT_ENGAGED');
    
    // Detect if any phase is march
    const hasMarches = actualPhases.some(p => isMarchPosture(p.posture));
    
    brigadeResult.ui_metadata = {
      // Role badge for UI display
      role_badge: getRoleBadge(missionRole, missionMultiplier),
      
      // Phase indicator
      phase_indicator: getPhaseIndicator(
        brigadeResult.phase_source,
        actualPhases.length
      ),
      
      // Operational state badge
      state_badge: getStateBadge(operationalState),
      
      // Movement indicator
      movement_indicator: {
        has_march: hasMarches,
        icon: hasMarches ? TACTICAL_ICONS.MARCH_TACTICAL : null,
        march_phases: actualPhases.filter(p => isMarchPosture(p.posture)).map(p => p.name)
      },
      
      // Combined casualty multiplier (approximate from first phase)
      casualty_multiplier: getCasualtyMultiplierSeverity(missionMultiplier),
      
      // Overall assessment
      overall_status: {
        combat_effective: brigadeResult.end_state.combat_effective,
        casualties_percent: Math.round((brigadeResult.totals.total_all / initialStrength) * 100),
        medical_status: brigadeResult.medical_personnel?.assessment || 'UNKNOWN',
        color: brigadeResult.end_state.combat_effective ? 
               UI_COLOR_SCHEME.severity.MODERATE : 
               UI_COLOR_SCHEME.severity.CRITICAL
      }
    };

    return brigadeResult;
  }

  /**
   * BUG-065: Extract actual personnel breakdown from forceBuilderResult
   * 
   * Instead of using fixed percentages (4% HQ, 37% Support), extract actual
   * breakdown values set by user in Tab1 via breakdown.command and breakdown.support.
   * 
   * @param {Object} forceResult - forceBuilderResult from scenario.force
   * @returns {Object} Personnel breakdown by category
   */
  extractPersonnelBreakdown(forceResult) {
    let totalCommand = 0;
    let totalSupport = 0;
    let totalCombat = 0;
    
    // Extract from Corps HQ
    if (forceResult.corps?.breakdown) {
      totalCommand += forceResult.corps.breakdown.command || 0;
      totalSupport += forceResult.corps.breakdown.support || 0;
    } else if (forceResult.corps?.strength) {
      // Fallback if no breakdown: assume all HQ is command (legacy behavior)
      totalCommand += forceResult.corps.strength;
    }
    
    // Extract from Division HQs
    if (forceResult.divisions && Array.isArray(forceResult.divisions)) {
      forceResult.divisions.forEach((division, index) => {
        if (division.breakdown) {
          totalCommand += division.breakdown.command || 0;
          totalSupport += division.breakdown.support || 0;
        } else if (division.strength) {
          // Fallback: estimate using default ratios if no breakdown
          const estimatedCommand = Math.round(division.strength * 0.15);
          const estimatedSupport = Math.round(division.strength * 0.85);
          totalCommand += estimatedCommand;
          totalSupport += estimatedSupport;
        }
      });
    }
    
    // BUG-198-FIX: Helper to accumulate one brigade's personnel contribution.
    // Shared by both the brigades[] array path and the BRIGADE-echelon singular
    // forceResult.brigade path (BUG-128 / BUG-187 pattern: singular vs array).
    const accumulateBrigade = (brigade) => {
      let brigadeCommand = 0;
      let brigadeSupport = 0;
      if (brigade.breakdown) {
        brigadeCommand = brigade.breakdown.command || 0;
        brigadeSupport = brigade.breakdown.support || 0;
        totalCommand += brigadeCommand;
        totalSupport += brigadeSupport;
      } else if (brigade.strength) {
        // Fallback: brigades typically have pure command HQ (100% command, 0% support)
        // Support is separate support battalion
        brigadeCommand = Math.round((brigade.strength || 0) * 0.05); // ~5% is HQ
        brigadeSupport = 0;
        totalCommand += brigadeCommand;
      }
      // Combat personnel = brigade strength minus HQ breakdown
      const brigadeHQ = brigadeCommand + brigadeSupport;
      const brigadeCombat = (brigade.strength || 0) - brigadeHQ;
      totalCombat += Math.max(0, brigadeCombat); // Ensure non-negative
    };

    // Extract from Brigade HQs — array path (CORPS / DIVISION echelon)
    if (forceResult.brigades && Array.isArray(forceResult.brigades)) {
      forceResult.brigades.forEach(accumulateBrigade);
    }

    // F2-ARCH-010: ForceService now always populates brigades[] for all echelons.
    // This singular fallback only fires for pre-F2 persisted data where brigades[]
    // is absent.  Guard: skip if brigades[] already handled the brigade above.
    if (
      (!forceResult.brigades || forceResult.brigades.length === 0) &&
      forceResult.brigade &&
      !Array.isArray(forceResult.brigade)
    ) {
      accumulateBrigade(forceResult.brigade);
    }

    const total = totalCommand + totalSupport + totalCombat;

    // v5.24.0: Read corps/division organic support troops from ForceService v2.13.0 fields.
    // These are the signal/medical/logistics/engineer/fires/aviation brigades that exist
    // OUTSIDE any brigade — previously invisible to the calculator entirely.
    // - forceResult.support_troops.total_organic_strength = corps-level support troops
    // - forceResult.division_support_strength = sum of all division support troops
    const corpsSupportTroopStrength =
      forceResult.support_troops?.total_organic_strength || 0;
    const divisionSupportTroopStrength =
      forceResult.division_support_strength || 0;
    const totalEchelonOrganicSupport = corpsSupportTroopStrength + divisionSupportTroopStrength;

    return {
      command: totalCommand,
      support: totalSupport,
      combat: totalCombat,
      total: total,
      // v5.24.0: Echelon organic support troops (not inside any brigade)
      echelon_organic_support: totalEchelonOrganicSupport,
      corps_support_strength: corpsSupportTroopStrength,
      division_support_strength: divisionSupportTroopStrength
    };
  }

  /**
   * Calculate HQ casualties using nationalStandards constants
   * Uses calculateHQCasualties from nationalStandards.js with proper constants
   * Reference: FORCE_STRUCTURE_VERIFICATION_REPORT.md
   */
  calculateHQCasualtiesMethod(force, opParams, duration) {
    // Extract force structure counts from forceBuilderResult
    const forceResult = force?.forceBuilderResult || {};
    
    const forceStructure = {
      corps: forceResult.corps_count || (forceResult.echelon === 'CORPS' ? 1 : 0),
      divisions: forceResult.divisions?.length || forceResult.division_count || 0,
      // F2-ARCH-010: brigades[] now populated for all echelons. Singular fallback for pre-F2 data.
      brigades: forceResult.brigades?.length || forceResult.brigade_count || (forceResult.brigade ? 1 : 0),
      battalions: forceResult.battalions?.length || forceResult.battalion_count || 0
    };
    
    // Get national standard from force or default to NATO
    const nationalStandard = force?.nationalStandard || 
                             forceResult?.nationalStandard || 
                             'NATO';
    
    // BUG-065: Updated casualty rate for command personnel
    // OLD: 0.5% per day for HQ elements
    // NEW: 0.4% per day for command personnel (research-validated)
    const baseCasualtyRate = 0.004; // 0.4% per day for command personnel
    
    // Apply threat modifier to base rate
    const threatLevel = opParams?.threat_level || 'PEER';
    const threatMod = threatLevel === 'PEER' ? 1.5 : 
                      threatLevel === 'NEAR_PEER' ? 1.2 : 1.0;
    const adjustedRate = baseCasualtyRate * threatMod;
    
    // Call nationalStandards calculateHQCasualties (uses proper constants)
    const hqResult = calculateHQCasualties(forceStructure, nationalStandard, adjustedRate, duration);
    
    // BUG-065: Extract actual command personnel from breakdown instead of using 4% estimate
    // OLD METHOD: hqPersonnel = totalForceStrength × 0.04 (fixed percentage)
    // NEW METHOD: Extract actual breakdown.command from all echelons
    const personnelBreakdown = this.extractPersonnelBreakdown(forceResult);
    const hqPersonnel = personnelBreakdown.command;
    
    
    // Convert to expected format with KIA/DOW/WIA/DNBI breakdown
    // Distribution based on HQ rear-area exposure profile
    const totalCasualties = hqResult.total;
    
    return {
      total_hq_personnel: hqPersonnel,  // Changed from 'personnel'
      total: totalCasualties,
      kia: Math.round(totalCasualties * 0.08),   // 8% KIA (lower than line units)
      dow: Math.round(totalCasualties * 0.04),   // 4% DOW
      wia: Math.round(totalCasualties * 0.48),   // 48% WIA
      dnbi: Math.round(totalCasualties * 0.40),  // 40% DNBI (higher for rear area)
      total_dnbi: Math.round(totalCasualties * 0.40),  // Added for Tab3 compatibility
      dnbi_rate_applied: adjustedRate,  // Added for Tab3 display
      
      // Additional detail from nationalStandards
      by_echelon: hqResult.casualties,
      personnel_breakdown: hqResult.casualties,  // Added alias for Tab3 compatibility
      national_standard: nationalStandard,
      
      // Added for Tab3 DNBI categories breakdown.
      // BUG-5J-001 FIX: Compute dnbiTotal once from the same value stored in .dnbi,
      // then derive categories from that shared base. The 5 independent Math.round() calls
      // can each round differently → category sum diverges from total by ±1.
      // Fix: final category absorbs rounding remainder, guaranteeing sum === total.
      dnbi_categories: (() => {
        const dnbiBase = Math.round(totalCasualties * 0.40);
        const musculo  = Math.round(dnbiBase * 0.50);
        const mental   = Math.round(dnbiBase * 0.23);
        const infect   = Math.round(dnbiBase * 0.15);
        const accident = Math.round(dnbiBase * 0.08);
        const other    = Math.max(0, dnbiBase - musculo - mental - infect - accident);
        return { musculoskeletal: musculo, mental_health: mental, infectious: infect, accidents: accident, other };
      })()
    };
  }

  /**
   * Calculate support unit casualties
   * BUG-065: Now uses actual breakdown.support instead of fixed 37% estimate
   * Research: Support personnel are 35-40% of force (not 32%)
   * Result: Combat 59%, HQ 4%, Support 37% = 100%
   * Reference: FORCE_STRUCTURE_VERIFICATION_REPORT.md
   */
  calculateSupportUnitCasualties(force, opParams, duration) {
    const forceResult = force?.forceBuilderResult || {};

    // BUG-065: Extract actual support personnel from breakdown
    // v5.24.0: Also reads echelon_organic_support from extractPersonnelBreakdown
    const personnelBreakdown = this.extractPersonnelBreakdown(forceResult);
    const supportPersonnel = personnelBreakdown.support;  // HQ internal support staff
    const echelonOrganicSupport = personnelBreakdown.echelon_organic_support || 0; // corps/div organic units

    // BUG-SUPPORT-001: Brigade support battalion personnel.
    // Brigade HQ breakdown_ratios.support = 0 (pure command HQ) — support battalion is NOT
    // tracked in breakdown.support. It is embedded in brigade.strength via support_ratio.
    // For ROMANIAN brigade: support_ratio=0.20 × strength=4000 = 800 personnel.
    // These are signal/medical/logistics/transport sub-units organic to the brigade.
    const brigadeArray = (forceResult.brigades?.length > 0)
      ? forceResult.brigades
      : (forceResult.brigade && !Array.isArray(forceResult.brigade) ? [forceResult.brigade] : []);
    const brigadeSupportBatPersonnel = brigadeArray.reduce((acc, brigade) => {
      const ns = brigade.nationalStandard ? NATIONAL_STANDARDS[brigade.nationalStandard] : null;
      const supportRatio = ns?.brigade?.support_ratio ?? 0.16; // NATO standard fallback
      return acc + Math.round((brigade.strength || 0) * supportRatio);
    }, 0);

    const threatLevel = opParams?.threat_level || 'PEER';
    const threatMod = threatLevel === 'PEER' ? 2.0 : threatLevel === 'NEAR_PEER' ? 1.5 : 1.0;

    // 2026-10-13 (Tab 2 plan 7.5, 7.6): the rear and support pool follows the same threat factors as the rear battalions: artillery threat
    // and insurgent activity (rearThreatFactor) and the technology environment (drones: rear_area_multiplier). The brigade support
    // battalion is INSIDE brigade.strength (its casualties are the rear battalion casualties of the brigade): it is not counted a second
    // time in this pool (switch SUPPORT_POOL_COUNTS_BRIGADE_SUPPORT).
    const _poolTa = opParams?.threat_assessment || {};
    const _poolThreat = THREAT_EFFECTS_USE ? rearThreatFactor(_poolTa).value : 1.0;
    const _poolTechKey = force?.force_quality?.technology_env;
    const _poolTech = (_poolTechKey && TECHNOLOGY_ENVIRONMENT[_poolTechKey]) ? TECHNOLOGY_ENVIRONMENT[_poolTechKey].rear_area_multiplier : 1.0;
    const poolRearFactor = _poolThreat * _poolTech;

    // HQ internal support staff: 0.7%/day (admin/comms/medical within HQ)
    const hqSupportRate = 0.007 * poolRearFactor;
    const hqSupportCasualties = Math.round(supportPersonnel * hqSupportRate * threatMod * duration);

    // Brigade support battalion: same rear-area rate as echelon organic (DNBI-dominant)
    const echelonSupportRate = BASE_CASUALTY_RATE.daily * SUPPORT_ECHELON_CASUALTY_MULTIPLIER * poolRearFactor;
    const brigadeSupportBatCasualties = SUPPORT_POOL_COUNTS_BRIGADE_SUPPORT ? Math.round(brigadeSupportBatPersonnel * echelonSupportRate * threatMod * duration) : 0;

    // ENH-018-C: Echelon organic support troops (signal/log/engineer/fires/aviation brigades)
    // Rear-area units — lower battle casualty exposure than HQ staff, DNBI-dominant.
    // Rate derived from constants: BASE_CASUALTY_RATE.daily × SUPPORT_ECHELON_CASUALTY_MULTIPLIER
    // = 0.015 × 0.20 = 0.003/day. Do NOT hardcode 0.003 — derive so rate scales with doctrine updates.
    // Distribution sourced from SUPPORT_ECHELON_CASUALTY_DISTRIBUTION (casualtyCalculatorConstants.js).
    const echelonSupportCasualties = Math.round(echelonOrganicSupport * echelonSupportRate * threatMod * duration);

    const totalCasualties = hqSupportCasualties + brigadeSupportBatCasualties + echelonSupportCasualties;
    const totalPersonnel = supportPersonnel + brigadeSupportBatPersonnel + echelonOrganicSupport;

    // Blended casualty distribution (weighted by population)
    // HQ support: 10% KIA / 5% DOW / 45% WIA / 40% DNBI (inline — HQ-specific rates)
    // Brigade support battalion + echelon organic: SUPPORT_ECHELON_CASUALTY_DISTRIBUTION (DNBI-dominant)
    const dist = SUPPORT_ECHELON_CASUALTY_DISTRIBUTION;
    const kia = Math.round(hqSupportCasualties * 0.10)
      + Math.round(brigadeSupportBatCasualties * dist.kia)
      + Math.round(echelonSupportCasualties * dist.kia);
    const dow = Math.round(hqSupportCasualties * 0.05)
      + Math.round(brigadeSupportBatCasualties * dist.dow)
      + Math.round(echelonSupportCasualties * dist.dow);
    const wia = Math.round(hqSupportCasualties * 0.45)
      + Math.round(brigadeSupportBatCasualties * dist.wia)
      + Math.round(echelonSupportCasualties * dist.wia);
    const dnbi = Math.round(hqSupportCasualties * 0.40)
      + Math.round(brigadeSupportBatCasualties * dist.dnbi)
      + Math.round(echelonSupportCasualties * dist.dnbi);

    // ENH-018-C: surgical load for echelon support WIA stream
    // SUPPORT_ECHELON_SURGICAL_FRACTION (0.60) > SURGICAL_RATE_OVERALL (0.40):
    // precision-fire / drone-strike wounds dominate CSA/DSA WIA — higher surgical requirement.
    const echelonWia = Math.round(brigadeSupportBatCasualties * dist.wia) + Math.round(echelonSupportCasualties * dist.wia);
    const echelonSurgicalCases = Math.round(echelonWia * SUPPORT_ECHELON_SURGICAL_FRACTION);

    return {
      personnel: totalPersonnel,                   // total support population
      hq_support_personnel: supportPersonnel,      // HQ internal staff
      brigade_support_personnel: brigadeSupportBatPersonnel, // BUG-SUPPORT-001: support battalions
      echelon_support_personnel: echelonOrganicSupport,  // corps/div organic units
      total: totalCasualties,
      kia,
      dow,
      wia,
      dnbi,
      // Sub-totals for Tab5 display
      hq_support_casualties: hqSupportCasualties,
      echelon_support_casualties: echelonSupportCasualties,
      // ENH-018-C: surgical load and evacuation routing flag
      echelon_surgical_cases: echelonSurgicalCases,
      echelon_bypasses_role2b: SUPPORT_ECHELON_BYPASSES_ROLE_2B
    };
  }

  /**
   * TASK 3.1: Calculate Corps and Division DNBI using operational_state
   * 
   * Each echelon (Corps, Divisions) has its own operational_state which determines
   * DNBI rate. This function calculates DNBI for non-brigade echelons.
   * 
   * @param {Object} force - Force structure with forceBuilderResult
   * @param {number} duration - Operation duration in days
   * @returns {Object} Echelon DNBI breakdown
   */
  /**
   * Assess MASCAL status
   */
  assessMASCALStatus(medicalConfig, medevacAnalysis) {
    const metrics = {
      mtf_utilization: medevacAnalysis?.capacity_utilization_percent || 50,
      surgical_backlog: medicalConfig?.surgical_config?.backlog?.hours || 0,
      blood_dos: resolveInput(medicalConfig?.blood_config?.days_of_supply, 'blood_days_of_supply', { zeroIsMissing: true }),
      golden_hour_percent: medevacAnalysis?.golden_hour_analysis?.blended_gh_achievement_percent || 60
    };

    if (evaluateMASCALStatus) {
      return evaluateMASCALStatus(metrics);
    }

    // Phase 6: Read user-configured thresholds; fall back to MASCAL_TRIGGERS constants.
    const userThresholds = medicalConfig?.mascal_config?.thresholds;
    const tMtfCritical  = userThresholds?.mtf_critical          ?? MASCAL_TRIGGERS?.mtf_utilization?.critical      ?? 95;
    const tMtfWarning   = userThresholds?.mtf_warning            ?? MASCAL_TRIGGERS?.mtf_utilization?.warning       ?? 80;
    const tSurgCritical = userThresholds?.surgical_queue_critical ?? MASCAL_TRIGGERS?.surgical_queue_hours?.critical ?? 8;
    const tSurgWarning  = userThresholds?.surgical_queue_warning  ?? MASCAL_TRIGGERS?.surgical_queue_hours?.warning  ?? 4;

    // Fallback MASCAL assessment using configurable thresholds
    if (metrics.mtf_utilization > tMtfCritical || metrics.blood_dos < 1 || metrics.surgical_backlog > tSurgCritical) {
      return { level: 'MASCAL_2', color: 'RED', description: 'Critical capacity exceeded' };
    } else if (metrics.mtf_utilization > tMtfWarning || metrics.blood_dos < 2 || metrics.surgical_backlog > tSurgWarning) {
      return { level: 'MASCAL_1', color: 'ORANGE', description: 'Capacity stressed' };
    } else if (metrics.mtf_utilization > Math.round(tMtfWarning * 0.75)) {
      return { level: 'ELEVATED', color: 'YELLOW', description: 'Elevated patient load' };
    }
    return { level: 'NORMAL', color: 'GREEN', description: 'Normal operations' };
  }

  /**
   * Calculate golden hour summary
   * CC-004: Enhanced to include cumulative POIÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã‚Â ÃƒÂ¢Ã¢â€šÂ¬Ã¢â€žÂ¢Surgery tracking
   */
  calculateGoldenHourSummary(brigadeResults) {
    let totalAchieved = 0;
    let totalNotAchieved = 0;
    let cumulativeAchieved = 0;
    let cumulativeNotAchieved = 0;
    let totalCumulativeTime = 0;
    let brigadeCount = 0;

    for (const brigade of brigadeResults) {
      // Legacy single-leg tracking
      totalAchieved += brigade.golden_hour?.days_achieved || 0;
      totalNotAchieved += brigade.golden_hour?.days_not_achieved || 0;
      
      // CC-004: Cumulative POIÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã‚Â ÃƒÂ¢Ã¢â€šÂ¬Ã¢â€žÂ¢Surgery tracking
      cumulativeAchieved += brigade.golden_hour?.cumulative_days_achieved || 0;
      cumulativeNotAchieved += brigade.golden_hour?.cumulative_days_not_achieved || 0;
      
      if (brigade.golden_hour?.average_cumulative_time_to_surgery) {
        totalCumulativeTime += brigade.golden_hour.average_cumulative_time_to_surgery;
        brigadeCount++;
      }
    }

    const total = totalAchieved + totalNotAchieved;
    const cumulativeTotal = cumulativeAchieved + cumulativeNotAchieved;
    
    return {
      // Legacy metrics (single-leg)
      days_achieved: totalAchieved,
      days_not_achieved: totalNotAchieved,
      achievement_percent: total > 0 ? Math.round((totalAchieved / total) * 100) : 0,
      
      // CC-004: Cumulative POIÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã‚Â ÃƒÂ¢Ã¢â€šÂ¬Ã¢â€žÂ¢Surgery metrics
      cumulative_days_achieved: cumulativeAchieved,
      cumulative_days_not_achieved: cumulativeNotAchieved,
      cumulative_achievement_percent: cumulativeTotal > 0 ? 
        Math.round((cumulativeAchieved / cumulativeTotal) * 100) : 0,
      average_time_to_surgery_min: brigadeCount > 0 ? 
        Math.round(totalCumulativeTime / brigadeCount) : 0,
      
      // CC-004: Assessment
      assessment: cumulativeTotal > 0 && cumulativeAchieved / cumulativeTotal >= 0.5 ?
        'ADEQUATE' : 'CRITICAL',
      warning: cumulativeTotal > 0 && cumulativeAchieved / cumulativeTotal < 0.5 ?
        'Golden Hour compliance below 50% - consider forward surgical capability' : null
    };
  }

  /**
   * Calculate overall golden hour percentage
   */
  calculateOverallGoldenHour(brigadeResults) {
    const summary = this.calculateGoldenHourSummary(brigadeResults);
    return summary.achievement_percent;
  }

  /**
   * Calculate total medical requirements
   */
  calculateTotalMedicalRequirements(brigadeResults) {
    const totals = {
      blood: { prbc: 0, ffp: 0, plt: 0, cryo: 0 },
      surgical: { hours: 0, cases: 0 },
      // PHASE 1.1: WBB summary tracking
      // BUG-109 FIX: days_activated used a plain array with .push() inside a per-brigade
      // loop, producing one entry per brigade per activated day (e.g. 16 brigades × 20 days
      // = up to 320 entries instead of 20). Root cause: the loop accumulates across all
      // brigades so each day index appears once per brigade that was active that day.
      // Fix: use a Set of day indices for deduplication; convert to sorted string array
      // after the loop. total_activations is now unique calendar days (any brigade active),
      // not brigade×day count — consistent with how the Tab5 card reads it.
      wbb: {
        total_units_contributed: 0,
        total_universal_units: 0,
        _activatedDayIndices: new Set() // internal — converted to days_activated after loop
      }
    };

    for (const brigade of brigadeResults) {
      totals.blood.prbc += brigade.totals.blood_consumed || 0;
      totals.surgical.hours += brigade.totals.surgical_hours || 0;
      
      // PHASE 1.1: Aggregate WBB data from all days
      if (brigade.by_day) {
        brigade.by_day.forEach((dayData, idx) => {
          if (dayData.resources?.wbb_status === 'ACTIVATED') {
            // BUG-109 FIX: accumulate units across all brigades (correct — additive)
            // but deduplicate day index (a day is activated once regardless of brigade count)
            totals.wbb.total_units_contributed += dayData.resources.wbb_contribution || 0;
            totals.wbb.total_universal_units += dayData.resources.wbb_universal_units || 0;
            totals.wbb._activatedDayIndices.add(idx);
          }
        });
      }
    }

    // BUG-109 FIX: Convert Set → sorted string array; derive total_activations from unique days
    totals.wbb.days_activated = [...totals.wbb._activatedDayIndices]
      .sort((a, b) => a - b)
      .map(i => `Day ${i + 1}`);
    totals.wbb.total_activations = totals.wbb.days_activated.length;
    delete totals.wbb._activatedDayIndices; // internal — do not expose in output

    // Estimate other blood products from PRBC
    totals.blood.ffp = Math.round(totals.blood.prbc * 0.8);
    totals.blood.plt = Math.round(totals.blood.prbc * 0.3);
    totals.blood.cryo = Math.round(totals.blood.prbc * 0.1);
    totals.blood.total_units = totals.blood.prbc + totals.blood.ffp + 
                                totals.blood.plt + totals.blood.cryo;

    // Estimate surgical cases
    totals.surgical.cases = Math.round(totals.surgical.hours / 2);

    return totals;
  }

  /**
   * Generate recommendations
   */
  generateRecommendations(results, medevacAnalysis, mascalStatus) {
    const recs = [];

    // MEDEVAC recommendations
    if (medevacAnalysis?.recommendations) {
      recs.push(...medevacAnalysis.recommendations);
    }

    // MASCAL recommendations
    if (mascalStatus?.level !== 'NORMAL') {
      recs.push(`MASCAL ${mascalStatus.level}: ${mascalStatus.description}`);
    }

    // Golden hour recommendations
    const ghPercent = results.medical_system_analysis?.overall_golden_hour_percent || 0;
    if (ghPercent < 50) {
      recs.push('Golden Hour achievement <50% - consider forward MTF positioning');
    }

    // Strength recommendations
    for (const brigade of results.by_brigade) {
      if (brigade.end_state?.strength_percent < 50) {
        recs.push(`${brigade.brigade_id}: Consider reconstitution (${brigade.end_state.strength_percent}% remaining)`);
      }
    }

    return recs;
  }

  /**
   * Generate warnings
   */
  generateWarnings(results, medevacAnalysis) {
    const warnings = [];

    // Check for critical states
    for (const brigade of results.by_brigade) {
      if (!brigade.end_state?.combat_effective) {
        warnings.push(`CRITICAL: ${brigade.brigade_id} combat ineffective`);
      }
    }

    // Check surgical backlog
    const maxBacklog = Math.max(...results.by_brigade.map(b => b.end_state?.surgical_backlog || 0));
    if (maxBacklog > 20) {
      warnings.push(`CRITICAL: Surgical backlog of ${maxBacklog} cases`);
    }

    // Check Blue Light
    if (medevacAnalysis?.blue_light?.should_activate) {
      warnings.push('Blue Light Protocol recommended - helicopter MEDEVAC degraded');
    }

    return warnings;
  }

  /**
   * Validate scenario
   */
  validateScenario(scenario) {
    const errors = [];

    if (!scenario) {
      errors.push('Scenario is required');
      return { valid: false, errors };
    }

    // v5.4.0: Check brigade data exists - defensive lookup across multiple locations
    // BUG-194 FIX: Added third path for BRIGADE echelon — singular result.brigade wrapped in array.
    // At BRIGADE echelon force.brigades[] and force.forceBuilderResult.brigades[] are both absent;
    // the top-level unit lives in force.forceBuilderResult.brigade (singular).
    const _fbr = scenario.force?.forceBuilderResult;
    const brigadeConfigs =
      Array.isArray(scenario.force?.brigades) && scenario.force.brigades.length > 0
        ? scenario.force.brigades
        : Array.isArray(_fbr?.brigades) && _fbr.brigades.length > 0
          ? _fbr.brigades
          : _fbr?.brigade
            ? [_fbr.brigade]   // BRIGADE echelon — wrap singular in array
            : null;
    if (!Array.isArray(brigadeConfigs) || brigadeConfigs.length === 0) {
      errors.push('At least one brigade required (force.brigades or force.forceBuilderResult.brigades must be an array)');
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }

  /**
   * Build scenario summary
   */
  buildScenarioSummary(scenario) {
    // v5.4.0: Defensive brigade lookup - check multiple locations
    // BUG-194 FIX: Added BRIGADE echelon singular path (forceBuilderResult.brigade).
    const _fbrSummary = scenario.force?.forceBuilderResult;
    const brigadeConfigs =
      Array.isArray(scenario.force?.brigades) && scenario.force.brigades.length > 0
        ? scenario.force.brigades
        : Array.isArray(_fbrSummary?.brigades) && _fbrSummary.brigades.length > 0
          ? _fbrSummary.brigades
          : _fbrSummary?.brigade
            ? [_fbrSummary.brigade]
            : [];
    
    return {
      duration_days: scenario.operation_parameters?.duration_days,  // v5.3.0: No fallback - validated at Line 5191
      brigade_count: brigadeConfigs.length,
      total_strength: brigadeConfigs.reduce(
        (sum, b) => sum + normalizeStrength(b.strength, 4500), 0
      ),
      mission_type: scenario.operation_parameters?.mission_type || 'DEFENSE',
      threat_level: scenario.operation_parameters?.threat_level || 'PEER',
      environment: scenario.environment || {}
    };
  }

  /**
   * Run with Monte Carlo
   */
  calculateWithMonteCarlo(scenario, iterations = 10000, deterministicResult = null) {
    return runMonteCarloSimulation({
      calculateFn: (s) => {
        // INC-003: console suppression noop removed — all debug console.log calls were
        // cleaned up in v4.3.5 and v5.34.1; remaining console.warn calls are legitimate
        // and must surface during MC iterations (not silenced).
        return this.calculateCasualties(s);
      },
      baseScenario: scenario,
      iterations,
      trackParameters: true,
      deterministicResult  // p50 guaranteed = deterministic calculation
    });
  }
}

// ============================================================================
// FACTORY FUNCTION
// ============================================================================

/**
 * Create a new CasualtyCalculator instance
 */
export function createCasualtyCalculator() {
  return new CasualtyCalculator();
}

/**
 * Calculate casualties (convenience function)
 * @param {Object} scenario - Scenario configuration
 * @param {Object} options - Calculation options (e.g., { final: true } for Monte Carlo)
 */
export function calculateCasualties(scenario, options) {
  const calculator = new CasualtyCalculator();
  return calculator.calculateCasualties(scenario, options);
}

/**
 * Calculate with Monte Carlo (convenience function)
 */
export function calculateCasualtiesWithMonteCarlo(scenario, iterations = 10000) {
  const calculator = new CasualtyCalculator();
  return calculator.calculateWithMonteCarlo(scenario, iterations);
}

// ============================================================================
// EXPORTS
// ============================================================================

export { CasualtyCalculator };
// 2026-10-18 (Tab 3 review): exported for tests
export { bloodStockPrbc, bloodStockPercent, surgicalTeamsFromTab3, resolveBloodStock };

// TCCC-2025 Summary export for Tab 5 reports
export { getTCCC2025Summary };

// CC-004 to CC-007: Re-export module utilities for Tab 5 reports
export { 
  calculateSupplyChain,
  getSupplyStatusColor,
  getSupplyStatusText,
  formatG4Request,
  DOS_THRESHOLDS
} from './modules/supplyChainModule.js';

export { 
  calculateStrengthProjection,
  getEffectivenessRating,
  formatG1Request,
  STRENGTH_EFFECTIVENESS,
  REPLACEMENT_THRESHOLDS
} from './modules/strengthProjectionModule.js';

export { 
  calculateTimeToSurgery,
  calculateAllTimesToSurgery,
  getGoldenHourSummary,
  recommendFSTAttachments,
  GOLDEN_HOUR_THRESHOLDS,
  SURGICAL_TEAMS
} from './modules/timeToSurgeryModule.js';

export { 
  calculateMethodBasedAssets,
  calculateAllFacilityAssets,
  getEchelonCapacitySummary,
  EVACUATION_METHODS,
  AUTO_THRESHOLDS
} from './modules/methodBasedAssetsModule.js';

// CC-007: Re-export CASEVAC constants from evacuationAssetFactors.js for Tab 5 reports
export {
  CASEVAC_CONFIGURATION,
  CASEVAC_DOW_PENALTIES,
  AUTO_METHOD_THRESHOLDS,
  PUSH_DISTANCE_RATIOS
} from '../constants/evacuationAssetFactors.js';

// CC-007: Export internal CASEVAC_CONFIG for backward compatibility
export { CASEVAC_CONFIG };

// PHASE 4.5.5: Export injury mechanism configuration for Tab 4 integration
export { 
  BLOOD_BY_MECHANISM_CONFIG,
  getInjuryMechanismDistribution,
  calculateBloodByMechanism
};

const CasualtyCalculatorModule = {
  CasualtyCalculator,
  createCasualtyCalculator,
  calculateCasualties,
  calculateCasualtiesWithMonteCarlo,
  getTCCC2025Summary
};

export default CasualtyCalculatorModule;
